/**
 * Throws an exception with given message. If you need to handle all errors in one place, redefined this method to your needed handler.
 * @param error
 */
const error = function (error) {
  if (isString(error)) {
    throw new Error(error);
  } else {
    throw error;
  }
};
const warn = function (error) {
  // eslint-disable-next-line no-console
  console.warn(error);
};
/**
 * Calls the 'func' callback in 'interval' ms after a series of subsequent function calls.
 * @param func - callback function
 * @param interval - interval in ms
 */

const debounce = function (func, interval) {
  let timer = null;
  return function () {
    if (timer) clearTimeout(timer);
    let args = arguments;
    timer = window.setTimeout(function () {
      timer = null;
      func.apply(this, args);
    }, interval);
  };
};
/**
 * Throttle function calls
 * @param func - callback function
 * @param interval - interval
 */

const throttle = function (func, interval) {
  let isThrottled = false;
  let savedArgs;
  let savedThis;

  function wrapper() {
    if (isThrottled) {
      savedArgs = arguments;
      savedThis = this;
      return;
    }

    func.apply(this, arguments);
    isThrottled = true;
    setTimeout(function () {
      isThrottled = false;

      if (savedArgs) {
        wrapper.apply(savedThis, savedArgs);
        savedArgs = savedThis = null;
      }
    }, interval);
  }

  return wrapper;
};
/**
 * Returns true if n is a finite number, otherwise false
 * @param n
 */

const isNumber = function (n) {
  return typeof n === 'number' && isFinite(n);
};
/**
 * Returns true if n is an integer number, otherwise false
 * @param n
 */

const isInteger = function (n) {
  return isNumber(n) && Math.round(n) === n;
};
/**
 * Returns true if s is a string, otherwise false
 * @param s
 */

const isString = function (s) {
  return typeof s === 'string';
};
/**
 * Returns a random GUID
 */

const getGuid = function () {
  //noinspection SpellCheckingInspection
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    let r = Math.random() * 16 | 0;
    let v = c == 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
/**
 * Returns true if at least one element of arr1 also exists in arr2
 * @param arr1
 * @param arr2
 */

const arrayIntersect = function (arr1, arr2) {
  for (let i = 0; i < arr1.length; i++) {
    if (arr2.indexOf(arr1[i]) !== -1) {
      return true;
    }
  }

  return false;
};
/**
 * Makes a deep copy af the array
 * @param arr
 */

const copyArray = function (arr) {
  let copy = [];

  for (let i = 0, l = arr.length; i < l; i++) {
    if (Array.isArray(arr[i])) {
      copy[i] = copyArray(arr[i]);
    } else {
      copy[i] = arr[i];
    }
  }

  return copy;
};
/**
 * Makes a deep copy of an object
 * @param obj
 * TODO: this will not copy the inner arrays properly
 */

const copyObject = function (obj) {
  if (!(obj instanceof Function) && obj instanceof Object) {
    let copy = Array.isArray(obj) ? [] : {};
    let keys = Object.keys(obj);

    for (let i = 0; i < keys.length; i++) {
      copy[keys[i]] = copyObject(obj[keys[i]]);
    }

    return copy;
  } else {
    return obj;
  }
};
/**
 * Creates a new <style> node and adds it to the document head section.
 * @param desc
 */

const setCssClasses = function (desc) {
  let classes = Object.keys(desc).map(key => {
    return getCssText(key, desc[key]);
  });
  setStyleNode(classes.join('\n'));
};

const getCssText = function (className, styles) {
  return '.' + className + '{' + styles + '}';
};
/**
 * Creates a new <style> node with the given code and adds it to the document head.
 * @param text
 */


const setStyleNode = function (text) {
  let node = document.createElement('style');
  node.type = 'text/css';

  if (node.styleSheet) {
    node.styleSheet.cssText = text;
  } else {
    node.appendChild(document.createTextNode(text));
  }

  document.head.appendChild(node);
};
/**
 * Contains the name of the current browser.
 */

const browser = /*#__PURE__*/function () {
  let ua = navigator.userAgent,
      tem,
      M = /*#__PURE__*/ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

  if (/trident/i.test(M[1])) {
    tem = /*#__PURE__*/ /\brv[ :]+(\d+)/g.exec(ua) || [];
    return 'IE ' + (tem[1] || '');
  }

  if (M[1] === 'Chrome') {
    tem = /*#__PURE__*/ua.match(/\bOPR\/(\d+)/);
    if (tem != null) return 'Opera ' + tem[1];
  }

  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
  if ((tem = /*#__PURE__*/ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);
  return M.join(' ');
}();
/**
 * Contains 'true' if the current browser is Internet Explorer of any version.
 */

const isIE = /*#__PURE__*/browser.search('IE') !== -1;
/**
 * Contains 'true' if the current browser is Chrome of any version.
 */

const isChrome = /*#__PURE__*/browser.search('Chrome') !== -1;
/**
 * Contains 'true' if the current browser is Opera of any version.
 */

const isOpera = /*#__PURE__*/browser.search('Opera') !== -1;
/**
 * Contains 'true' if the current browser is run on a touch device.
 */

const isTouch = ('ontouchstart' in document.documentElement);
/* Simple polyfil for tests */

if (typeof window.requestAnimationFrame === 'undefined') {
  window.requestAnimationFrame = callback => {
    setTimeout(callback, 1000 / 30);
    return 0;
  };
}
/**
 * async loadImage function
 * @param image
 * @param src
 * @param signal
 */


function loadImage(image, src, signal) {
  if (signal && !src.includes(";base64")) {
    return fetch(src, {
      signal
    }).then(res => res.blob()).then(data => {
      const url = URL.createObjectURL(data);
      image.src = url;
    });
  }

  image.src = src;
  return new Promise((res, rej) => {
    if (image.complete) {
      return res();
    }

    image.onload = () => res();

    image.onerror = error => rej(error);
  });
}
const getParameterValue = value => {
  if (value === null || value === void 0 ? void 0 : value.defaultValue) {
    return value.defaultValue;
  }

  return value;
};

var utilsModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  error: error,
  warn: warn,
  debounce: debounce,
  throttle: throttle,
  isNumber: isNumber,
  isInteger: isInteger,
  isString: isString,
  getGuid: getGuid,
  arrayIntersect: arrayIntersect,
  copyArray: copyArray,
  copyObject: copyObject,
  setCssClasses: setCssClasses,
  setStyleNode: setStyleNode,
  browser: browser,
  isIE: isIE,
  isChrome: isChrome,
  isOpera: isOpera,
  isTouch: isTouch,
  loadImage: loadImage,
  getParameterValue: getParameterValue
});

var MouseEventFlags;

(function (MouseEventFlags) {
  MouseEventFlags[MouseEventFlags["None"] = 0] = "None";
  MouseEventFlags[MouseEventFlags["MouseDown"] = 2] = "MouseDown";
  MouseEventFlags[MouseEventFlags["MouseUp"] = 4] = "MouseUp";
  MouseEventFlags[MouseEventFlags["MouseClick"] = 8] = "MouseClick";
  MouseEventFlags[MouseEventFlags["MouseMove"] = 16] = "MouseMove";
  MouseEventFlags[MouseEventFlags["MouseOver"] = 32] = "MouseOver";
  MouseEventFlags[MouseEventFlags["MouseOut"] = 64] = "MouseOut";
  MouseEventFlags[MouseEventFlags["DoubleClick"] = 128] = "DoubleClick";
  MouseEventFlags[MouseEventFlags["DragStart"] = 256] = "DragStart";
  MouseEventFlags[MouseEventFlags["Drag"] = 512] = "Drag";
  MouseEventFlags[MouseEventFlags["DragEnd"] = 1024] = "DragEnd";
})(MouseEventFlags || (MouseEventFlags = {}));

const mouseEvents = {
  click: {
    type: 'click',
    flag: MouseEventFlags.MouseClick
  },
  doubleClick: {
    type: 'dblclick',
    flag: MouseEventFlags.DoubleClick
  },
  mouseDown: {
    type: 'mousedown',
    flag: MouseEventFlags.MouseDown
  },
  mouseUp: {
    type: 'mouseup',
    flag: MouseEventFlags.MouseUp
  },
  mouseOver: {
    type: 'mouseover',
    flag: MouseEventFlags.MouseOver
  },
  mouseOut: {
    type: 'mouseout',
    flag: MouseEventFlags.MouseOut
  },
  mouseMove: {
    type: 'mousemove',
    flag: MouseEventFlags.MouseMove
  },
  dragStart: {
    type: 'dragStart',
    flag: MouseEventFlags.DragStart
  },
  drag: {
    type: 'drag',
    flag: MouseEventFlags.Drag
  },
  dragEnd: {
    type: 'dragEnd',
    flag: MouseEventFlags.DragEnd
  }
};
const eventTypeFlags = {};
Object.keys(mouseEvents).forEach(eventName => {
  eventTypeFlags[mouseEvents[eventName].type] = mouseEvents[eventName].flag;
});
/**
 * Base of all sGis library events
 */

class sGisEvent {
  /**
   * @param type - name of the event
   */
  constructor(type) {
    this._cancelPropagation = false;
    this.type = type;
  }
  /**
   * Prevents any further event handlers to be called for this event.
   */


  stopPropagation() {
    this._cancelPropagation = true;
  }
  /**
   * Whether the .stopPropagation() method has been called for this event.
   */


  get isCanceled() {
    return this._cancelPropagation;
  }

}
/**
 * Provides methods for handling events.
 * @alias sGis.EventHandler
 */

class EventHandler {
  constructor() {
    // This initialization makes the properties not enumerable and guaranties a valid value is there at all times
    Object.defineProperty(this, '_eventHandlers', {
      value: {}
    });
    Object.defineProperty(this, '_prohibitedEvents', {
      value: []
    });
    Object.defineProperty(this, 'eventFlags', {
      value: MouseEventFlags.None,
      writable: true
    });
  }
  /**
   * Triggers event with the given parameters. It is supposed to be used to transfer event from one object to another (for example, from layer to a feature).
   * @param event - event object of the original event
   */


  forwardEvent(event) {
    if (this._prohibitedEvents && this._prohibitedEvents.indexOf(event.type) !== -1) return;
    let eventType = event.type;

    if (this._eventHandlers && this._eventHandlers[eventType]) {
      let handlerList = copyArray(this._eventHandlers[eventType]); //This is needed in case one of the handlers is deleted in the process of handling

      for (let i = 0; i < handlerList.length; i++) {
        if (handlerList[i].oneTime) {
          let currentIndex = this._eventHandlers[eventType].indexOf(handlerList[i]);

          this._eventHandlers[eventType].splice(currentIndex, 1);
        }

        handlerList[i].handler.call(this, event);
        if (event.isCanceled) break;
      }
    }
  }
  /**
   * Triggers the event of the given type. Each handler will be triggered one by one in the order they were added. Returns
   * the event object, or null in case the event was not triggered (if it is prohibited).
   * @param event - event object or exact name of the event to be triggered.
   * @param parameters - [JS ONLY] parameters to be transferred to the event object. Applied only if the first argument is string.
   */


  fire(event, parameters) {
    if (typeof event === 'string') {
      event = new sGisEvent(event);
      if (parameters) Object.assign(event, parameters);
      event.sourceObject = this;
    }

    if (this._prohibitedEvents.indexOf(event.type) !== -1) return null;
    this.forwardEvent(event);
    return event;
  }
  /**
   * Sets a listener for the given event type.
   * @param description - description of the event. Can contain any number of type names and namespaces (namespaces start with .), but must have at least one of either..
   * @param handler - handler to be executed. The handler is called in the event source object context.
   * @param oneTime - if set to true, the event will be triggered only once.
   */


  addListener(description, handler, oneTime = false) {
    let types = getTypes(description);
    let namespaces = getNamespaces(description);
    if (types.length === 0) error('No event types are specified.');
    if (!handler) error('No handler is given.');

    for (let i = 0; i < types.length; i++) {
      if (!this._eventHandlers[types[i]]) this._eventHandlers[types[i]] = [];

      this._eventHandlers[types[i]].push({
        handler: handler,
        namespaces: namespaces,
        oneTime
      });

      if (eventTypeFlags[types[i]]) this.eventFlags = this.eventFlags | eventTypeFlags[types[i]];
    }
  }
  /**
   * Sets a one time handler for the given event. This handler is removed from the list of handlers just before it is called.
   * @param description - description of the event. Can contain <u>ONLY ONE EVENT TYPE</u> and any number of namespaces (namespaces start with .).
   * @param handler - handler to be executed. The handler is called in the event source object context.
   */


  once(description, handler) {
    this.addListener(description, handler, true);
  }
  /**
   * Removes the given handlers from the event listener list.
   * @param description - description of the event. Can contain any number of type names and namespaces, but must have at least one of either.
   * @param handler - handler to be removed. If no handler is specified, all handlers from the given namespaces will be removed. If no handler and namespace are specified, error will be thrown.
   */


  removeListener(description, handler) {
    let types = getTypes(description);
    let namespaces = getNamespaces(description);
    if (types.length === 0) types = Object.keys(this._eventHandlers);

    for (let i = 0; i < types.length; i++) {
      if (this._eventHandlers[types[i]]) {
        for (let j = this._eventHandlers[types[i]].length - 1; j >= 0; j--) {
          if ((namespaces === null || namespaces.length === 0 || arrayIntersect(this._eventHandlers[types[i]][j].namespaces, namespaces)) && (!handler || this._eventHandlers[types[i]][j].handler === handler)) {
            this._eventHandlers[types[i]].splice(j, 1);
          }
        }

        if (this._eventHandlers[types[i]].length === 0 && eventTypeFlags[types[i]]) {
          this.eventFlags = this.eventFlags & ~eventTypeFlags[types[i]];
        }
      }
    }
  }
  /**
   * Prohibits triggering of the event. The prohibitions are stacked - if the same event is prohibited N times, you need to allow it N times to make it work.
   * @param type - name of the event to be prohibited.
   */


  prohibitEvent(type) {
    this._prohibitedEvents.push(type);
  }
  /**
   * Allows a previously prohibited event. The prohibitions are stacked - if the same event is prohibited N times, you need to allow it N times to make it work. If no prohibitions were set for the event, the operation is ignored.
   * @param type - name of the event to be allowed.
   */


  allowEvent(type) {
    let index = this._prohibitedEvents.indexOf(type);

    if (index !== -1) this._prohibitedEvents.splice(index, 1);
  }
  /**
   * Checks if the object has the handler for the given event type.
   * @param type - name of the event.
   * @param handler - handler to be checked
   */


  hasListener(type, handler) {
    if (this._eventHandlers[type]) {
      for (let i = 0; i < this._eventHandlers[type].length; i++) {
        if (this._eventHandlers[type][i].handler === handler) return true;
      }
    }

    return false;
  }
  /**
   * Checks if the object has any handlers corresponding to the following description.
   * @param description - description of the event. Can contain any number of type names and namespaces (namespaces start with .), but must have at least one of either.
   * @returns true if the object has at least one handler of the given types with the given namespaces. If no event type is given, checks if there are any handlers in the given namespaces exist. If no namespace is given, the namespace check is ignored.
   */


  hasListeners(description) {
    let types = getTypes(description);
    let namespaces = getNamespaces(description);
    if (types.length === 0) types = Object.keys(this._eventHandlers);

    for (let i = 0; i < types.length; i++) {
      if (this._eventHandlers[types[i]] && this._eventHandlers[types[i]].length > 0) {
        if (namespaces.length > 0) {
          for (let j = 0; j < this._eventHandlers[types[i]].length; j++) {
            if (arrayIntersect(this._eventHandlers[types[i]][j].namespaces, namespaces)) {
              return true;
            }
          }
        } else {
          return true;
        }
      }
    }

    return false;
  }
  /**
   * Removes all event listeners from the object.
   */


  removeAllListeners() {
    Object.keys(this._eventHandlers).forEach(key => delete this._eventHandlers[key]);
  }
  /**
   * @see EventHandler#addListener
   */


  on(description, handler, oneTime = false) {
    this.addListener.apply(this, arguments);
  }
  /**
   * @see EventHandler#removeListener
   */


  off(description, handler) {
    this.removeListener.apply(this, arguments);
  }

}

function getTypes(string) {
  return string.replace(/\.[A-Za-z0-9_-]+/g, '').match(/[A-Za-z0-9_-]+/g) || [];
}

function getNamespaces(string) {
  return string.match(/\.[A-Za-z0-9_-]+/g) || [];
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * Some property of the layer has been changed
 * @event PropertyChangeEvent
 */

class PropertyChangeEvent extends sGisEvent {
  /**
   * @param propertyName - ame of the property that has been changed
   */
  constructor(propertyName) {
    super(PropertyChangeEvent.type);
    this.property = propertyName;
  }

}
PropertyChangeEvent.type = 'propertyChange';
/**
 * Visibility property of the layer has been changed.
 * @event VisibilityChangeEvent
 */

class VisibilityChangeEvent extends sGisEvent {
  constructor() {
    super(VisibilityChangeEvent.type);
  }

}
VisibilityChangeEvent.type = 'visibilityChange';
/**
 * Layer unable to be loaded.
 * @event LayerErrorEvent
 */

class LayerErrorEvent extends sGisEvent {
  constructor(error) {
    super(LayerErrorEvent.type);
    this.error = error;
  }

}
LayerErrorEvent.type = 'layerError';
/**
 * Base class for all map layers. A layer is a container for features, that is responsible for filter out (or create)
 * features for requested bbox and resolution.
 * @alias sGis.Layer
 */

class Layer extends EventHandler {
  /**
   * @param __namedParameters - properties to be set to the corresponding fields
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance
   */
  constructor({
    delayedUpdate = false,
    resolutionLimits = [-1, -1],
    opacity = 1,
    isDisplayed = true
  } = {}, extensions) {
    super();
    this.delayedUpdate = delayedUpdate;
    this._resolutionLimits = resolutionLimits;
    this._opacity = opacity;
    this._isDisplayed = isDisplayed;
    if (extensions) Object.assign(this, extensions);
  }
  /**
   * Whether the layer is drawn to map
   * @fires [[VisibilityChangeEvent]]
   */


  get isDisplayed() {
    return this._isDisplayed;
  }

  set isDisplayed(bool) {
    this._isDisplayed = bool;
    this.fire(new VisibilityChangeEvent());
  }
  /**
   * Return true if the layer is displayed and the resolution is inside the limits
   * @param resolution
   */


  checkVisibility(resolution) {
    return this._isDisplayed && (this.resolutionLimits[0] < 0 || resolution >= this.resolutionLimits[0]) && (this.resolutionLimits[1] < 0 || resolution <= this.resolutionLimits[1]);
  }
  /**
   * Makes the layer visible
   * @fires [[PropertyChangeEvent]]
   */


  show() {
    this.isDisplayed = true;
  }
  /**
   * Makes the layer invisible
   * @fires [[PropertyChangeEvent]]
   */


  hide() {
    this.isDisplayed = false;
  }
  /**
   * Opacity of the layer. It sets the opacity of all features in this layer. Valid values: [0..1].
   * @fires [[PropertyChangeEvent]]
   */


  get opacity() {
    return this.getOpacity();
  }

  set opacity(opacity) {
    this.setOpacity(opacity);
  }

  getOpacity() {
    return this._opacity;
  }

  setOpacity(opacity) {
    opacity = opacity < 0 ? 0 : opacity > 1 ? 1 : opacity;
    this._opacity = opacity;
    this.fire(new PropertyChangeEvent('opacity'));
  }
  /**
   * Min and max resolution between which the layer will be displayed. Negative values are treated as no limit.
   * @fires [[PropertyChangeEvent]]
   */


  get resolutionLimits() {
    return this._resolutionLimits;
  }

  set resolutionLimits(limits) {
    this._resolutionLimits = limits;
    this.fire(new PropertyChangeEvent('resolutionLimits'));
  }
  /**
   * Forces redrawing of the layer
   * @fires [[PropertyChangeEvent]]
   */


  redraw() {
    this.fire(new PropertyChangeEvent('content'));
  }

}

class Render {
  constructor() {
    this._listensFor = MouseEventFlags.None;
    this._eventHandler = null;
    this.contourIndex = -1;
    this.pointIndex = -1;
    this.updateNeeded = true;
    /**
     * Flag for custom feature render cache and render on separate canvas.
     */

    this.isComplex = false;
  }

  get listensFor() {
    return this._listensFor;
  }

  triggerEvent(event) {
    if (this._eventHandler) this._eventHandler(event);
  }

  listenFor(eventFlags, handler) {
    this._eventHandler = handler;
    this._listensFor = eventFlags;
  }

}
class StaticRender extends Render {}
class VectorRender extends StaticRender {
  drawOnCanvas(ctx, domPainter) {
    ctx.save();
    this.setupRender(ctx, domPainter);
    this.renderObject(ctx, domPainter);
    ctx.restore();
  }

}
class DynamicRender extends Render {
  constructor({
    node,
    update,
    redraw,
    onRender
  }) {
    super();
    this.node = node;
    this.update = update;
    this.onRender = onRender;
    this.redraw = redraw;
  }

  contains(position) {
    return false;
  }

}

class StaticImageRender extends StaticRender {
  constructor({
    src,
    width = 0,
    height = 0,
    onLoad = null,
    onLoadStart = null,
    onLoadEnd = null,
    onError = null,
    opacity = 1,
    offset = [0, 0]
  }) {
    super();
    this.offset = offset;
    this.onLoad = onLoad;
    this.onLoadStart = onLoadStart;
    this.onLoadEnd = onLoadEnd;
    this.onError = onError;
    this._opacity = opacity;
    this._width = width;
    this._height = height;
    this._src = src;
    this._aborted = false;
    this._complete = false;

    this._createNode();
  }

  _createNode() {
    this._node = new Image();
    if (this._width > 0) this._node.width = this._width;
    if (this._height > 0) this._node.height = this._height;
    this._node.style.opacity = this._opacity.toString();
    const controller = new AbortController();
    const signal = controller.signal;
    this._controller = controller;
    this.onLoadStart && this.onLoadStart();
    this.readyPromise = loadImage(this._node, this._src, signal);
    this.readyPromise.then(() => {
      this._complete = true;
      this.onLoad && this.onLoad();
    }).catch(error => {
      if (this._aborted) {
        return;
      }

      this.onError && this.onError(error);
    }).finally(() => {
      this.onLoadEnd && this.onLoadEnd();
    });
  }

  get node() {
    if (this._node) return this._node;

    this._createNode();

    return this._node;
  }

  get width() {
    return this._width;
  }

  get height() {
    return this._height;
  } // working only for base64 src


  get isReady() {
    return this._node && this._node.complete;
  }

  get error() {
    return this._node && (this._node.complete && this._node.src || this.isImageLoaded && this._node.parentElement) && this._node.naturalWidth === 0;
  }

  get opacity() {
    return parseFloat(this.node.style.opacity);
  }

  set opacity(value) {
    this._opacity = value;
    if (this.node) this.node.style.opacity = value.toString();
  }

  get isImageLoaded() {
    return this._complete;
  }

  deleteNode() {
    this._aborted = true;

    this._controller.abort();

    if (this._node) {
      this._node.src = "";
    }
  }

}

/**
 * New features are added to the feature layer
 * @event FeaturesAddEvent
 */

class FeaturesAddEvent extends sGisEvent {
  constructor(features) {
    super(FeaturesAddEvent.type);
    this.features = features;
  }

}
FeaturesAddEvent.type = 'featuresAdd';
/**
 * Some features were removed from the feature layer
 * @event FeaturesRemoveEvent
 */

class FeaturesRemoveEvent extends sGisEvent {
  constructor(features) {
    super(FeaturesRemoveEvent.type);
    this.features = features;
  }

}
FeaturesRemoveEvent.type = 'featuresRemove';
/**
 * A layer that contains arbitrary set of features.
 * @alias sGis.FeatureLayer
 */

class FeatureLayer extends Layer {
  /**
   * @param __namedParameters - properties to be set to the corresponding fields.
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance.
   */
  constructor(_a = {}, extensions) {
    var {
      delayedUpdate = true,
      features = []
    } = _a,
        layerParams = __rest(_a, ["delayedUpdate", "features"]);

    super(Object.assign({
      delayedUpdate
    }, layerParams), extensions);
    this._features = features;
  }

  getRenders(bbox, resolution, painter) {
    let renders = [];
    this.getFeatures(bbox, resolution).forEach(feature => {
      renders = renders.concat(feature.render(resolution, bbox.crs, painter));
      renders.forEach(render => {
        if (render instanceof StaticImageRender) {
          render.onLoad = () => {
            this.redraw();
          };
        }
      });
    });
    return renders;
  }

  getFeatures(bbox, resolution) {
    if (!this.checkVisibility(resolution)) return [];
    return this._features.filter(feature => feature.crs.canProjectTo(bbox.crs) && (feature.persistOnMap || feature.bbox.intersects(bbox)));
  }
  /**
   * Adds a feature or an array of features to the layer.
   * @param features - features to add.
   * @throws if one of the features is already in the layer.
   * @fires FeaturesAddEvent
   */


  add(features) {
    const toAdd = Array.isArray(features) ? features : [features];
    if (toAdd.length === 0) return;
    toAdd.forEach(f => {
      if (this._features.indexOf(f) !== -1) error(new Error(`Feature ${f} is already in the layer`));
    });
    this._features = this._features.concat(toAdd);
    this.fire(new FeaturesAddEvent(toAdd));
    this.redraw();
  }
  /**
   * Removes a feature or an array of features from the layer.
   * @param features - feature or features to be removed.
   * @throws if the one of the features is not in the layer.
   * @fires [[FeaturesRemoveEvent]]
   */


  remove(features) {
    const toRemove = Array.isArray(features) ? features : [features];
    if (toRemove.length === 0) return;
    toRemove.forEach(f => {
      let index = this._features.indexOf(f);

      if (index === -1) error(new Error(`Feature ${f} is not in the layer`));

      this._features.splice(index, 1);
    });
    this.fire(new FeaturesRemoveEvent(toRemove));
    this.redraw();
  }
  /**
   * Returns true if the given feature is in the layer.
   * @param feature
   */


  has(feature) {
    return this._features.indexOf(feature) !== -1;
  }
  /**
   * Moves the given feature to the top of the layer (end of the list). If the feature is not in the layer, the command is ignored.
   * @param feature
   */


  moveToTop(feature) {
    let index = this._features.indexOf(feature);

    if (index !== -1) {
      this._features.splice(index, 1);

      this._features.push(feature);

      this.redraw();
    }
  }
  /**
   * List of features in the layer. If assigned, it removes all features and add new ones, firing all the respective events.
   * @fires [[FeaturesAddEvent]]
   * @fires [[FeaturesRemoveEvent]]
   */


  get features() {
    return this._features;
  }

  set features(features) {
    const currFeatures = this._features;
    this._features = [];
    this.fire(new FeaturesRemoveEvent(currFeatures));
    this.add(features);
    this.redraw();
  }

}

function globals(defs) {
  defs('EPSG:4326', "+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees");
  defs('EPSG:4269', "+title=NAD83 (long/lat) +proj=longlat +a=6378137.0 +b=6356752.31414036 +ellps=GRS80 +datum=NAD83 +units=degrees");
  defs('EPSG:3857', "+title=WGS 84 / Pseudo-Mercator +proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +no_defs");

  defs.WGS84 = defs['EPSG:4326'];
  defs['EPSG:3785'] = defs['EPSG:3857']; // maintain backward compat, official code is 3857
  defs.GOOGLE = defs['EPSG:3857'];
  defs['EPSG:900913'] = defs['EPSG:3857'];
  defs['EPSG:102113'] = defs['EPSG:3857'];
}

var PJD_3PARAM = 1;
var PJD_7PARAM = 2;
var PJD_GRIDSHIFT = 3;
var PJD_WGS84 = 4; // WGS84 or equivalent
var PJD_NODATUM = 5; // WGS84 or equivalent
var SRS_WGS84_SEMIMAJOR = 6378137.0;  // only used in grid shift transforms
var SRS_WGS84_SEMIMINOR = 6356752.314;  // only used in grid shift transforms
var SRS_WGS84_ESQUARED = 0.0066943799901413165; // only used in grid shift transforms
var SEC_TO_RAD = 4.84813681109535993589914102357e-6;
var HALF_PI = Math.PI/2;
// ellipoid pj_set_ell.c
var SIXTH = 0.1666666666666666667;
/* 1/6 */
var RA4 = 0.04722222222222222222;
/* 17/360 */
var RA6 = 0.02215608465608465608;
var EPSLN = 1.0e-10;
// you'd think you could use Number.EPSILON above but that makes
// Mollweide get into an infinate loop.

var D2R$1 = 0.01745329251994329577;
var R2D = 57.29577951308232088;
var FORTPI = Math.PI/4;
var TWO_PI = Math.PI * 2;
// SPI is slightly greater than Math.PI, so values that exceed the -180..180
// degree range by a tiny amount don't get wrapped. This prevents points that
// have drifted from their original location along the 180th meridian (due to
// floating point error) from changing their sign.
var SPI = 3.14159265359;

var exports$2 = {};

exports$2.greenwich = 0.0; //"0dE",
exports$2.lisbon = -9.131906111111; //"9d07'54.862\"W",
exports$2.paris = 2.337229166667; //"2d20'14.025\"E",
exports$2.bogota = -74.080916666667; //"74d04'51.3\"W",
exports$2.madrid = -3.687938888889; //"3d41'16.58\"W",
exports$2.rome = 12.452333333333; //"12d27'8.4\"E",
exports$2.bern = 7.439583333333; //"7d26'22.5\"E",
exports$2.jakarta = 106.807719444444; //"106d48'27.79\"E",
exports$2.ferro = -17.666666666667; //"17d40'W",
exports$2.brussels = 4.367975; //"4d22'4.71\"E",
exports$2.stockholm = 18.058277777778; //"18d3'29.8\"E",
exports$2.athens = 23.7163375; //"23d42'58.815\"E",
exports$2.oslo = 10.722916666667; //"10d43'22.5\"E"

var units = {
  ft: {to_meter: 0.3048},
  'us-ft': {to_meter: 1200 / 3937}
};

var ignoredChar = /[\s_\-\/\(\)]/g;
function match(obj, key) {
  if (obj[key]) {
    return obj[key];
  }
  var keys = Object.keys(obj);
  var lkey = key.toLowerCase().replace(ignoredChar, '');
  var i = -1;
  var testkey, processedKey;
  while (++i < keys.length) {
    testkey = keys[i];
    processedKey = testkey.toLowerCase().replace(ignoredChar, '');
    if (processedKey === lkey) {
      return obj[testkey];
    }
  }
}

function projStr(defData) {
  var self = {};
  var paramObj = defData.split('+').map(function(v) {
    return v.trim();
  }).filter(function(a) {
    return a;
  }).reduce(function(p, a) {
    var split = a.split('=');
    split.push(true);
    p[split[0].toLowerCase()] = split[1];
    return p;
  }, {});
  var paramName, paramVal, paramOutname;
  var params = {
    proj: 'projName',
    datum: 'datumCode',
    rf: function(v) {
      self.rf = parseFloat(v);
    },
    lat_0: function(v) {
      self.lat0 = v * D2R$1;
    },
    lat_1: function(v) {
      self.lat1 = v * D2R$1;
    },
    lat_2: function(v) {
      self.lat2 = v * D2R$1;
    },
    lat_ts: function(v) {
      self.lat_ts = v * D2R$1;
    },
    lon_0: function(v) {
      self.long0 = v * D2R$1;
    },
    lon_1: function(v) {
      self.long1 = v * D2R$1;
    },
    lon_2: function(v) {
      self.long2 = v * D2R$1;
    },
    alpha: function(v) {
      self.alpha = parseFloat(v) * D2R$1;
    },
    gamma: function(v) {
      self.rectified_grid_angle = parseFloat(v);
    },
    lonc: function(v) {
      self.longc = v * D2R$1;
    },
    x_0: function(v) {
      self.x0 = parseFloat(v);
    },
    y_0: function(v) {
      self.y0 = parseFloat(v);
    },
    k_0: function(v) {
      self.k0 = parseFloat(v);
    },
    k: function(v) {
      self.k0 = parseFloat(v);
    },
    a: function(v) {
      self.a = parseFloat(v);
    },
    b: function(v) {
      self.b = parseFloat(v);
    },
    r_a: function() {
      self.R_A = true;
    },
    zone: function(v) {
      self.zone = parseInt(v, 10);
    },
    south: function() {
      self.utmSouth = true;
    },
    towgs84: function(v) {
      self.datum_params = v.split(",").map(function(a) {
        return parseFloat(a);
      });
    },
    to_meter: function(v) {
      self.to_meter = parseFloat(v);
    },
    units: function(v) {
      self.units = v;
      var unit = match(units, v);
      if (unit) {
        self.to_meter = unit.to_meter;
      }
    },
    from_greenwich: function(v) {
      self.from_greenwich = v * D2R$1;
    },
    pm: function(v) {
      var pm = match(exports$2, v);
      self.from_greenwich = (pm ? pm : parseFloat(v)) * D2R$1;
    },
    nadgrids: function(v) {
      if (v === '@null') {
        self.datumCode = 'none';
      }
      else {
        self.nadgrids = v;
      }
    },
    axis: function(v) {
      var legalAxis = "ewnsud";
      if (v.length === 3 && legalAxis.indexOf(v.substr(0, 1)) !== -1 && legalAxis.indexOf(v.substr(1, 1)) !== -1 && legalAxis.indexOf(v.substr(2, 1)) !== -1) {
        self.axis = v;
      }
    },
    approx: function() {
      self.approx = true;
    }
  };
  for (paramName in paramObj) {
    paramVal = paramObj[paramName];
    if (paramName in params) {
      paramOutname = params[paramName];
      if (typeof paramOutname === 'function') {
        paramOutname(paramVal);
      }
      else {
        self[paramOutname] = paramVal;
      }
    }
    else {
      self[paramName] = paramVal;
    }
  }
  if(typeof self.datumCode === 'string' && self.datumCode !== "WGS84"){
    self.datumCode = self.datumCode.toLowerCase();
  }
  return self;
}

var NEUTRAL = 1;
var KEYWORD = 2;
var NUMBER = 3;
var QUOTED = 4;
var AFTERQUOTE = 5;
var ENDED = -1;
var whitespace = /\s/;
var latin = /[A-Za-z]/;
var keyword = /[A-Za-z84_]/;
var endThings = /[,\]]/;
var digets = /[\d\.E\-\+]/;
// const ignoredChar = /[\s_\-\/\(\)]/g;
function Parser(text) {
  if (typeof text !== 'string') {
    throw new Error('not a string');
  }
  this.text = text.trim();
  this.level = 0;
  this.place = 0;
  this.root = null;
  this.stack = [];
  this.currentObject = null;
  this.state = NEUTRAL;
}
Parser.prototype.readCharicter = function() {
  var char = this.text[this.place++];
  if (this.state !== QUOTED) {
    while (whitespace.test(char)) {
      if (this.place >= this.text.length) {
        return;
      }
      char = this.text[this.place++];
    }
  }
  switch (this.state) {
    case NEUTRAL:
      return this.neutral(char);
    case KEYWORD:
      return this.keyword(char)
    case QUOTED:
      return this.quoted(char);
    case AFTERQUOTE:
      return this.afterquote(char);
    case NUMBER:
      return this.number(char);
    case ENDED:
      return;
  }
};
Parser.prototype.afterquote = function(char) {
  if (char === '"') {
    this.word += '"';
    this.state = QUOTED;
    return;
  }
  if (endThings.test(char)) {
    this.word = this.word.trim();
    this.afterItem(char);
    return;
  }
  throw new Error('havn\'t handled "' +char + '" in afterquote yet, index ' + this.place);
};
Parser.prototype.afterItem = function(char) {
  if (char === ',') {
    if (this.word !== null) {
      this.currentObject.push(this.word);
    }
    this.word = null;
    this.state = NEUTRAL;
    return;
  }
  if (char === ']') {
    this.level--;
    if (this.word !== null) {
      this.currentObject.push(this.word);
      this.word = null;
    }
    this.state = NEUTRAL;
    this.currentObject = this.stack.pop();
    if (!this.currentObject) {
      this.state = ENDED;
    }

    return;
  }
};
Parser.prototype.number = function(char) {
  if (digets.test(char)) {
    this.word += char;
    return;
  }
  if (endThings.test(char)) {
    this.word = parseFloat(this.word);
    this.afterItem(char);
    return;
  }
  throw new Error('havn\'t handled "' +char + '" in number yet, index ' + this.place);
};
Parser.prototype.quoted = function(char) {
  if (char === '"') {
    this.state = AFTERQUOTE;
    return;
  }
  this.word += char;
  return;
};
Parser.prototype.keyword = function(char) {
  if (keyword.test(char)) {
    this.word += char;
    return;
  }
  if (char === '[') {
    var newObjects = [];
    newObjects.push(this.word);
    this.level++;
    if (this.root === null) {
      this.root = newObjects;
    } else {
      this.currentObject.push(newObjects);
    }
    this.stack.push(this.currentObject);
    this.currentObject = newObjects;
    this.state = NEUTRAL;
    return;
  }
  if (endThings.test(char)) {
    this.afterItem(char);
    return;
  }
  throw new Error('havn\'t handled "' +char + '" in keyword yet, index ' + this.place);
};
Parser.prototype.neutral = function(char) {
  if (latin.test(char)) {
    this.word = char;
    this.state = KEYWORD;
    return;
  }
  if (char === '"') {
    this.word = '';
    this.state = QUOTED;
    return;
  }
  if (digets.test(char)) {
    this.word = char;
    this.state = NUMBER;
    return;
  }
  if (endThings.test(char)) {
    this.afterItem(char);
    return;
  }
  throw new Error('havn\'t handled "' +char + '" in neutral yet, index ' + this.place);
};
Parser.prototype.output = function() {
  while (this.place < this.text.length) {
    this.readCharicter();
  }
  if (this.state === ENDED) {
    return this.root;
  }
  throw new Error('unable to parse string "' +this.text + '". State is ' + this.state);
};

function parseString(txt) {
  var parser = new Parser(txt);
  return parser.output();
}

function mapit(obj, key, value) {
  if (Array.isArray(key)) {
    value.unshift(key);
    key = null;
  }
  var thing = key ? {} : obj;

  var out = value.reduce(function(newObj, item) {
    sExpr(item, newObj);
    return newObj
  }, thing);
  if (key) {
    obj[key] = out;
  }
}

function sExpr(v, obj) {
  if (!Array.isArray(v)) {
    obj[v] = true;
    return;
  }
  var key = v.shift();
  if (key === 'PARAMETER') {
    key = v.shift();
  }
  if (v.length === 1) {
    if (Array.isArray(v[0])) {
      obj[key] = {};
      sExpr(v[0], obj[key]);
      return;
    }
    obj[key] = v[0];
    return;
  }
  if (!v.length) {
    obj[key] = true;
    return;
  }
  if (key === 'TOWGS84') {
    obj[key] = v;
    return;
  }
  if (key === 'AXIS') {
    if (!(key in obj)) {
      obj[key] = [];
    }
    obj[key].push(v);
    return;
  }
  if (!Array.isArray(key)) {
    obj[key] = {};
  }

  var i;
  switch (key) {
    case 'UNIT':
    case 'PRIMEM':
    case 'VERT_DATUM':
      obj[key] = {
        name: v[0].toLowerCase(),
        convert: v[1]
      };
      if (v.length === 3) {
        sExpr(v[2], obj[key]);
      }
      return;
    case 'SPHEROID':
    case 'ELLIPSOID':
      obj[key] = {
        name: v[0],
        a: v[1],
        rf: v[2]
      };
      if (v.length === 4) {
        sExpr(v[3], obj[key]);
      }
      return;
    case 'PROJECTEDCRS':
    case 'PROJCRS':
    case 'GEOGCS':
    case 'GEOCCS':
    case 'PROJCS':
    case 'LOCAL_CS':
    case 'GEODCRS':
    case 'GEODETICCRS':
    case 'GEODETICDATUM':
    case 'EDATUM':
    case 'ENGINEERINGDATUM':
    case 'VERT_CS':
    case 'VERTCRS':
    case 'VERTICALCRS':
    case 'COMPD_CS':
    case 'COMPOUNDCRS':
    case 'ENGINEERINGCRS':
    case 'ENGCRS':
    case 'FITTED_CS':
    case 'LOCAL_DATUM':
    case 'DATUM':
      v[0] = ['name', v[0]];
      mapit(obj, key, v);
      return;
    default:
      i = -1;
      while (++i < v.length) {
        if (!Array.isArray(v[i])) {
          return sExpr(v, obj[key]);
        }
      }
      return mapit(obj, key, v);
  }
}

var D2R = 0.01745329251994329577;



function rename(obj, params) {
  var outName = params[0];
  var inName = params[1];
  if (!(outName in obj) && (inName in obj)) {
    obj[outName] = obj[inName];
    if (params.length === 3) {
      obj[outName] = params[2](obj[outName]);
    }
  }
}

function d2r(input) {
  return input * D2R;
}

function cleanWKT(wkt) {
  if (wkt.type === 'GEOGCS') {
    wkt.projName = 'longlat';
  } else if (wkt.type === 'LOCAL_CS') {
    wkt.projName = 'identity';
    wkt.local = true;
  } else {
    if (typeof wkt.PROJECTION === 'object') {
      wkt.projName = Object.keys(wkt.PROJECTION)[0];
    } else {
      wkt.projName = wkt.PROJECTION;
    }
  }
  if (wkt.AXIS) {
    var axisOrder = '';
    for (var i = 0, ii = wkt.AXIS.length; i < ii; ++i) {
      var axis = [wkt.AXIS[i][0].toLowerCase(), wkt.AXIS[i][1].toLowerCase()];
      if (axis[0].indexOf('north') !== -1 || ((axis[0] === 'y' || axis[0] === 'lat') && axis[1] === 'north')) {
        axisOrder += 'n';
      } else if (axis[0].indexOf('south') !== -1 || ((axis[0] === 'y' || axis[0] === 'lat') && axis[1] === 'south')) {
        axisOrder += 's';
      } else if (axis[0].indexOf('east') !== -1 || ((axis[0] === 'x' || axis[0] === 'lon') && axis[1] === 'east')) {
        axisOrder += 'e';
      } else if (axis[0].indexOf('west') !== -1 || ((axis[0] === 'x' || axis[0] === 'lon') && axis[1] === 'west')) {
        axisOrder += 'w';
      }
    }
    if (axisOrder.length === 2) {
      axisOrder += 'u';
    }
    if (axisOrder.length === 3) {
      wkt.axis = axisOrder;
    }
  }
  if (wkt.UNIT) {
    wkt.units = wkt.UNIT.name.toLowerCase();
    if (wkt.units === 'metre') {
      wkt.units = 'meter';
    }
    if (wkt.UNIT.convert) {
      if (wkt.type === 'GEOGCS') {
        if (wkt.DATUM && wkt.DATUM.SPHEROID) {
          wkt.to_meter = wkt.UNIT.convert*wkt.DATUM.SPHEROID.a;
        }
      } else {
        wkt.to_meter = wkt.UNIT.convert;
      }
    }
  }
  var geogcs = wkt.GEOGCS;
  if (wkt.type === 'GEOGCS') {
    geogcs = wkt;
  }
  if (geogcs) {
    //if(wkt.GEOGCS.PRIMEM&&wkt.GEOGCS.PRIMEM.convert){
    //  wkt.from_greenwich=wkt.GEOGCS.PRIMEM.convert*D2R;
    //}
    if (geogcs.DATUM) {
      wkt.datumCode = geogcs.DATUM.name.toLowerCase();
    } else {
      wkt.datumCode = geogcs.name.toLowerCase();
    }
    if (wkt.datumCode.slice(0, 2) === 'd_') {
      wkt.datumCode = wkt.datumCode.slice(2);
    }
    if (wkt.datumCode === 'new_zealand_geodetic_datum_1949' || wkt.datumCode === 'new_zealand_1949') {
      wkt.datumCode = 'nzgd49';
    }
    if (wkt.datumCode === 'wgs_1984' || wkt.datumCode === 'world_geodetic_system_1984') {
      if (wkt.PROJECTION === 'Mercator_Auxiliary_Sphere') {
        wkt.sphere = true;
      }
      wkt.datumCode = 'wgs84';
    }
    if (wkt.datumCode.slice(-6) === '_ferro') {
      wkt.datumCode = wkt.datumCode.slice(0, - 6);
    }
    if (wkt.datumCode.slice(-8) === '_jakarta') {
      wkt.datumCode = wkt.datumCode.slice(0, - 8);
    }
    if (~wkt.datumCode.indexOf('belge')) {
      wkt.datumCode = 'rnb72';
    }
    if (geogcs.DATUM && geogcs.DATUM.SPHEROID) {
      wkt.ellps = geogcs.DATUM.SPHEROID.name.replace('_19', '').replace(/[Cc]larke\_18/, 'clrk');
      if (wkt.ellps.toLowerCase().slice(0, 13) === 'international') {
        wkt.ellps = 'intl';
      }

      wkt.a = geogcs.DATUM.SPHEROID.a;
      wkt.rf = parseFloat(geogcs.DATUM.SPHEROID.rf, 10);
    }

    if (geogcs.DATUM && geogcs.DATUM.TOWGS84) {
      wkt.datum_params = geogcs.DATUM.TOWGS84;
    }
    if (~wkt.datumCode.indexOf('osgb_1936')) {
      wkt.datumCode = 'osgb36';
    }
    if (~wkt.datumCode.indexOf('osni_1952')) {
      wkt.datumCode = 'osni52';
    }
    if (~wkt.datumCode.indexOf('tm65')
      || ~wkt.datumCode.indexOf('geodetic_datum_of_1965')) {
      wkt.datumCode = 'ire65';
    }
    if (wkt.datumCode === 'ch1903+') {
      wkt.datumCode = 'ch1903';
    }
    if (~wkt.datumCode.indexOf('israel')) {
      wkt.datumCode = 'isr93';
    }
  }
  if (wkt.b && !isFinite(wkt.b)) {
    wkt.b = wkt.a;
  }

  function toMeter(input) {
    var ratio = wkt.to_meter || 1;
    return input * ratio;
  }
  var renamer = function(a) {
    return rename(wkt, a);
  };
  var list = [
    ['standard_parallel_1', 'Standard_Parallel_1'],
    ['standard_parallel_1', 'Latitude of 1st standard parallel'],
    ['standard_parallel_2', 'Standard_Parallel_2'],
    ['standard_parallel_2', 'Latitude of 2nd standard parallel'],
    ['false_easting', 'False_Easting'],
    ['false_easting', 'False easting'],
    ['false-easting', 'Easting at false origin'],
    ['false_northing', 'False_Northing'],
    ['false_northing', 'False northing'],
    ['false_northing', 'Northing at false origin'],
    ['central_meridian', 'Central_Meridian'],
    ['central_meridian', 'Longitude of natural origin'],
    ['central_meridian', 'Longitude of false origin'],
    ['latitude_of_origin', 'Latitude_Of_Origin'],
    ['latitude_of_origin', 'Central_Parallel'],
    ['latitude_of_origin', 'Latitude of natural origin'],
    ['latitude_of_origin', 'Latitude of false origin'],
    ['scale_factor', 'Scale_Factor'],
    ['k0', 'scale_factor'],
    ['latitude_of_center', 'Latitude_Of_Center'],
    ['latitude_of_center', 'Latitude_of_center'],
    ['lat0', 'latitude_of_center', d2r],
    ['longitude_of_center', 'Longitude_Of_Center'],
    ['longitude_of_center', 'Longitude_of_center'],
    ['longc', 'longitude_of_center', d2r],
    ['x0', 'false_easting', toMeter],
    ['y0', 'false_northing', toMeter],
    ['long0', 'central_meridian', d2r],
    ['lat0', 'latitude_of_origin', d2r],
    ['lat0', 'standard_parallel_1', d2r],
    ['lat1', 'standard_parallel_1', d2r],
    ['lat2', 'standard_parallel_2', d2r],
    ['azimuth', 'Azimuth'],
    ['alpha', 'azimuth', d2r],
    ['srsCode', 'name']
  ];
  list.forEach(renamer);
  if (!wkt.long0 && wkt.longc && (wkt.projName === 'Albers_Conic_Equal_Area' || wkt.projName === 'Lambert_Azimuthal_Equal_Area')) {
    wkt.long0 = wkt.longc;
  }
  if (!wkt.lat_ts && wkt.lat1 && (wkt.projName === 'Stereographic_South_Pole' || wkt.projName === 'Polar Stereographic (variant B)')) {
    wkt.lat0 = d2r(wkt.lat1 > 0 ? 90 : -90);
    wkt.lat_ts = wkt.lat1;
  } else if (!wkt.lat_ts && wkt.lat0 && wkt.projName === 'Polar_Stereographic') {
    wkt.lat_ts = wkt.lat0;
    wkt.lat0 = d2r(wkt.lat0 > 0 ? 90 : -90);
  }
}
function wkt(wkt) {
  var lisp = parseString(wkt);
  var type = lisp.shift();
  var name = lisp.shift();
  lisp.unshift(['name', name]);
  lisp.unshift(['type', type]);
  var obj = {};
  sExpr(lisp, obj);
  cleanWKT(obj);
  return obj;
}

function defs(name) {
  /*global console*/
  var that = this;
  if (arguments.length === 2) {
    var def = arguments[1];
    if (typeof def === 'string') {
      if (def.charAt(0) === '+') {
        defs[name] = projStr(arguments[1]);
      }
      else {
        defs[name] = wkt(arguments[1]);
      }
    } else {
      defs[name] = def;
    }
  }
  else if (arguments.length === 1) {
    if (Array.isArray(name)) {
      return name.map(function(v) {
        if (Array.isArray(v)) {
          defs.apply(that, v);
        }
        else {
          defs(v);
        }
      });
    }
    else if (typeof name === 'string') {
      if (name in defs) {
        return defs[name];
      }
    }
    else if ('EPSG' in name) {
      defs['EPSG:' + name.EPSG] = name;
    }
    else if ('ESRI' in name) {
      defs['ESRI:' + name.ESRI] = name;
    }
    else if ('IAU2000' in name) {
      defs['IAU2000:' + name.IAU2000] = name;
    }
    else {
      console.log(name);
    }
    return;
  }


}
globals(defs);

function testObj(code){
  return typeof code === 'string';
}
function testDef(code){
  return code in defs;
}
var codeWords = ['PROJECTEDCRS', 'PROJCRS', 'GEOGCS','GEOCCS','PROJCS','LOCAL_CS', 'GEODCRS', 'GEODETICCRS', 'GEODETICDATUM', 'ENGCRS', 'ENGINEERINGCRS'];
function testWKT(code){
  return codeWords.some(function (word) {
    return code.indexOf(word) > -1;
  });
}
var codes = ['3857', '900913', '3785', '102113'];
function checkMercator(item) {
  var auth = match(item, 'authority');
  if (!auth) {
    return;
  }
  var code = match(auth, 'epsg');
  return code && codes.indexOf(code) > -1;
}
function checkProjStr(item) {
  var ext = match(item, 'extension');
  if (!ext) {
    return;
  }
  return match(ext, 'proj4');
}
function testProj(code){
  return code[0] === '+';
}
function parse(code){
  if (testObj(code)) {
    //check to see if this is a WKT string
    if (testDef(code)) {
      return defs[code];
    }
    if (testWKT(code)) {
      var out = wkt(code);
      // test of spetial case, due to this being a very common and often malformed
      if (checkMercator(out)) {
        return defs['EPSG:3857'];
      }
      var maybeProjStr = checkProjStr(out);
      if (maybeProjStr) {
        return projStr(maybeProjStr);
      }
      return out;
    }
    if (testProj(code)) {
      return projStr(code);
    }
  }else {
    return code;
  }
}

function extend$1(destination, source) {
  destination = destination || {};
  var value, property;
  if (!source) {
    return destination;
  }
  for (property in source) {
    value = source[property];
    if (value !== undefined) {
      destination[property] = value;
    }
  }
  return destination;
}

function msfnz(eccent, sinphi, cosphi) {
  var con = eccent * sinphi;
  return cosphi / (Math.sqrt(1 - con * con));
}

function sign(x) {
  return x<0 ? -1 : 1;
}

function adjust_lon(x) {
  return (Math.abs(x) <= SPI) ? x : (x - (sign(x) * TWO_PI));
}

function tsfnz(eccent, phi, sinphi) {
  var con = eccent * sinphi;
  var com = 0.5 * eccent;
  con = Math.pow(((1 - con) / (1 + con)), com);
  return (Math.tan(0.5 * (HALF_PI - phi)) / con);
}

function phi2z(eccent, ts) {
  var eccnth = 0.5 * eccent;
  var con, dphi;
  var phi = HALF_PI - 2 * Math.atan(ts);
  for (var i = 0; i <= 15; i++) {
    con = eccent * Math.sin(phi);
    dphi = HALF_PI - 2 * Math.atan(ts * (Math.pow(((1 - con) / (1 + con)), eccnth))) - phi;
    phi += dphi;
    if (Math.abs(dphi) <= 0.0000000001) {
      return phi;
    }
  }
  //console.log("phi2z has NoConvergence");
  return -9999;
}

function init$w() {
  var con = this.b / this.a;
  this.es = 1 - con * con;
  if(!('x0' in this)){
    this.x0 = 0;
  }
  if(!('y0' in this)){
    this.y0 = 0;
  }
  this.e = Math.sqrt(this.es);
  if (this.lat_ts) {
    if (this.sphere) {
      this.k0 = Math.cos(this.lat_ts);
    }
    else {
      this.k0 = msfnz(this.e, Math.sin(this.lat_ts), Math.cos(this.lat_ts));
    }
  }
  else {
    if (!this.k0) {
      if (this.k) {
        this.k0 = this.k;
      }
      else {
        this.k0 = 1;
      }
    }
  }
}

/* Mercator forward equations--mapping lat,long to x,y
  --------------------------------------------------*/

function forward$u(p) {
  var lon = p.x;
  var lat = p.y;
  // convert to radians
  if (lat * R2D > 90 && lat * R2D < -90 && lon * R2D > 180 && lon * R2D < -180) {
    return null;
  }

  var x, y;
  if (Math.abs(Math.abs(lat) - HALF_PI) <= EPSLN) {
    return null;
  }
  else {
    if (this.sphere) {
      x = this.x0 + this.a * this.k0 * adjust_lon(lon - this.long0);
      y = this.y0 + this.a * this.k0 * Math.log(Math.tan(FORTPI + 0.5 * lat));
    }
    else {
      var sinphi = Math.sin(lat);
      var ts = tsfnz(this.e, lat, sinphi);
      x = this.x0 + this.a * this.k0 * adjust_lon(lon - this.long0);
      y = this.y0 - this.a * this.k0 * Math.log(ts);
    }
    p.x = x;
    p.y = y;
    return p;
  }
}

/* Mercator inverse equations--mapping x,y to lat/long
  --------------------------------------------------*/
function inverse$u(p) {

  var x = p.x - this.x0;
  var y = p.y - this.y0;
  var lon, lat;

  if (this.sphere) {
    lat = HALF_PI - 2 * Math.atan(Math.exp(-y / (this.a * this.k0)));
  }
  else {
    var ts = Math.exp(-y / (this.a * this.k0));
    lat = phi2z(this.e, ts);
    if (lat === -9999) {
      return null;
    }
  }
  lon = adjust_lon(this.long0 + x / (this.a * this.k0));

  p.x = lon;
  p.y = lat;
  return p;
}

var names$w = ["Mercator", "Popular Visualisation Pseudo Mercator", "Mercator_1SP", "Mercator_Auxiliary_Sphere", "merc"];
var merc = {
  init: init$w,
  forward: forward$u,
  inverse: inverse$u,
  names: names$w
};

function init$v() {
  //no-op for longlat
}

function identity$2(pt) {
  return pt;
}
var names$v = ["longlat", "identity"];
var longlat = {
  init: init$v,
  forward: identity$2,
  inverse: identity$2,
  names: names$v
};

var projs = [merc, longlat];
var names$u = {};
var projStore = [];

function add(proj, i) {
  var len = projStore.length;
  if (!proj.names) {
    console.log(i);
    return true;
  }
  projStore[len] = proj;
  proj.names.forEach(function(n) {
    names$u[n.toLowerCase()] = len;
  });
  return this;
}

function get(name) {
  if (!name) {
    return false;
  }
  var n = name.toLowerCase();
  if (typeof names$u[n] !== 'undefined' && projStore[names$u[n]]) {
    return projStore[names$u[n]];
  }
}

function start() {
  projs.forEach(add);
}
var projections = {
  start: start,
  add: add,
  get: get
};

var exports$1 = {};
exports$1.MERIT = {
  a: 6378137.0,
  rf: 298.257,
  ellipseName: "MERIT 1983"
};

exports$1.SGS85 = {
  a: 6378136.0,
  rf: 298.257,
  ellipseName: "Soviet Geodetic System 85"
};

exports$1.GRS80 = {
  a: 6378137.0,
  rf: 298.257222101,
  ellipseName: "GRS 1980(IUGG, 1980)"
};

exports$1.IAU76 = {
  a: 6378140.0,
  rf: 298.257,
  ellipseName: "IAU 1976"
};

exports$1.airy = {
  a: 6377563.396,
  b: 6356256.910,
  ellipseName: "Airy 1830"
};

exports$1.APL4 = {
  a: 6378137,
  rf: 298.25,
  ellipseName: "Appl. Physics. 1965"
};

exports$1.NWL9D = {
  a: 6378145.0,
  rf: 298.25,
  ellipseName: "Naval Weapons Lab., 1965"
};

exports$1.mod_airy = {
  a: 6377340.189,
  b: 6356034.446,
  ellipseName: "Modified Airy"
};

exports$1.andrae = {
  a: 6377104.43,
  rf: 300.0,
  ellipseName: "Andrae 1876 (Den., Iclnd.)"
};

exports$1.aust_SA = {
  a: 6378160.0,
  rf: 298.25,
  ellipseName: "Australian Natl & S. Amer. 1969"
};

exports$1.GRS67 = {
  a: 6378160.0,
  rf: 298.2471674270,
  ellipseName: "GRS 67(IUGG 1967)"
};

exports$1.bessel = {
  a: 6377397.155,
  rf: 299.1528128,
  ellipseName: "Bessel 1841"
};

exports$1.bess_nam = {
  a: 6377483.865,
  rf: 299.1528128,
  ellipseName: "Bessel 1841 (Namibia)"
};

exports$1.clrk66 = {
  a: 6378206.4,
  b: 6356583.8,
  ellipseName: "Clarke 1866"
};

exports$1.clrk80 = {
  a: 6378249.145,
  rf: 293.4663,
  ellipseName: "Clarke 1880 mod."
};

exports$1.clrk80ign = {
  a: 6378249.2,
  b: 6356515,
  rf: 293.4660213,
  ellipseName: "Clarke 1880 (IGN)"
};

exports$1.clrk58 = {
  a: 6378293.645208759,
  rf: 294.2606763692654,
  ellipseName: "Clarke 1858"
};

exports$1.CPM = {
  a: 6375738.7,
  rf: 334.29,
  ellipseName: "Comm. des Poids et Mesures 1799"
};

exports$1.delmbr = {
  a: 6376428.0,
  rf: 311.5,
  ellipseName: "Delambre 1810 (Belgium)"
};

exports$1.engelis = {
  a: 6378136.05,
  rf: 298.2566,
  ellipseName: "Engelis 1985"
};

exports$1.evrst30 = {
  a: 6377276.345,
  rf: 300.8017,
  ellipseName: "Everest 1830"
};

exports$1.evrst48 = {
  a: 6377304.063,
  rf: 300.8017,
  ellipseName: "Everest 1948"
};

exports$1.evrst56 = {
  a: 6377301.243,
  rf: 300.8017,
  ellipseName: "Everest 1956"
};

exports$1.evrst69 = {
  a: 6377295.664,
  rf: 300.8017,
  ellipseName: "Everest 1969"
};

exports$1.evrstSS = {
  a: 6377298.556,
  rf: 300.8017,
  ellipseName: "Everest (Sabah & Sarawak)"
};

exports$1.fschr60 = {
  a: 6378166.0,
  rf: 298.3,
  ellipseName: "Fischer (Mercury Datum) 1960"
};

exports$1.fschr60m = {
  a: 6378155.0,
  rf: 298.3,
  ellipseName: "Fischer 1960"
};

exports$1.fschr68 = {
  a: 6378150.0,
  rf: 298.3,
  ellipseName: "Fischer 1968"
};

exports$1.helmert = {
  a: 6378200.0,
  rf: 298.3,
  ellipseName: "Helmert 1906"
};

exports$1.hough = {
  a: 6378270.0,
  rf: 297.0,
  ellipseName: "Hough"
};

exports$1.intl = {
  a: 6378388.0,
  rf: 297.0,
  ellipseName: "International 1909 (Hayford)"
};

exports$1.kaula = {
  a: 6378163.0,
  rf: 298.24,
  ellipseName: "Kaula 1961"
};

exports$1.lerch = {
  a: 6378139.0,
  rf: 298.257,
  ellipseName: "Lerch 1979"
};

exports$1.mprts = {
  a: 6397300.0,
  rf: 191.0,
  ellipseName: "Maupertius 1738"
};

exports$1.new_intl = {
  a: 6378157.5,
  b: 6356772.2,
  ellipseName: "New International 1967"
};

exports$1.plessis = {
  a: 6376523.0,
  rf: 6355863.0,
  ellipseName: "Plessis 1817 (France)"
};

exports$1.krass = {
  a: 6378245.0,
  rf: 298.3,
  ellipseName: "Krassovsky, 1942"
};

exports$1.SEasia = {
  a: 6378155.0,
  b: 6356773.3205,
  ellipseName: "Southeast Asia"
};

exports$1.walbeck = {
  a: 6376896.0,
  b: 6355834.8467,
  ellipseName: "Walbeck"
};

exports$1.WGS60 = {
  a: 6378165.0,
  rf: 298.3,
  ellipseName: "WGS 60"
};

exports$1.WGS66 = {
  a: 6378145.0,
  rf: 298.25,
  ellipseName: "WGS 66"
};

exports$1.WGS7 = {
  a: 6378135.0,
  rf: 298.26,
  ellipseName: "WGS 72"
};

var WGS84 = exports$1.WGS84 = {
  a: 6378137.0,
  rf: 298.257223563,
  ellipseName: "WGS 84"
};

exports$1.sphere = {
  a: 6370997.0,
  b: 6370997.0,
  ellipseName: "Normal Sphere (r=6370997)"
};

function eccentricity(a, b, rf, R_A) {
  var a2 = a * a; // used in geocentric
  var b2 = b * b; // used in geocentric
  var es = (a2 - b2) / a2; // e ^ 2
  var e = 0;
  if (R_A) {
    a *= 1 - es * (SIXTH + es * (RA4 + es * RA6));
    a2 = a * a;
    es = 0;
  } else {
    e = Math.sqrt(es); // eccentricity
  }
  var ep2 = (a2 - b2) / b2; // used in geocentric
  return {
    es: es,
    e: e,
    ep2: ep2
  };
}
function sphere(a, b, rf, ellps, sphere) {
  if (!a) { // do we have an ellipsoid?
    var ellipse = match(exports$1, ellps);
    if (!ellipse) {
      ellipse = WGS84;
    }
    a = ellipse.a;
    b = ellipse.b;
    rf = ellipse.rf;
  }

  if (rf && !b) {
    b = (1.0 - 1.0 / rf) * a;
  }
  if (rf === 0 || Math.abs(a - b) < EPSLN) {
    sphere = true;
    b = a;
  }
  return {
    a: a,
    b: b,
    rf: rf,
    sphere: sphere
  };
}

var exports = {};
exports.wgs84 = {
  towgs84: "0,0,0",
  ellipse: "WGS84",
  datumName: "WGS84"
};

exports.ch1903 = {
  towgs84: "674.374,15.056,405.346",
  ellipse: "bessel",
  datumName: "swiss"
};

exports.ggrs87 = {
  towgs84: "-199.87,74.79,246.62",
  ellipse: "GRS80",
  datumName: "Greek_Geodetic_Reference_System_1987"
};

exports.nad83 = {
  towgs84: "0,0,0",
  ellipse: "GRS80",
  datumName: "North_American_Datum_1983"
};

exports.nad27 = {
  nadgrids: "@conus,@alaska,@ntv2_0.gsb,@ntv1_can.dat",
  ellipse: "clrk66",
  datumName: "North_American_Datum_1927"
};

exports.potsdam = {
  towgs84: "598.1,73.7,418.2,0.202,0.045,-2.455,6.7",
  ellipse: "bessel",
  datumName: "Potsdam Rauenberg 1950 DHDN"
};

exports.carthage = {
  towgs84: "-263.0,6.0,431.0",
  ellipse: "clark80",
  datumName: "Carthage 1934 Tunisia"
};

exports.hermannskogel = {
  towgs84: "577.326,90.129,463.919,5.137,1.474,5.297,2.4232",
  ellipse: "bessel",
  datumName: "Hermannskogel"
};

exports.militargeographische_institut = {
  towgs84: "577.326,90.129,463.919,5.137,1.474,5.297,2.4232",
  ellipse: "bessel",
  datumName: "Militar-Geographische Institut"
};

exports.osni52 = {
  towgs84: "482.530,-130.596,564.557,-1.042,-0.214,-0.631,8.15",
  ellipse: "airy",
  datumName: "Irish National"
};

exports.ire65 = {
  towgs84: "482.530,-130.596,564.557,-1.042,-0.214,-0.631,8.15",
  ellipse: "mod_airy",
  datumName: "Ireland 1965"
};

exports.rassadiran = {
  towgs84: "-133.63,-157.5,-158.62",
  ellipse: "intl",
  datumName: "Rassadiran"
};

exports.nzgd49 = {
  towgs84: "59.47,-5.04,187.44,0.47,-0.1,1.024,-4.5993",
  ellipse: "intl",
  datumName: "New Zealand Geodetic Datum 1949"
};

exports.osgb36 = {
  towgs84: "446.448,-125.157,542.060,0.1502,0.2470,0.8421,-20.4894",
  ellipse: "airy",
  datumName: "Airy 1830"
};

exports.s_jtsk = {
  towgs84: "589,76,480",
  ellipse: 'bessel',
  datumName: 'S-JTSK (Ferro)'
};

exports.beduaram = {
  towgs84: '-106,-87,188',
  ellipse: 'clrk80',
  datumName: 'Beduaram'
};

exports.gunung_segara = {
  towgs84: '-403,684,41',
  ellipse: 'bessel',
  datumName: 'Gunung Segara Jakarta'
};

exports.rnb72 = {
  towgs84: "106.869,-52.2978,103.724,-0.33657,0.456955,-1.84218,1",
  ellipse: "intl",
  datumName: "Reseau National Belge 1972"
};

function datum(datumCode, datum_params, a, b, es, ep2, nadgrids) {
  var out = {};

  if (datumCode === undefined || datumCode === 'none') {
    out.datum_type = PJD_NODATUM;
  } else {
    out.datum_type = PJD_WGS84;
  }

  if (datum_params) {
    out.datum_params = datum_params.map(parseFloat);
    if (out.datum_params[0] !== 0 || out.datum_params[1] !== 0 || out.datum_params[2] !== 0) {
      out.datum_type = PJD_3PARAM;
    }
    if (out.datum_params.length > 3) {
      if (out.datum_params[3] !== 0 || out.datum_params[4] !== 0 || out.datum_params[5] !== 0 || out.datum_params[6] !== 0) {
        out.datum_type = PJD_7PARAM;
        out.datum_params[3] *= SEC_TO_RAD;
        out.datum_params[4] *= SEC_TO_RAD;
        out.datum_params[5] *= SEC_TO_RAD;
        out.datum_params[6] = (out.datum_params[6] / 1000000.0) + 1.0;
      }
    }
  }

  if (nadgrids) {
    out.datum_type = PJD_GRIDSHIFT;
    out.grids = nadgrids;
  }
  out.a = a; //datum object also uses these values
  out.b = b;
  out.es = es;
  out.ep2 = ep2;
  return out;
}

/**
 * Resources for details of NTv2 file formats:
 * - https://web.archive.org/web/20140127204822if_/http://www.mgs.gov.on.ca:80/stdprodconsume/groups/content/@mgs/@iandit/documents/resourcelist/stel02_047447.pdf
 * - http://mimaka.com/help/gs/html/004_NTV2%20Data%20Format.htm
 */

var loadedNadgrids = {};

/**
 * Load a binary NTv2 file (.gsb) to a key that can be used in a proj string like +nadgrids=<key>. Pass the NTv2 file
 * as an ArrayBuffer.
 */
function nadgrid(key, data) {
  var view = new DataView(data);
  var isLittleEndian = detectLittleEndian(view);
  var header = readHeader(view, isLittleEndian);
  var subgrids = readSubgrids(view, header, isLittleEndian);
  var nadgrid = {header: header, subgrids: subgrids};
  loadedNadgrids[key] = nadgrid;
  return nadgrid;
}

/**
 * Given a proj4 value for nadgrids, return an array of loaded grids
 */
function getNadgrids(nadgrids) {
  // Format details: http://proj.maptools.org/gen_parms.html
  if (nadgrids === undefined) { return null; }
  var grids = nadgrids.split(',');
  return grids.map(parseNadgridString);
}

function parseNadgridString(value) {
  if (value.length === 0) {
    return null;
  }
  var optional = value[0] === '@';
  if (optional) {
    value = value.slice(1);
  }
  if (value === 'null') {
    return {name: 'null', mandatory: !optional, grid: null, isNull: true};
  }
  return {
    name: value,
    mandatory: !optional,
    grid: loadedNadgrids[value] || null,
    isNull: false
  };
}

function secondsToRadians(seconds) {
  return (seconds / 3600) * Math.PI / 180;
}

function detectLittleEndian(view) {
  var nFields = view.getInt32(8, false);
  if (nFields === 11) {
    return false;
  }
  nFields = view.getInt32(8, true);
  if (nFields !== 11) {
    console.warn('Failed to detect nadgrid endian-ness, defaulting to little-endian');
  }
  return true;
}

function readHeader(view, isLittleEndian) {
  return {
    nFields: view.getInt32(8, isLittleEndian),
    nSubgridFields: view.getInt32(24, isLittleEndian),
    nSubgrids: view.getInt32(40, isLittleEndian),
    shiftType: decodeString(view, 56, 56 + 8).trim(),
    fromSemiMajorAxis: view.getFloat64(120, isLittleEndian),
    fromSemiMinorAxis: view.getFloat64(136, isLittleEndian),
    toSemiMajorAxis: view.getFloat64(152, isLittleEndian),
    toSemiMinorAxis: view.getFloat64(168, isLittleEndian),
  };
}

function decodeString(view, start, end) {
  return String.fromCharCode.apply(null, new Uint8Array(view.buffer.slice(start, end)));
}

function readSubgrids(view, header, isLittleEndian) {
  var gridOffset = 176;
  var grids = [];
  for (var i = 0; i < header.nSubgrids; i++) {
    var subHeader = readGridHeader(view, gridOffset, isLittleEndian);
    var nodes = readGridNodes(view, gridOffset, subHeader, isLittleEndian);
    var lngColumnCount = Math.round(
      1 + (subHeader.upperLongitude - subHeader.lowerLongitude) / subHeader.longitudeInterval);
    var latColumnCount = Math.round(
      1 + (subHeader.upperLatitude - subHeader.lowerLatitude) / subHeader.latitudeInterval);
    // Proj4 operates on radians whereas the coordinates are in seconds in the grid
    grids.push({
      ll: [secondsToRadians(subHeader.lowerLongitude), secondsToRadians(subHeader.lowerLatitude)],
      del: [secondsToRadians(subHeader.longitudeInterval), secondsToRadians(subHeader.latitudeInterval)],
      lim: [lngColumnCount, latColumnCount],
      count: subHeader.gridNodeCount,
      cvs: mapNodes(nodes)
    });
    gridOffset += 176 + subHeader.gridNodeCount * 16;
  }
  return grids;
}

function mapNodes(nodes) {
  return nodes.map(function (r) {return [secondsToRadians(r.longitudeShift), secondsToRadians(r.latitudeShift)];});
}

function readGridHeader(view, offset, isLittleEndian) {
  return {
    name: decodeString(view, offset + 8, offset + 16).trim(),
    parent: decodeString(view, offset + 24, offset + 24 + 8).trim(),
    lowerLatitude: view.getFloat64(offset + 72, isLittleEndian),
    upperLatitude: view.getFloat64(offset + 88, isLittleEndian),
    lowerLongitude: view.getFloat64(offset + 104, isLittleEndian),
    upperLongitude: view.getFloat64(offset + 120, isLittleEndian),
    latitudeInterval: view.getFloat64(offset + 136, isLittleEndian),
    longitudeInterval: view.getFloat64(offset + 152, isLittleEndian),
    gridNodeCount: view.getInt32(offset + 168, isLittleEndian)
  };
}

function readGridNodes(view, offset, gridHeader, isLittleEndian) {
  var nodesOffset = offset + 176;
  var gridRecordLength = 16;
  var gridShiftRecords = [];
  for (var i = 0; i < gridHeader.gridNodeCount; i++) {
    var record = {
      latitudeShift: view.getFloat32(nodesOffset + i * gridRecordLength, isLittleEndian),
      longitudeShift: view.getFloat32(nodesOffset + i * gridRecordLength + 4, isLittleEndian),
      latitudeAccuracy: view.getFloat32(nodesOffset + i * gridRecordLength + 8, isLittleEndian),
      longitudeAccuracy: view.getFloat32(nodesOffset + i * gridRecordLength + 12, isLittleEndian),
    };
    gridShiftRecords.push(record);
  }
  return gridShiftRecords;
}

function Projection(srsCode,callback) {
  if (!(this instanceof Projection)) {
    return new Projection(srsCode);
  }
  callback = callback || function(error){
    if(error){
      throw error;
    }
  };
  var json = parse(srsCode);
  if(typeof json !== 'object'){
    callback(srsCode);
    return;
  }
  var ourProj = Projection.projections.get(json.projName);
  if(!ourProj){
    callback(srsCode);
    return;
  }
  if (json.datumCode && json.datumCode !== 'none') {
    var datumDef = match(exports, json.datumCode);
    if (datumDef) {
      json.datum_params = json.datum_params || (datumDef.towgs84 ? datumDef.towgs84.split(',') : null);
      json.ellps = datumDef.ellipse;
      json.datumName = datumDef.datumName ? datumDef.datumName : json.datumCode;
    }
  }
  json.k0 = json.k0 || 1.0;
  json.axis = json.axis || 'enu';
  json.ellps = json.ellps || 'wgs84';
  json.lat1 = json.lat1 || json.lat0; // Lambert_Conformal_Conic_1SP, for example, needs this

  var sphere_ = sphere(json.a, json.b, json.rf, json.ellps, json.sphere);
  var ecc = eccentricity(sphere_.a, sphere_.b, sphere_.rf, json.R_A);
  var nadgrids = getNadgrids(json.nadgrids);
  var datumObj = json.datum || datum(json.datumCode, json.datum_params, sphere_.a, sphere_.b, ecc.es, ecc.ep2,
    nadgrids);

  extend$1(this, json); // transfer everything over from the projection because we don't know what we'll need
  extend$1(this, ourProj); // transfer all the methods from the projection

  // copy the 4 things over we calculated in deriveConstants.sphere
  this.a = sphere_.a;
  this.b = sphere_.b;
  this.rf = sphere_.rf;
  this.sphere = sphere_.sphere;

  // copy the 3 things we calculated in deriveConstants.eccentricity
  this.es = ecc.es;
  this.e = ecc.e;
  this.ep2 = ecc.ep2;

  // add in the datum object
  this.datum = datumObj;

  // init the projection
  this.init();

  // legecy callback from back in the day when it went to spatialreference.org
  callback(null, this);

}
Projection.projections = projections;
Projection.projections.start();

function compareDatums(source, dest) {
  if (source.datum_type !== dest.datum_type) {
    return false; // false, datums are not equal
  } else if (source.a !== dest.a || Math.abs(source.es - dest.es) > 0.000000000050) {
    // the tolerance for es is to ensure that GRS80 and WGS84
    // are considered identical
    return false;
  } else if (source.datum_type === PJD_3PARAM) {
    return (source.datum_params[0] === dest.datum_params[0] && source.datum_params[1] === dest.datum_params[1] && source.datum_params[2] === dest.datum_params[2]);
  } else if (source.datum_type === PJD_7PARAM) {
    return (source.datum_params[0] === dest.datum_params[0] && source.datum_params[1] === dest.datum_params[1] && source.datum_params[2] === dest.datum_params[2] && source.datum_params[3] === dest.datum_params[3] && source.datum_params[4] === dest.datum_params[4] && source.datum_params[5] === dest.datum_params[5] && source.datum_params[6] === dest.datum_params[6]);
  } else {
    return true; // datums are equal
  }
} // cs_compare_datums()

/*
 * The function Convert_Geodetic_To_Geocentric converts geodetic coordinates
 * (latitude, longitude, and height) to geocentric coordinates (X, Y, Z),
 * according to the current ellipsoid parameters.
 *
 *    Latitude  : Geodetic latitude in radians                     (input)
 *    Longitude : Geodetic longitude in radians                    (input)
 *    Height    : Geodetic height, in meters                       (input)
 *    X         : Calculated Geocentric X coordinate, in meters    (output)
 *    Y         : Calculated Geocentric Y coordinate, in meters    (output)
 *    Z         : Calculated Geocentric Z coordinate, in meters    (output)
 *
 */
function geodeticToGeocentric(p, es, a) {
  var Longitude = p.x;
  var Latitude = p.y;
  var Height = p.z ? p.z : 0; //Z value not always supplied

  var Rn; /*  Earth radius at location  */
  var Sin_Lat; /*  Math.sin(Latitude)  */
  var Sin2_Lat; /*  Square of Math.sin(Latitude)  */
  var Cos_Lat; /*  Math.cos(Latitude)  */

  /*
   ** Don't blow up if Latitude is just a little out of the value
   ** range as it may just be a rounding issue.  Also removed longitude
   ** test, it should be wrapped by Math.cos() and Math.sin().  NFW for PROJ.4, Sep/2001.
   */
  if (Latitude < -HALF_PI && Latitude > -1.001 * HALF_PI) {
    Latitude = -HALF_PI;
  } else if (Latitude > HALF_PI && Latitude < 1.001 * HALF_PI) {
    Latitude = HALF_PI;
  } else if (Latitude < -HALF_PI) {
    /* Latitude out of range */
    //..reportError('geocent:lat out of range:' + Latitude);
    return { x: -Infinity, y: -Infinity, z: p.z };
  } else if (Latitude > HALF_PI) {
    /* Latitude out of range */
    return { x: Infinity, y: Infinity, z: p.z };
  }

  if (Longitude > Math.PI) {
    Longitude -= (2 * Math.PI);
  }
  Sin_Lat = Math.sin(Latitude);
  Cos_Lat = Math.cos(Latitude);
  Sin2_Lat = Sin_Lat * Sin_Lat;
  Rn = a / (Math.sqrt(1.0e0 - es * Sin2_Lat));
  return {
    x: (Rn + Height) * Cos_Lat * Math.cos(Longitude),
    y: (Rn + Height) * Cos_Lat * Math.sin(Longitude),
    z: ((Rn * (1 - es)) + Height) * Sin_Lat
  };
} // cs_geodetic_to_geocentric()

function geocentricToGeodetic(p, es, a, b) {
  /* local defintions and variables */
  /* end-criterium of loop, accuracy of sin(Latitude) */
  var genau = 1e-12;
  var genau2 = (genau * genau);
  var maxiter = 30;

  var P; /* distance between semi-minor axis and location */
  var RR; /* distance between center and location */
  var CT; /* sin of geocentric latitude */
  var ST; /* cos of geocentric latitude */
  var RX;
  var RK;
  var RN; /* Earth radius at location */
  var CPHI0; /* cos of start or old geodetic latitude in iterations */
  var SPHI0; /* sin of start or old geodetic latitude in iterations */
  var CPHI; /* cos of searched geodetic latitude */
  var SPHI; /* sin of searched geodetic latitude */
  var SDPHI; /* end-criterium: addition-theorem of sin(Latitude(iter)-Latitude(iter-1)) */
  var iter; /* # of continous iteration, max. 30 is always enough (s.a.) */

  var X = p.x;
  var Y = p.y;
  var Z = p.z ? p.z : 0.0; //Z value not always supplied
  var Longitude;
  var Latitude;
  var Height;

  P = Math.sqrt(X * X + Y * Y);
  RR = Math.sqrt(X * X + Y * Y + Z * Z);

  /*      special cases for latitude and longitude */
  if (P / a < genau) {

    /*  special case, if P=0. (X=0., Y=0.) */
    Longitude = 0.0;

    /*  if (X,Y,Z)=(0.,0.,0.) then Height becomes semi-minor axis
     *  of ellipsoid (=center of mass), Latitude becomes PI/2 */
    if (RR / a < genau) {
      Latitude = HALF_PI;
      Height = -b;
      return {
        x: p.x,
        y: p.y,
        z: p.z
      };
    }
  } else {
    /*  ellipsoidal (geodetic) longitude
     *  interval: -PI < Longitude <= +PI */
    Longitude = Math.atan2(Y, X);
  }

  /* --------------------------------------------------------------
   * Following iterative algorithm was developped by
   * "Institut for Erdmessung", University of Hannover, July 1988.
   * Internet: www.ife.uni-hannover.de
   * Iterative computation of CPHI,SPHI and Height.
   * Iteration of CPHI and SPHI to 10**-12 radian resp.
   * 2*10**-7 arcsec.
   * --------------------------------------------------------------
   */
  CT = Z / RR;
  ST = P / RR;
  RX = 1.0 / Math.sqrt(1.0 - es * (2.0 - es) * ST * ST);
  CPHI0 = ST * (1.0 - es) * RX;
  SPHI0 = CT * RX;
  iter = 0;

  /* loop to find sin(Latitude) resp. Latitude
   * until |sin(Latitude(iter)-Latitude(iter-1))| < genau */
  do {
    iter++;
    RN = a / Math.sqrt(1.0 - es * SPHI0 * SPHI0);

    /*  ellipsoidal (geodetic) height */
    Height = P * CPHI0 + Z * SPHI0 - RN * (1.0 - es * SPHI0 * SPHI0);

    RK = es * RN / (RN + Height);
    RX = 1.0 / Math.sqrt(1.0 - RK * (2.0 - RK) * ST * ST);
    CPHI = ST * (1.0 - RK) * RX;
    SPHI = CT * RX;
    SDPHI = SPHI * CPHI0 - CPHI * SPHI0;
    CPHI0 = CPHI;
    SPHI0 = SPHI;
  }
  while (SDPHI * SDPHI > genau2 && iter < maxiter);

  /*      ellipsoidal (geodetic) latitude */
  Latitude = Math.atan(SPHI / Math.abs(CPHI));
  return {
    x: Longitude,
    y: Latitude,
    z: Height
  };
} // cs_geocentric_to_geodetic()

/****************************************************************/
// pj_geocentic_to_wgs84( p )
//  p = point to transform in geocentric coordinates (x,y,z)


/** point object, nothing fancy, just allows values to be
    passed back and forth by reference rather than by value.
    Other point classes may be used as long as they have
    x and y properties, which will get modified in the transform method.
*/
function geocentricToWgs84(p, datum_type, datum_params) {

  if (datum_type === PJD_3PARAM) {
    // if( x[io] === HUGE_VAL )
    //    continue;
    return {
      x: p.x + datum_params[0],
      y: p.y + datum_params[1],
      z: p.z + datum_params[2],
    };
  } else if (datum_type === PJD_7PARAM) {
    var Dx_BF = datum_params[0];
    var Dy_BF = datum_params[1];
    var Dz_BF = datum_params[2];
    var Rx_BF = datum_params[3];
    var Ry_BF = datum_params[4];
    var Rz_BF = datum_params[5];
    var M_BF = datum_params[6];
    // if( x[io] === HUGE_VAL )
    //    continue;
    return {
      x: M_BF * (p.x - Rz_BF * p.y + Ry_BF * p.z) + Dx_BF,
      y: M_BF * (Rz_BF * p.x + p.y - Rx_BF * p.z) + Dy_BF,
      z: M_BF * (-Ry_BF * p.x + Rx_BF * p.y + p.z) + Dz_BF
    };
  }
} // cs_geocentric_to_wgs84

/****************************************************************/
// pj_geocentic_from_wgs84()
//  coordinate system definition,
//  point to transform in geocentric coordinates (x,y,z)
function geocentricFromWgs84(p, datum_type, datum_params) {

  if (datum_type === PJD_3PARAM) {
    //if( x[io] === HUGE_VAL )
    //    continue;
    return {
      x: p.x - datum_params[0],
      y: p.y - datum_params[1],
      z: p.z - datum_params[2],
    };

  } else if (datum_type === PJD_7PARAM) {
    var Dx_BF = datum_params[0];
    var Dy_BF = datum_params[1];
    var Dz_BF = datum_params[2];
    var Rx_BF = datum_params[3];
    var Ry_BF = datum_params[4];
    var Rz_BF = datum_params[5];
    var M_BF = datum_params[6];
    var x_tmp = (p.x - Dx_BF) / M_BF;
    var y_tmp = (p.y - Dy_BF) / M_BF;
    var z_tmp = (p.z - Dz_BF) / M_BF;
    //if( x[io] === HUGE_VAL )
    //    continue;

    return {
      x: x_tmp + Rz_BF * y_tmp - Ry_BF * z_tmp,
      y: -Rz_BF * x_tmp + y_tmp + Rx_BF * z_tmp,
      z: Ry_BF * x_tmp - Rx_BF * y_tmp + z_tmp
    };
  } //cs_geocentric_from_wgs84()
}

function checkParams(type) {
  return (type === PJD_3PARAM || type === PJD_7PARAM);
}

function datum_transform(source, dest, point) {
  // Short cut if the datums are identical.
  if (compareDatums(source, dest)) {
    return point; // in this case, zero is sucess,
    // whereas cs_compare_datums returns 1 to indicate TRUE
    // confusing, should fix this
  }

  // Explicitly skip datum transform by setting 'datum=none' as parameter for either source or dest
  if (source.datum_type === PJD_NODATUM || dest.datum_type === PJD_NODATUM) {
    return point;
  }

  // If this datum requires grid shifts, then apply it to geodetic coordinates.
  var source_a = source.a;
  var source_es = source.es;
  if (source.datum_type === PJD_GRIDSHIFT) {
    var gridShiftCode = applyGridShift(source, false, point);
    if (gridShiftCode !== 0) {
      return undefined;
    }
    source_a = SRS_WGS84_SEMIMAJOR;
    source_es = SRS_WGS84_ESQUARED;
  }

  var dest_a = dest.a;
  var dest_b = dest.b;
  var dest_es = dest.es;
  if (dest.datum_type === PJD_GRIDSHIFT) {
    dest_a = SRS_WGS84_SEMIMAJOR;
    dest_b = SRS_WGS84_SEMIMINOR;
    dest_es = SRS_WGS84_ESQUARED;
  }

  // Do we need to go through geocentric coordinates?
  if (source_es === dest_es && source_a === dest_a && !checkParams(source.datum_type) &&  !checkParams(dest.datum_type)) {
    return point;
  }

  // Convert to geocentric coordinates.
  point = geodeticToGeocentric(point, source_es, source_a);
  // Convert between datums
  if (checkParams(source.datum_type)) {
    point = geocentricToWgs84(point, source.datum_type, source.datum_params);
  }
  if (checkParams(dest.datum_type)) {
    point = geocentricFromWgs84(point, dest.datum_type, dest.datum_params);
  }
  point = geocentricToGeodetic(point, dest_es, dest_a, dest_b);

  if (dest.datum_type === PJD_GRIDSHIFT) {
    var destGridShiftResult = applyGridShift(dest, true, point);
    if (destGridShiftResult !== 0) {
      return undefined;
    }
  }

  return point;
}

function applyGridShift(source, inverse, point) {
  if (source.grids === null || source.grids.length === 0) {
    console.log('Grid shift grids not found');
    return -1;
  }
  var input = {x: -point.x, y: point.y};
  var output = {x: Number.NaN, y: Number.NaN};
  var attemptedGrids = [];
  outer:
  for (var i = 0; i < source.grids.length; i++) {
    var grid = source.grids[i];
    attemptedGrids.push(grid.name);
    if (grid.isNull) {
      output = input;
      break;
    }
    if (grid.grid === null) {
      if (grid.mandatory) {
        console.log("Unable to find mandatory grid '" + grid.name + "'");
        return -1;
      }
      continue;
    }
    var subgrids = grid.grid.subgrids;
    for (var j = 0, jj = subgrids.length; j < jj; j++) {
      var subgrid = subgrids[j];
      // skip tables that don't match our point at all
      var epsilon = (Math.abs(subgrid.del[1]) + Math.abs(subgrid.del[0])) / 10000.0;
      var minX = subgrid.ll[0] - epsilon;
      var minY = subgrid.ll[1] - epsilon;
      var maxX = subgrid.ll[0] + (subgrid.lim[0] - 1) * subgrid.del[0] + epsilon;
      var maxY = subgrid.ll[1] + (subgrid.lim[1] - 1) * subgrid.del[1] + epsilon;
      if (minY > input.y || minX > input.x || maxY < input.y || maxX < input.x ) {
        continue;
      }
      output = applySubgridShift(input, inverse, subgrid);
      if (!isNaN(output.x)) {
        break outer;
      }
    }
  }
  if (isNaN(output.x)) {
    console.log("Failed to find a grid shift table for location '"+
      -input.x * R2D + " " + input.y * R2D + " tried: '" + attemptedGrids + "'");
    return -1;
  }
  point.x = -output.x;
  point.y = output.y;
  return 0;
}

function applySubgridShift(pin, inverse, ct) {
  var val = {x: Number.NaN, y: Number.NaN};
  if (isNaN(pin.x)) { return val; }
  var tb = {x: pin.x, y: pin.y};
  tb.x -= ct.ll[0];
  tb.y -= ct.ll[1];
  tb.x = adjust_lon(tb.x - Math.PI) + Math.PI;
  var t = nadInterpolate(tb, ct);
  if (inverse) {
    if (isNaN(t.x)) {
      return val;
    }
    t.x = tb.x - t.x;
    t.y = tb.y - t.y;
    var i = 9, tol = 1e-12;
    var dif, del;
    do {
      del = nadInterpolate(t, ct);
      if (isNaN(del.x)) {
        console.log("Inverse grid shift iteration failed, presumably at grid edge.  Using first approximation.");
        break;
      }
      dif = {x: tb.x - (del.x + t.x), y: tb.y - (del.y + t.y)};
      t.x += dif.x;
      t.y += dif.y;
    } while (i-- && Math.abs(dif.x) > tol && Math.abs(dif.y) > tol);
    if (i < 0) {
      console.log("Inverse grid shift iterator failed to converge.");
      return val;
    }
    val.x = adjust_lon(t.x + ct.ll[0]);
    val.y = t.y + ct.ll[1];
  } else {
    if (!isNaN(t.x)) {
      val.x = pin.x + t.x;
      val.y = pin.y + t.y;
    }
  }
  return val;
}

function nadInterpolate(pin, ct) {
  var t = {x: pin.x / ct.del[0], y: pin.y / ct.del[1]};
  var indx = {x: Math.floor(t.x), y: Math.floor(t.y)};
  var frct = {x: t.x - 1.0 * indx.x, y: t.y - 1.0 * indx.y};
  var val= {x: Number.NaN, y: Number.NaN};
  var inx;
  if (indx.x < 0 || indx.x >= ct.lim[0]) {
    return val;
  }
  if (indx.y < 0 || indx.y >= ct.lim[1]) {
    return val;
  }
  inx = (indx.y * ct.lim[0]) + indx.x;
  var f00 = {x: ct.cvs[inx][0], y: ct.cvs[inx][1]};
  inx++;
  var f10= {x: ct.cvs[inx][0], y: ct.cvs[inx][1]};
  inx += ct.lim[0];
  var f11 = {x: ct.cvs[inx][0], y: ct.cvs[inx][1]};
  inx--;
  var f01 = {x: ct.cvs[inx][0], y: ct.cvs[inx][1]};
  var m11 = frct.x * frct.y, m10 = frct.x * (1.0 - frct.y),
    m00 = (1.0 - frct.x) * (1.0 - frct.y), m01 = (1.0 - frct.x) * frct.y;
  val.x = (m00 * f00.x + m10 * f10.x + m01 * f01.x + m11 * f11.x);
  val.y = (m00 * f00.y + m10 * f10.y + m01 * f01.y + m11 * f11.y);
  return val;
}

function adjust_axis(crs, denorm, point) {
  var xin = point.x,
    yin = point.y,
    zin = point.z || 0.0;
  var v, t, i;
  var out = {};
  for (i = 0; i < 3; i++) {
    if (denorm && i === 2 && point.z === undefined) {
      continue;
    }
    if (i === 0) {
      v = xin;
      if ("ew".indexOf(crs.axis[i]) !== -1) {
        t = 'x';
      } else {
        t = 'y';
      }

    }
    else if (i === 1) {
      v = yin;
      if ("ns".indexOf(crs.axis[i]) !== -1) {
        t = 'y';
      } else {
        t = 'x';
      }
    }
    else {
      v = zin;
      t = 'z';
    }
    switch (crs.axis[i]) {
    case 'e':
      out[t] = v;
      break;
    case 'w':
      out[t] = -v;
      break;
    case 'n':
      out[t] = v;
      break;
    case 's':
      out[t] = -v;
      break;
    case 'u':
      if (point[t] !== undefined) {
        out.z = v;
      }
      break;
    case 'd':
      if (point[t] !== undefined) {
        out.z = -v;
      }
      break;
    default:
      //console.log("ERROR: unknow axis ("+crs.axis[i]+") - check definition of "+crs.projName);
      return null;
    }
  }
  return out;
}

function common (array){
  var out = {
    x: array[0],
    y: array[1]
  };
  if (array.length>2) {
    out.z = array[2];
  }
  if (array.length>3) {
    out.m = array[3];
  }
  return out;
}

function checkSanity (point) {
  checkCoord(point.x);
  checkCoord(point.y);
}
function checkCoord(num) {
  if (typeof Number.isFinite === 'function') {
    if (Number.isFinite(num)) {
      return;
    }
    throw new TypeError('coordinates must be finite numbers');
  }
  if (typeof num !== 'number' || num !== num || !isFinite(num)) {
    throw new TypeError('coordinates must be finite numbers');
  }
}

function checkNotWGS(source, dest) {
  return (
    (source.datum.datum_type === PJD_3PARAM || source.datum.datum_type === PJD_7PARAM || source.datum.datum_type === PJD_GRIDSHIFT) && dest.datumCode !== 'WGS84') ||
    ((dest.datum.datum_type === PJD_3PARAM || dest.datum.datum_type === PJD_7PARAM || dest.datum.datum_type === PJD_GRIDSHIFT) && source.datumCode !== 'WGS84');
}

function transform$1(source, dest, point, enforceAxis) {
  var wgs84;
  if (Array.isArray(point)) {
    point = common(point);
  } else {
    // Clone the point object so inputs don't get modified
    point = {
      x: point.x,
      y: point.y,
      z: point.z,
      m: point.m
    };
  }
  var hasZ = point.z !== undefined;
  checkSanity(point);
  // Workaround for datum shifts towgs84, if either source or destination projection is not wgs84
  if (source.datum && dest.datum && checkNotWGS(source, dest)) {
    wgs84 = new Projection('WGS84');
    point = transform$1(source, wgs84, point, enforceAxis);
    source = wgs84;
  }
  // DGR, 2010/11/12
  if (enforceAxis && source.axis !== 'enu') {
    point = adjust_axis(source, false, point);
  }
  // Transform source points to long/lat, if they aren't already.
  if (source.projName === 'longlat') {
    point = {
      x: point.x * D2R$1,
      y: point.y * D2R$1,
      z: point.z || 0
    };
  } else {
    if (source.to_meter) {
      point = {
        x: point.x * source.to_meter,
        y: point.y * source.to_meter,
        z: point.z || 0
      };
    }
    point = source.inverse(point); // Convert Cartesian to longlat
    if (!point) {
      return;
    }
  }
  // Adjust for the prime meridian if necessary
  if (source.from_greenwich) {
    point.x += source.from_greenwich;
  }

  // Convert datums if needed, and if possible.
  point = datum_transform(source.datum, dest.datum, point);
  if (!point) {
    return;
  }

  // Adjust for the prime meridian if necessary
  if (dest.from_greenwich) {
    point = {
      x: point.x - dest.from_greenwich,
      y: point.y,
      z: point.z || 0
    };
  }

  if (dest.projName === 'longlat') {
    // convert radians to decimal degrees
    point = {
      x: point.x * R2D,
      y: point.y * R2D,
      z: point.z || 0
    };
  } else { // else project
    point = dest.forward(point);
    if (dest.to_meter) {
      point = {
        x: point.x / dest.to_meter,
        y: point.y / dest.to_meter,
        z: point.z || 0
      };
    }
  }

  // DGR, 2010/11/12
  if (enforceAxis && dest.axis !== 'enu') {
    return adjust_axis(dest, true, point);
  }

  if (point && !hasZ) {
    delete point.z;
  }
  return point;
}

var wgs84$1 = Projection('WGS84');

function transformer$1(from, to, coords, enforceAxis) {
  var transformedArray, out, keys;
  if (Array.isArray(coords)) {
    transformedArray = transform$1(from, to, coords, enforceAxis) || {x: NaN, y: NaN};
    if (coords.length > 2) {
      if ((typeof from.name !== 'undefined' && from.name === 'geocent') || (typeof to.name !== 'undefined' && to.name === 'geocent')) {
        if (typeof transformedArray.z === 'number') {
          return [transformedArray.x, transformedArray.y, transformedArray.z].concat(coords.splice(3));
        } else {
          return [transformedArray.x, transformedArray.y, coords[2]].concat(coords.splice(3));
        }
      } else {
        return [transformedArray.x, transformedArray.y].concat(coords.splice(2));
      }
    } else {
      return [transformedArray.x, transformedArray.y];
    }
  } else {
    out = transform$1(from, to, coords, enforceAxis);
    keys = Object.keys(coords);
    if (keys.length === 2) {
      return out;
    }
    keys.forEach(function (key) {
      if ((typeof from.name !== 'undefined' && from.name === 'geocent') || (typeof to.name !== 'undefined' && to.name === 'geocent')) {
        if (key === 'x' || key === 'y' || key === 'z') {
          return;
        }
      } else {
        if (key === 'x' || key === 'y') {
          return;
        }
      }
      out[key] = coords[key];
    });
    return out;
  }
}

function checkProj(item) {
  if (item instanceof Projection) {
    return item;
  }
  if (item.oProj) {
    return item.oProj;
  }
  return Projection(item);
}

function proj4(fromProj, toProj, coord) {
  fromProj = checkProj(fromProj);
  var single = false;
  var obj;
  if (typeof toProj === 'undefined') {
    toProj = fromProj;
    fromProj = wgs84$1;
    single = true;
  } else if (typeof toProj.x !== 'undefined' || Array.isArray(toProj)) {
    coord = toProj;
    toProj = fromProj;
    fromProj = wgs84$1;
    single = true;
  }
  toProj = checkProj(toProj);
  if (coord) {
    return transformer$1(fromProj, toProj, coord);
  } else {
    obj = {
      forward: function (coords, enforceAxis) {
        return transformer$1(fromProj, toProj, coords, enforceAxis);
      },
      inverse: function (coords, enforceAxis) {
        return transformer$1(toProj, fromProj, coords, enforceAxis);
      }
    };
    if (single) {
      obj.oProj = toProj;
    }
    return obj;
  }
}

/**
 * UTM zones are grouped, and assigned to one of a group of 6
 * sets.
 *
 * {int} @private
 */
var NUM_100K_SETS = 6;

/**
 * The column letters (for easting) of the lower left value, per
 * set.
 *
 * {string} @private
 */
var SET_ORIGIN_COLUMN_LETTERS = 'AJSAJS';

/**
 * The row letters (for northing) of the lower left value, per
 * set.
 *
 * {string} @private
 */
var SET_ORIGIN_ROW_LETTERS = 'AFAFAF';

var A = 65; // A
var I = 73; // I
var O = 79; // O
var V = 86; // V
var Z = 90; // Z
var mgrs = {
  forward: forward$t,
  inverse: inverse$t,
  toPoint: toPoint
};
/**
 * Conversion of lat/lon to MGRS.
 *
 * @param {object} ll Object literal with lat and lon properties on a
 *     WGS84 ellipsoid.
 * @param {int} accuracy Accuracy in digits (5 for 1 m, 4 for 10 m, 3 for
 *      100 m, 2 for 1000 m or 1 for 10000 m). Optional, default is 5.
 * @return {string} the MGRS string for the given location and accuracy.
 */
function forward$t(ll, accuracy) {
  accuracy = accuracy || 5; // default accuracy 1m
  return encode(LLtoUTM({
    lat: ll[1],
    lon: ll[0]
  }), accuracy);
}
/**
 * Conversion of MGRS to lat/lon.
 *
 * @param {string} mgrs MGRS string.
 * @return {array} An array with left (longitude), bottom (latitude), right
 *     (longitude) and top (latitude) values in WGS84, representing the
 *     bounding box for the provided MGRS reference.
 */
function inverse$t(mgrs) {
  var bbox = UTMtoLL(decode(mgrs.toUpperCase()));
  if (bbox.lat && bbox.lon) {
    return [bbox.lon, bbox.lat, bbox.lon, bbox.lat];
  }
  return [bbox.left, bbox.bottom, bbox.right, bbox.top];
}
function toPoint(mgrs) {
  var bbox = UTMtoLL(decode(mgrs.toUpperCase()));
  if (bbox.lat && bbox.lon) {
    return [bbox.lon, bbox.lat];
  }
  return [(bbox.left + bbox.right) / 2, (bbox.top + bbox.bottom) / 2];
}/**
 * Conversion from degrees to radians.
 *
 * @private
 * @param {number} deg the angle in degrees.
 * @return {number} the angle in radians.
 */
function degToRad$1(deg) {
  return (deg * (Math.PI / 180.0));
}

/**
 * Conversion from radians to degrees.
 *
 * @private
 * @param {number} rad the angle in radians.
 * @return {number} the angle in degrees.
 */
function radToDeg$1(rad) {
  return (180.0 * (rad / Math.PI));
}

/**
 * Converts a set of Longitude and Latitude co-ordinates to UTM
 * using the WGS84 ellipsoid.
 *
 * @private
 * @param {object} ll Object literal with lat and lon properties
 *     representing the WGS84 coordinate to be converted.
 * @return {object} Object literal containing the UTM value with easting,
 *     northing, zoneNumber and zoneLetter properties, and an optional
 *     accuracy property in digits. Returns null if the conversion failed.
 */
function LLtoUTM(ll) {
  var Lat = ll.lat;
  var Long = ll.lon;
  var a = 6378137.0; //ellip.radius;
  var eccSquared = 0.00669438; //ellip.eccsq;
  var k0 = 0.9996;
  var LongOrigin;
  var eccPrimeSquared;
  var N, T, C, A, M;
  var LatRad = degToRad$1(Lat);
  var LongRad = degToRad$1(Long);
  var LongOriginRad;
  var ZoneNumber;
  // (int)
  ZoneNumber = Math.floor((Long + 180) / 6) + 1;

  //Make sure the longitude 180.00 is in Zone 60
  if (Long === 180) {
    ZoneNumber = 60;
  }

  // Special zone for Norway
  if (Lat >= 56.0 && Lat < 64.0 && Long >= 3.0 && Long < 12.0) {
    ZoneNumber = 32;
  }

  // Special zones for Svalbard
  if (Lat >= 72.0 && Lat < 84.0) {
    if (Long >= 0.0 && Long < 9.0) {
      ZoneNumber = 31;
    }
    else if (Long >= 9.0 && Long < 21.0) {
      ZoneNumber = 33;
    }
    else if (Long >= 21.0 && Long < 33.0) {
      ZoneNumber = 35;
    }
    else if (Long >= 33.0 && Long < 42.0) {
      ZoneNumber = 37;
    }
  }

  LongOrigin = (ZoneNumber - 1) * 6 - 180 + 3; //+3 puts origin
  // in middle of
  // zone
  LongOriginRad = degToRad$1(LongOrigin);

  eccPrimeSquared = (eccSquared) / (1 - eccSquared);

  N = a / Math.sqrt(1 - eccSquared * Math.sin(LatRad) * Math.sin(LatRad));
  T = Math.tan(LatRad) * Math.tan(LatRad);
  C = eccPrimeSquared * Math.cos(LatRad) * Math.cos(LatRad);
  A = Math.cos(LatRad) * (LongRad - LongOriginRad);

  M = a * ((1 - eccSquared / 4 - 3 * eccSquared * eccSquared / 64 - 5 * eccSquared * eccSquared * eccSquared / 256) * LatRad - (3 * eccSquared / 8 + 3 * eccSquared * eccSquared / 32 + 45 * eccSquared * eccSquared * eccSquared / 1024) * Math.sin(2 * LatRad) + (15 * eccSquared * eccSquared / 256 + 45 * eccSquared * eccSquared * eccSquared / 1024) * Math.sin(4 * LatRad) - (35 * eccSquared * eccSquared * eccSquared / 3072) * Math.sin(6 * LatRad));

  var UTMEasting = (k0 * N * (A + (1 - T + C) * A * A * A / 6.0 + (5 - 18 * T + T * T + 72 * C - 58 * eccPrimeSquared) * A * A * A * A * A / 120.0) + 500000.0);

  var UTMNorthing = (k0 * (M + N * Math.tan(LatRad) * (A * A / 2 + (5 - T + 9 * C + 4 * C * C) * A * A * A * A / 24.0 + (61 - 58 * T + T * T + 600 * C - 330 * eccPrimeSquared) * A * A * A * A * A * A / 720.0)));
  if (Lat < 0.0) {
    UTMNorthing += 10000000.0; //10000000 meter offset for
    // southern hemisphere
  }

  return {
    northing: Math.round(UTMNorthing),
    easting: Math.round(UTMEasting),
    zoneNumber: ZoneNumber,
    zoneLetter: getLetterDesignator(Lat)
  };
}

/**
 * Converts UTM coords to lat/long, using the WGS84 ellipsoid. This is a convenience
 * class where the Zone can be specified as a single string eg."60N" which
 * is then broken down into the ZoneNumber and ZoneLetter.
 *
 * @private
 * @param {object} utm An object literal with northing, easting, zoneNumber
 *     and zoneLetter properties. If an optional accuracy property is
 *     provided (in meters), a bounding box will be returned instead of
 *     latitude and longitude.
 * @return {object} An object literal containing either lat and lon values
 *     (if no accuracy was provided), or top, right, bottom and left values
 *     for the bounding box calculated according to the provided accuracy.
 *     Returns null if the conversion failed.
 */
function UTMtoLL(utm) {

  var UTMNorthing = utm.northing;
  var UTMEasting = utm.easting;
  var zoneLetter = utm.zoneLetter;
  var zoneNumber = utm.zoneNumber;
  // check the ZoneNummber is valid
  if (zoneNumber < 0 || zoneNumber > 60) {
    return null;
  }

  var k0 = 0.9996;
  var a = 6378137.0; //ellip.radius;
  var eccSquared = 0.00669438; //ellip.eccsq;
  var eccPrimeSquared;
  var e1 = (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared));
  var N1, T1, C1, R1, D, M;
  var LongOrigin;
  var mu, phi1Rad;

  // remove 500,000 meter offset for longitude
  var x = UTMEasting - 500000.0;
  var y = UTMNorthing;

  // We must know somehow if we are in the Northern or Southern
  // hemisphere, this is the only time we use the letter So even
  // if the Zone letter isn't exactly correct it should indicate
  // the hemisphere correctly
  if (zoneLetter < 'N') {
    y -= 10000000.0; // remove 10,000,000 meter offset used
    // for southern hemisphere
  }

  // There are 60 zones with zone 1 being at West -180 to -174
  LongOrigin = (zoneNumber - 1) * 6 - 180 + 3; // +3 puts origin
  // in middle of
  // zone

  eccPrimeSquared = (eccSquared) / (1 - eccSquared);

  M = y / k0;
  mu = M / (a * (1 - eccSquared / 4 - 3 * eccSquared * eccSquared / 64 - 5 * eccSquared * eccSquared * eccSquared / 256));

  phi1Rad = mu + (3 * e1 / 2 - 27 * e1 * e1 * e1 / 32) * Math.sin(2 * mu) + (21 * e1 * e1 / 16 - 55 * e1 * e1 * e1 * e1 / 32) * Math.sin(4 * mu) + (151 * e1 * e1 * e1 / 96) * Math.sin(6 * mu);
  // double phi1 = ProjMath.radToDeg(phi1Rad);

  N1 = a / Math.sqrt(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad));
  T1 = Math.tan(phi1Rad) * Math.tan(phi1Rad);
  C1 = eccPrimeSquared * Math.cos(phi1Rad) * Math.cos(phi1Rad);
  R1 = a * (1 - eccSquared) / Math.pow(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad), 1.5);
  D = x / (N1 * k0);

  var lat = phi1Rad - (N1 * Math.tan(phi1Rad) / R1) * (D * D / 2 - (5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * eccPrimeSquared) * D * D * D * D / 24 + (61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * eccPrimeSquared - 3 * C1 * C1) * D * D * D * D * D * D / 720);
  lat = radToDeg$1(lat);

  var lon = (D - (1 + 2 * T1 + C1) * D * D * D / 6 + (5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * eccPrimeSquared + 24 * T1 * T1) * D * D * D * D * D / 120) / Math.cos(phi1Rad);
  lon = LongOrigin + radToDeg$1(lon);

  var result;
  if (utm.accuracy) {
    var topRight = UTMtoLL({
      northing: utm.northing + utm.accuracy,
      easting: utm.easting + utm.accuracy,
      zoneLetter: utm.zoneLetter,
      zoneNumber: utm.zoneNumber
    });
    result = {
      top: topRight.lat,
      right: topRight.lon,
      bottom: lat,
      left: lon
    };
  }
  else {
    result = {
      lat: lat,
      lon: lon
    };
  }
  return result;
}

/**
 * Calculates the MGRS letter designator for the given latitude.
 *
 * @private
 * @param {number} lat The latitude in WGS84 to get the letter designator
 *     for.
 * @return {char} The letter designator.
 */
function getLetterDesignator(lat) {
  //This is here as an error flag to show that the Latitude is
  //outside MGRS limits
  var LetterDesignator = 'Z';

  if ((84 >= lat) && (lat >= 72)) {
    LetterDesignator = 'X';
  }
  else if ((72 > lat) && (lat >= 64)) {
    LetterDesignator = 'W';
  }
  else if ((64 > lat) && (lat >= 56)) {
    LetterDesignator = 'V';
  }
  else if ((56 > lat) && (lat >= 48)) {
    LetterDesignator = 'U';
  }
  else if ((48 > lat) && (lat >= 40)) {
    LetterDesignator = 'T';
  }
  else if ((40 > lat) && (lat >= 32)) {
    LetterDesignator = 'S';
  }
  else if ((32 > lat) && (lat >= 24)) {
    LetterDesignator = 'R';
  }
  else if ((24 > lat) && (lat >= 16)) {
    LetterDesignator = 'Q';
  }
  else if ((16 > lat) && (lat >= 8)) {
    LetterDesignator = 'P';
  }
  else if ((8 > lat) && (lat >= 0)) {
    LetterDesignator = 'N';
  }
  else if ((0 > lat) && (lat >= -8)) {
    LetterDesignator = 'M';
  }
  else if ((-8 > lat) && (lat >= -16)) {
    LetterDesignator = 'L';
  }
  else if ((-16 > lat) && (lat >= -24)) {
    LetterDesignator = 'K';
  }
  else if ((-24 > lat) && (lat >= -32)) {
    LetterDesignator = 'J';
  }
  else if ((-32 > lat) && (lat >= -40)) {
    LetterDesignator = 'H';
  }
  else if ((-40 > lat) && (lat >= -48)) {
    LetterDesignator = 'G';
  }
  else if ((-48 > lat) && (lat >= -56)) {
    LetterDesignator = 'F';
  }
  else if ((-56 > lat) && (lat >= -64)) {
    LetterDesignator = 'E';
  }
  else if ((-64 > lat) && (lat >= -72)) {
    LetterDesignator = 'D';
  }
  else if ((-72 > lat) && (lat >= -80)) {
    LetterDesignator = 'C';
  }
  return LetterDesignator;
}

/**
 * Encodes a UTM location as MGRS string.
 *
 * @private
 * @param {object} utm An object literal with easting, northing,
 *     zoneLetter, zoneNumber
 * @param {number} accuracy Accuracy in digits (1-5).
 * @return {string} MGRS string for the given UTM location.
 */
function encode(utm, accuracy) {
  // prepend with leading zeroes
  var seasting = "00000" + utm.easting,
    snorthing = "00000" + utm.northing;

  return utm.zoneNumber + utm.zoneLetter + get100kID(utm.easting, utm.northing, utm.zoneNumber) + seasting.substr(seasting.length - 5, accuracy) + snorthing.substr(snorthing.length - 5, accuracy);
}

/**
 * Get the two letter 100k designator for a given UTM easting,
 * northing and zone number value.
 *
 * @private
 * @param {number} easting
 * @param {number} northing
 * @param {number} zoneNumber
 * @return the two letter 100k designator for the given UTM location.
 */
function get100kID(easting, northing, zoneNumber) {
  var setParm = get100kSetForZone(zoneNumber);
  var setColumn = Math.floor(easting / 100000);
  var setRow = Math.floor(northing / 100000) % 20;
  return getLetter100kID(setColumn, setRow, setParm);
}

/**
 * Given a UTM zone number, figure out the MGRS 100K set it is in.
 *
 * @private
 * @param {number} i An UTM zone number.
 * @return {number} the 100k set the UTM zone is in.
 */
function get100kSetForZone(i) {
  var setParm = i % NUM_100K_SETS;
  if (setParm === 0) {
    setParm = NUM_100K_SETS;
  }

  return setParm;
}

/**
 * Get the two-letter MGRS 100k designator given information
 * translated from the UTM northing, easting and zone number.
 *
 * @private
 * @param {number} column the column index as it relates to the MGRS
 *        100k set spreadsheet, created from the UTM easting.
 *        Values are 1-8.
 * @param {number} row the row index as it relates to the MGRS 100k set
 *        spreadsheet, created from the UTM northing value. Values
 *        are from 0-19.
 * @param {number} parm the set block, as it relates to the MGRS 100k set
 *        spreadsheet, created from the UTM zone. Values are from
 *        1-60.
 * @return two letter MGRS 100k code.
 */
function getLetter100kID(column, row, parm) {
  // colOrigin and rowOrigin are the letters at the origin of the set
  var index = parm - 1;
  var colOrigin = SET_ORIGIN_COLUMN_LETTERS.charCodeAt(index);
  var rowOrigin = SET_ORIGIN_ROW_LETTERS.charCodeAt(index);

  // colInt and rowInt are the letters to build to return
  var colInt = colOrigin + column - 1;
  var rowInt = rowOrigin + row;
  var rollover = false;

  if (colInt > Z) {
    colInt = colInt - Z + A - 1;
    rollover = true;
  }

  if (colInt === I || (colOrigin < I && colInt > I) || ((colInt > I || colOrigin < I) && rollover)) {
    colInt++;
  }

  if (colInt === O || (colOrigin < O && colInt > O) || ((colInt > O || colOrigin < O) && rollover)) {
    colInt++;

    if (colInt === I) {
      colInt++;
    }
  }

  if (colInt > Z) {
    colInt = colInt - Z + A - 1;
  }

  if (rowInt > V) {
    rowInt = rowInt - V + A - 1;
    rollover = true;
  }
  else {
    rollover = false;
  }

  if (((rowInt === I) || ((rowOrigin < I) && (rowInt > I))) || (((rowInt > I) || (rowOrigin < I)) && rollover)) {
    rowInt++;
  }

  if (((rowInt === O) || ((rowOrigin < O) && (rowInt > O))) || (((rowInt > O) || (rowOrigin < O)) && rollover)) {
    rowInt++;

    if (rowInt === I) {
      rowInt++;
    }
  }

  if (rowInt > V) {
    rowInt = rowInt - V + A - 1;
  }

  var twoLetter = String.fromCharCode(colInt) + String.fromCharCode(rowInt);
  return twoLetter;
}

/**
 * Decode the UTM parameters from a MGRS string.
 *
 * @private
 * @param {string} mgrsString an UPPERCASE coordinate string is expected.
 * @return {object} An object literal with easting, northing, zoneLetter,
 *     zoneNumber and accuracy (in meters) properties.
 */
function decode(mgrsString) {

  if (mgrsString && mgrsString.length === 0) {
    throw ("MGRSPoint coverting from nothing");
  }

  var length = mgrsString.length;

  var hunK = null;
  var sb = "";
  var testChar;
  var i = 0;

  // get Zone number
  while (!(/[A-Z]/).test(testChar = mgrsString.charAt(i))) {
    if (i >= 2) {
      throw ("MGRSPoint bad conversion from: " + mgrsString);
    }
    sb += testChar;
    i++;
  }

  var zoneNumber = parseInt(sb, 10);

  if (i === 0 || i + 3 > length) {
    // A good MGRS string has to be 4-5 digits long,
    // ##AAA/#AAA at least.
    throw ("MGRSPoint bad conversion from: " + mgrsString);
  }

  var zoneLetter = mgrsString.charAt(i++);

  // Should we check the zone letter here? Why not.
  if (zoneLetter <= 'A' || zoneLetter === 'B' || zoneLetter === 'Y' || zoneLetter >= 'Z' || zoneLetter === 'I' || zoneLetter === 'O') {
    throw ("MGRSPoint zone letter " + zoneLetter + " not handled: " + mgrsString);
  }

  hunK = mgrsString.substring(i, i += 2);

  var set = get100kSetForZone(zoneNumber);

  var east100k = getEastingFromChar(hunK.charAt(0), set);
  var north100k = getNorthingFromChar(hunK.charAt(1), set);

  // We have a bug where the northing may be 2000000 too low.
  // How
  // do we know when to roll over?

  while (north100k < getMinNorthing(zoneLetter)) {
    north100k += 2000000;
  }

  // calculate the char index for easting/northing separator
  var remainder = length - i;

  if (remainder % 2 !== 0) {
    throw ("MGRSPoint has to have an even number \nof digits after the zone letter and two 100km letters - front \nhalf for easting meters, second half for \nnorthing meters" + mgrsString);
  }

  var sep = remainder / 2;

  var sepEasting = 0.0;
  var sepNorthing = 0.0;
  var accuracyBonus, sepEastingString, sepNorthingString, easting, northing;
  if (sep > 0) {
    accuracyBonus = 100000.0 / Math.pow(10, sep);
    sepEastingString = mgrsString.substring(i, i + sep);
    sepEasting = parseFloat(sepEastingString) * accuracyBonus;
    sepNorthingString = mgrsString.substring(i + sep);
    sepNorthing = parseFloat(sepNorthingString) * accuracyBonus;
  }

  easting = sepEasting + east100k;
  northing = sepNorthing + north100k;

  return {
    easting: easting,
    northing: northing,
    zoneLetter: zoneLetter,
    zoneNumber: zoneNumber,
    accuracy: accuracyBonus
  };
}

/**
 * Given the first letter from a two-letter MGRS 100k zone, and given the
 * MGRS table set for the zone number, figure out the easting value that
 * should be added to the other, secondary easting value.
 *
 * @private
 * @param {char} e The first letter from a two-letter MGRS 100´k zone.
 * @param {number} set The MGRS table set for the zone number.
 * @return {number} The easting value for the given letter and set.
 */
function getEastingFromChar(e, set) {
  // colOrigin is the letter at the origin of the set for the
  // column
  var curCol = SET_ORIGIN_COLUMN_LETTERS.charCodeAt(set - 1);
  var eastingValue = 100000.0;
  var rewindMarker = false;

  while (curCol !== e.charCodeAt(0)) {
    curCol++;
    if (curCol === I) {
      curCol++;
    }
    if (curCol === O) {
      curCol++;
    }
    if (curCol > Z) {
      if (rewindMarker) {
        throw ("Bad character: " + e);
      }
      curCol = A;
      rewindMarker = true;
    }
    eastingValue += 100000.0;
  }

  return eastingValue;
}

/**
 * Given the second letter from a two-letter MGRS 100k zone, and given the
 * MGRS table set for the zone number, figure out the northing value that
 * should be added to the other, secondary northing value. You have to
 * remember that Northings are determined from the equator, and the vertical
 * cycle of letters mean a 2000000 additional northing meters. This happens
 * approx. every 18 degrees of latitude. This method does *NOT* count any
 * additional northings. You have to figure out how many 2000000 meters need
 * to be added for the zone letter of the MGRS coordinate.
 *
 * @private
 * @param {char} n Second letter of the MGRS 100k zone
 * @param {number} set The MGRS table set number, which is dependent on the
 *     UTM zone number.
 * @return {number} The northing value for the given letter and set.
 */
function getNorthingFromChar(n, set) {

  if (n > 'V') {
    throw ("MGRSPoint given invalid Northing " + n);
  }

  // rowOrigin is the letter at the origin of the set for the
  // column
  var curRow = SET_ORIGIN_ROW_LETTERS.charCodeAt(set - 1);
  var northingValue = 0.0;
  var rewindMarker = false;

  while (curRow !== n.charCodeAt(0)) {
    curRow++;
    if (curRow === I) {
      curRow++;
    }
    if (curRow === O) {
      curRow++;
    }
    // fixing a bug making whole application hang in this loop
    // when 'n' is a wrong character
    if (curRow > V) {
      if (rewindMarker) { // making sure that this loop ends
        throw ("Bad character: " + n);
      }
      curRow = A;
      rewindMarker = true;
    }
    northingValue += 100000.0;
  }

  return northingValue;
}

/**
 * The function getMinNorthing returns the minimum northing value of a MGRS
 * zone.
 *
 * Ported from Geotrans' c Lattitude_Band_Value structure table.
 *
 * @private
 * @param {char} zoneLetter The MGRS zone to get the min northing for.
 * @return {number}
 */
function getMinNorthing(zoneLetter) {
  var northing;
  switch (zoneLetter) {
  case 'C':
    northing = 1100000.0;
    break;
  case 'D':
    northing = 2000000.0;
    break;
  case 'E':
    northing = 2800000.0;
    break;
  case 'F':
    northing = 3700000.0;
    break;
  case 'G':
    northing = 4600000.0;
    break;
  case 'H':
    northing = 5500000.0;
    break;
  case 'J':
    northing = 6400000.0;
    break;
  case 'K':
    northing = 7300000.0;
    break;
  case 'L':
    northing = 8200000.0;
    break;
  case 'M':
    northing = 9100000.0;
    break;
  case 'N':
    northing = 0.0;
    break;
  case 'P':
    northing = 800000.0;
    break;
  case 'Q':
    northing = 1700000.0;
    break;
  case 'R':
    northing = 2600000.0;
    break;
  case 'S':
    northing = 3500000.0;
    break;
  case 'T':
    northing = 4400000.0;
    break;
  case 'U':
    northing = 5300000.0;
    break;
  case 'V':
    northing = 6200000.0;
    break;
  case 'W':
    northing = 7000000.0;
    break;
  case 'X':
    northing = 7900000.0;
    break;
  default:
    northing = -1.0;
  }
  if (northing >= 0.0) {
    return northing;
  }
  else {
    throw ("Invalid zone letter: " + zoneLetter);
  }

}

function Point$2(x, y, z) {
  if (!(this instanceof Point$2)) {
    return new Point$2(x, y, z);
  }
  if (Array.isArray(x)) {
    this.x = x[0];
    this.y = x[1];
    this.z = x[2] || 0.0;
  } else if(typeof x === 'object') {
    this.x = x.x;
    this.y = x.y;
    this.z = x.z || 0.0;
  } else if (typeof x === 'string' && typeof y === 'undefined') {
    var coords = x.split(',');
    this.x = parseFloat(coords[0], 10);
    this.y = parseFloat(coords[1], 10);
    this.z = parseFloat(coords[2], 10) || 0.0;
  } else {
    this.x = x;
    this.y = y;
    this.z = z || 0.0;
  }
  console.warn('proj4.Point will be removed in version 3, use proj4.toPoint');
}

Point$2.fromMGRS = function(mgrsStr) {
  return new Point$2(toPoint(mgrsStr));
};
Point$2.prototype.toMGRS = function(accuracy) {
  return forward$t([this.x, this.y], accuracy);
};

var C00 = 1;
var C02 = 0.25;
var C04 = 0.046875;
var C06 = 0.01953125;
var C08 = 0.01068115234375;
var C22 = 0.75;
var C44 = 0.46875;
var C46 = 0.01302083333333333333;
var C48 = 0.00712076822916666666;
var C66 = 0.36458333333333333333;
var C68 = 0.00569661458333333333;
var C88 = 0.3076171875;

function pj_enfn(es) {
  var en = [];
  en[0] = C00 - es * (C02 + es * (C04 + es * (C06 + es * C08)));
  en[1] = es * (C22 - es * (C04 + es * (C06 + es * C08)));
  var t = es * es;
  en[2] = t * (C44 - es * (C46 + es * C48));
  t *= es;
  en[3] = t * (C66 - es * C68);
  en[4] = t * es * C88;
  return en;
}

function pj_mlfn(phi, sphi, cphi, en) {
  cphi *= sphi;
  sphi *= sphi;
  return (en[0] * phi - cphi * (en[1] + sphi * (en[2] + sphi * (en[3] + sphi * en[4]))));
}

var MAX_ITER$3 = 20;

function pj_inv_mlfn(arg, es, en) {
  var k = 1 / (1 - es);
  var phi = arg;
  for (var i = MAX_ITER$3; i; --i) { /* rarely goes over 2 iterations */
    var s = Math.sin(phi);
    var t = 1 - es * s * s;
    //t = this.pj_mlfn(phi, s, Math.cos(phi), en) - arg;
    //phi -= t * (t * Math.sqrt(t)) * k;
    t = (pj_mlfn(phi, s, Math.cos(phi), en) - arg) * (t * Math.sqrt(t)) * k;
    phi -= t;
    if (Math.abs(t) < EPSLN) {
      return phi;
    }
  }
  //..reportError("cass:pj_inv_mlfn: Convergence error");
  return phi;
}

// Heavily based on this tmerc projection implementation

function init$u() {
  this.x0 = this.x0 !== undefined ? this.x0 : 0;
  this.y0 = this.y0 !== undefined ? this.y0 : 0;
  this.long0 = this.long0 !== undefined ? this.long0 : 0;
  this.lat0 = this.lat0 !== undefined ? this.lat0 : 0;

  if (this.es) {
    this.en = pj_enfn(this.es);
    this.ml0 = pj_mlfn(this.lat0, Math.sin(this.lat0), Math.cos(this.lat0), this.en);
  }
}

/**
    Transverse Mercator Forward  - long/lat to x/y
    long/lat in radians
  */
function forward$s(p) {
  var lon = p.x;
  var lat = p.y;

  var delta_lon = adjust_lon(lon - this.long0);
  var con;
  var x, y;
  var sin_phi = Math.sin(lat);
  var cos_phi = Math.cos(lat);

  if (!this.es) {
    var b = cos_phi * Math.sin(delta_lon);

    if ((Math.abs(Math.abs(b) - 1)) < EPSLN) {
      return (93);
    }
    else {
      x = 0.5 * this.a * this.k0 * Math.log((1 + b) / (1 - b)) + this.x0;
      y = cos_phi * Math.cos(delta_lon) / Math.sqrt(1 - Math.pow(b, 2));
      b = Math.abs(y);

      if (b >= 1) {
        if ((b - 1) > EPSLN) {
          return (93);
        }
        else {
          y = 0;
        }
      }
      else {
        y = Math.acos(y);
      }

      if (lat < 0) {
        y = -y;
      }

      y = this.a * this.k0 * (y - this.lat0) + this.y0;
    }
  }
  else {
    var al = cos_phi * delta_lon;
    var als = Math.pow(al, 2);
    var c = this.ep2 * Math.pow(cos_phi, 2);
    var cs = Math.pow(c, 2);
    var tq = Math.abs(cos_phi) > EPSLN ? Math.tan(lat) : 0;
    var t = Math.pow(tq, 2);
    var ts = Math.pow(t, 2);
    con = 1 - this.es * Math.pow(sin_phi, 2);
    al = al / Math.sqrt(con);
    var ml = pj_mlfn(lat, sin_phi, cos_phi, this.en);

    x = this.a * (this.k0 * al * (1 +
      als / 6 * (1 - t + c +
      als / 20 * (5 - 18 * t + ts + 14 * c - 58 * t * c +
      als / 42 * (61 + 179 * ts - ts * t - 479 * t))))) +
      this.x0;

    y = this.a * (this.k0 * (ml - this.ml0 +
      sin_phi * delta_lon * al / 2 * (1 +
      als / 12 * (5 - t + 9 * c + 4 * cs +
      als / 30 * (61 + ts - 58 * t + 270 * c - 330 * t * c +
      als / 56 * (1385 + 543 * ts - ts * t - 3111 * t)))))) +
      this.y0;
  }

  p.x = x;
  p.y = y;

  return p;
}

/**
    Transverse Mercator Inverse  -  x/y to long/lat
  */
function inverse$s(p) {
  var con, phi;
  var lat, lon;
  var x = (p.x - this.x0) * (1 / this.a);
  var y = (p.y - this.y0) * (1 / this.a);

  if (!this.es) {
    var f = Math.exp(x / this.k0);
    var g = 0.5 * (f - 1 / f);
    var temp = this.lat0 + y / this.k0;
    var h = Math.cos(temp);
    con = Math.sqrt((1 - Math.pow(h, 2)) / (1 + Math.pow(g, 2)));
    lat = Math.asin(con);

    if (y < 0) {
      lat = -lat;
    }

    if ((g === 0) && (h === 0)) {
      lon = 0;
    }
    else {
      lon = adjust_lon(Math.atan2(g, h) + this.long0);
    }
  }
  else { // ellipsoidal form
    con = this.ml0 + y / this.k0;
    phi = pj_inv_mlfn(con, this.es, this.en);

    if (Math.abs(phi) < HALF_PI) {
      var sin_phi = Math.sin(phi);
      var cos_phi = Math.cos(phi);
      var tan_phi = Math.abs(cos_phi) > EPSLN ? Math.tan(phi) : 0;
      var c = this.ep2 * Math.pow(cos_phi, 2);
      var cs = Math.pow(c, 2);
      var t = Math.pow(tan_phi, 2);
      var ts = Math.pow(t, 2);
      con = 1 - this.es * Math.pow(sin_phi, 2);
      var d = x * Math.sqrt(con) / this.k0;
      var ds = Math.pow(d, 2);
      con = con * tan_phi;

      lat = phi - (con * ds / (1 - this.es)) * 0.5 * (1 -
        ds / 12 * (5 + 3 * t - 9 * c * t + c - 4 * cs -
        ds / 30 * (61 + 90 * t - 252 * c * t + 45 * ts + 46 * c -
        ds / 56 * (1385 + 3633 * t + 4095 * ts + 1574 * ts * t))));

      lon = adjust_lon(this.long0 + (d * (1 -
        ds / 6 * (1 + 2 * t + c -
        ds / 20 * (5 + 28 * t + 24 * ts + 8 * c * t + 6 * c -
        ds / 42 * (61 + 662 * t + 1320 * ts + 720 * ts * t)))) / cos_phi));
    }
    else {
      lat = HALF_PI * sign(y);
      lon = 0;
    }
  }

  p.x = lon;
  p.y = lat;

  return p;
}

var names$t = ["Fast_Transverse_Mercator", "Fast Transverse Mercator"];
var tmerc = {
  init: init$u,
  forward: forward$s,
  inverse: inverse$s,
  names: names$t
};

function sinh(x) {
  var r = Math.exp(x);
  r = (r - 1 / r) / 2;
  return r;
}

function hypot(x, y) {
  x = Math.abs(x);
  y = Math.abs(y);
  var a = Math.max(x, y);
  var b = Math.min(x, y) / (a ? a : 1);

  return a * Math.sqrt(1 + Math.pow(b, 2));
}

function log1py(x) {
  var y = 1 + x;
  var z = y - 1;

  return z === 0 ? x : x * Math.log(y) / z;
}

function asinhy(x) {
  var y = Math.abs(x);
  y = log1py(y * (1 + y / (hypot(1, y) + 1)));

  return x < 0 ? -y : y;
}

function gatg(pp, B) {
  var cos_2B = 2 * Math.cos(2 * B);
  var i = pp.length - 1;
  var h1 = pp[i];
  var h2 = 0;
  var h;

  while (--i >= 0) {
    h = -h2 + cos_2B * h1 + pp[i];
    h2 = h1;
    h1 = h;
  }

  return (B + h * Math.sin(2 * B));
}

function clens(pp, arg_r) {
  var r = 2 * Math.cos(arg_r);
  var i = pp.length - 1;
  var hr1 = pp[i];
  var hr2 = 0;
  var hr;

  while (--i >= 0) {
    hr = -hr2 + r * hr1 + pp[i];
    hr2 = hr1;
    hr1 = hr;
  }

  return Math.sin(arg_r) * hr;
}

function cosh(x) {
  var r = Math.exp(x);
  r = (r + 1 / r) / 2;
  return r;
}

function clens_cmplx(pp, arg_r, arg_i) {
  var sin_arg_r = Math.sin(arg_r);
  var cos_arg_r = Math.cos(arg_r);
  var sinh_arg_i = sinh(arg_i);
  var cosh_arg_i = cosh(arg_i);
  var r = 2 * cos_arg_r * cosh_arg_i;
  var i = -2 * sin_arg_r * sinh_arg_i;
  var j = pp.length - 1;
  var hr = pp[j];
  var hi1 = 0;
  var hr1 = 0;
  var hi = 0;
  var hr2;
  var hi2;

  while (--j >= 0) {
    hr2 = hr1;
    hi2 = hi1;
    hr1 = hr;
    hi1 = hi;
    hr = -hr2 + r * hr1 - i * hi1 + pp[j];
    hi = -hi2 + i * hr1 + r * hi1;
  }

  r = sin_arg_r * cosh_arg_i;
  i = cos_arg_r * sinh_arg_i;

  return [r * hr - i * hi, r * hi + i * hr];
}

// Heavily based on this etmerc projection implementation

function init$t() {
  if (!this.approx && (isNaN(this.es) || this.es <= 0)) {
    throw new Error('Incorrect elliptical usage. Try using the +approx option in the proj string, or PROJECTION["Fast_Transverse_Mercator"] in the WKT.');
  }
  if (this.approx) {
    // When '+approx' is set, use tmerc instead
    tmerc.init.apply(this);
    this.forward = tmerc.forward;
    this.inverse = tmerc.inverse;
  }

  this.x0 = this.x0 !== undefined ? this.x0 : 0;
  this.y0 = this.y0 !== undefined ? this.y0 : 0;
  this.long0 = this.long0 !== undefined ? this.long0 : 0;
  this.lat0 = this.lat0 !== undefined ? this.lat0 : 0;

  this.cgb = [];
  this.cbg = [];
  this.utg = [];
  this.gtu = [];

  var f = this.es / (1 + Math.sqrt(1 - this.es));
  var n = f / (2 - f);
  var np = n;

  this.cgb[0] = n * (2 + n * (-2 / 3 + n * (-2 + n * (116 / 45 + n * (26 / 45 + n * (-2854 / 675 ))))));
  this.cbg[0] = n * (-2 + n * ( 2 / 3 + n * ( 4 / 3 + n * (-82 / 45 + n * (32 / 45 + n * (4642 / 4725))))));

  np = np * n;
  this.cgb[1] = np * (7 / 3 + n * (-8 / 5 + n * (-227 / 45 + n * (2704 / 315 + n * (2323 / 945)))));
  this.cbg[1] = np * (5 / 3 + n * (-16 / 15 + n * ( -13 / 9 + n * (904 / 315 + n * (-1522 / 945)))));

  np = np * n;
  this.cgb[2] = np * (56 / 15 + n * (-136 / 35 + n * (-1262 / 105 + n * (73814 / 2835))));
  this.cbg[2] = np * (-26 / 15 + n * (34 / 21 + n * (8 / 5 + n * (-12686 / 2835))));

  np = np * n;
  this.cgb[3] = np * (4279 / 630 + n * (-332 / 35 + n * (-399572 / 14175)));
  this.cbg[3] = np * (1237 / 630 + n * (-12 / 5 + n * ( -24832 / 14175)));

  np = np * n;
  this.cgb[4] = np * (4174 / 315 + n * (-144838 / 6237));
  this.cbg[4] = np * (-734 / 315 + n * (109598 / 31185));

  np = np * n;
  this.cgb[5] = np * (601676 / 22275);
  this.cbg[5] = np * (444337 / 155925);

  np = Math.pow(n, 2);
  this.Qn = this.k0 / (1 + n) * (1 + np * (1 / 4 + np * (1 / 64 + np / 256)));

  this.utg[0] = n * (-0.5 + n * ( 2 / 3 + n * (-37 / 96 + n * ( 1 / 360 + n * (81 / 512 + n * (-96199 / 604800))))));
  this.gtu[0] = n * (0.5 + n * (-2 / 3 + n * (5 / 16 + n * (41 / 180 + n * (-127 / 288 + n * (7891 / 37800))))));

  this.utg[1] = np * (-1 / 48 + n * (-1 / 15 + n * (437 / 1440 + n * (-46 / 105 + n * (1118711 / 3870720)))));
  this.gtu[1] = np * (13 / 48 + n * (-3 / 5 + n * (557 / 1440 + n * (281 / 630 + n * (-1983433 / 1935360)))));

  np = np * n;
  this.utg[2] = np * (-17 / 480 + n * (37 / 840 + n * (209 / 4480 + n * (-5569 / 90720 ))));
  this.gtu[2] = np * (61 / 240 + n * (-103 / 140 + n * (15061 / 26880 + n * (167603 / 181440))));

  np = np * n;
  this.utg[3] = np * (-4397 / 161280 + n * (11 / 504 + n * (830251 / 7257600)));
  this.gtu[3] = np * (49561 / 161280 + n * (-179 / 168 + n * (6601661 / 7257600)));

  np = np * n;
  this.utg[4] = np * (-4583 / 161280 + n * (108847 / 3991680));
  this.gtu[4] = np * (34729 / 80640 + n * (-3418889 / 1995840));

  np = np * n;
  this.utg[5] = np * (-20648693 / 638668800);
  this.gtu[5] = np * (212378941 / 319334400);

  var Z = gatg(this.cbg, this.lat0);
  this.Zb = -this.Qn * (Z + clens(this.gtu, 2 * Z));
}

function forward$r(p) {
  var Ce = adjust_lon(p.x - this.long0);
  var Cn = p.y;

  Cn = gatg(this.cbg, Cn);
  var sin_Cn = Math.sin(Cn);
  var cos_Cn = Math.cos(Cn);
  var sin_Ce = Math.sin(Ce);
  var cos_Ce = Math.cos(Ce);

  Cn = Math.atan2(sin_Cn, cos_Ce * cos_Cn);
  Ce = Math.atan2(sin_Ce * cos_Cn, hypot(sin_Cn, cos_Cn * cos_Ce));
  Ce = asinhy(Math.tan(Ce));

  var tmp = clens_cmplx(this.gtu, 2 * Cn, 2 * Ce);

  Cn = Cn + tmp[0];
  Ce = Ce + tmp[1];

  var x;
  var y;

  if (Math.abs(Ce) <= 2.623395162778) {
    x = this.a * (this.Qn * Ce) + this.x0;
    y = this.a * (this.Qn * Cn + this.Zb) + this.y0;
  }
  else {
    x = Infinity;
    y = Infinity;
  }

  p.x = x;
  p.y = y;

  return p;
}

function inverse$r(p) {
  var Ce = (p.x - this.x0) * (1 / this.a);
  var Cn = (p.y - this.y0) * (1 / this.a);

  Cn = (Cn - this.Zb) / this.Qn;
  Ce = Ce / this.Qn;

  var lon;
  var lat;

  if (Math.abs(Ce) <= 2.623395162778) {
    var tmp = clens_cmplx(this.utg, 2 * Cn, 2 * Ce);

    Cn = Cn + tmp[0];
    Ce = Ce + tmp[1];
    Ce = Math.atan(sinh(Ce));

    var sin_Cn = Math.sin(Cn);
    var cos_Cn = Math.cos(Cn);
    var sin_Ce = Math.sin(Ce);
    var cos_Ce = Math.cos(Ce);

    Cn = Math.atan2(sin_Cn * cos_Ce, hypot(sin_Ce, cos_Ce * cos_Cn));
    Ce = Math.atan2(sin_Ce, cos_Ce * cos_Cn);

    lon = adjust_lon(Ce + this.long0);
    lat = gatg(this.cgb, Cn);
  }
  else {
    lon = Infinity;
    lat = Infinity;
  }

  p.x = lon;
  p.y = lat;

  return p;
}

var names$s = ["Extended_Transverse_Mercator", "Extended Transverse Mercator", "etmerc", "Transverse_Mercator", "Transverse Mercator", "Gauss Kruger", "Gauss_Kruger", "tmerc"];
var etmerc = {
  init: init$t,
  forward: forward$r,
  inverse: inverse$r,
  names: names$s
};

function adjust_zone(zone, lon) {
  if (zone === undefined) {
    zone = Math.floor((adjust_lon(lon) + Math.PI) * 30 / Math.PI) + 1;

    if (zone < 0) {
      return 0;
    } else if (zone > 60) {
      return 60;
    }
  }
  return zone;
}

var dependsOn = 'etmerc';


function init$s() {
  var zone = adjust_zone(this.zone, this.long0);
  if (zone === undefined) {
    throw new Error('unknown utm zone');
  }
  this.lat0 = 0;
  this.long0 =  ((6 * Math.abs(zone)) - 183) * D2R$1;
  this.x0 = 500000;
  this.y0 = this.utmSouth ? 10000000 : 0;
  this.k0 = 0.9996;

  etmerc.init.apply(this);
  this.forward = etmerc.forward;
  this.inverse = etmerc.inverse;
}

var names$r = ["Universal Transverse Mercator System", "utm"];
var utm = {
  init: init$s,
  names: names$r,
  dependsOn: dependsOn
};

function srat(esinp, exp) {
  return (Math.pow((1 - esinp) / (1 + esinp), exp));
}

var MAX_ITER$2 = 20;

function init$r() {
  var sphi = Math.sin(this.lat0);
  var cphi = Math.cos(this.lat0);
  cphi *= cphi;
  this.rc = Math.sqrt(1 - this.es) / (1 - this.es * sphi * sphi);
  this.C = Math.sqrt(1 + this.es * cphi * cphi / (1 - this.es));
  this.phic0 = Math.asin(sphi / this.C);
  this.ratexp = 0.5 * this.C * this.e;
  this.K = Math.tan(0.5 * this.phic0 + FORTPI) / (Math.pow(Math.tan(0.5 * this.lat0 + FORTPI), this.C) * srat(this.e * sphi, this.ratexp));
}

function forward$q(p) {
  var lon = p.x;
  var lat = p.y;

  p.y = 2 * Math.atan(this.K * Math.pow(Math.tan(0.5 * lat + FORTPI), this.C) * srat(this.e * Math.sin(lat), this.ratexp)) - HALF_PI;
  p.x = this.C * lon;
  return p;
}

function inverse$q(p) {
  var DEL_TOL = 1e-14;
  var lon = p.x / this.C;
  var lat = p.y;
  var num = Math.pow(Math.tan(0.5 * lat + FORTPI) / this.K, 1 / this.C);
  for (var i = MAX_ITER$2; i > 0; --i) {
    lat = 2 * Math.atan(num * srat(this.e * Math.sin(p.y), - 0.5 * this.e)) - HALF_PI;
    if (Math.abs(lat - p.y) < DEL_TOL) {
      break;
    }
    p.y = lat;
  }
  /* convergence failed */
  if (!i) {
    return null;
  }
  p.x = lon;
  p.y = lat;
  return p;
}

var names$q = ["gauss"];
var gauss = {
  init: init$r,
  forward: forward$q,
  inverse: inverse$q,
  names: names$q
};

function init$q() {
  gauss.init.apply(this);
  if (!this.rc) {
    return;
  }
  this.sinc0 = Math.sin(this.phic0);
  this.cosc0 = Math.cos(this.phic0);
  this.R2 = 2 * this.rc;
  if (!this.title) {
    this.title = "Oblique Stereographic Alternative";
  }
}

function forward$p(p) {
  var sinc, cosc, cosl, k;
  p.x = adjust_lon(p.x - this.long0);
  gauss.forward.apply(this, [p]);
  sinc = Math.sin(p.y);
  cosc = Math.cos(p.y);
  cosl = Math.cos(p.x);
  k = this.k0 * this.R2 / (1 + this.sinc0 * sinc + this.cosc0 * cosc * cosl);
  p.x = k * cosc * Math.sin(p.x);
  p.y = k * (this.cosc0 * sinc - this.sinc0 * cosc * cosl);
  p.x = this.a * p.x + this.x0;
  p.y = this.a * p.y + this.y0;
  return p;
}

function inverse$p(p) {
  var sinc, cosc, lon, lat, rho;
  p.x = (p.x - this.x0) / this.a;
  p.y = (p.y - this.y0) / this.a;

  p.x /= this.k0;
  p.y /= this.k0;
  if ((rho = hypot(p.x, p.y))) {
    var c = 2 * Math.atan2(rho, this.R2);
    sinc = Math.sin(c);
    cosc = Math.cos(c);
    lat = Math.asin(cosc * this.sinc0 + p.y * sinc * this.cosc0 / rho);
    lon = Math.atan2(p.x * sinc, rho * this.cosc0 * cosc - p.y * this.sinc0 * sinc);
  }
  else {
    lat = this.phic0;
    lon = 0;
  }

  p.x = lon;
  p.y = lat;
  gauss.inverse.apply(this, [p]);
  p.x = adjust_lon(p.x + this.long0);
  return p;
}

var names$p = ["Stereographic_North_Pole", "Oblique_Stereographic", "sterea","Oblique Stereographic Alternative","Double_Stereographic"];
var sterea = {
  init: init$q,
  forward: forward$p,
  inverse: inverse$p,
  names: names$p
};

function ssfn_(phit, sinphi, eccen) {
  sinphi *= eccen;
  return (Math.tan(0.5 * (HALF_PI + phit)) * Math.pow((1 - sinphi) / (1 + sinphi), 0.5 * eccen));
}

function init$p() {

  // setting default parameters
  this.x0 = this.x0 || 0;
  this.y0 = this.y0 || 0;
  this.lat0 = this.lat0 || 0;
  this.long0 = this.long0 || 0;

  this.coslat0 = Math.cos(this.lat0);
  this.sinlat0 = Math.sin(this.lat0);
  if (this.sphere) {
    if (this.k0 === 1 && !isNaN(this.lat_ts) && Math.abs(this.coslat0) <= EPSLN) {
      this.k0 = 0.5 * (1 + sign(this.lat0) * Math.sin(this.lat_ts));
    }
  }
  else {
    if (Math.abs(this.coslat0) <= EPSLN) {
      if (this.lat0 > 0) {
        //North pole
        //trace('stere:north pole');
        this.con = 1;
      }
      else {
        //South pole
        //trace('stere:south pole');
        this.con = -1;
      }
    }
    this.cons = Math.sqrt(Math.pow(1 + this.e, 1 + this.e) * Math.pow(1 - this.e, 1 - this.e));
    if (this.k0 === 1 && !isNaN(this.lat_ts) && Math.abs(this.coslat0) <= EPSLN && Math.abs(Math.cos(this.lat_ts)) > EPSLN) {
      // When k0 is 1 (default value) and lat_ts is a vaild number and lat0 is at a pole and lat_ts is not at a pole
      // Recalculate k0 using formula 21-35 from p161 of Snyder, 1987
      this.k0 = 0.5 * this.cons * msfnz(this.e, Math.sin(this.lat_ts), Math.cos(this.lat_ts)) / tsfnz(this.e, this.con * this.lat_ts, this.con * Math.sin(this.lat_ts));
    }
    this.ms1 = msfnz(this.e, this.sinlat0, this.coslat0);
    this.X0 = 2 * Math.atan(this.ssfn_(this.lat0, this.sinlat0, this.e)) - HALF_PI;
    this.cosX0 = Math.cos(this.X0);
    this.sinX0 = Math.sin(this.X0);
  }
}

// Stereographic forward equations--mapping lat,long to x,y
function forward$o(p) {
  var lon = p.x;
  var lat = p.y;
  var sinlat = Math.sin(lat);
  var coslat = Math.cos(lat);
  var A, X, sinX, cosX, ts, rh;
  var dlon = adjust_lon(lon - this.long0);

  if (Math.abs(Math.abs(lon - this.long0) - Math.PI) <= EPSLN && Math.abs(lat + this.lat0) <= EPSLN) {
    //case of the origine point
    //trace('stere:this is the origin point');
    p.x = NaN;
    p.y = NaN;
    return p;
  }
  if (this.sphere) {
    //trace('stere:sphere case');
    A = 2 * this.k0 / (1 + this.sinlat0 * sinlat + this.coslat0 * coslat * Math.cos(dlon));
    p.x = this.a * A * coslat * Math.sin(dlon) + this.x0;
    p.y = this.a * A * (this.coslat0 * sinlat - this.sinlat0 * coslat * Math.cos(dlon)) + this.y0;
    return p;
  }
  else {
    X = 2 * Math.atan(this.ssfn_(lat, sinlat, this.e)) - HALF_PI;
    cosX = Math.cos(X);
    sinX = Math.sin(X);
    if (Math.abs(this.coslat0) <= EPSLN) {
      ts = tsfnz(this.e, lat * this.con, this.con * sinlat);
      rh = 2 * this.a * this.k0 * ts / this.cons;
      p.x = this.x0 + rh * Math.sin(lon - this.long0);
      p.y = this.y0 - this.con * rh * Math.cos(lon - this.long0);
      //trace(p.toString());
      return p;
    }
    else if (Math.abs(this.sinlat0) < EPSLN) {
      //Eq
      //trace('stere:equateur');
      A = 2 * this.a * this.k0 / (1 + cosX * Math.cos(dlon));
      p.y = A * sinX;
    }
    else {
      //other case
      //trace('stere:normal case');
      A = 2 * this.a * this.k0 * this.ms1 / (this.cosX0 * (1 + this.sinX0 * sinX + this.cosX0 * cosX * Math.cos(dlon)));
      p.y = A * (this.cosX0 * sinX - this.sinX0 * cosX * Math.cos(dlon)) + this.y0;
    }
    p.x = A * cosX * Math.sin(dlon) + this.x0;
  }
  //trace(p.toString());
  return p;
}

//* Stereographic inverse equations--mapping x,y to lat/long
function inverse$o(p) {
  p.x -= this.x0;
  p.y -= this.y0;
  var lon, lat, ts, ce, Chi;
  var rh = Math.sqrt(p.x * p.x + p.y * p.y);
  if (this.sphere) {
    var c = 2 * Math.atan(rh / (2 * this.a * this.k0));
    lon = this.long0;
    lat = this.lat0;
    if (rh <= EPSLN) {
      p.x = lon;
      p.y = lat;
      return p;
    }
    lat = Math.asin(Math.cos(c) * this.sinlat0 + p.y * Math.sin(c) * this.coslat0 / rh);
    if (Math.abs(this.coslat0) < EPSLN) {
      if (this.lat0 > 0) {
        lon = adjust_lon(this.long0 + Math.atan2(p.x, - 1 * p.y));
      }
      else {
        lon = adjust_lon(this.long0 + Math.atan2(p.x, p.y));
      }
    }
    else {
      lon = adjust_lon(this.long0 + Math.atan2(p.x * Math.sin(c), rh * this.coslat0 * Math.cos(c) - p.y * this.sinlat0 * Math.sin(c)));
    }
    p.x = lon;
    p.y = lat;
    return p;
  }
  else {
    if (Math.abs(this.coslat0) <= EPSLN) {
      if (rh <= EPSLN) {
        lat = this.lat0;
        lon = this.long0;
        p.x = lon;
        p.y = lat;
        //trace(p.toString());
        return p;
      }
      p.x *= this.con;
      p.y *= this.con;
      ts = rh * this.cons / (2 * this.a * this.k0);
      lat = this.con * phi2z(this.e, ts);
      lon = this.con * adjust_lon(this.con * this.long0 + Math.atan2(p.x, - 1 * p.y));
    }
    else {
      ce = 2 * Math.atan(rh * this.cosX0 / (2 * this.a * this.k0 * this.ms1));
      lon = this.long0;
      if (rh <= EPSLN) {
        Chi = this.X0;
      }
      else {
        Chi = Math.asin(Math.cos(ce) * this.sinX0 + p.y * Math.sin(ce) * this.cosX0 / rh);
        lon = adjust_lon(this.long0 + Math.atan2(p.x * Math.sin(ce), rh * this.cosX0 * Math.cos(ce) - p.y * this.sinX0 * Math.sin(ce)));
      }
      lat = -1 * phi2z(this.e, Math.tan(0.5 * (HALF_PI + Chi)));
    }
  }
  p.x = lon;
  p.y = lat;

  //trace(p.toString());
  return p;

}

var names$o = ["stere", "Stereographic_South_Pole", "Polar Stereographic (variant B)", "Polar_Stereographic"];
var stere = {
  init: init$p,
  forward: forward$o,
  inverse: inverse$o,
  names: names$o,
  ssfn_: ssfn_
};

/*
  references:
    Formules et constantes pour le Calcul pour la
    projection cylindrique conforme à axe oblique et pour la transformation entre
    des systèmes de référence.
    http://www.swisstopo.admin.ch/internet/swisstopo/fr/home/topics/survey/sys/refsys/switzerland.parsysrelated1.31216.downloadList.77004.DownloadFile.tmp/swissprojectionfr.pdf
  */

function init$o() {
  var phy0 = this.lat0;
  this.lambda0 = this.long0;
  var sinPhy0 = Math.sin(phy0);
  var semiMajorAxis = this.a;
  var invF = this.rf;
  var flattening = 1 / invF;
  var e2 = 2 * flattening - Math.pow(flattening, 2);
  var e = this.e = Math.sqrt(e2);
  this.R = this.k0 * semiMajorAxis * Math.sqrt(1 - e2) / (1 - e2 * Math.pow(sinPhy0, 2));
  this.alpha = Math.sqrt(1 + e2 / (1 - e2) * Math.pow(Math.cos(phy0), 4));
  this.b0 = Math.asin(sinPhy0 / this.alpha);
  var k1 = Math.log(Math.tan(Math.PI / 4 + this.b0 / 2));
  var k2 = Math.log(Math.tan(Math.PI / 4 + phy0 / 2));
  var k3 = Math.log((1 + e * sinPhy0) / (1 - e * sinPhy0));
  this.K = k1 - this.alpha * k2 + this.alpha * e / 2 * k3;
}

function forward$n(p) {
  var Sa1 = Math.log(Math.tan(Math.PI / 4 - p.y / 2));
  var Sa2 = this.e / 2 * Math.log((1 + this.e * Math.sin(p.y)) / (1 - this.e * Math.sin(p.y)));
  var S = -this.alpha * (Sa1 + Sa2) + this.K;

  // spheric latitude
  var b = 2 * (Math.atan(Math.exp(S)) - Math.PI / 4);

  // spheric longitude
  var I = this.alpha * (p.x - this.lambda0);

  // psoeudo equatorial rotation
  var rotI = Math.atan(Math.sin(I) / (Math.sin(this.b0) * Math.tan(b) + Math.cos(this.b0) * Math.cos(I)));

  var rotB = Math.asin(Math.cos(this.b0) * Math.sin(b) - Math.sin(this.b0) * Math.cos(b) * Math.cos(I));

  p.y = this.R / 2 * Math.log((1 + Math.sin(rotB)) / (1 - Math.sin(rotB))) + this.y0;
  p.x = this.R * rotI + this.x0;
  return p;
}

function inverse$n(p) {
  var Y = p.x - this.x0;
  var X = p.y - this.y0;

  var rotI = Y / this.R;
  var rotB = 2 * (Math.atan(Math.exp(X / this.R)) - Math.PI / 4);

  var b = Math.asin(Math.cos(this.b0) * Math.sin(rotB) + Math.sin(this.b0) * Math.cos(rotB) * Math.cos(rotI));
  var I = Math.atan(Math.sin(rotI) / (Math.cos(this.b0) * Math.cos(rotI) - Math.sin(this.b0) * Math.tan(rotB)));

  var lambda = this.lambda0 + I / this.alpha;

  var S = 0;
  var phy = b;
  var prevPhy = -1000;
  var iteration = 0;
  while (Math.abs(phy - prevPhy) > 0.0000001) {
    if (++iteration > 20) {
      //...reportError("omercFwdInfinity");
      return;
    }
    //S = Math.log(Math.tan(Math.PI / 4 + phy / 2));
    S = 1 / this.alpha * (Math.log(Math.tan(Math.PI / 4 + b / 2)) - this.K) + this.e * Math.log(Math.tan(Math.PI / 4 + Math.asin(this.e * Math.sin(phy)) / 2));
    prevPhy = phy;
    phy = 2 * Math.atan(Math.exp(S)) - Math.PI / 2;
  }

  p.x = lambda;
  p.y = phy;
  return p;
}

var names$n = ["somerc"];
var somerc = {
  init: init$o,
  forward: forward$n,
  inverse: inverse$n,
  names: names$n
};

var TOL = 1e-7;

function isTypeA(P) {
  var typeAProjections = ['Hotine_Oblique_Mercator','Hotine_Oblique_Mercator_Azimuth_Natural_Origin'];
  var projectionName = typeof P.PROJECTION === "object" ? Object.keys(P.PROJECTION)[0] : P.PROJECTION;
  
  return 'no_uoff' in P || 'no_off' in P || typeAProjections.indexOf(projectionName) !== -1;
}


/* Initialize the Oblique Mercator  projection
    ------------------------------------------*/
function init$n() {  
  var con, com, cosph0, D, F, H, L, sinph0, p, J, gamma = 0,
    gamma0, lamc = 0, lam1 = 0, lam2 = 0, phi1 = 0, phi2 = 0, alpha_c = 0;
  
  // only Type A uses the no_off or no_uoff property
  // https://github.com/OSGeo/proj.4/issues/104
  this.no_off = isTypeA(this);
  this.no_rot = 'no_rot' in this;
  
  var alp = false;
  if ("alpha" in this) {
    alp = true;
  }

  var gam = false;
  if ("rectified_grid_angle" in this) {
    gam = true;
  }

  if (alp) {
    alpha_c = this.alpha;
  }
  
  if (gam) {
    gamma = (this.rectified_grid_angle * D2R$1);
  }
  
  if (alp || gam) {
    lamc = this.longc;
  } else {
    lam1 = this.long1;
    phi1 = this.lat1;
    lam2 = this.long2;
    phi2 = this.lat2;
    
    if (Math.abs(phi1 - phi2) <= TOL || (con = Math.abs(phi1)) <= TOL ||
        Math.abs(con - HALF_PI) <= TOL || Math.abs(Math.abs(this.lat0) - HALF_PI) <= TOL ||
        Math.abs(Math.abs(phi2) - HALF_PI) <= TOL) {
      throw new Error();
    }
  }
  
  var one_es = 1.0 - this.es;
  com = Math.sqrt(one_es);
  
  if (Math.abs(this.lat0) > EPSLN) {
    sinph0 = Math.sin(this.lat0);
    cosph0 = Math.cos(this.lat0);
    con = 1 - this.es * sinph0 * sinph0;
    this.B = cosph0 * cosph0;
    this.B = Math.sqrt(1 + this.es * this.B * this.B / one_es);
    this.A = this.B * this.k0 * com / con;
    D = this.B * com / (cosph0 * Math.sqrt(con));
    F = D * D -1;
    
    if (F <= 0) {
      F = 0;
    } else {
      F = Math.sqrt(F);
      if (this.lat0 < 0) {
        F = -F;
      }
    }
    
    this.E = F += D;
    this.E *= Math.pow(tsfnz(this.e, this.lat0, sinph0), this.B);
  } else {
    this.B = 1 / com;
    this.A = this.k0;
    this.E = D = F = 1;
  }
  
  if (alp || gam) {
    if (alp) {
      gamma0 = Math.asin(Math.sin(alpha_c) / D);
      if (!gam) {
        gamma = alpha_c;
      }
    } else {
      gamma0 = gamma;
      alpha_c = Math.asin(D * Math.sin(gamma0));
    }
    this.lam0 = lamc - Math.asin(0.5 * (F - 1 / F) * Math.tan(gamma0)) / this.B;
  } else {
    H = Math.pow(tsfnz(this.e, phi1, Math.sin(phi1)), this.B);
    L = Math.pow(tsfnz(this.e, phi2, Math.sin(phi2)), this.B);
    F = this.E / H;
    p = (L - H) / (L + H);
    J = this.E * this.E;
    J = (J - L * H) / (J + L * H);
    con = lam1 - lam2;
    
    if (con < -Math.pi) {
      lam2 -=TWO_PI;
    } else if (con > Math.pi) {
      lam2 += TWO_PI;
    }
    
    this.lam0 = adjust_lon(0.5 * (lam1 + lam2) - Math.atan(J * Math.tan(0.5 * this.B * (lam1 - lam2)) / p) / this.B);
    gamma0 = Math.atan(2 * Math.sin(this.B * adjust_lon(lam1 - this.lam0)) / (F - 1 / F));
    gamma = alpha_c = Math.asin(D * Math.sin(gamma0));
  }
  
  this.singam = Math.sin(gamma0);
  this.cosgam = Math.cos(gamma0);
  this.sinrot = Math.sin(gamma);
  this.cosrot = Math.cos(gamma);
  
  this.rB = 1 / this.B;
  this.ArB = this.A * this.rB;
  this.BrA = 1 / this.ArB;
  this.A * this.B;
  
  if (this.no_off) {
    this.u_0 = 0;
  } else {
    this.u_0 = Math.abs(this.ArB * Math.atan(Math.sqrt(D * D - 1) / Math.cos(alpha_c)));
    
    if (this.lat0 < 0) {
      this.u_0 = - this.u_0;
    }  
  }
    
  F = 0.5 * gamma0;
  this.v_pole_n = this.ArB * Math.log(Math.tan(FORTPI - F));
  this.v_pole_s = this.ArB * Math.log(Math.tan(FORTPI + F));
}


/* Oblique Mercator forward equations--mapping lat,long to x,y
    ----------------------------------------------------------*/
function forward$m(p) {
  var coords = {};
  var S, T, U, V, W, temp, u, v;
  p.x = p.x - this.lam0;
  
  if (Math.abs(Math.abs(p.y) - HALF_PI) > EPSLN) {
    W = this.E / Math.pow(tsfnz(this.e, p.y, Math.sin(p.y)), this.B);
    
    temp = 1 / W;
    S = 0.5 * (W - temp);
    T = 0.5 * (W + temp);
    V = Math.sin(this.B * p.x);
    U = (S * this.singam - V * this.cosgam) / T;
        
    if (Math.abs(Math.abs(U) - 1.0) < EPSLN) {
      throw new Error();
    }
    
    v = 0.5 * this.ArB * Math.log((1 - U)/(1 + U));
    temp = Math.cos(this.B * p.x);
    
    if (Math.abs(temp) < TOL) {
      u = this.A * p.x;
    } else {
      u = this.ArB * Math.atan2((S * this.cosgam + V * this.singam), temp);
    }    
  } else {
    v = p.y > 0 ? this.v_pole_n : this.v_pole_s;
    u = this.ArB * p.y;
  }
     
  if (this.no_rot) {
    coords.x = u;
    coords.y = v;
  } else {
    u -= this.u_0;
    coords.x = v * this.cosrot + u * this.sinrot;
    coords.y = u * this.cosrot - v * this.sinrot;
  }
  
  coords.x = (this.a * coords.x + this.x0);
  coords.y = (this.a * coords.y + this.y0);
  
  return coords;
}

function inverse$m(p) {
  var u, v, Qp, Sp, Tp, Vp, Up;
  var coords = {};
  
  p.x = (p.x - this.x0) * (1.0 / this.a);
  p.y = (p.y - this.y0) * (1.0 / this.a);

  if (this.no_rot) {
    v = p.y;
    u = p.x;
  } else {
    v = p.x * this.cosrot - p.y * this.sinrot;
    u = p.y * this.cosrot + p.x * this.sinrot + this.u_0;
  }
  
  Qp = Math.exp(-this.BrA * v);
  Sp = 0.5 * (Qp - 1 / Qp);
  Tp = 0.5 * (Qp + 1 / Qp);
  Vp = Math.sin(this.BrA * u);
  Up = (Vp * this.cosgam + Sp * this.singam) / Tp;
  
  if (Math.abs(Math.abs(Up) - 1) < EPSLN) {
    coords.x = 0;
    coords.y = Up < 0 ? -HALF_PI : HALF_PI;
  } else {
    coords.y = this.E / Math.sqrt((1 + Up) / (1 - Up));
    coords.y = phi2z(this.e, Math.pow(coords.y, 1 / this.B));
    
    if (coords.y === Infinity) {
      throw new Error();
    }
        
    coords.x = -this.rB * Math.atan2((Sp * this.cosgam - Vp * this.singam), Math.cos(this.BrA * u));
  }
  
  coords.x += this.lam0;
  
  return coords;
}

var names$m = ["Hotine_Oblique_Mercator", "Hotine Oblique Mercator", "Hotine_Oblique_Mercator_Azimuth_Natural_Origin", "Hotine_Oblique_Mercator_Two_Point_Natural_Origin", "Hotine_Oblique_Mercator_Azimuth_Center", "Oblique_Mercator", "omerc"];
var omerc = {
  init: init$n,
  forward: forward$m,
  inverse: inverse$m,
  names: names$m
};

function init$m() {
  
  //double lat0;                    /* the reference latitude               */
  //double long0;                   /* the reference longitude              */
  //double lat1;                    /* first standard parallel              */
  //double lat2;                    /* second standard parallel             */
  //double r_maj;                   /* major axis                           */
  //double r_min;                   /* minor axis                           */
  //double false_east;              /* x offset in meters                   */
  //double false_north;             /* y offset in meters                   */
  
  //the above value can be set with proj4.defs
  //example: proj4.defs("EPSG:2154","+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");

  if (!this.lat2) {
    this.lat2 = this.lat1;
  } //if lat2 is not defined
  if (!this.k0) {
    this.k0 = 1;
  }
  this.x0 = this.x0 || 0;
  this.y0 = this.y0 || 0;
  // Standard Parallels cannot be equal and on opposite sides of the equator
  if (Math.abs(this.lat1 + this.lat2) < EPSLN) {
    return;
  }

  var temp = this.b / this.a;
  this.e = Math.sqrt(1 - temp * temp);

  var sin1 = Math.sin(this.lat1);
  var cos1 = Math.cos(this.lat1);
  var ms1 = msfnz(this.e, sin1, cos1);
  var ts1 = tsfnz(this.e, this.lat1, sin1);

  var sin2 = Math.sin(this.lat2);
  var cos2 = Math.cos(this.lat2);
  var ms2 = msfnz(this.e, sin2, cos2);
  var ts2 = tsfnz(this.e, this.lat2, sin2);

  var ts0 = tsfnz(this.e, this.lat0, Math.sin(this.lat0));

  if (Math.abs(this.lat1 - this.lat2) > EPSLN) {
    this.ns = Math.log(ms1 / ms2) / Math.log(ts1 / ts2);
  }
  else {
    this.ns = sin1;
  }
  if (isNaN(this.ns)) {
    this.ns = sin1;
  }
  this.f0 = ms1 / (this.ns * Math.pow(ts1, this.ns));
  this.rh = this.a * this.f0 * Math.pow(ts0, this.ns);
  if (!this.title) {
    this.title = "Lambert Conformal Conic";
  }
}

// Lambert Conformal conic forward equations--mapping lat,long to x,y
// -----------------------------------------------------------------
function forward$l(p) {

  var lon = p.x;
  var lat = p.y;

  // singular cases :
  if (Math.abs(2 * Math.abs(lat) - Math.PI) <= EPSLN) {
    lat = sign(lat) * (HALF_PI - 2 * EPSLN);
  }

  var con = Math.abs(Math.abs(lat) - HALF_PI);
  var ts, rh1;
  if (con > EPSLN) {
    ts = tsfnz(this.e, lat, Math.sin(lat));
    rh1 = this.a * this.f0 * Math.pow(ts, this.ns);
  }
  else {
    con = lat * this.ns;
    if (con <= 0) {
      return null;
    }
    rh1 = 0;
  }
  var theta = this.ns * adjust_lon(lon - this.long0);
  p.x = this.k0 * (rh1 * Math.sin(theta)) + this.x0;
  p.y = this.k0 * (this.rh - rh1 * Math.cos(theta)) + this.y0;

  return p;
}

// Lambert Conformal Conic inverse equations--mapping x,y to lat/long
// -----------------------------------------------------------------
function inverse$l(p) {

  var rh1, con, ts;
  var lat, lon;
  var x = (p.x - this.x0) / this.k0;
  var y = (this.rh - (p.y - this.y0) / this.k0);
  if (this.ns > 0) {
    rh1 = Math.sqrt(x * x + y * y);
    con = 1;
  }
  else {
    rh1 = -Math.sqrt(x * x + y * y);
    con = -1;
  }
  var theta = 0;
  if (rh1 !== 0) {
    theta = Math.atan2((con * x), (con * y));
  }
  if ((rh1 !== 0) || (this.ns > 0)) {
    con = 1 / this.ns;
    ts = Math.pow((rh1 / (this.a * this.f0)), con);
    lat = phi2z(this.e, ts);
    if (lat === -9999) {
      return null;
    }
  }
  else {
    lat = -HALF_PI;
  }
  lon = adjust_lon(theta / this.ns + this.long0);

  p.x = lon;
  p.y = lat;
  return p;
}

var names$l = [
  "Lambert Tangential Conformal Conic Projection",
  "Lambert_Conformal_Conic",
  "Lambert_Conformal_Conic_1SP",
  "Lambert_Conformal_Conic_2SP",
  "lcc",
  "Lambert Conic Conformal (1SP)",
  "Lambert Conic Conformal (2SP)"
];

var lcc = {
  init: init$m,
  forward: forward$l,
  inverse: inverse$l,
  names: names$l
};

function init$l() {
  this.a = 6377397.155;
  this.es = 0.006674372230614;
  this.e = Math.sqrt(this.es);
  if (!this.lat0) {
    this.lat0 = 0.863937979737193;
  }
  if (!this.long0) {
    this.long0 = 0.7417649320975901 - 0.308341501185665;
  }
  /* if scale not set default to 0.9999 */
  if (!this.k0) {
    this.k0 = 0.9999;
  }
  this.s45 = 0.785398163397448; /* 45 */
  this.s90 = 2 * this.s45;
  this.fi0 = this.lat0;
  this.e2 = this.es;
  this.e = Math.sqrt(this.e2);
  this.alfa = Math.sqrt(1 + (this.e2 * Math.pow(Math.cos(this.fi0), 4)) / (1 - this.e2));
  this.uq = 1.04216856380474;
  this.u0 = Math.asin(Math.sin(this.fi0) / this.alfa);
  this.g = Math.pow((1 + this.e * Math.sin(this.fi0)) / (1 - this.e * Math.sin(this.fi0)), this.alfa * this.e / 2);
  this.k = Math.tan(this.u0 / 2 + this.s45) / Math.pow(Math.tan(this.fi0 / 2 + this.s45), this.alfa) * this.g;
  this.k1 = this.k0;
  this.n0 = this.a * Math.sqrt(1 - this.e2) / (1 - this.e2 * Math.pow(Math.sin(this.fi0), 2));
  this.s0 = 1.37008346281555;
  this.n = Math.sin(this.s0);
  this.ro0 = this.k1 * this.n0 / Math.tan(this.s0);
  this.ad = this.s90 - this.uq;
}

/* ellipsoid */
/* calculate xy from lat/lon */
/* Constants, identical to inverse transform function */
function forward$k(p) {
  var gfi, u, deltav, s, d, eps, ro;
  var lon = p.x;
  var lat = p.y;
  var delta_lon = adjust_lon(lon - this.long0);
  /* Transformation */
  gfi = Math.pow(((1 + this.e * Math.sin(lat)) / (1 - this.e * Math.sin(lat))), (this.alfa * this.e / 2));
  u = 2 * (Math.atan(this.k * Math.pow(Math.tan(lat / 2 + this.s45), this.alfa) / gfi) - this.s45);
  deltav = -delta_lon * this.alfa;
  s = Math.asin(Math.cos(this.ad) * Math.sin(u) + Math.sin(this.ad) * Math.cos(u) * Math.cos(deltav));
  d = Math.asin(Math.cos(u) * Math.sin(deltav) / Math.cos(s));
  eps = this.n * d;
  ro = this.ro0 * Math.pow(Math.tan(this.s0 / 2 + this.s45), this.n) / Math.pow(Math.tan(s / 2 + this.s45), this.n);
  p.y = ro * Math.cos(eps) / 1;
  p.x = ro * Math.sin(eps) / 1;

  if (!this.czech) {
    p.y *= -1;
    p.x *= -1;
  }
  return (p);
}

/* calculate lat/lon from xy */
function inverse$k(p) {
  var u, deltav, s, d, eps, ro, fi1;
  var ok;

  /* Transformation */
  /* revert y, x*/
  var tmp = p.x;
  p.x = p.y;
  p.y = tmp;
  if (!this.czech) {
    p.y *= -1;
    p.x *= -1;
  }
  ro = Math.sqrt(p.x * p.x + p.y * p.y);
  eps = Math.atan2(p.y, p.x);
  d = eps / Math.sin(this.s0);
  s = 2 * (Math.atan(Math.pow(this.ro0 / ro, 1 / this.n) * Math.tan(this.s0 / 2 + this.s45)) - this.s45);
  u = Math.asin(Math.cos(this.ad) * Math.sin(s) - Math.sin(this.ad) * Math.cos(s) * Math.cos(d));
  deltav = Math.asin(Math.cos(s) * Math.sin(d) / Math.cos(u));
  p.x = this.long0 - deltav / this.alfa;
  fi1 = u;
  ok = 0;
  var iter = 0;
  do {
    p.y = 2 * (Math.atan(Math.pow(this.k, - 1 / this.alfa) * Math.pow(Math.tan(u / 2 + this.s45), 1 / this.alfa) * Math.pow((1 + this.e * Math.sin(fi1)) / (1 - this.e * Math.sin(fi1)), this.e / 2)) - this.s45);
    if (Math.abs(fi1 - p.y) < 0.0000000001) {
      ok = 1;
    }
    fi1 = p.y;
    iter += 1;
  } while (ok === 0 && iter < 15);
  if (iter >= 15) {
    return null;
  }

  return (p);
}

var names$k = ["Krovak", "krovak"];
var krovak = {
  init: init$l,
  forward: forward$k,
  inverse: inverse$k,
  names: names$k
};

function mlfn(e0, e1, e2, e3, phi) {
  return (e0 * phi - e1 * Math.sin(2 * phi) + e2 * Math.sin(4 * phi) - e3 * Math.sin(6 * phi));
}

function e0fn(x) {
  return (1 - 0.25 * x * (1 + x / 16 * (3 + 1.25 * x)));
}

function e1fn(x) {
  return (0.375 * x * (1 + 0.25 * x * (1 + 0.46875 * x)));
}

function e2fn(x) {
  return (0.05859375 * x * x * (1 + 0.75 * x));
}

function e3fn(x) {
  return (x * x * x * (35 / 3072));
}

function gN(a, e, sinphi) {
  var temp = e * sinphi;
  return a / Math.sqrt(1 - temp * temp);
}

function adjust_lat(x) {
  return (Math.abs(x) < HALF_PI) ? x : (x - (sign(x) * Math.PI));
}

function imlfn(ml, e0, e1, e2, e3) {
  var phi;
  var dphi;

  phi = ml / e0;
  for (var i = 0; i < 15; i++) {
    dphi = (ml - (e0 * phi - e1 * Math.sin(2 * phi) + e2 * Math.sin(4 * phi) - e3 * Math.sin(6 * phi))) / (e0 - 2 * e1 * Math.cos(2 * phi) + 4 * e2 * Math.cos(4 * phi) - 6 * e3 * Math.cos(6 * phi));
    phi += dphi;
    if (Math.abs(dphi) <= 0.0000000001) {
      return phi;
    }
  }

  //..reportError("IMLFN-CONV:Latitude failed to converge after 15 iterations");
  return NaN;
}

function init$k() {
  if (!this.sphere) {
    this.e0 = e0fn(this.es);
    this.e1 = e1fn(this.es);
    this.e2 = e2fn(this.es);
    this.e3 = e3fn(this.es);
    this.ml0 = this.a * mlfn(this.e0, this.e1, this.e2, this.e3, this.lat0);
  }
}

/* Cassini forward equations--mapping lat,long to x,y
  -----------------------------------------------------------------------*/
function forward$j(p) {

  /* Forward equations
      -----------------*/
  var x, y;
  var lam = p.x;
  var phi = p.y;
  lam = adjust_lon(lam - this.long0);

  if (this.sphere) {
    x = this.a * Math.asin(Math.cos(phi) * Math.sin(lam));
    y = this.a * (Math.atan2(Math.tan(phi), Math.cos(lam)) - this.lat0);
  }
  else {
    //ellipsoid
    var sinphi = Math.sin(phi);
    var cosphi = Math.cos(phi);
    var nl = gN(this.a, this.e, sinphi);
    var tl = Math.tan(phi) * Math.tan(phi);
    var al = lam * Math.cos(phi);
    var asq = al * al;
    var cl = this.es * cosphi * cosphi / (1 - this.es);
    var ml = this.a * mlfn(this.e0, this.e1, this.e2, this.e3, phi);

    x = nl * al * (1 - asq * tl * (1 / 6 - (8 - tl + 8 * cl) * asq / 120));
    y = ml - this.ml0 + nl * sinphi / cosphi * asq * (0.5 + (5 - tl + 6 * cl) * asq / 24);


  }

  p.x = x + this.x0;
  p.y = y + this.y0;
  return p;
}

/* Inverse equations
  -----------------*/
function inverse$j(p) {
  p.x -= this.x0;
  p.y -= this.y0;
  var x = p.x / this.a;
  var y = p.y / this.a;
  var phi, lam;

  if (this.sphere) {
    var dd = y + this.lat0;
    phi = Math.asin(Math.sin(dd) * Math.cos(x));
    lam = Math.atan2(Math.tan(x), Math.cos(dd));
  }
  else {
    /* ellipsoid */
    var ml1 = this.ml0 / this.a + y;
    var phi1 = imlfn(ml1, this.e0, this.e1, this.e2, this.e3);
    if (Math.abs(Math.abs(phi1) - HALF_PI) <= EPSLN) {
      p.x = this.long0;
      p.y = HALF_PI;
      if (y < 0) {
        p.y *= -1;
      }
      return p;
    }
    var nl1 = gN(this.a, this.e, Math.sin(phi1));

    var rl1 = nl1 * nl1 * nl1 / this.a / this.a * (1 - this.es);
    var tl1 = Math.pow(Math.tan(phi1), 2);
    var dl = x * this.a / nl1;
    var dsq = dl * dl;
    phi = phi1 - nl1 * Math.tan(phi1) / rl1 * dl * dl * (0.5 - (1 + 3 * tl1) * dl * dl / 24);
    lam = dl * (1 - dsq * (tl1 / 3 + (1 + 3 * tl1) * tl1 * dsq / 15)) / Math.cos(phi1);

  }

  p.x = adjust_lon(lam + this.long0);
  p.y = adjust_lat(phi);
  return p;

}

var names$j = ["Cassini", "Cassini_Soldner", "cass"];
var cass = {
  init: init$k,
  forward: forward$j,
  inverse: inverse$j,
  names: names$j
};

function qsfnz(eccent, sinphi) {
  var con;
  if (eccent > 1.0e-7) {
    con = eccent * sinphi;
    return ((1 - eccent * eccent) * (sinphi / (1 - con * con) - (0.5 / eccent) * Math.log((1 - con) / (1 + con))));
  }
  else {
    return (2 * sinphi);
  }
}

/*
  reference
    "New Equal-Area Map Projections for Noncircular Regions", John P. Snyder,
    The American Cartographer, Vol 15, No. 4, October 1988, pp. 341-355.
  */

var S_POLE = 1;

var N_POLE = 2;
var EQUIT = 3;
var OBLIQ = 4;

/* Initialize the Lambert Azimuthal Equal Area projection
  ------------------------------------------------------*/
function init$j() {
  var t = Math.abs(this.lat0);
  if (Math.abs(t - HALF_PI) < EPSLN) {
    this.mode = this.lat0 < 0 ? this.S_POLE : this.N_POLE;
  }
  else if (Math.abs(t) < EPSLN) {
    this.mode = this.EQUIT;
  }
  else {
    this.mode = this.OBLIQ;
  }
  if (this.es > 0) {
    var sinphi;

    this.qp = qsfnz(this.e, 1);
    this.mmf = 0.5 / (1 - this.es);
    this.apa = authset(this.es);
    switch (this.mode) {
    case this.N_POLE:
      this.dd = 1;
      break;
    case this.S_POLE:
      this.dd = 1;
      break;
    case this.EQUIT:
      this.rq = Math.sqrt(0.5 * this.qp);
      this.dd = 1 / this.rq;
      this.xmf = 1;
      this.ymf = 0.5 * this.qp;
      break;
    case this.OBLIQ:
      this.rq = Math.sqrt(0.5 * this.qp);
      sinphi = Math.sin(this.lat0);
      this.sinb1 = qsfnz(this.e, sinphi) / this.qp;
      this.cosb1 = Math.sqrt(1 - this.sinb1 * this.sinb1);
      this.dd = Math.cos(this.lat0) / (Math.sqrt(1 - this.es * sinphi * sinphi) * this.rq * this.cosb1);
      this.ymf = (this.xmf = this.rq) / this.dd;
      this.xmf *= this.dd;
      break;
    }
  }
  else {
    if (this.mode === this.OBLIQ) {
      this.sinph0 = Math.sin(this.lat0);
      this.cosph0 = Math.cos(this.lat0);
    }
  }
}

/* Lambert Azimuthal Equal Area forward equations--mapping lat,long to x,y
  -----------------------------------------------------------------------*/
function forward$i(p) {

  /* Forward equations
      -----------------*/
  var x, y, coslam, sinlam, sinphi, q, sinb, cosb, b, cosphi;
  var lam = p.x;
  var phi = p.y;

  lam = adjust_lon(lam - this.long0);
  if (this.sphere) {
    sinphi = Math.sin(phi);
    cosphi = Math.cos(phi);
    coslam = Math.cos(lam);
    if (this.mode === this.OBLIQ || this.mode === this.EQUIT) {
      y = (this.mode === this.EQUIT) ? 1 + cosphi * coslam : 1 + this.sinph0 * sinphi + this.cosph0 * cosphi * coslam;
      if (y <= EPSLN) {
        return null;
      }
      y = Math.sqrt(2 / y);
      x = y * cosphi * Math.sin(lam);
      y *= (this.mode === this.EQUIT) ? sinphi : this.cosph0 * sinphi - this.sinph0 * cosphi * coslam;
    }
    else if (this.mode === this.N_POLE || this.mode === this.S_POLE) {
      if (this.mode === this.N_POLE) {
        coslam = -coslam;
      }
      if (Math.abs(phi + this.lat0) < EPSLN) {
        return null;
      }
      y = FORTPI - phi * 0.5;
      y = 2 * ((this.mode === this.S_POLE) ? Math.cos(y) : Math.sin(y));
      x = y * Math.sin(lam);
      y *= coslam;
    }
  }
  else {
    sinb = 0;
    cosb = 0;
    b = 0;
    coslam = Math.cos(lam);
    sinlam = Math.sin(lam);
    sinphi = Math.sin(phi);
    q = qsfnz(this.e, sinphi);
    if (this.mode === this.OBLIQ || this.mode === this.EQUIT) {
      sinb = q / this.qp;
      cosb = Math.sqrt(1 - sinb * sinb);
    }
    switch (this.mode) {
    case this.OBLIQ:
      b = 1 + this.sinb1 * sinb + this.cosb1 * cosb * coslam;
      break;
    case this.EQUIT:
      b = 1 + cosb * coslam;
      break;
    case this.N_POLE:
      b = HALF_PI + phi;
      q = this.qp - q;
      break;
    case this.S_POLE:
      b = phi - HALF_PI;
      q = this.qp + q;
      break;
    }
    if (Math.abs(b) < EPSLN) {
      return null;
    }
    switch (this.mode) {
    case this.OBLIQ:
    case this.EQUIT:
      b = Math.sqrt(2 / b);
      if (this.mode === this.OBLIQ) {
        y = this.ymf * b * (this.cosb1 * sinb - this.sinb1 * cosb * coslam);
      }
      else {
        y = (b = Math.sqrt(2 / (1 + cosb * coslam))) * sinb * this.ymf;
      }
      x = this.xmf * b * cosb * sinlam;
      break;
    case this.N_POLE:
    case this.S_POLE:
      if (q >= 0) {
        x = (b = Math.sqrt(q)) * sinlam;
        y = coslam * ((this.mode === this.S_POLE) ? b : -b);
      }
      else {
        x = y = 0;
      }
      break;
    }
  }

  p.x = this.a * x + this.x0;
  p.y = this.a * y + this.y0;
  return p;
}

/* Inverse equations
  -----------------*/
function inverse$i(p) {
  p.x -= this.x0;
  p.y -= this.y0;
  var x = p.x / this.a;
  var y = p.y / this.a;
  var lam, phi, cCe, sCe, q, rho, ab;
  if (this.sphere) {
    var cosz = 0,
      rh, sinz = 0;

    rh = Math.sqrt(x * x + y * y);
    phi = rh * 0.5;
    if (phi > 1) {
      return null;
    }
    phi = 2 * Math.asin(phi);
    if (this.mode === this.OBLIQ || this.mode === this.EQUIT) {
      sinz = Math.sin(phi);
      cosz = Math.cos(phi);
    }
    switch (this.mode) {
    case this.EQUIT:
      phi = (Math.abs(rh) <= EPSLN) ? 0 : Math.asin(y * sinz / rh);
      x *= sinz;
      y = cosz * rh;
      break;
    case this.OBLIQ:
      phi = (Math.abs(rh) <= EPSLN) ? this.lat0 : Math.asin(cosz * this.sinph0 + y * sinz * this.cosph0 / rh);
      x *= sinz * this.cosph0;
      y = (cosz - Math.sin(phi) * this.sinph0) * rh;
      break;
    case this.N_POLE:
      y = -y;
      phi = HALF_PI - phi;
      break;
    case this.S_POLE:
      phi -= HALF_PI;
      break;
    }
    lam = (y === 0 && (this.mode === this.EQUIT || this.mode === this.OBLIQ)) ? 0 : Math.atan2(x, y);
  }
  else {
    ab = 0;
    if (this.mode === this.OBLIQ || this.mode === this.EQUIT) {
      x /= this.dd;
      y *= this.dd;
      rho = Math.sqrt(x * x + y * y);
      if (rho < EPSLN) {
        p.x = this.long0;
        p.y = this.lat0;
        return p;
      }
      sCe = 2 * Math.asin(0.5 * rho / this.rq);
      cCe = Math.cos(sCe);
      x *= (sCe = Math.sin(sCe));
      if (this.mode === this.OBLIQ) {
        ab = cCe * this.sinb1 + y * sCe * this.cosb1 / rho;
        q = this.qp * ab;
        y = rho * this.cosb1 * cCe - y * this.sinb1 * sCe;
      }
      else {
        ab = y * sCe / rho;
        q = this.qp * ab;
        y = rho * cCe;
      }
    }
    else if (this.mode === this.N_POLE || this.mode === this.S_POLE) {
      if (this.mode === this.N_POLE) {
        y = -y;
      }
      q = (x * x + y * y);
      if (!q) {
        p.x = this.long0;
        p.y = this.lat0;
        return p;
      }
      ab = 1 - q / this.qp;
      if (this.mode === this.S_POLE) {
        ab = -ab;
      }
    }
    lam = Math.atan2(x, y);
    phi = authlat(Math.asin(ab), this.apa);
  }

  p.x = adjust_lon(this.long0 + lam);
  p.y = phi;
  return p;
}

/* determine latitude from authalic latitude */
var P00 = 0.33333333333333333333;

var P01 = 0.17222222222222222222;
var P02 = 0.10257936507936507936;
var P10 = 0.06388888888888888888;
var P11 = 0.06640211640211640211;
var P20 = 0.01641501294219154443;

function authset(es) {
  var t;
  var APA = [];
  APA[0] = es * P00;
  t = es * es;
  APA[0] += t * P01;
  APA[1] = t * P10;
  t *= es;
  APA[0] += t * P02;
  APA[1] += t * P11;
  APA[2] = t * P20;
  return APA;
}

function authlat(beta, APA) {
  var t = beta + beta;
  return (beta + APA[0] * Math.sin(t) + APA[1] * Math.sin(t + t) + APA[2] * Math.sin(t + t + t));
}

var names$i = ["Lambert Azimuthal Equal Area", "Lambert_Azimuthal_Equal_Area", "laea"];
var laea = {
  init: init$j,
  forward: forward$i,
  inverse: inverse$i,
  names: names$i,
  S_POLE: S_POLE,
  N_POLE: N_POLE,
  EQUIT: EQUIT,
  OBLIQ: OBLIQ
};

function asinz(x) {
  if (Math.abs(x) > 1) {
    x = (x > 1) ? 1 : -1;
  }
  return Math.asin(x);
}

function init$i() {

  if (Math.abs(this.lat1 + this.lat2) < EPSLN) {
    return;
  }
  this.temp = this.b / this.a;
  this.es = 1 - Math.pow(this.temp, 2);
  this.e3 = Math.sqrt(this.es);

  this.sin_po = Math.sin(this.lat1);
  this.cos_po = Math.cos(this.lat1);
  this.t1 = this.sin_po;
  this.con = this.sin_po;
  this.ms1 = msfnz(this.e3, this.sin_po, this.cos_po);
  this.qs1 = qsfnz(this.e3, this.sin_po);

  this.sin_po = Math.sin(this.lat2);
  this.cos_po = Math.cos(this.lat2);
  this.t2 = this.sin_po;
  this.ms2 = msfnz(this.e3, this.sin_po, this.cos_po);
  this.qs2 = qsfnz(this.e3, this.sin_po);

  this.sin_po = Math.sin(this.lat0);
  this.cos_po = Math.cos(this.lat0);
  this.t3 = this.sin_po;
  this.qs0 = qsfnz(this.e3, this.sin_po);

  if (Math.abs(this.lat1 - this.lat2) > EPSLN) {
    this.ns0 = (this.ms1 * this.ms1 - this.ms2 * this.ms2) / (this.qs2 - this.qs1);
  }
  else {
    this.ns0 = this.con;
  }
  this.c = this.ms1 * this.ms1 + this.ns0 * this.qs1;
  this.rh = this.a * Math.sqrt(this.c - this.ns0 * this.qs0) / this.ns0;
}

/* Albers Conical Equal Area forward equations--mapping lat,long to x,y
  -------------------------------------------------------------------*/
function forward$h(p) {

  var lon = p.x;
  var lat = p.y;

  this.sin_phi = Math.sin(lat);
  this.cos_phi = Math.cos(lat);

  var qs = qsfnz(this.e3, this.sin_phi);
  var rh1 = this.a * Math.sqrt(this.c - this.ns0 * qs) / this.ns0;
  var theta = this.ns0 * adjust_lon(lon - this.long0);
  var x = rh1 * Math.sin(theta) + this.x0;
  var y = this.rh - rh1 * Math.cos(theta) + this.y0;

  p.x = x;
  p.y = y;
  return p;
}

function inverse$h(p) {
  var rh1, qs, con, theta, lon, lat;

  p.x -= this.x0;
  p.y = this.rh - p.y + this.y0;
  if (this.ns0 >= 0) {
    rh1 = Math.sqrt(p.x * p.x + p.y * p.y);
    con = 1;
  }
  else {
    rh1 = -Math.sqrt(p.x * p.x + p.y * p.y);
    con = -1;
  }
  theta = 0;
  if (rh1 !== 0) {
    theta = Math.atan2(con * p.x, con * p.y);
  }
  con = rh1 * this.ns0 / this.a;
  if (this.sphere) {
    lat = Math.asin((this.c - con * con) / (2 * this.ns0));
  }
  else {
    qs = (this.c - con * con) / this.ns0;
    lat = this.phi1z(this.e3, qs);
  }

  lon = adjust_lon(theta / this.ns0 + this.long0);
  p.x = lon;
  p.y = lat;
  return p;
}

/* Function to compute phi1, the latitude for the inverse of the
   Albers Conical Equal-Area projection.
-------------------------------------------*/
function phi1z(eccent, qs) {
  var sinphi, cosphi, con, com, dphi;
  var phi = asinz(0.5 * qs);
  if (eccent < EPSLN) {
    return phi;
  }

  var eccnts = eccent * eccent;
  for (var i = 1; i <= 25; i++) {
    sinphi = Math.sin(phi);
    cosphi = Math.cos(phi);
    con = eccent * sinphi;
    com = 1 - con * con;
    dphi = 0.5 * com * com / cosphi * (qs / (1 - eccnts) - sinphi / com + 0.5 / eccent * Math.log((1 - con) / (1 + con)));
    phi = phi + dphi;
    if (Math.abs(dphi) <= 1e-7) {
      return phi;
    }
  }
  return null;
}

var names$h = ["Albers_Conic_Equal_Area", "Albers", "aea"];
var aea = {
  init: init$i,
  forward: forward$h,
  inverse: inverse$h,
  names: names$h,
  phi1z: phi1z
};

/*
  reference:
    Wolfram Mathworld "Gnomonic Projection"
    http://mathworld.wolfram.com/GnomonicProjection.html
    Accessed: 12th November 2009
  */
function init$h() {

  /* Place parameters in static storage for common use
      -------------------------------------------------*/
  this.sin_p14 = Math.sin(this.lat0);
  this.cos_p14 = Math.cos(this.lat0);
  // Approximation for projecting points to the horizon (infinity)
  this.infinity_dist = 1000 * this.a;
  this.rc = 1;
}

/* Gnomonic forward equations--mapping lat,long to x,y
    ---------------------------------------------------*/
function forward$g(p) {
  var sinphi, cosphi; /* sin and cos value        */
  var dlon; /* delta longitude value      */
  var coslon; /* cos of longitude        */
  var ksp; /* scale factor          */
  var g;
  var x, y;
  var lon = p.x;
  var lat = p.y;
  /* Forward equations
      -----------------*/
  dlon = adjust_lon(lon - this.long0);

  sinphi = Math.sin(lat);
  cosphi = Math.cos(lat);

  coslon = Math.cos(dlon);
  g = this.sin_p14 * sinphi + this.cos_p14 * cosphi * coslon;
  ksp = 1;
  if ((g > 0) || (Math.abs(g) <= EPSLN)) {
    x = this.x0 + this.a * ksp * cosphi * Math.sin(dlon) / g;
    y = this.y0 + this.a * ksp * (this.cos_p14 * sinphi - this.sin_p14 * cosphi * coslon) / g;
  }
  else {

    // Point is in the opposing hemisphere and is unprojectable
    // We still need to return a reasonable point, so we project
    // to infinity, on a bearing
    // equivalent to the northern hemisphere equivalent
    // This is a reasonable approximation for short shapes and lines that
    // straddle the horizon.

    x = this.x0 + this.infinity_dist * cosphi * Math.sin(dlon);
    y = this.y0 + this.infinity_dist * (this.cos_p14 * sinphi - this.sin_p14 * cosphi * coslon);

  }
  p.x = x;
  p.y = y;
  return p;
}

function inverse$g(p) {
  var rh; /* Rho */
  var sinc, cosc;
  var c;
  var lon, lat;

  /* Inverse equations
      -----------------*/
  p.x = (p.x - this.x0) / this.a;
  p.y = (p.y - this.y0) / this.a;

  p.x /= this.k0;
  p.y /= this.k0;

  if ((rh = Math.sqrt(p.x * p.x + p.y * p.y))) {
    c = Math.atan2(rh, this.rc);
    sinc = Math.sin(c);
    cosc = Math.cos(c);

    lat = asinz(cosc * this.sin_p14 + (p.y * sinc * this.cos_p14) / rh);
    lon = Math.atan2(p.x * sinc, rh * this.cos_p14 * cosc - p.y * this.sin_p14 * sinc);
    lon = adjust_lon(this.long0 + lon);
  }
  else {
    lat = this.phic0;
    lon = 0;
  }

  p.x = lon;
  p.y = lat;
  return p;
}

var names$g = ["gnom"];
var gnom = {
  init: init$h,
  forward: forward$g,
  inverse: inverse$g,
  names: names$g
};

function iqsfnz(eccent, q) {
  var temp = 1 - (1 - eccent * eccent) / (2 * eccent) * Math.log((1 - eccent) / (1 + eccent));
  if (Math.abs(Math.abs(q) - temp) < 1.0E-6) {
    if (q < 0) {
      return (-1 * HALF_PI);
    }
    else {
      return HALF_PI;
    }
  }
  //var phi = 0.5* q/(1-eccent*eccent);
  var phi = Math.asin(0.5 * q);
  var dphi;
  var sin_phi;
  var cos_phi;
  var con;
  for (var i = 0; i < 30; i++) {
    sin_phi = Math.sin(phi);
    cos_phi = Math.cos(phi);
    con = eccent * sin_phi;
    dphi = Math.pow(1 - con * con, 2) / (2 * cos_phi) * (q / (1 - eccent * eccent) - sin_phi / (1 - con * con) + 0.5 / eccent * Math.log((1 - con) / (1 + con)));
    phi += dphi;
    if (Math.abs(dphi) <= 0.0000000001) {
      return phi;
    }
  }

  //console.log("IQSFN-CONV:Latitude failed to converge after 30 iterations");
  return NaN;
}

/*
  reference:
    "Cartographic Projection Procedures for the UNIX Environment-
    A User's Manual" by Gerald I. Evenden,
    USGS Open File Report 90-284and Release 4 Interim Reports (2003)
*/
function init$g() {
  //no-op
  if (!this.sphere) {
    this.k0 = msfnz(this.e, Math.sin(this.lat_ts), Math.cos(this.lat_ts));
  }
}

/* Cylindrical Equal Area forward equations--mapping lat,long to x,y
    ------------------------------------------------------------*/
function forward$f(p) {
  var lon = p.x;
  var lat = p.y;
  var x, y;
  /* Forward equations
      -----------------*/
  var dlon = adjust_lon(lon - this.long0);
  if (this.sphere) {
    x = this.x0 + this.a * dlon * Math.cos(this.lat_ts);
    y = this.y0 + this.a * Math.sin(lat) / Math.cos(this.lat_ts);
  }
  else {
    var qs = qsfnz(this.e, Math.sin(lat));
    x = this.x0 + this.a * this.k0 * dlon;
    y = this.y0 + this.a * qs * 0.5 / this.k0;
  }

  p.x = x;
  p.y = y;
  return p;
}

/* Cylindrical Equal Area inverse equations--mapping x,y to lat/long
    ------------------------------------------------------------*/
function inverse$f(p) {
  p.x -= this.x0;
  p.y -= this.y0;
  var lon, lat;

  if (this.sphere) {
    lon = adjust_lon(this.long0 + (p.x / this.a) / Math.cos(this.lat_ts));
    lat = Math.asin((p.y / this.a) * Math.cos(this.lat_ts));
  }
  else {
    lat = iqsfnz(this.e, 2 * p.y * this.k0 / this.a);
    lon = adjust_lon(this.long0 + p.x / (this.a * this.k0));
  }

  p.x = lon;
  p.y = lat;
  return p;
}

var names$f = ["cea"];
var cea = {
  init: init$g,
  forward: forward$f,
  inverse: inverse$f,
  names: names$f
};

function init$f() {

  this.x0 = this.x0 || 0;
  this.y0 = this.y0 || 0;
  this.lat0 = this.lat0 || 0;
  this.long0 = this.long0 || 0;
  this.lat_ts = this.lat_ts || 0;
  this.title = this.title || "Equidistant Cylindrical (Plate Carre)";

  this.rc = Math.cos(this.lat_ts);
}

// forward equations--mapping lat,long to x,y
// -----------------------------------------------------------------
function forward$e(p) {

  var lon = p.x;
  var lat = p.y;

  var dlon = adjust_lon(lon - this.long0);
  var dlat = adjust_lat(lat - this.lat0);
  p.x = this.x0 + (this.a * dlon * this.rc);
  p.y = this.y0 + (this.a * dlat);
  return p;
}

// inverse equations--mapping x,y to lat/long
// -----------------------------------------------------------------
function inverse$e(p) {

  var x = p.x;
  var y = p.y;

  p.x = adjust_lon(this.long0 + ((x - this.x0) / (this.a * this.rc)));
  p.y = adjust_lat(this.lat0 + ((y - this.y0) / (this.a)));
  return p;
}

var names$e = ["Equirectangular", "Equidistant_Cylindrical", "eqc"];
var eqc = {
  init: init$f,
  forward: forward$e,
  inverse: inverse$e,
  names: names$e
};

var MAX_ITER$1 = 20;

function init$e() {
  /* Place parameters in static storage for common use
      -------------------------------------------------*/
  this.temp = this.b / this.a;
  this.es = 1 - Math.pow(this.temp, 2); // devait etre dans tmerc.js mais n y est pas donc je commente sinon retour de valeurs nulles
  this.e = Math.sqrt(this.es);
  this.e0 = e0fn(this.es);
  this.e1 = e1fn(this.es);
  this.e2 = e2fn(this.es);
  this.e3 = e3fn(this.es);
  this.ml0 = this.a * mlfn(this.e0, this.e1, this.e2, this.e3, this.lat0); //si que des zeros le calcul ne se fait pas
}

/* Polyconic forward equations--mapping lat,long to x,y
    ---------------------------------------------------*/
function forward$d(p) {
  var lon = p.x;
  var lat = p.y;
  var x, y, el;
  var dlon = adjust_lon(lon - this.long0);
  el = dlon * Math.sin(lat);
  if (this.sphere) {
    if (Math.abs(lat) <= EPSLN) {
      x = this.a * dlon;
      y = -1 * this.a * this.lat0;
    }
    else {
      x = this.a * Math.sin(el) / Math.tan(lat);
      y = this.a * (adjust_lat(lat - this.lat0) + (1 - Math.cos(el)) / Math.tan(lat));
    }
  }
  else {
    if (Math.abs(lat) <= EPSLN) {
      x = this.a * dlon;
      y = -1 * this.ml0;
    }
    else {
      var nl = gN(this.a, this.e, Math.sin(lat)) / Math.tan(lat);
      x = nl * Math.sin(el);
      y = this.a * mlfn(this.e0, this.e1, this.e2, this.e3, lat) - this.ml0 + nl * (1 - Math.cos(el));
    }

  }
  p.x = x + this.x0;
  p.y = y + this.y0;
  return p;
}

/* Inverse equations
  -----------------*/
function inverse$d(p) {
  var lon, lat, x, y, i;
  var al, bl;
  var phi, dphi;
  x = p.x - this.x0;
  y = p.y - this.y0;

  if (this.sphere) {
    if (Math.abs(y + this.a * this.lat0) <= EPSLN) {
      lon = adjust_lon(x / this.a + this.long0);
      lat = 0;
    }
    else {
      al = this.lat0 + y / this.a;
      bl = x * x / this.a / this.a + al * al;
      phi = al;
      var tanphi;
      for (i = MAX_ITER$1; i; --i) {
        tanphi = Math.tan(phi);
        dphi = -1 * (al * (phi * tanphi + 1) - phi - 0.5 * (phi * phi + bl) * tanphi) / ((phi - al) / tanphi - 1);
        phi += dphi;
        if (Math.abs(dphi) <= EPSLN) {
          lat = phi;
          break;
        }
      }
      lon = adjust_lon(this.long0 + (Math.asin(x * Math.tan(phi) / this.a)) / Math.sin(lat));
    }
  }
  else {
    if (Math.abs(y + this.ml0) <= EPSLN) {
      lat = 0;
      lon = adjust_lon(this.long0 + x / this.a);
    }
    else {

      al = (this.ml0 + y) / this.a;
      bl = x * x / this.a / this.a + al * al;
      phi = al;
      var cl, mln, mlnp, ma;
      var con;
      for (i = MAX_ITER$1; i; --i) {
        con = this.e * Math.sin(phi);
        cl = Math.sqrt(1 - con * con) * Math.tan(phi);
        mln = this.a * mlfn(this.e0, this.e1, this.e2, this.e3, phi);
        mlnp = this.e0 - 2 * this.e1 * Math.cos(2 * phi) + 4 * this.e2 * Math.cos(4 * phi) - 6 * this.e3 * Math.cos(6 * phi);
        ma = mln / this.a;
        dphi = (al * (cl * ma + 1) - ma - 0.5 * cl * (ma * ma + bl)) / (this.es * Math.sin(2 * phi) * (ma * ma + bl - 2 * al * ma) / (4 * cl) + (al - ma) * (cl * mlnp - 2 / Math.sin(2 * phi)) - mlnp);
        phi -= dphi;
        if (Math.abs(dphi) <= EPSLN) {
          lat = phi;
          break;
        }
      }

      //lat=phi4z(this.e,this.e0,this.e1,this.e2,this.e3,al,bl,0,0);
      cl = Math.sqrt(1 - this.es * Math.pow(Math.sin(lat), 2)) * Math.tan(lat);
      lon = adjust_lon(this.long0 + Math.asin(x * cl / this.a) / Math.sin(lat));
    }
  }

  p.x = lon;
  p.y = lat;
  return p;
}

var names$d = ["Polyconic", "poly"];
var poly = {
  init: init$e,
  forward: forward$d,
  inverse: inverse$d,
  names: names$d
};

function init$d() {
  this.A = [];
  this.A[1] = 0.6399175073;
  this.A[2] = -0.1358797613;
  this.A[3] = 0.063294409;
  this.A[4] = -0.02526853;
  this.A[5] = 0.0117879;
  this.A[6] = -0.0055161;
  this.A[7] = 0.0026906;
  this.A[8] = -0.001333;
  this.A[9] = 0.00067;
  this.A[10] = -0.00034;

  this.B_re = [];
  this.B_im = [];
  this.B_re[1] = 0.7557853228;
  this.B_im[1] = 0;
  this.B_re[2] = 0.249204646;
  this.B_im[2] = 0.003371507;
  this.B_re[3] = -0.001541739;
  this.B_im[3] = 0.041058560;
  this.B_re[4] = -0.10162907;
  this.B_im[4] = 0.01727609;
  this.B_re[5] = -0.26623489;
  this.B_im[5] = -0.36249218;
  this.B_re[6] = -0.6870983;
  this.B_im[6] = -1.1651967;

  this.C_re = [];
  this.C_im = [];
  this.C_re[1] = 1.3231270439;
  this.C_im[1] = 0;
  this.C_re[2] = -0.577245789;
  this.C_im[2] = -0.007809598;
  this.C_re[3] = 0.508307513;
  this.C_im[3] = -0.112208952;
  this.C_re[4] = -0.15094762;
  this.C_im[4] = 0.18200602;
  this.C_re[5] = 1.01418179;
  this.C_im[5] = 1.64497696;
  this.C_re[6] = 1.9660549;
  this.C_im[6] = 2.5127645;

  this.D = [];
  this.D[1] = 1.5627014243;
  this.D[2] = 0.5185406398;
  this.D[3] = -0.03333098;
  this.D[4] = -0.1052906;
  this.D[5] = -0.0368594;
  this.D[6] = 0.007317;
  this.D[7] = 0.01220;
  this.D[8] = 0.00394;
  this.D[9] = -0.0013;
}

/**
    New Zealand Map Grid Forward  - long/lat to x/y
    long/lat in radians
  */
function forward$c(p) {
  var n;
  var lon = p.x;
  var lat = p.y;

  var delta_lat = lat - this.lat0;
  var delta_lon = lon - this.long0;

  // 1. Calculate d_phi and d_psi    ...                          // and d_lambda
  // For this algorithm, delta_latitude is in seconds of arc x 10-5, so we need to scale to those units. Longitude is radians.
  var d_phi = delta_lat / SEC_TO_RAD * 1E-5;
  var d_lambda = delta_lon;
  var d_phi_n = 1; // d_phi^0

  var d_psi = 0;
  for (n = 1; n <= 10; n++) {
    d_phi_n = d_phi_n * d_phi;
    d_psi = d_psi + this.A[n] * d_phi_n;
  }

  // 2. Calculate theta
  var th_re = d_psi;
  var th_im = d_lambda;

  // 3. Calculate z
  var th_n_re = 1;
  var th_n_im = 0; // theta^0
  var th_n_re1;
  var th_n_im1;

  var z_re = 0;
  var z_im = 0;
  for (n = 1; n <= 6; n++) {
    th_n_re1 = th_n_re * th_re - th_n_im * th_im;
    th_n_im1 = th_n_im * th_re + th_n_re * th_im;
    th_n_re = th_n_re1;
    th_n_im = th_n_im1;
    z_re = z_re + this.B_re[n] * th_n_re - this.B_im[n] * th_n_im;
    z_im = z_im + this.B_im[n] * th_n_re + this.B_re[n] * th_n_im;
  }

  // 4. Calculate easting and northing
  p.x = (z_im * this.a) + this.x0;
  p.y = (z_re * this.a) + this.y0;

  return p;
}

/**
    New Zealand Map Grid Inverse  -  x/y to long/lat
  */
function inverse$c(p) {
  var n;
  var x = p.x;
  var y = p.y;

  var delta_x = x - this.x0;
  var delta_y = y - this.y0;

  // 1. Calculate z
  var z_re = delta_y / this.a;
  var z_im = delta_x / this.a;

  // 2a. Calculate theta - first approximation gives km accuracy
  var z_n_re = 1;
  var z_n_im = 0; // z^0
  var z_n_re1;
  var z_n_im1;

  var th_re = 0;
  var th_im = 0;
  for (n = 1; n <= 6; n++) {
    z_n_re1 = z_n_re * z_re - z_n_im * z_im;
    z_n_im1 = z_n_im * z_re + z_n_re * z_im;
    z_n_re = z_n_re1;
    z_n_im = z_n_im1;
    th_re = th_re + this.C_re[n] * z_n_re - this.C_im[n] * z_n_im;
    th_im = th_im + this.C_im[n] * z_n_re + this.C_re[n] * z_n_im;
  }

  // 2b. Iterate to refine the accuracy of the calculation
  //        0 iterations gives km accuracy
  //        1 iteration gives m accuracy -- good enough for most mapping applications
  //        2 iterations bives mm accuracy
  for (var i = 0; i < this.iterations; i++) {
    var th_n_re = th_re;
    var th_n_im = th_im;
    var th_n_re1;
    var th_n_im1;

    var num_re = z_re;
    var num_im = z_im;
    for (n = 2; n <= 6; n++) {
      th_n_re1 = th_n_re * th_re - th_n_im * th_im;
      th_n_im1 = th_n_im * th_re + th_n_re * th_im;
      th_n_re = th_n_re1;
      th_n_im = th_n_im1;
      num_re = num_re + (n - 1) * (this.B_re[n] * th_n_re - this.B_im[n] * th_n_im);
      num_im = num_im + (n - 1) * (this.B_im[n] * th_n_re + this.B_re[n] * th_n_im);
    }

    th_n_re = 1;
    th_n_im = 0;
    var den_re = this.B_re[1];
    var den_im = this.B_im[1];
    for (n = 2; n <= 6; n++) {
      th_n_re1 = th_n_re * th_re - th_n_im * th_im;
      th_n_im1 = th_n_im * th_re + th_n_re * th_im;
      th_n_re = th_n_re1;
      th_n_im = th_n_im1;
      den_re = den_re + n * (this.B_re[n] * th_n_re - this.B_im[n] * th_n_im);
      den_im = den_im + n * (this.B_im[n] * th_n_re + this.B_re[n] * th_n_im);
    }

    // Complex division
    var den2 = den_re * den_re + den_im * den_im;
    th_re = (num_re * den_re + num_im * den_im) / den2;
    th_im = (num_im * den_re - num_re * den_im) / den2;
  }

  // 3. Calculate d_phi              ...                                    // and d_lambda
  var d_psi = th_re;
  var d_lambda = th_im;
  var d_psi_n = 1; // d_psi^0

  var d_phi = 0;
  for (n = 1; n <= 9; n++) {
    d_psi_n = d_psi_n * d_psi;
    d_phi = d_phi + this.D[n] * d_psi_n;
  }

  // 4. Calculate latitude and longitude
  // d_phi is calcuated in second of arc * 10^-5, so we need to scale back to radians. d_lambda is in radians.
  var lat = this.lat0 + (d_phi * SEC_TO_RAD * 1E5);
  var lon = this.long0 + d_lambda;

  p.x = lon;
  p.y = lat;

  return p;
}

var names$c = ["New_Zealand_Map_Grid", "nzmg"];
var nzmg = {
  init: init$d,
  forward: forward$c,
  inverse: inverse$c,
  names: names$c
};

/*
  reference
    "New Equal-Area Map Projections for Noncircular Regions", John P. Snyder,
    The American Cartographer, Vol 15, No. 4, October 1988, pp. 341-355.
  */


/* Initialize the Miller Cylindrical projection
  -------------------------------------------*/
function init$c() {
  //no-op
}

/* Miller Cylindrical forward equations--mapping lat,long to x,y
    ------------------------------------------------------------*/
function forward$b(p) {
  var lon = p.x;
  var lat = p.y;
  /* Forward equations
      -----------------*/
  var dlon = adjust_lon(lon - this.long0);
  var x = this.x0 + this.a * dlon;
  var y = this.y0 + this.a * Math.log(Math.tan((Math.PI / 4) + (lat / 2.5))) * 1.25;

  p.x = x;
  p.y = y;
  return p;
}

/* Miller Cylindrical inverse equations--mapping x,y to lat/long
    ------------------------------------------------------------*/
function inverse$b(p) {
  p.x -= this.x0;
  p.y -= this.y0;

  var lon = adjust_lon(this.long0 + p.x / this.a);
  var lat = 2.5 * (Math.atan(Math.exp(0.8 * p.y / this.a)) - Math.PI / 4);

  p.x = lon;
  p.y = lat;
  return p;
}

var names$b = ["Miller_Cylindrical", "mill"];
var mill = {
  init: init$c,
  forward: forward$b,
  inverse: inverse$b,
  names: names$b
};

var MAX_ITER = 20;


function init$b() {
  /* Place parameters in static storage for common use
    -------------------------------------------------*/


  if (!this.sphere) {
    this.en = pj_enfn(this.es);
  }
  else {
    this.n = 1;
    this.m = 0;
    this.es = 0;
    this.C_y = Math.sqrt((this.m + 1) / this.n);
    this.C_x = this.C_y / (this.m + 1);
  }

}

/* Sinusoidal forward equations--mapping lat,long to x,y
  -----------------------------------------------------*/
function forward$a(p) {
  var x, y;
  var lon = p.x;
  var lat = p.y;
  /* Forward equations
    -----------------*/
  lon = adjust_lon(lon - this.long0);

  if (this.sphere) {
    if (!this.m) {
      lat = this.n !== 1 ? Math.asin(this.n * Math.sin(lat)) : lat;
    }
    else {
      var k = this.n * Math.sin(lat);
      for (var i = MAX_ITER; i; --i) {
        var V = (this.m * lat + Math.sin(lat) - k) / (this.m + Math.cos(lat));
        lat -= V;
        if (Math.abs(V) < EPSLN) {
          break;
        }
      }
    }
    x = this.a * this.C_x * lon * (this.m + Math.cos(lat));
    y = this.a * this.C_y * lat;

  }
  else {

    var s = Math.sin(lat);
    var c = Math.cos(lat);
    y = this.a * pj_mlfn(lat, s, c, this.en);
    x = this.a * lon * c / Math.sqrt(1 - this.es * s * s);
  }

  p.x = x;
  p.y = y;
  return p;
}

function inverse$a(p) {
  var lat, temp, lon, s;

  p.x -= this.x0;
  lon = p.x / this.a;
  p.y -= this.y0;
  lat = p.y / this.a;

  if (this.sphere) {
    lat /= this.C_y;
    lon = lon / (this.C_x * (this.m + Math.cos(lat)));
    if (this.m) {
      lat = asinz((this.m * lat + Math.sin(lat)) / this.n);
    }
    else if (this.n !== 1) {
      lat = asinz(Math.sin(lat) / this.n);
    }
    lon = adjust_lon(lon + this.long0);
    lat = adjust_lat(lat);
  }
  else {
    lat = pj_inv_mlfn(p.y / this.a, this.es, this.en);
    s = Math.abs(lat);
    if (s < HALF_PI) {
      s = Math.sin(lat);
      temp = this.long0 + p.x * Math.sqrt(1 - this.es * s * s) / (this.a * Math.cos(lat));
      //temp = this.long0 + p.x / (this.a * Math.cos(lat));
      lon = adjust_lon(temp);
    }
    else if ((s - EPSLN) < HALF_PI) {
      lon = this.long0;
    }
  }
  p.x = lon;
  p.y = lat;
  return p;
}

var names$a = ["Sinusoidal", "sinu"];
var sinu = {
  init: init$b,
  forward: forward$a,
  inverse: inverse$a,
  names: names$a
};

function init$a() {}
/* Mollweide forward equations--mapping lat,long to x,y
    ----------------------------------------------------*/
function forward$9(p) {

  /* Forward equations
      -----------------*/
  var lon = p.x;
  var lat = p.y;

  var delta_lon = adjust_lon(lon - this.long0);
  var theta = lat;
  var con = Math.PI * Math.sin(lat);

  /* Iterate using the Newton-Raphson method to find theta
      -----------------------------------------------------*/
  while (true) {
    var delta_theta = -(theta + Math.sin(theta) - con) / (1 + Math.cos(theta));
    theta += delta_theta;
    if (Math.abs(delta_theta) < EPSLN) {
      break;
    }
  }
  theta /= 2;

  /* If the latitude is 90 deg, force the x coordinate to be "0 + false easting"
       this is done here because of precision problems with "cos(theta)"
       --------------------------------------------------------------------------*/
  if (Math.PI / 2 - Math.abs(lat) < EPSLN) {
    delta_lon = 0;
  }
  var x = 0.900316316158 * this.a * delta_lon * Math.cos(theta) + this.x0;
  var y = 1.4142135623731 * this.a * Math.sin(theta) + this.y0;

  p.x = x;
  p.y = y;
  return p;
}

function inverse$9(p) {
  var theta;
  var arg;

  /* Inverse equations
      -----------------*/
  p.x -= this.x0;
  p.y -= this.y0;
  arg = p.y / (1.4142135623731 * this.a);

  /* Because of division by zero problems, 'arg' can not be 1.  Therefore
       a number very close to one is used instead.
       -------------------------------------------------------------------*/
  if (Math.abs(arg) > 0.999999999999) {
    arg = 0.999999999999;
  }
  theta = Math.asin(arg);
  var lon = adjust_lon(this.long0 + (p.x / (0.900316316158 * this.a * Math.cos(theta))));
  if (lon < (-Math.PI)) {
    lon = -Math.PI;
  }
  if (lon > Math.PI) {
    lon = Math.PI;
  }
  arg = (2 * theta + Math.sin(2 * theta)) / Math.PI;
  if (Math.abs(arg) > 1) {
    arg = 1;
  }
  var lat = Math.asin(arg);

  p.x = lon;
  p.y = lat;
  return p;
}

var names$9 = ["Mollweide", "moll"];
var moll = {
  init: init$a,
  forward: forward$9,
  inverse: inverse$9,
  names: names$9
};

function init$9() {

  /* Place parameters in static storage for common use
      -------------------------------------------------*/
  // Standard Parallels cannot be equal and on opposite sides of the equator
  if (Math.abs(this.lat1 + this.lat2) < EPSLN) {
    return;
  }
  this.lat2 = this.lat2 || this.lat1;
  this.temp = this.b / this.a;
  this.es = 1 - Math.pow(this.temp, 2);
  this.e = Math.sqrt(this.es);
  this.e0 = e0fn(this.es);
  this.e1 = e1fn(this.es);
  this.e2 = e2fn(this.es);
  this.e3 = e3fn(this.es);

  this.sinphi = Math.sin(this.lat1);
  this.cosphi = Math.cos(this.lat1);

  this.ms1 = msfnz(this.e, this.sinphi, this.cosphi);
  this.ml1 = mlfn(this.e0, this.e1, this.e2, this.e3, this.lat1);

  if (Math.abs(this.lat1 - this.lat2) < EPSLN) {
    this.ns = this.sinphi;
  }
  else {
    this.sinphi = Math.sin(this.lat2);
    this.cosphi = Math.cos(this.lat2);
    this.ms2 = msfnz(this.e, this.sinphi, this.cosphi);
    this.ml2 = mlfn(this.e0, this.e1, this.e2, this.e3, this.lat2);
    this.ns = (this.ms1 - this.ms2) / (this.ml2 - this.ml1);
  }
  this.g = this.ml1 + this.ms1 / this.ns;
  this.ml0 = mlfn(this.e0, this.e1, this.e2, this.e3, this.lat0);
  this.rh = this.a * (this.g - this.ml0);
}

/* Equidistant Conic forward equations--mapping lat,long to x,y
  -----------------------------------------------------------*/
function forward$8(p) {
  var lon = p.x;
  var lat = p.y;
  var rh1;

  /* Forward equations
      -----------------*/
  if (this.sphere) {
    rh1 = this.a * (this.g - lat);
  }
  else {
    var ml = mlfn(this.e0, this.e1, this.e2, this.e3, lat);
    rh1 = this.a * (this.g - ml);
  }
  var theta = this.ns * adjust_lon(lon - this.long0);
  var x = this.x0 + rh1 * Math.sin(theta);
  var y = this.y0 + this.rh - rh1 * Math.cos(theta);
  p.x = x;
  p.y = y;
  return p;
}

/* Inverse equations
  -----------------*/
function inverse$8(p) {
  p.x -= this.x0;
  p.y = this.rh - p.y + this.y0;
  var con, rh1, lat, lon;
  if (this.ns >= 0) {
    rh1 = Math.sqrt(p.x * p.x + p.y * p.y);
    con = 1;
  }
  else {
    rh1 = -Math.sqrt(p.x * p.x + p.y * p.y);
    con = -1;
  }
  var theta = 0;
  if (rh1 !== 0) {
    theta = Math.atan2(con * p.x, con * p.y);
  }

  if (this.sphere) {
    lon = adjust_lon(this.long0 + theta / this.ns);
    lat = adjust_lat(this.g - rh1 / this.a);
    p.x = lon;
    p.y = lat;
    return p;
  }
  else {
    var ml = this.g - rh1 / this.a;
    lat = imlfn(ml, this.e0, this.e1, this.e2, this.e3);
    lon = adjust_lon(this.long0 + theta / this.ns);
    p.x = lon;
    p.y = lat;
    return p;
  }

}

var names$8 = ["Equidistant_Conic", "eqdc"];
var eqdc = {
  init: init$9,
  forward: forward$8,
  inverse: inverse$8,
  names: names$8
};

/* Initialize the Van Der Grinten projection
  ----------------------------------------*/
function init$8() {
  //this.R = 6370997; //Radius of earth
  this.R = this.a;
}

function forward$7(p) {

  var lon = p.x;
  var lat = p.y;

  /* Forward equations
    -----------------*/
  var dlon = adjust_lon(lon - this.long0);
  var x, y;

  if (Math.abs(lat) <= EPSLN) {
    x = this.x0 + this.R * dlon;
    y = this.y0;
  }
  var theta = asinz(2 * Math.abs(lat / Math.PI));
  if ((Math.abs(dlon) <= EPSLN) || (Math.abs(Math.abs(lat) - HALF_PI) <= EPSLN)) {
    x = this.x0;
    if (lat >= 0) {
      y = this.y0 + Math.PI * this.R * Math.tan(0.5 * theta);
    }
    else {
      y = this.y0 + Math.PI * this.R * -Math.tan(0.5 * theta);
    }
    //  return(OK);
  }
  var al = 0.5 * Math.abs((Math.PI / dlon) - (dlon / Math.PI));
  var asq = al * al;
  var sinth = Math.sin(theta);
  var costh = Math.cos(theta);

  var g = costh / (sinth + costh - 1);
  var gsq = g * g;
  var m = g * (2 / sinth - 1);
  var msq = m * m;
  var con = Math.PI * this.R * (al * (g - msq) + Math.sqrt(asq * (g - msq) * (g - msq) - (msq + asq) * (gsq - msq))) / (msq + asq);
  if (dlon < 0) {
    con = -con;
  }
  x = this.x0 + con;
  //con = Math.abs(con / (Math.PI * this.R));
  var q = asq + g;
  con = Math.PI * this.R * (m * q - al * Math.sqrt((msq + asq) * (asq + 1) - q * q)) / (msq + asq);
  if (lat >= 0) {
    //y = this.y0 + Math.PI * this.R * Math.sqrt(1 - con * con - 2 * al * con);
    y = this.y0 + con;
  }
  else {
    //y = this.y0 - Math.PI * this.R * Math.sqrt(1 - con * con - 2 * al * con);
    y = this.y0 - con;
  }
  p.x = x;
  p.y = y;
  return p;
}

/* Van Der Grinten inverse equations--mapping x,y to lat/long
  ---------------------------------------------------------*/
function inverse$7(p) {
  var lon, lat;
  var xx, yy, xys, c1, c2, c3;
  var a1;
  var m1;
  var con;
  var th1;
  var d;

  /* inverse equations
    -----------------*/
  p.x -= this.x0;
  p.y -= this.y0;
  con = Math.PI * this.R;
  xx = p.x / con;
  yy = p.y / con;
  xys = xx * xx + yy * yy;
  c1 = -Math.abs(yy) * (1 + xys);
  c2 = c1 - 2 * yy * yy + xx * xx;
  c3 = -2 * c1 + 1 + 2 * yy * yy + xys * xys;
  d = yy * yy / c3 + (2 * c2 * c2 * c2 / c3 / c3 / c3 - 9 * c1 * c2 / c3 / c3) / 27;
  a1 = (c1 - c2 * c2 / 3 / c3) / c3;
  m1 = 2 * Math.sqrt(-a1 / 3);
  con = ((3 * d) / a1) / m1;
  if (Math.abs(con) > 1) {
    if (con >= 0) {
      con = 1;
    }
    else {
      con = -1;
    }
  }
  th1 = Math.acos(con) / 3;
  if (p.y >= 0) {
    lat = (-m1 * Math.cos(th1 + Math.PI / 3) - c2 / 3 / c3) * Math.PI;
  }
  else {
    lat = -(-m1 * Math.cos(th1 + Math.PI / 3) - c2 / 3 / c3) * Math.PI;
  }

  if (Math.abs(xx) < EPSLN) {
    lon = this.long0;
  }
  else {
    lon = adjust_lon(this.long0 + Math.PI * (xys - 1 + Math.sqrt(1 + 2 * (xx * xx - yy * yy) + xys * xys)) / 2 / xx);
  }

  p.x = lon;
  p.y = lat;
  return p;
}

var names$7 = ["Van_der_Grinten_I", "VanDerGrinten", "vandg"];
var vandg = {
  init: init$8,
  forward: forward$7,
  inverse: inverse$7,
  names: names$7
};

function init$7() {
  this.sin_p12 = Math.sin(this.lat0);
  this.cos_p12 = Math.cos(this.lat0);
}

function forward$6(p) {
  var lon = p.x;
  var lat = p.y;
  var sinphi = Math.sin(p.y);
  var cosphi = Math.cos(p.y);
  var dlon = adjust_lon(lon - this.long0);
  var e0, e1, e2, e3, Mlp, Ml, tanphi, Nl1, Nl, psi, Az, G, H, GH, Hs, c, kp, cos_c, s, s2, s3, s4, s5;
  if (this.sphere) {
    if (Math.abs(this.sin_p12 - 1) <= EPSLN) {
      //North Pole case
      p.x = this.x0 + this.a * (HALF_PI - lat) * Math.sin(dlon);
      p.y = this.y0 - this.a * (HALF_PI - lat) * Math.cos(dlon);
      return p;
    }
    else if (Math.abs(this.sin_p12 + 1) <= EPSLN) {
      //South Pole case
      p.x = this.x0 + this.a * (HALF_PI + lat) * Math.sin(dlon);
      p.y = this.y0 + this.a * (HALF_PI + lat) * Math.cos(dlon);
      return p;
    }
    else {
      //default case
      cos_c = this.sin_p12 * sinphi + this.cos_p12 * cosphi * Math.cos(dlon);
      c = Math.acos(cos_c);
      kp = c ? c / Math.sin(c) : 1;
      p.x = this.x0 + this.a * kp * cosphi * Math.sin(dlon);
      p.y = this.y0 + this.a * kp * (this.cos_p12 * sinphi - this.sin_p12 * cosphi * Math.cos(dlon));
      return p;
    }
  }
  else {
    e0 = e0fn(this.es);
    e1 = e1fn(this.es);
    e2 = e2fn(this.es);
    e3 = e3fn(this.es);
    if (Math.abs(this.sin_p12 - 1) <= EPSLN) {
      //North Pole case
      Mlp = this.a * mlfn(e0, e1, e2, e3, HALF_PI);
      Ml = this.a * mlfn(e0, e1, e2, e3, lat);
      p.x = this.x0 + (Mlp - Ml) * Math.sin(dlon);
      p.y = this.y0 - (Mlp - Ml) * Math.cos(dlon);
      return p;
    }
    else if (Math.abs(this.sin_p12 + 1) <= EPSLN) {
      //South Pole case
      Mlp = this.a * mlfn(e0, e1, e2, e3, HALF_PI);
      Ml = this.a * mlfn(e0, e1, e2, e3, lat);
      p.x = this.x0 + (Mlp + Ml) * Math.sin(dlon);
      p.y = this.y0 + (Mlp + Ml) * Math.cos(dlon);
      return p;
    }
    else {
      //Default case
      tanphi = sinphi / cosphi;
      Nl1 = gN(this.a, this.e, this.sin_p12);
      Nl = gN(this.a, this.e, sinphi);
      psi = Math.atan((1 - this.es) * tanphi + this.es * Nl1 * this.sin_p12 / (Nl * cosphi));
      Az = Math.atan2(Math.sin(dlon), this.cos_p12 * Math.tan(psi) - this.sin_p12 * Math.cos(dlon));
      if (Az === 0) {
        s = Math.asin(this.cos_p12 * Math.sin(psi) - this.sin_p12 * Math.cos(psi));
      }
      else if (Math.abs(Math.abs(Az) - Math.PI) <= EPSLN) {
        s = -Math.asin(this.cos_p12 * Math.sin(psi) - this.sin_p12 * Math.cos(psi));
      }
      else {
        s = Math.asin(Math.sin(dlon) * Math.cos(psi) / Math.sin(Az));
      }
      G = this.e * this.sin_p12 / Math.sqrt(1 - this.es);
      H = this.e * this.cos_p12 * Math.cos(Az) / Math.sqrt(1 - this.es);
      GH = G * H;
      Hs = H * H;
      s2 = s * s;
      s3 = s2 * s;
      s4 = s3 * s;
      s5 = s4 * s;
      c = Nl1 * s * (1 - s2 * Hs * (1 - Hs) / 6 + s3 / 8 * GH * (1 - 2 * Hs) + s4 / 120 * (Hs * (4 - 7 * Hs) - 3 * G * G * (1 - 7 * Hs)) - s5 / 48 * GH);
      p.x = this.x0 + c * Math.sin(Az);
      p.y = this.y0 + c * Math.cos(Az);
      return p;
    }
  }


}

function inverse$6(p) {
  p.x -= this.x0;
  p.y -= this.y0;
  var rh, z, sinz, cosz, lon, lat, con, e0, e1, e2, e3, Mlp, M, N1, psi, Az, cosAz, tmp, A, B, D, Ee, F, sinpsi;
  if (this.sphere) {
    rh = Math.sqrt(p.x * p.x + p.y * p.y);
    if (rh > (2 * HALF_PI * this.a)) {
      return;
    }
    z = rh / this.a;

    sinz = Math.sin(z);
    cosz = Math.cos(z);

    lon = this.long0;
    if (Math.abs(rh) <= EPSLN) {
      lat = this.lat0;
    }
    else {
      lat = asinz(cosz * this.sin_p12 + (p.y * sinz * this.cos_p12) / rh);
      con = Math.abs(this.lat0) - HALF_PI;
      if (Math.abs(con) <= EPSLN) {
        if (this.lat0 >= 0) {
          lon = adjust_lon(this.long0 + Math.atan2(p.x, - p.y));
        }
        else {
          lon = adjust_lon(this.long0 - Math.atan2(-p.x, p.y));
        }
      }
      else {
        /*con = cosz - this.sin_p12 * Math.sin(lat);
        if ((Math.abs(con) < EPSLN) && (Math.abs(p.x) < EPSLN)) {
          //no-op, just keep the lon value as is
        } else {
          var temp = Math.atan2((p.x * sinz * this.cos_p12), (con * rh));
          lon = adjust_lon(this.long0 + Math.atan2((p.x * sinz * this.cos_p12), (con * rh)));
        }*/
        lon = adjust_lon(this.long0 + Math.atan2(p.x * sinz, rh * this.cos_p12 * cosz - p.y * this.sin_p12 * sinz));
      }
    }

    p.x = lon;
    p.y = lat;
    return p;
  }
  else {
    e0 = e0fn(this.es);
    e1 = e1fn(this.es);
    e2 = e2fn(this.es);
    e3 = e3fn(this.es);
    if (Math.abs(this.sin_p12 - 1) <= EPSLN) {
      //North pole case
      Mlp = this.a * mlfn(e0, e1, e2, e3, HALF_PI);
      rh = Math.sqrt(p.x * p.x + p.y * p.y);
      M = Mlp - rh;
      lat = imlfn(M / this.a, e0, e1, e2, e3);
      lon = adjust_lon(this.long0 + Math.atan2(p.x, - 1 * p.y));
      p.x = lon;
      p.y = lat;
      return p;
    }
    else if (Math.abs(this.sin_p12 + 1) <= EPSLN) {
      //South pole case
      Mlp = this.a * mlfn(e0, e1, e2, e3, HALF_PI);
      rh = Math.sqrt(p.x * p.x + p.y * p.y);
      M = rh - Mlp;

      lat = imlfn(M / this.a, e0, e1, e2, e3);
      lon = adjust_lon(this.long0 + Math.atan2(p.x, p.y));
      p.x = lon;
      p.y = lat;
      return p;
    }
    else {
      //default case
      rh = Math.sqrt(p.x * p.x + p.y * p.y);
      Az = Math.atan2(p.x, p.y);
      N1 = gN(this.a, this.e, this.sin_p12);
      cosAz = Math.cos(Az);
      tmp = this.e * this.cos_p12 * cosAz;
      A = -tmp * tmp / (1 - this.es);
      B = 3 * this.es * (1 - A) * this.sin_p12 * this.cos_p12 * cosAz / (1 - this.es);
      D = rh / N1;
      Ee = D - A * (1 + A) * Math.pow(D, 3) / 6 - B * (1 + 3 * A) * Math.pow(D, 4) / 24;
      F = 1 - A * Ee * Ee / 2 - D * Ee * Ee * Ee / 6;
      psi = Math.asin(this.sin_p12 * Math.cos(Ee) + this.cos_p12 * Math.sin(Ee) * cosAz);
      lon = adjust_lon(this.long0 + Math.asin(Math.sin(Az) * Math.sin(Ee) / Math.cos(psi)));
      sinpsi = Math.sin(psi);
      lat = Math.atan2((sinpsi - this.es * F * this.sin_p12) * Math.tan(psi), sinpsi * (1 - this.es));
      p.x = lon;
      p.y = lat;
      return p;
    }
  }

}

var names$6 = ["Azimuthal_Equidistant", "aeqd"];
var aeqd = {
  init: init$7,
  forward: forward$6,
  inverse: inverse$6,
  names: names$6
};

function init$6() {
  //double temp;      /* temporary variable    */

  /* Place parameters in static storage for common use
      -------------------------------------------------*/
  this.sin_p14 = Math.sin(this.lat0);
  this.cos_p14 = Math.cos(this.lat0);
}

/* Orthographic forward equations--mapping lat,long to x,y
    ---------------------------------------------------*/
function forward$5(p) {
  var sinphi, cosphi; /* sin and cos value        */
  var dlon; /* delta longitude value      */
  var coslon; /* cos of longitude        */
  var ksp; /* scale factor          */
  var g, x, y;
  var lon = p.x;
  var lat = p.y;
  /* Forward equations
      -----------------*/
  dlon = adjust_lon(lon - this.long0);

  sinphi = Math.sin(lat);
  cosphi = Math.cos(lat);

  coslon = Math.cos(dlon);
  g = this.sin_p14 * sinphi + this.cos_p14 * cosphi * coslon;
  ksp = 1;
  if ((g > 0) || (Math.abs(g) <= EPSLN)) {
    x = this.a * ksp * cosphi * Math.sin(dlon);
    y = this.y0 + this.a * ksp * (this.cos_p14 * sinphi - this.sin_p14 * cosphi * coslon);
  }
  p.x = x;
  p.y = y;
  return p;
}

function inverse$5(p) {
  var rh; /* height above ellipsoid      */
  var z; /* angle          */
  var sinz, cosz; /* sin of z and cos of z      */
  var con;
  var lon, lat;
  /* Inverse equations
      -----------------*/
  p.x -= this.x0;
  p.y -= this.y0;
  rh = Math.sqrt(p.x * p.x + p.y * p.y);
  z = asinz(rh / this.a);

  sinz = Math.sin(z);
  cosz = Math.cos(z);

  lon = this.long0;
  if (Math.abs(rh) <= EPSLN) {
    lat = this.lat0;
    p.x = lon;
    p.y = lat;
    return p;
  }
  lat = asinz(cosz * this.sin_p14 + (p.y * sinz * this.cos_p14) / rh);
  con = Math.abs(this.lat0) - HALF_PI;
  if (Math.abs(con) <= EPSLN) {
    if (this.lat0 >= 0) {
      lon = adjust_lon(this.long0 + Math.atan2(p.x, - p.y));
    }
    else {
      lon = adjust_lon(this.long0 - Math.atan2(-p.x, p.y));
    }
    p.x = lon;
    p.y = lat;
    return p;
  }
  lon = adjust_lon(this.long0 + Math.atan2((p.x * sinz), rh * this.cos_p14 * cosz - p.y * this.sin_p14 * sinz));
  p.x = lon;
  p.y = lat;
  return p;
}

var names$5 = ["ortho"];
var ortho = {
  init: init$6,
  forward: forward$5,
  inverse: inverse$5,
  names: names$5
};

// QSC projection rewritten from the original PROJ4

/* constants */
var FACE_ENUM = {
    FRONT: 1,
    RIGHT: 2,
    BACK: 3,
    LEFT: 4,
    TOP: 5,
    BOTTOM: 6
};

var AREA_ENUM = {
    AREA_0: 1,
    AREA_1: 2,
    AREA_2: 3,
    AREA_3: 4
};

function init$5() {

  this.x0 = this.x0 || 0;
  this.y0 = this.y0 || 0;
  this.lat0 = this.lat0 || 0;
  this.long0 = this.long0 || 0;
  this.lat_ts = this.lat_ts || 0;
  this.title = this.title || "Quadrilateralized Spherical Cube";

  /* Determine the cube face from the center of projection. */
  if (this.lat0 >= HALF_PI - FORTPI / 2.0) {
    this.face = FACE_ENUM.TOP;
  } else if (this.lat0 <= -(HALF_PI - FORTPI / 2.0)) {
    this.face = FACE_ENUM.BOTTOM;
  } else if (Math.abs(this.long0) <= FORTPI) {
    this.face = FACE_ENUM.FRONT;
  } else if (Math.abs(this.long0) <= HALF_PI + FORTPI) {
    this.face = this.long0 > 0.0 ? FACE_ENUM.RIGHT : FACE_ENUM.LEFT;
  } else {
    this.face = FACE_ENUM.BACK;
  }

  /* Fill in useful values for the ellipsoid <-> sphere shift
   * described in [LK12]. */
  if (this.es !== 0) {
    this.one_minus_f = 1 - (this.a - this.b) / this.a;
    this.one_minus_f_squared = this.one_minus_f * this.one_minus_f;
  }
}

// QSC forward equations--mapping lat,long to x,y
// -----------------------------------------------------------------
function forward$4(p) {
  var xy = {x: 0, y: 0};
  var lat, lon;
  var theta, phi;
  var t, mu;
  /* nu; */
  var area = {value: 0};

  // move lon according to projection's lon
  p.x -= this.long0;

  /* Convert the geodetic latitude to a geocentric latitude.
   * This corresponds to the shift from the ellipsoid to the sphere
   * described in [LK12]. */
  if (this.es !== 0) {//if (P->es != 0) {
    lat = Math.atan(this.one_minus_f_squared * Math.tan(p.y));
  } else {
    lat = p.y;
  }

  /* Convert the input lat, lon into theta, phi as used by QSC.
   * This depends on the cube face and the area on it.
   * For the top and bottom face, we can compute theta and phi
   * directly from phi, lam. For the other faces, we must use
   * unit sphere cartesian coordinates as an intermediate step. */
  lon = p.x; //lon = lp.lam;
  if (this.face === FACE_ENUM.TOP) {
    phi = HALF_PI - lat;
    if (lon >= FORTPI && lon <= HALF_PI + FORTPI) {
      area.value = AREA_ENUM.AREA_0;
      theta = lon - HALF_PI;
    } else if (lon > HALF_PI + FORTPI || lon <= -(HALF_PI + FORTPI)) {
      area.value = AREA_ENUM.AREA_1;
      theta = (lon > 0.0 ? lon - SPI : lon + SPI);
    } else if (lon > -(HALF_PI + FORTPI) && lon <= -FORTPI) {
      area.value = AREA_ENUM.AREA_2;
      theta = lon + HALF_PI;
    } else {
      area.value = AREA_ENUM.AREA_3;
      theta = lon;
    }
  } else if (this.face === FACE_ENUM.BOTTOM) {
    phi = HALF_PI + lat;
    if (lon >= FORTPI && lon <= HALF_PI + FORTPI) {
      area.value = AREA_ENUM.AREA_0;
      theta = -lon + HALF_PI;
    } else if (lon < FORTPI && lon >= -FORTPI) {
      area.value = AREA_ENUM.AREA_1;
      theta = -lon;
    } else if (lon < -FORTPI && lon >= -(HALF_PI + FORTPI)) {
      area.value = AREA_ENUM.AREA_2;
      theta = -lon - HALF_PI;
    } else {
      area.value = AREA_ENUM.AREA_3;
      theta = (lon > 0.0 ? -lon + SPI : -lon - SPI);
    }
  } else {
    var q, r, s;
    var sinlat, coslat;
    var sinlon, coslon;

    if (this.face === FACE_ENUM.RIGHT) {
      lon = qsc_shift_lon_origin(lon, +HALF_PI);
    } else if (this.face === FACE_ENUM.BACK) {
      lon = qsc_shift_lon_origin(lon, +SPI);
    } else if (this.face === FACE_ENUM.LEFT) {
      lon = qsc_shift_lon_origin(lon, -HALF_PI);
    }
    sinlat = Math.sin(lat);
    coslat = Math.cos(lat);
    sinlon = Math.sin(lon);
    coslon = Math.cos(lon);
    q = coslat * coslon;
    r = coslat * sinlon;
    s = sinlat;

    if (this.face === FACE_ENUM.FRONT) {
      phi = Math.acos(q);
      theta = qsc_fwd_equat_face_theta(phi, s, r, area);
    } else if (this.face === FACE_ENUM.RIGHT) {
      phi = Math.acos(r);
      theta = qsc_fwd_equat_face_theta(phi, s, -q, area);
    } else if (this.face === FACE_ENUM.BACK) {
      phi = Math.acos(-q);
      theta = qsc_fwd_equat_face_theta(phi, s, -r, area);
    } else if (this.face === FACE_ENUM.LEFT) {
      phi = Math.acos(-r);
      theta = qsc_fwd_equat_face_theta(phi, s, q, area);
    } else {
      /* Impossible */
      phi = theta = 0;
      area.value = AREA_ENUM.AREA_0;
    }
  }

  /* Compute mu and nu for the area of definition.
   * For mu, see Eq. (3-21) in [OL76], but note the typos:
   * compare with Eq. (3-14). For nu, see Eq. (3-38). */
  mu = Math.atan((12 / SPI) * (theta + Math.acos(Math.sin(theta) * Math.cos(FORTPI)) - HALF_PI));
  t = Math.sqrt((1 - Math.cos(phi)) / (Math.cos(mu) * Math.cos(mu)) / (1 - Math.cos(Math.atan(1 / Math.cos(theta)))));

  /* Apply the result to the real area. */
  if (area.value === AREA_ENUM.AREA_1) {
    mu += HALF_PI;
  } else if (area.value === AREA_ENUM.AREA_2) {
    mu += SPI;
  } else if (area.value === AREA_ENUM.AREA_3) {
    mu += 1.5 * SPI;
  }

  /* Now compute x, y from mu and nu */
  xy.x = t * Math.cos(mu);
  xy.y = t * Math.sin(mu);
  xy.x = xy.x * this.a + this.x0;
  xy.y = xy.y * this.a + this.y0;

  p.x = xy.x;
  p.y = xy.y;
  return p;
}

// QSC inverse equations--mapping x,y to lat/long
// -----------------------------------------------------------------
function inverse$4(p) {
  var lp = {lam: 0, phi: 0};
  var mu, nu, cosmu, tannu;
  var tantheta, theta, cosphi, phi;
  var t;
  var area = {value: 0};

  /* de-offset */
  p.x = (p.x - this.x0) / this.a;
  p.y = (p.y - this.y0) / this.a;

  /* Convert the input x, y to the mu and nu angles as used by QSC.
   * This depends on the area of the cube face. */
  nu = Math.atan(Math.sqrt(p.x * p.x + p.y * p.y));
  mu = Math.atan2(p.y, p.x);
  if (p.x >= 0.0 && p.x >= Math.abs(p.y)) {
    area.value = AREA_ENUM.AREA_0;
  } else if (p.y >= 0.0 && p.y >= Math.abs(p.x)) {
    area.value = AREA_ENUM.AREA_1;
    mu -= HALF_PI;
  } else if (p.x < 0.0 && -p.x >= Math.abs(p.y)) {
    area.value = AREA_ENUM.AREA_2;
    mu = (mu < 0.0 ? mu + SPI : mu - SPI);
  } else {
    area.value = AREA_ENUM.AREA_3;
    mu += HALF_PI;
  }

  /* Compute phi and theta for the area of definition.
   * The inverse projection is not described in the original paper, but some
   * good hints can be found here (as of 2011-12-14):
   * http://fits.gsfc.nasa.gov/fitsbits/saf.93/saf.9302
   * (search for "Message-Id: <9302181759.AA25477 at fits.cv.nrao.edu>") */
  t = (SPI / 12) * Math.tan(mu);
  tantheta = Math.sin(t) / (Math.cos(t) - (1 / Math.sqrt(2)));
  theta = Math.atan(tantheta);
  cosmu = Math.cos(mu);
  tannu = Math.tan(nu);
  cosphi = 1 - cosmu * cosmu * tannu * tannu * (1 - Math.cos(Math.atan(1 / Math.cos(theta))));
  if (cosphi < -1) {
    cosphi = -1;
  } else if (cosphi > +1) {
    cosphi = +1;
  }

  /* Apply the result to the real area on the cube face.
   * For the top and bottom face, we can compute phi and lam directly.
   * For the other faces, we must use unit sphere cartesian coordinates
   * as an intermediate step. */
  if (this.face === FACE_ENUM.TOP) {
    phi = Math.acos(cosphi);
    lp.phi = HALF_PI - phi;
    if (area.value === AREA_ENUM.AREA_0) {
      lp.lam = theta + HALF_PI;
    } else if (area.value === AREA_ENUM.AREA_1) {
      lp.lam = (theta < 0.0 ? theta + SPI : theta - SPI);
    } else if (area.value === AREA_ENUM.AREA_2) {
      lp.lam = theta - HALF_PI;
    } else /* area.value == AREA_ENUM.AREA_3 */ {
      lp.lam = theta;
    }
  } else if (this.face === FACE_ENUM.BOTTOM) {
    phi = Math.acos(cosphi);
    lp.phi = phi - HALF_PI;
    if (area.value === AREA_ENUM.AREA_0) {
      lp.lam = -theta + HALF_PI;
    } else if (area.value === AREA_ENUM.AREA_1) {
      lp.lam = -theta;
    } else if (area.value === AREA_ENUM.AREA_2) {
      lp.lam = -theta - HALF_PI;
    } else /* area.value == AREA_ENUM.AREA_3 */ {
      lp.lam = (theta < 0.0 ? -theta - SPI : -theta + SPI);
    }
  } else {
    /* Compute phi and lam via cartesian unit sphere coordinates. */
    var q, r, s;
    q = cosphi;
    t = q * q;
    if (t >= 1) {
      s = 0;
    } else {
      s = Math.sqrt(1 - t) * Math.sin(theta);
    }
    t += s * s;
    if (t >= 1) {
      r = 0;
    } else {
      r = Math.sqrt(1 - t);
    }
    /* Rotate q,r,s into the correct area. */
    if (area.value === AREA_ENUM.AREA_1) {
      t = r;
      r = -s;
      s = t;
    } else if (area.value === AREA_ENUM.AREA_2) {
      r = -r;
      s = -s;
    } else if (area.value === AREA_ENUM.AREA_3) {
      t = r;
      r = s;
      s = -t;
    }
    /* Rotate q,r,s into the correct cube face. */
    if (this.face === FACE_ENUM.RIGHT) {
      t = q;
      q = -r;
      r = t;
    } else if (this.face === FACE_ENUM.BACK) {
      q = -q;
      r = -r;
    } else if (this.face === FACE_ENUM.LEFT) {
      t = q;
      q = r;
      r = -t;
    }
    /* Now compute phi and lam from the unit sphere coordinates. */
    lp.phi = Math.acos(-s) - HALF_PI;
    lp.lam = Math.atan2(r, q);
    if (this.face === FACE_ENUM.RIGHT) {
      lp.lam = qsc_shift_lon_origin(lp.lam, -HALF_PI);
    } else if (this.face === FACE_ENUM.BACK) {
      lp.lam = qsc_shift_lon_origin(lp.lam, -SPI);
    } else if (this.face === FACE_ENUM.LEFT) {
      lp.lam = qsc_shift_lon_origin(lp.lam, +HALF_PI);
    }
  }

  /* Apply the shift from the sphere to the ellipsoid as described
   * in [LK12]. */
  if (this.es !== 0) {
    var invert_sign;
    var tanphi, xa;
    invert_sign = (lp.phi < 0 ? 1 : 0);
    tanphi = Math.tan(lp.phi);
    xa = this.b / Math.sqrt(tanphi * tanphi + this.one_minus_f_squared);
    lp.phi = Math.atan(Math.sqrt(this.a * this.a - xa * xa) / (this.one_minus_f * xa));
    if (invert_sign) {
      lp.phi = -lp.phi;
    }
  }

  lp.lam += this.long0;
  p.x = lp.lam;
  p.y = lp.phi;
  return p;
}

/* Helper function for forward projection: compute the theta angle
 * and determine the area number. */
function qsc_fwd_equat_face_theta(phi, y, x, area) {
  var theta;
  if (phi < EPSLN) {
    area.value = AREA_ENUM.AREA_0;
    theta = 0.0;
  } else {
    theta = Math.atan2(y, x);
    if (Math.abs(theta) <= FORTPI) {
      area.value = AREA_ENUM.AREA_0;
    } else if (theta > FORTPI && theta <= HALF_PI + FORTPI) {
      area.value = AREA_ENUM.AREA_1;
      theta -= HALF_PI;
    } else if (theta > HALF_PI + FORTPI || theta <= -(HALF_PI + FORTPI)) {
      area.value = AREA_ENUM.AREA_2;
      theta = (theta >= 0.0 ? theta - SPI : theta + SPI);
    } else {
      area.value = AREA_ENUM.AREA_3;
      theta += HALF_PI;
    }
  }
  return theta;
}

/* Helper function: shift the longitude. */
function qsc_shift_lon_origin(lon, offset) {
  var slon = lon + offset;
  if (slon < -SPI) {
    slon += TWO_PI;
  } else if (slon > +SPI) {
    slon -= TWO_PI;
  }
  return slon;
}

var names$4 = ["Quadrilateralized Spherical Cube", "Quadrilateralized_Spherical_Cube", "qsc"];
var qsc = {
  init: init$5,
  forward: forward$4,
  inverse: inverse$4,
  names: names$4
};

// Robinson projection

var COEFS_X = [
    [1.0000, 2.2199e-17, -7.15515e-05, 3.1103e-06],
    [0.9986, -0.000482243, -2.4897e-05, -1.3309e-06],
    [0.9954, -0.00083103, -4.48605e-05, -9.86701e-07],
    [0.9900, -0.00135364, -5.9661e-05, 3.6777e-06],
    [0.9822, -0.00167442, -4.49547e-06, -5.72411e-06],
    [0.9730, -0.00214868, -9.03571e-05, 1.8736e-08],
    [0.9600, -0.00305085, -9.00761e-05, 1.64917e-06],
    [0.9427, -0.00382792, -6.53386e-05, -2.6154e-06],
    [0.9216, -0.00467746, -0.00010457, 4.81243e-06],
    [0.8962, -0.00536223, -3.23831e-05, -5.43432e-06],
    [0.8679, -0.00609363, -0.000113898, 3.32484e-06],
    [0.8350, -0.00698325, -6.40253e-05, 9.34959e-07],
    [0.7986, -0.00755338, -5.00009e-05, 9.35324e-07],
    [0.7597, -0.00798324, -3.5971e-05, -2.27626e-06],
    [0.7186, -0.00851367, -7.01149e-05, -8.6303e-06],
    [0.6732, -0.00986209, -0.000199569, 1.91974e-05],
    [0.6213, -0.010418, 8.83923e-05, 6.24051e-06],
    [0.5722, -0.00906601, 0.000182, 6.24051e-06],
    [0.5322, -0.00677797, 0.000275608, 6.24051e-06]
];

var COEFS_Y = [
    [-5.20417e-18, 0.0124, 1.21431e-18, -8.45284e-11],
    [0.0620, 0.0124, -1.26793e-09, 4.22642e-10],
    [0.1240, 0.0124, 5.07171e-09, -1.60604e-09],
    [0.1860, 0.0123999, -1.90189e-08, 6.00152e-09],
    [0.2480, 0.0124002, 7.10039e-08, -2.24e-08],
    [0.3100, 0.0123992, -2.64997e-07, 8.35986e-08],
    [0.3720, 0.0124029, 9.88983e-07, -3.11994e-07],
    [0.4340, 0.0123893, -3.69093e-06, -4.35621e-07],
    [0.4958, 0.0123198, -1.02252e-05, -3.45523e-07],
    [0.5571, 0.0121916, -1.54081e-05, -5.82288e-07],
    [0.6176, 0.0119938, -2.41424e-05, -5.25327e-07],
    [0.6769, 0.011713, -3.20223e-05, -5.16405e-07],
    [0.7346, 0.0113541, -3.97684e-05, -6.09052e-07],
    [0.7903, 0.0109107, -4.89042e-05, -1.04739e-06],
    [0.8435, 0.0103431, -6.4615e-05, -1.40374e-09],
    [0.8936, 0.00969686, -6.4636e-05, -8.547e-06],
    [0.9394, 0.00840947, -0.000192841, -4.2106e-06],
    [0.9761, 0.00616527, -0.000256, -4.2106e-06],
    [1.0000, 0.00328947, -0.000319159, -4.2106e-06]
];

var FXC = 0.8487;
var FYC = 1.3523;
var C1 = R2D/5; // rad to 5-degree interval
var RC1 = 1/C1;
var NODES = 18;

var poly3_val = function(coefs, x) {
    return coefs[0] + x * (coefs[1] + x * (coefs[2] + x * coefs[3]));
};

var poly3_der = function(coefs, x) {
    return coefs[1] + x * (2 * coefs[2] + x * 3 * coefs[3]);
};

function newton_rapshon(f_df, start, max_err, iters) {
    var x = start;
    for (; iters; --iters) {
        var upd = f_df(x);
        x -= upd;
        if (Math.abs(upd) < max_err) {
            break;
        }
    }
    return x;
}

function init$4() {
    this.x0 = this.x0 || 0;
    this.y0 = this.y0 || 0;
    this.long0 = this.long0 || 0;
    this.es = 0;
    this.title = this.title || "Robinson";
}

function forward$3(ll) {
    var lon = adjust_lon(ll.x - this.long0);

    var dphi = Math.abs(ll.y);
    var i = Math.floor(dphi * C1);
    if (i < 0) {
        i = 0;
    } else if (i >= NODES) {
        i = NODES - 1;
    }
    dphi = R2D * (dphi - RC1 * i);
    var xy = {
        x: poly3_val(COEFS_X[i], dphi) * lon,
        y: poly3_val(COEFS_Y[i], dphi)
    };
    if (ll.y < 0) {
        xy.y = -xy.y;
    }

    xy.x = xy.x * this.a * FXC + this.x0;
    xy.y = xy.y * this.a * FYC + this.y0;
    return xy;
}

function inverse$3(xy) {
    var ll = {
        x: (xy.x - this.x0) / (this.a * FXC),
        y: Math.abs(xy.y - this.y0) / (this.a * FYC)
    };

    if (ll.y >= 1) { // pathologic case
        ll.x /= COEFS_X[NODES][0];
        ll.y = xy.y < 0 ? -HALF_PI : HALF_PI;
    } else {
        // find table interval
        var i = Math.floor(ll.y * NODES);
        if (i < 0) {
            i = 0;
        } else if (i >= NODES) {
            i = NODES - 1;
        }
        for (;;) {
            if (COEFS_Y[i][0] > ll.y) {
                --i;
            } else if (COEFS_Y[i+1][0] <= ll.y) {
                ++i;
            } else {
                break;
            }
        }
        // linear interpolation in 5 degree interval
        var coefs = COEFS_Y[i];
        var t = 5 * (ll.y - coefs[0]) / (COEFS_Y[i+1][0] - coefs[0]);
        // find t so that poly3_val(coefs, t) = ll.y
        t = newton_rapshon(function(x) {
            return (poly3_val(coefs, x) - ll.y) / poly3_der(coefs, x);
        }, t, EPSLN, 100);

        ll.x /= poly3_val(COEFS_X[i], t);
        ll.y = (5 * i + t) * D2R$1;
        if (xy.y < 0) {
            ll.y = -ll.y;
        }
    }

    ll.x = adjust_lon(ll.x + this.long0);
    return ll;
}

var names$3 = ["Robinson", "robin"];
var robin = {
  init: init$4,
  forward: forward$3,
  inverse: inverse$3,
  names: names$3
};

function init$3() {
    this.name = 'geocent';

}

function forward$2(p) {
    var point = geodeticToGeocentric(p, this.es, this.a);
    return point;
}

function inverse$2(p) {
    var point = geocentricToGeodetic(p, this.es, this.a, this.b);
    return point;
}

var names$2 = ["Geocentric", 'geocentric', "geocent", "Geocent"];
var geocent = {
    init: init$3,
    forward: forward$2,
    inverse: inverse$2,
    names: names$2
};

var mode = {
  N_POLE: 0,
  S_POLE: 1,
  EQUIT: 2,
  OBLIQ: 3
};

var params = {
  h:     { def: 100000, num: true },           // default is Karman line, no default in PROJ.7
  azi:   { def: 0, num: true, degrees: true }, // default is North
  tilt:  { def: 0, num: true, degrees: true }, // default is Nadir
  long0: { def: 0, num: true },                // default is Greenwich, conversion to rad is automatic
  lat0:  { def: 0, num: true }                 // default is Equator, conversion to rad is automatic
};

function init$2() {
  Object.keys(params).forEach(function (p) {
    if (typeof this[p] === "undefined") {
      this[p] = params[p].def;
    } else if (params[p].num && isNaN(this[p])) {
      throw new Error("Invalid parameter value, must be numeric " + p + " = " + this[p]);
    } else if (params[p].num) {
      this[p] = parseFloat(this[p]);
    }
    if (params[p].degrees) {
      this[p] = this[p] * D2R$1;
    }
  }.bind(this));

  if (Math.abs((Math.abs(this.lat0) - HALF_PI)) < EPSLN) {
    this.mode = this.lat0 < 0 ? mode.S_POLE : mode.N_POLE;
  } else if (Math.abs(this.lat0) < EPSLN) {
    this.mode = mode.EQUIT;
  } else {
    this.mode = mode.OBLIQ;
    this.sinph0 = Math.sin(this.lat0);
    this.cosph0 = Math.cos(this.lat0);
  }

  this.pn1 = this.h / this.a;  // Normalize relative to the Earth's radius

  if (this.pn1 <= 0 || this.pn1 > 1e10) {
    throw new Error("Invalid height");
  }
  
  this.p = 1 + this.pn1;
  this.rp = 1 / this.p;
  this.h1 = 1 / this.pn1;
  this.pfact = (this.p + 1) * this.h1;
  this.es = 0;

  var omega = this.tilt;
  var gamma = this.azi;
  this.cg = Math.cos(gamma);
  this.sg = Math.sin(gamma);
  this.cw = Math.cos(omega);
  this.sw = Math.sin(omega);
}

function forward$1(p) {
  p.x -= this.long0;
  var sinphi = Math.sin(p.y);
  var cosphi = Math.cos(p.y);
  var coslam = Math.cos(p.x);
  var x, y;
  switch (this.mode) {
    case mode.OBLIQ:
      y = this.sinph0 * sinphi + this.cosph0 * cosphi * coslam;
      break;
    case mode.EQUIT:
      y = cosphi * coslam;
      break;
    case mode.S_POLE:
      y = -sinphi;
      break;
    case mode.N_POLE:
      y = sinphi;
      break;
  }
  y = this.pn1 / (this.p - y);
  x = y * cosphi * Math.sin(p.x);

  switch (this.mode) {
    case mode.OBLIQ:
      y *= this.cosph0 * sinphi - this.sinph0 * cosphi * coslam;
      break;
    case mode.EQUIT:
      y *= sinphi;
      break;
    case mode.N_POLE:
      y *= -(cosphi * coslam);
      break;
    case mode.S_POLE:
      y *= cosphi * coslam;
      break;
  }

  // Tilt 
  var yt, ba;
  yt = y * this.cg + x * this.sg;
  ba = 1 / (yt * this.sw * this.h1 + this.cw);
  x = (x * this.cg - y * this.sg) * this.cw * ba;
  y = yt * ba;

  p.x = x * this.a;
  p.y = y * this.a;
  return p;
}

function inverse$1(p) {
  p.x /= this.a;
  p.y /= this.a;
  var r = { x: p.x, y: p.y };

  // Un-Tilt
  var bm, bq, yt;
  yt = 1 / (this.pn1 - p.y * this.sw);
  bm = this.pn1 * p.x * yt;
  bq = this.pn1 * p.y * this.cw * yt;
  p.x = bm * this.cg + bq * this.sg;
  p.y = bq * this.cg - bm * this.sg;

  var rh = hypot(p.x, p.y);
  if (Math.abs(rh) < EPSLN) {
    r.x = 0;
    r.y = p.y;
  } else {
    var cosz, sinz;
    sinz = 1 - rh * rh * this.pfact;
    sinz = (this.p - Math.sqrt(sinz)) / (this.pn1 / rh + rh / this.pn1);
    cosz = Math.sqrt(1 - sinz * sinz);
    switch (this.mode) {
      case mode.OBLIQ:
        r.y = Math.asin(cosz * this.sinph0 + p.y * sinz * this.cosph0 / rh);
        p.y = (cosz - this.sinph0 * Math.sin(r.y)) * rh;
        p.x *= sinz * this.cosph0;
        break;
      case mode.EQUIT:
        r.y = Math.asin(p.y * sinz / rh);
        p.y = cosz * rh;
        p.x *= sinz;
        break;
      case mode.N_POLE:
        r.y = Math.asin(cosz);
        p.y = -p.y;
        break;
      case mode.S_POLE:
        r.y = -Math.asin(cosz);
        break;
    }
    r.x = Math.atan2(p.x, p.y);
  }

  p.x = r.x + this.long0;
  p.y = r.y;
  return p;
}

var names$1 = ["Tilted_Perspective", "tpers"];
var tpers = {
  init: init$2,
  forward: forward$1,
  inverse: inverse$1,
  names: names$1
};

function init$1() {
    this.flip_axis = (this.sweep === 'x' ? 1 : 0);
    this.h = Number(this.h);
    this.radius_g_1 = this.h / this.a;

    if (this.radius_g_1 <= 0 || this.radius_g_1 > 1e10) {
        throw new Error();
    }

    this.radius_g = 1.0 + this.radius_g_1;
    this.C = this.radius_g * this.radius_g - 1.0;

    if (this.es !== 0.0) {
        var one_es = 1.0 - this.es;
        var rone_es = 1 / one_es;

        this.radius_p = Math.sqrt(one_es);
        this.radius_p2 = one_es;
        this.radius_p_inv2 = rone_es;

        this.shape = 'ellipse'; // Use as a condition in the forward and inverse functions.
    } else {
        this.radius_p = 1.0;
        this.radius_p2 = 1.0;
        this.radius_p_inv2 = 1.0;

        this.shape = 'sphere';  // Use as a condition in the forward and inverse functions.
    }

    if (!this.title) {
        this.title = "Geostationary Satellite View";
    }
}

function forward(p) {
    var lon = p.x;
    var lat = p.y;
    var tmp, v_x, v_y, v_z;
    lon = lon - this.long0;

    if (this.shape === 'ellipse') {
        lat = Math.atan(this.radius_p2 * Math.tan(lat));
        var r = this.radius_p / hypot(this.radius_p * Math.cos(lat), Math.sin(lat));

        v_x = r * Math.cos(lon) * Math.cos(lat);
        v_y = r * Math.sin(lon) * Math.cos(lat);
        v_z = r * Math.sin(lat);

        if (((this.radius_g - v_x) * v_x - v_y * v_y - v_z * v_z * this.radius_p_inv2) < 0.0) {
            p.x = Number.NaN;
            p.y = Number.NaN;
            return p;
        }

        tmp = this.radius_g - v_x;
        if (this.flip_axis) {
            p.x = this.radius_g_1 * Math.atan(v_y / hypot(v_z, tmp));
            p.y = this.radius_g_1 * Math.atan(v_z / tmp);
        } else {
            p.x = this.radius_g_1 * Math.atan(v_y / tmp);
            p.y = this.radius_g_1 * Math.atan(v_z / hypot(v_y, tmp));
        }
    } else if (this.shape === 'sphere') {
        tmp = Math.cos(lat);
        v_x = Math.cos(lon) * tmp;
        v_y = Math.sin(lon) * tmp;
        v_z = Math.sin(lat);
        tmp = this.radius_g - v_x;

        if (this.flip_axis) {
            p.x = this.radius_g_1 * Math.atan(v_y / hypot(v_z, tmp));
            p.y = this.radius_g_1 * Math.atan(v_z / tmp);
        } else {
            p.x = this.radius_g_1 * Math.atan(v_y / tmp);
            p.y = this.radius_g_1 * Math.atan(v_z / hypot(v_y, tmp));
        }
    }
    p.x = p.x * this.a;
    p.y = p.y * this.a;
    return p;
}

function inverse(p) {
    var v_x = -1.0;
    var v_y = 0.0;
    var v_z = 0.0;
    var a, b, det, k;

    p.x = p.x / this.a;
    p.y = p.y / this.a;

    if (this.shape === 'ellipse') {
        if (this.flip_axis) {
            v_z = Math.tan(p.y / this.radius_g_1);
            v_y = Math.tan(p.x / this.radius_g_1) * hypot(1.0, v_z);
        } else {
            v_y = Math.tan(p.x / this.radius_g_1);
            v_z = Math.tan(p.y / this.radius_g_1) * hypot(1.0, v_y);
        }

        var v_zp = v_z / this.radius_p;
        a = v_y * v_y + v_zp * v_zp + v_x * v_x;
        b = 2 * this.radius_g * v_x;
        det = (b * b) - 4 * a * this.C;

        if (det < 0.0) {
            p.x = Number.NaN;
            p.y = Number.NaN;
            return p;
        }

        k = (-b - Math.sqrt(det)) / (2.0 * a);
        v_x = this.radius_g + k * v_x;
        v_y *= k;
        v_z *= k;

        p.x = Math.atan2(v_y, v_x);
        p.y = Math.atan(v_z * Math.cos(p.x) / v_x);
        p.y = Math.atan(this.radius_p_inv2 * Math.tan(p.y));
    } else if (this.shape === 'sphere') {
        if (this.flip_axis) {
            v_z = Math.tan(p.y / this.radius_g_1);
            v_y = Math.tan(p.x / this.radius_g_1) * Math.sqrt(1.0 + v_z * v_z);
        } else {
            v_y = Math.tan(p.x / this.radius_g_1);
            v_z = Math.tan(p.y / this.radius_g_1) * Math.sqrt(1.0 + v_y * v_y);
        }

        a = v_y * v_y + v_z * v_z + v_x * v_x;
        b = 2 * this.radius_g * v_x;
        det = (b * b) - 4 * a * this.C;
        if (det < 0.0) {
            p.x = Number.NaN;
            p.y = Number.NaN;
            return p;
        }

        k = (-b - Math.sqrt(det)) / (2.0 * a);
        v_x = this.radius_g + k * v_x;
        v_y *= k;
        v_z *= k;

        p.x = Math.atan2(v_y, v_x);
        p.y = Math.atan(v_z * Math.cos(p.x) / v_x);
    }
    p.x = p.x + this.long0;
    return p;
}

var names = ["Geostationary Satellite View", "Geostationary_Satellite", "geos"];
var geos = {
    init: init$1,
    forward: forward,
    inverse: inverse,
    names: names,
};

function includedProjections(proj4){
  proj4.Proj.projections.add(tmerc);
  proj4.Proj.projections.add(etmerc);
  proj4.Proj.projections.add(utm);
  proj4.Proj.projections.add(sterea);
  proj4.Proj.projections.add(stere);
  proj4.Proj.projections.add(somerc);
  proj4.Proj.projections.add(omerc);
  proj4.Proj.projections.add(lcc);
  proj4.Proj.projections.add(krovak);
  proj4.Proj.projections.add(cass);
  proj4.Proj.projections.add(laea);
  proj4.Proj.projections.add(aea);
  proj4.Proj.projections.add(gnom);
  proj4.Proj.projections.add(cea);
  proj4.Proj.projections.add(eqc);
  proj4.Proj.projections.add(poly);
  proj4.Proj.projections.add(nzmg);
  proj4.Proj.projections.add(mill);
  proj4.Proj.projections.add(sinu);
  proj4.Proj.projections.add(moll);
  proj4.Proj.projections.add(eqdc);
  proj4.Proj.projections.add(vandg);
  proj4.Proj.projections.add(aeqd);
  proj4.Proj.projections.add(ortho);
  proj4.Proj.projections.add(qsc);
  proj4.Proj.projections.add(robin);
  proj4.Proj.projections.add(geocent);
  proj4.Proj.projections.add(tpers);
  proj4.Proj.projections.add(geos);
}

proj4.defaultDatum = 'WGS84'; //default datum
proj4.Proj = Projection;
proj4.WGS84 = new proj4.Proj('WGS84');
proj4.Point = Point$2;
proj4.toPoint = common;
proj4.defs = defs;
proj4.nadgrid = nadgrid;
proj4.transform = transform$1;
proj4.mgrs = mgrs;
proj4.version = '__VERSION__';
includedProjections(proj4);

/**
 * Converts degrees to radians
 */
const degToRad = d => d / 180 * Math.PI;
/**
 * Converts radians to degrees
 */

const radToDeg = r => r / Math.PI * 180;
const TOLERANCE$1 = 0.000001;
/**
 * Returns true if a and b differ less then tolerance
 */

const softEquals = function softEquals(a, b, tolerance = TOLERANCE$1) {
  return Math.abs(a - b) <= tolerance;
};
/**
 * Prepares the set of coordinates for matrix operations
 * @param coord
 * @param center - the center of the operation
 */

const extendCoordinates = function (coord, center) {
  coord.forEach(point => {
    point[0] = point[0] - center[0];
    point[1] = point[1] - center[1]; // FIX_ME:
    // @ts-ignore

    point[2] = 1;
  });
};
/**
 * Takes extended coordinates and make them plain again
 * @param coord
 * @param center - the center of the operation
 */

const collapseCoordinates = function (coord, center) {
  coord.forEach(point => {
    point[0] = point[0] + center[0];
    point[1] = point[1] + center[1];
    point.splice(2, 1);
  });
};
/**
 * Returns a array with render coordinates of Polygon with simplification
 * @param feature
 * @param resolution
 * @param crs
 * @param tolerance - the tolerance of simplification. Points that are overflow other points or lines with given tolerance will be excluded from the result
 */

const getRenderedPolygonCoordinates = (feature, resolution, crs, tolerance = 1) => {
  let projected = feature.crs.equals(crs) ? feature.rings : feature.projectTo(crs).rings;
  return simplifyCoordinates(projected.map(ring => {
    return ring.map(point => {
      return [Math.round(point[0] / resolution), Math.round(point[1] / -resolution)];
    });
  }), tolerance);
};
/**
 * Returns a new array with simplified coordinates
 * @param rings - array of coordinate contours
 * @param tolerance - the tolerance of simplification. Points that are overflow other points or lines with given tolerance will be excluded from the result
 */

const simplifyCoordinates = function (rings, tolerance) {
  let result = [];

  for (let ring = 0, l = rings.length; ring < l; ring++) {
    let simplified = [rings[ring][0]];

    for (let i = 1, len = rings[ring].length - 1; i < len; i++) {
      if (!softEquals(rings[ring][i][0], simplified[simplified.length - 1][0], tolerance) || !softEquals(rings[ring][i][1], simplified[simplified.length - 1][1], tolerance)) {
        simplified.push(rings[ring][i]);
      }
    }

    if (simplified[simplified.length - 1] !== rings[ring][rings[ring].length - 1]) simplified.push(rings[ring][rings[ring].length - 1]);
    result[ring] = simplified;
  }

  return result;
};
/**
 * Multiplies matrix a by matrix b
 */

const multiplyMatrix = function (a, b) {
  let c = [];

  for (let i = 0, m = a.length; i < m; i++) {
    c[i] = [];

    for (let j = 0, q = b[0].length; j < q; j++) {
      c[i][j] = 0;

      for (let r = 0, n = b.length; r < n; r++) {
        c[i][j] += a[i][r] * b[r][j];
      }
    }
  }

  return c;
};
const squareDistance = function (a, b) {
  return (a[0] - b[0]) * (a[0] - b[0]) + (a[1] - b[1]) * (a[1] - b[1]);
};
/**
 * counter-clockwise - positive angle (@radians param)
 * clockwise - negative angle (@radians param)
 */

function rotate$1(cx, cy, x, y, radians) {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [cos * (x - cx) + sin * (y - cy) + cx, cos * (y - cy) - sin * (x - cx) + cy];
}

var mathModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  degToRad: degToRad,
  radToDeg: radToDeg,
  softEquals: softEquals,
  extendCoordinates: extendCoordinates,
  collapseCoordinates: collapseCoordinates,
  getRenderedPolygonCoordinates: getRenderedPolygonCoordinates,
  simplifyCoordinates: simplifyCoordinates,
  multiplyMatrix: multiplyMatrix,
  squareDistance: squareDistance,
  rotate: rotate$1
});

let identityProjection = ([x, y]) => [x, y];
/**
 * @alias sGis.Crs
 */


class Crs$1 {
  /**
   * @constructor
   * @param description - properties describing crs. If given, the values will be set to the corresponding fields of the crs instance.
   * @param projectionMap - set of projections to different coordinate systems. If not specified, .setProjectionTo() method
   *                        must be used in order to enable projecting to a needed crs.
   */
  constructor(description, projectionMap) {
    this._projections = new Map();
    this._discoveryMode = false;
    if (description) Object.assign(this, description);
    if (projectionMap) this._projections = projectionMap;
  }
  /**
   * Returns string definition of the crs.<br>
   *     If .wkid is set, returning value will be wkid string;<br>
   *     Else if .wkt is set, returning value will be wkt;<br>
   *     Otherwise .details property will be returned.
   */


  toString() {
    if (this.wkid) return this.wkid.toString();
    if (this.wkt) return this.wkt;
    if (this.proj4Representation) return this.proj4Representation;
    return this.details || 'Unknown crs';
  }
  /**
   * Returns true if given crs represents the same spatial reference system. The objects are compared by wkid or by wkt text.
   * In case neither is specified, true will be returned only if both this and 'crs' represent same JS object.
   */


  equals(crs) {
    if (this === crs) return true;
    if (this.wkid && this.wkid === crs.wkid) return true;
    if (this.proj4Representation && this.proj4Representation === crs.proj4Representation) return true;
    return !!this.wkt && this.wkt === crs.wkt;
  }
  /**
   * Returns projection function from the current coordinate system to the specified one. If it is possible to
   * project current crs to the target through another crs, returning function will apply this route.<br>
   * E.g. if trying to project from WebMercator to EllipticalMercator, the returned Projection function will
   * first project from WebMercator to Lat/Lon, and then project the result to EllipticalMercator.
   */


  projectionTo(crs) {
    return this._projections.get(crs) || this._discoverProjectionTo(crs);
  }
  /**
   * Returns true if the current coordinate system can be projected to the given crs. Projections through other coordinate
   * systems are also considered if discovered.
   */


  canProjectTo(crs) {
    return this.projectionTo(crs) !== null;
  }
  /**
   * Sets the projection function from current crs to the given one.
   */


  setProjectionTo(crs, projection) {
    this._projections.set(crs, projection);
  }

  proj4Projection(crs) {
    if (!this.proj4Representation || typeof crs !== "string" && !crs.proj4Representation) {
      return null;
    }

    return coordinates => proj4(this.proj4Representation, typeof crs === "string" ? crs : crs.proj4Representation, coordinates);
  }

  _discoverProjectionTo(crs) {
    if (this._discoveryMode) return null;
    if (this.equals(crs)) return identityProjection;
    this._discoveryMode = true;

    for (let [ownCrs, func] of this._projections) {
      if (ownCrs.equals(crs)) {
        this._projections.set(crs, func);

        break;
      }

      const innerProjection = ownCrs._discoverProjectionTo(crs);

      if (innerProjection) {
        let result = function ([x, y]) {
          return innerProjection(func([x, y]));
        };

        this._projections.set(crs, result);

        break;
      }
    }

    this._discoveryMode = false;
    return this._projections.get(crs) || null;
  }

}
/**
 * Plain euclidean coordinate system. This projection cannot be projected to any other projection.
 * @alias sGis.CRS.plain
 */

const plain = /*#__PURE__*/new Crs$1({
  details: 'Plain crs without any projection functions'
});
const OCG84_WKT = 'GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137,298.257223563]],' + 'PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]]';
/**
 * Geographic coordinate system on WGS84 datum, which has longitude set as first coordinate, and latitude as second coordinate.<br>
 * <b>NOTE:</b> A lot of GIS software incorrectly implement EPSG:4326 as this longitude/latitude coordinate system. If you
 * are used to using such systems as OpenLayers or PostGIS, you want to use this crs instead of 4326 in those system.
 * @alias sGis.CRS.wgs84
 */

const wgs84 = /*#__PURE__*/new Crs$1({
  wkid: 84,
  authority: 'OCG',
  wkt: OCG84_WKT,
  proj4Representation: "WGS84"
});
const EPSG4326_WKT = `GEODCRS["WGS 84",
  DATUM["World Geodetic System 1984",
    ELLIPSOID["WGS 84",6378137,298.257223563,LENGTHUNIT["metre",1.0]]],
  CS[ellipsoidal,2],
    AXIS["latitude",north,ORDER[1]],
    AXIS["longitude",east,ORDER[2]],
    ANGLEUNIT["degree",0.01745329252],
  ID["EPSG",4326]]`;
/**
 * Geographic coordinate system on WGS84 datum with latitude being first coordinate, and longitude as second coordinate.<br>
 * <b>NOTE:</b> This coordinate system is registered by EPSG as EPSG:4326, but a lot of GIS software implements 4326 as
 * longitude/latitude. If you are used to that coordinate order use wgs84 crs instead of this one.
 * @alias sGis.CRS.geo
 */

const geo = /*#__PURE__*/new Crs$1({
  wkid: 4326,
  authority: 'EPSG',
  wkt: EPSG4326_WKT,
  proj4Representation: "EPSG:4326"
});
geo.setProjectionTo(wgs84, ([x, y]) => [y, x]);
wgs84.setProjectionTo(geo, ([x, y]) => [y, x]);
const EPSG3857_WKT = 'PROJCS["WGS 84 / Pseudo-Mercator",GEOGCS["GCS_WGS_1984",' + 'DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137,298.257223563]],PRIMEM["Greenwich",0],' + 'UNIT["Degree",0.017453292519943295]],PROJECTION["Mercator"],PARAMETER["central_meridian",0],' + 'PARAMETER["scale_factor",1],PARAMETER["false_easting",0],PARAMETER["false_northing",0],UNIT["Meter",1]]';
/**
 * Coordinate system used by many gis web applications. The coordinates are given in meters, as projected by Mercator projection
 * using WGS84 datum assuming that the Earth is a sphere. Has high distortions near the poles.
 * @alias sGis.CRS.webMercator
 */

const webMercator = /*#__PURE__*/new Crs$1({
  wkid: 3857,
  authority: 'EPSG',
  wkt: EPSG3857_WKT,
  proj4Representation: "EPSG:3857"
});
{
  const a = 6378137;
  webMercator.setProjectionTo(wgs84, ([x, y]) => {
    let rLat = Math.PI / 2 - 2 * Math.atan(Math.exp(-y / a));
    let rLong = x / a;
    let lon = radToDeg(rLong);
    let lat = radToDeg(rLat);
    return [lon, lat];
  });
  wgs84.setProjectionTo(webMercator, ([x, y]) => {
    let rLon = degToRad(x);
    let rLat = degToRad(y);
    let X = a * rLon;
    let Y = a * Math.log(Math.tan(Math.PI / 4 + rLat / 2));
    return [X, Y];
  });
}
const EPSG3395_WKT = 'PROJCS["WGS 84 / World Mercator",GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",' + 'SPHEROID["WGS_1984",6378137,298.257223563]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]],' + 'PROJECTION["Mercator"],PARAMETER["central_meridian",0],PARAMETER["scale_factor",1],' + 'PARAMETER["false_easting",0],PARAMETER["false_northing",0],UNIT["Meter",1]]';
/**
 * Mercator projection from WGS84 datum. This crs considers the fact that the Earth is not a sphere. Coordinates are in meters.
 * @alias sGis.CRS.ellipticalMercator
 */

const ellipticalMercator = /*#__PURE__*/new Crs$1({
  wkid: 3395,
  authority: 'EPSG',
  wkt: EPSG3395_WKT
});
{
  const a = 6378137;
  const b = 6356752.3142451793;
  const e = /*#__PURE__*/Math.sqrt(1 - b * b / a / a);
  const eh = e / 2;
  const pih = Math.PI / 2;
  ellipticalMercator.setProjectionTo(wgs84, ([x, y]) => {
    let ts = Math.exp(-y / a);
    let phi = pih - 2 * Math.atan(ts);
    let i = 0;
    let dphi = 1;

    while (Math.abs(dphi) > 0.000000001 && i++ < 15) {
      let con = e * Math.sin(phi);
      dphi = pih - 2 * Math.atan(ts * Math.pow((1 - con) / (1 + con), eh)) - phi;
      phi += dphi;
    }

    let rLong = x / a,
        rLat = phi,
        lon = radToDeg(rLong),
        lat = radToDeg(rLat);
    return [lon, lat];
  });
  wgs84.setProjectionTo(ellipticalMercator, ([x, y]) => {
    let rLat = degToRad(y);
    let rLon = degToRad(x);
    let X = a * rLon;
    let Y = a * Math.log(Math.tan(Math.PI / 4 + rLat / 2) * Math.pow((1 - e * Math.sin(rLat)) / (1 + e * Math.sin(rLat)), e / 2));
    return [X, Y];
  });
} //http://mathworld.wolfram.com/AlbersEqual-AreaConicProjection.html

/**
 * Class constructor of <a href="https://en.wikipedia.org/wiki/Albers_projection">Alber's equal area projections</a> from wgs84 datum.
 * @alias sGis.CRS.AlbersEqualArea
 */

class AlbersEqualArea extends Crs$1 {
  /**
   * @param lat0 - latitude of origin
   * @param lon0 - longitude of origin
   * @param stLat1 - first standard parallel
   * @param stLat2 - second standard parallel
   */
  constructor(lat0, lon0, stLat1, stLat2) {
    super({
      details: 'Albers Equal-Area Conic Projection: ' + lat0 + ',' + lon0 + ',' + stLat1 + ',' + stLat2
    });
    this.R = 6372795;

    let _lat0 = degToRad(lat0);

    let _lon0 = degToRad(lon0);

    let _stLat1 = degToRad(stLat1);

    let _stLat2 = degToRad(stLat2);

    let _n = (Math.sin(_stLat1) + Math.sin(_stLat2)) / 2;

    let _c = Math.pow(Math.cos(_stLat1), 2) + 2 * _n * Math.sin(_stLat1);

    let _ro0 = Math.sqrt(_c - 2 * _n * Math.sin(_lat0)) / _n;

    this.setProjectionTo(wgs84, ([x, y]) => {
      let xRad = x / this.R;
      let yRad = y / this.R;
      let th = Math.atan(xRad / (_ro0 - yRad));
      let ro = xRad / Math.sin(th);
      let rLat = Math.asin((_c - ro * ro * _n * _n) / 2 / _n);
      let rLon = _lon0 + th / _n;
      let lat = radToDeg(rLat);
      let lon = radToDeg(rLon);
      return [lon, lat];
    });
    wgs84.setProjectionTo(this, ([lon, lat]) => {
      let rLon = degToRad(lon),
          rLat = degToRad(lat),
          th = _n * (rLon - _lon0),
          ro = Math.sqrt(_c - 2 * _n * Math.sin(rLat)) / _n,
          x = ro * Math.sin(th) * this.R,
          y = (_ro0 - ro * Math.cos(th)) * this.R;

      return [x, y];
    });
  }

}
/**
 * Alber's equal area projection with parameters 0, 180, 60, 50. Used internally for precise geographic area calculations.
 * @alias sGis.CRS.conicEqualArea
 */

const conicEqualArea = /*#__PURE__*/new AlbersEqualArea(0, 180, 60, 50);
const proj4Projection = proj4;

var CrsModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Crs: Crs$1,
  plain: plain,
  wgs84: wgs84,
  geo: geo,
  webMercator: webMercator,
  ellipticalMercator: ellipticalMercator,
  AlbersEqualArea: AlbersEqualArea,
  conicEqualArea: conicEqualArea,
  proj4Projection: proj4Projection
});

/**
 * Feature is an abstract representation of a geographical object.
 * @alias sGis.Feature
 */

class Feature extends EventHandler {
  constructor({
    crs = geo,
    symbol,
    persistOnMap = false
  } = {}) {
    super();
    this._hidden = false;
    this._symbolContainer = new FeatureSymbolContainer(this, symbol);
    this._crs = crs;
    this.persistOnMap = persistOnMap;
  }
  /**
   * Sets default coordinate system for all features.<br><br>
   *     <strong>
   *     NOTE: This method affects all already created features that do not have explicitly specified crs.
   *     You should use this function only when initializing the library.
   *     </strong>
   * @param crs
   */


  static setDefaultCrs(crs) {
    Feature.prototype._crs = crs;
  }
  /**
   * Calls the render method of the feature's symbol (or temp symbol if it is set) to render the feature. The render result
   * is cached inside the feature class.
   * @param resolution
   * @param crs
   * @param painter
   */


  render(resolution, crs, painter) {
    return this._symbolContainer.render(resolution, crs, painter);
  }
  /**
   * Resets the rendered cache of the feature, making it to redraw in the next redraw cycle.
   */


  redraw() {
    this._symbolContainer.reset();
  }
  /**
   * Prevents feature from rendering.
   */


  hide() {
    this._hidden = true;
  }
  /**
   * Allows feature to render after it was hidden.
   */


  show() {
    this._hidden = false;
  }
  /**
   * Sets a temporary symbol for the feature. This symbol is used instead of the original symbol until cleared.
   * @param symbol
   */


  setTempSymbol(symbol) {
    this._symbolContainer.setTempSymbol(symbol);
  }
  /**
   * Clears the previously set temporary symbol, restoring the original symbol.
   */


  clearTempSymbol() {
    this._symbolContainer.clearTempSymbol();
  }
  /**
   * Returns true, if a temporary symbol is currently set for this feature.
   */


  get isTempSymbolSet() {
    return this._symbolContainer.isTempSymbolSet;
  }
  /**
   * Returns the original symbol of the feature. If temporary symbol is not set, the returned value will be same as value of the .symbol property.
   */


  get originalSymbol() {
    return this._symbolContainer.originalSymbol;
  }
  /**
   * Coordinate system of the feature.
   */


  get crs() {
    return this._crs;
  }
  /**
   * Current symbol of the feature. If temporary symbol is set, the value will be the temporary symbol.
   */


  get symbol() {
    return this._symbolContainer.symbol;
  }

  set symbol(symbol) {
    this._symbolContainer.symbol = symbol;
  }
  /**
   * Specifies weather the feature is hidden by .hide() method.
   */


  get hidden() {
    return this._hidden;
  }

}

class FeatureSymbolContainer {
  constructor(feature, symbol) {
    this._feature = feature;
    this._symbol = symbol;
  }

  get symbol() {
    return this._tempSymbol || this._symbol;
  }

  set symbol(symbol) {
    this._symbol = symbol;
    this.reset();
  }
  /**
   * Renders the feature with the given parameters.
   * @param resolution
   * @param crs
   * @param painter
   */


  render(resolution, crs, painter) {
    if (this._feature.hidden || !this.symbol) return [];
    if (this._cached && !this._needToRender(resolution, crs)) return this._cached.renders;
    this._cached = {
      resolution: resolution,
      crs: crs,
      renders: this.symbol.renderFunction(this._feature, resolution, crs, painter)
    };
    if (this._feature.eventFlags !== MouseEventFlags.None) this._cached.renders.forEach(render => {
      render.listenFor(this._feature.eventFlags, event => {
        this._feature.fire(event);
      });
    });
    return this._cached.renders;
  }

  _needToRender(resolution, crs) {
    return !this._cached || this._cached.resolution !== resolution || this._cached.crs !== crs || this._cached.renders.length === 0 || this._cached.renders.filter(render => render.isComplex).length > 0;
  }

  reset() {
    this._cached = undefined;
    let dynamicFeature = this._feature;

    if (dynamicFeature.__dynamicSymbolRender) {
      dynamicFeature.__dynamicSymbolRender.update();

      dynamicFeature.__dynamicSymbolRender.redraw(dynamicFeature);
    }
  }

  setTempSymbol(symbol) {
    this._tempSymbol = symbol;
    this.reset();
  }

  clearTempSymbol() {
    this._tempSymbol = undefined;
    this.reset();
  }

  get isTempSymbolSet() {
    return !!this._tempSymbol;
  }

  get originalSymbol() {
    return this._symbol;
  }

}

/**
 * Simple geographical point
 * @alias sGis.Point
 */

class Point$1 {
  /**
   * @param position - point position
   * @param crs - point coordinate system
   */
  constructor(position, crs = geo) {
    this.crs = crs;
    this.position = position;
  }
  /**
   * Returns a new point with same position in new crs
   * @param newCrs - target coordinate system
   * @throws Cannot project to specified crs.
   */


  projectTo(newCrs) {
    const projection = this.crs.projectionTo(newCrs);

    if (projection) {
      return new Point$1(projection(this.position), newCrs);
    } else {
      return error(new Error("Cannot project point to crs: " + newCrs.toString()));
    }
  }
  /**
   * Returns a copy of the point
   */


  clone() {
    return new Point$1(this.position, this.crs);
  }
  /**
   * Getter returns clone of this point. Setter set point position.
   */


  get point() {
    return this.clone();
  }

  set point(point) {
    this.position = point.projectTo(this.crs).position;
  }

  get x() {
    return this.position[0];
  }

  set x(x) {
    this.position[0] = x;
  }

  get y() {
    return this.position[1];
  }

  set y(y) {
    this.position[1] = y;
  }
  /**
   * Returns true if the target point has the same position and crs as the current one
   * @param point - target point for comparison
   */


  equals(point) {
    return softEquals(point.x, this.x) && softEquals(point.y, this.y) && point.crs.equals(this.crs);
  }
  /**
   * Coordinates / position of this point
   */


  get coordinates() {
    return [this.position[0], this.position[1]];
  }

  set coordinates(position) {
    this.position = [position[0], position[1]];
  }

}

/**
 * Object representing a rectangular area on a map between two points.
 * @alias sGis.Bbox
 */

class Bbox {
  /**
   * Resulting rectangle will be the minimum rectangle that includes both points. The order and relative position of
   * points can be arbitrary.
   * @param point1 - first corner point of rectangle
   * @param point2 - second corner point of rectangle
   * @param crs - coordinate system of the point coordinates
   */
  constructor(point1, point2, crs = geo) {
    this._crs = crs;
    this._p = [Math.min(point1[0], point2[0]), Math.min(point1[1], point2[1]), Math.max(point1[0], point2[0]), Math.max(point1[1], point2[1])];
  }
  /**
   * Returns a new Bbox in the specified coordinate system
   * @param crs - target coordinate system
   * @throws If the instance coordinates cannot be projected into the target crs
   */


  projectTo(crs) {
    let projected1 = new Point$1([this._p[0], this._p[1]], this._crs).projectTo(crs).position;
    let projected2 = new Point$1([this._p[2], this._p[3]], this._crs).projectTo(crs).position;
    return new Bbox(projected1, projected2, crs);
  }
  /**
   * Center point of the bounding box
   */


  get center() {
    return [(this.xMax + this.xMin) / 2, (this.yMax + this.yMin) / 2];
  }
  /**
   * Returns a copy of the bbox
   */


  clone() {
    return this.projectTo(this._crs);
  }
  /**
   * Returns true if the given bbox is equal (geographically) to the target bbox. It will return false if the target
   * bbox is set in different coordinate system or if any of the 4 coordinates are different by more then 0.0001%.
   * @param bbox - target bbox
   */


  equals(bbox) {
    let target = bbox.coordinates;

    for (let i = 0; i < 4; i++) if (!softEquals(this._p[i], target[i])) return false;

    return this._crs.equals(bbox.crs);
  }
  /**
   * Returns combination of the current bbox with the given one. It will include be a minimum rectangle that includes
   * both bboxes.
   * @param bbox
   */


  intersect(bbox) {
    bbox = bbox.crs === this.crs ? bbox : bbox.projectTo(this.crs);
    return new Bbox([Math.min(this.xMin, bbox.xMin), Math.min(this.yMin, bbox.yMin)], [Math.max(this.xMax, bbox.xMax), Math.max(this.yMax, bbox.yMax)], this.crs);
  }
  /**
   * Returns true if at list one point of the given bbox lies inside the target bbox.
   * <br><bNOTE</b> that this method will return true if on of the bboxes is completely inside the other.
   * It will return false if bboxes are adjustened, e.g. a side of one bbox touches a side of another one.
   * @param bbox - target bbox
   */


  intersects(bbox) {
    let projected = bbox.projectTo(this._crs);
    return this.xMax > projected.xMin && this.xMin < projected.xMax && this.yMax > projected.yMin && this.yMin < projected.yMax;
  }
  /**
   * Returns true, if the target point is inside the bbox or lies on one of its sides
   * @param point
   */


  contains(point) {
    let projected = point.projectTo(this.crs);
    return this.xMin <= projected.x && this.xMax >= projected.x && this.yMin <= projected.y && this.yMax >= projected.y;
  }
  /**
   * Coordinate system of the bbox.
   */


  get crs() {
    return this._crs;
  }
  /**
   * Coordinate of the right border of the bbox. Cannot be assigned value less then xMin.
   */


  get xMax() {
    return this._p[2];
  }

  set xMax(value) {
    if (value < this.xMin) error('Max value cannot be lower than the min value');
    this._p[2] = value;
  }
  /**
   * Coordinate of the top border of the bbox. Cannot be assigned value less then yMin.
   */


  get yMax() {
    return this._p[3];
  }

  set yMax(value) {
    if (value < this.yMin) error('Max value cannot be lower than the min value');
    this._p[3] = value;
  }
  /**
   * Coordinate of the left border of the bbox. Cannot be assigned value larger then xMax.
   */


  get xMin() {
    return this._p[0];
  }

  set xMin(value) {
    if (value > this.xMax) error('Min value cannot be higher than the max value');
    this._p[0] = value;
  }
  /**
   * Coordinate of the bottom border of the bbox. Cannot be assigned value larger then yMax.
   */


  get yMin() {
    return this._p[1];
  }

  set yMin(value) {
    if (value > this.yMax) error('Min value cannot be higher than the max value');
    this._p[1] = value;
  }
  /**
   * Width of the bbox.
   */


  get width() {
    return this.xMax - this.xMin;
  }
  /**
   * Height of the bbox.
   */


  get height() {
    return this.yMax - this.yMin;
  }
  /**
   * Coordinates of the bbox in the form [xMin, yMin, xMax, yMax].
   */


  get coordinates() {
    return [this._p[0], this._p[1], this._p[2], this._p[3]];
  }
  /**
   * Creates a bbox from a point and offset. The created bbox will be a square area around the point min and max
   * coordinates of which are 'offset' away from the point. Bbox will have point's crs.
   * @param point - center point of created bbox.
   * @param offset - distance from center to the borders of bbox.
   */


  static fromPoint(point, offset) {
    return new Bbox([point.x - offset, point.y - offset], [point.x + offset, point.y + offset], point.crs);
  }

  offset(offset) {
    return new Bbox([this.xMin - offset[0], this.yMin - offset[1]], [this.xMax + offset[0], this.yMax + offset[1]], this.crs);
  }

}

/**
 * Utility class for working with different representations of colors in browsers
 * @alias sGis.utils.Color
 */

class Color$1 {
  /**
   * @param string - any valid css color string
   */
  constructor(string) {
    this._original = string;
    if (!isString(string)) string = string.toString();
    this._color = string && string.trim() || 'transparent';

    this._setChannels();
  }

  _setChannels() {
    let format = this.format;

    if (format && formats[format]) {
      this._channels = formats[format](this._color);
    } else {
      this._channels = {
        r: NaN,
        g: NaN,
        b: NaN,
        a: NaN
      };
    }
  }

  get _min() {
    return Math.min(this._channels.r, this._channels.g, this._channels.b);
  }

  get _max() {
    return Math.max(this._channels.r, this._channels.g, this._channels.b);
  }

  get _delta() {
    return this._max - this._min;
  }
  /**
   * Returns the color as a string in the requested format
   * @param format - target format. Available values: "hex" - #AARRGGBB, "rgb" - "rgb(r, g, b)", "rgba" - "rgba(r, g, b, a)"
   */


  toString(format) {
    if (format === 'hex') {
      return '#' + decToHex(this.a) + decToHex(this.r) + decToHex(this.g) + decToHex(this.b);
    } else if (format === 'rgb') {
      return 'rgb(' + this.r + ',' + this.g + ',' + this.b + ')';
    } else {
      return 'rgba(' + this.r + ',' + this.g + ',' + this.b + ',' + (this.a / 255).toFixed(2).replace(/\.*0+$/, '') + ')';
    }
  }
  /**
   * Returns the string given to the constructor of the instance
   * @type {String}
   */


  get original() {
    return this._original;
  }
  /**
   * Returns true if the instance represents a valid color, i.e. that after parsing of the string given to the constructor,
   * the format is recognized and values of all channels are resolved to valid numbers.
   * @returns {Boolean}
   */


  get isValid() {
    return isNumber(this._channels.a) && isNumber(this._channels.r) && isNumber(this._channels.g) && isNumber(this._channels.b);
  }
  /**
   * Converts HSV color value into RGB
   * @param h - hue value
   * @param s - saturation value
   * @param v - 'value' value
   */


  setHsv(h, s, v) {
    let rgbColors = [];
    h /= 60;
    s /= 100;
    v /= 100;
    let floor = Math.floor(h);
    let mod = floor % 6;
    let f = h - floor;
    let p = Math.round(255 * v * (1 - s));
    let q = Math.round(255 * v * (1 - s * f));
    let t = Math.round(255 * v * (1 - s * (1 - f)));
    v = Math.round(255 * v);

    switch (mod) {
      case 0:
        rgbColors = [v, t, p];
        break;

      case 1:
        rgbColors = [q, v, p];
        break;

      case 2:
        rgbColors = [p, v, t];
        break;

      case 3:
        rgbColors = [p, q, v];
        break;

      case 4:
        rgbColors = [t, p, v];
        break;

      case 5:
        rgbColors = [v, p, q];
        break;
    }

    this._channels.r = rgbColors[0];
    this._channels.g = rgbColors[1];
    this._channels.b = rgbColors[2];
    return this;
  }
  /**
   * Returns the format of the input color sting. Possible values: hex3, hex6, hex4, hex8, rgb, rgba, name.
   * (name is the named css color values like "white")
   */


  get format() {
    if (this._color.substr(0, 1) === '#' && this._color.search(/[^#0-9a-fA-F]/) === -1) {
      if (this._color.length === 4) {
        return 'hex3';
      } else if (this._color.length === 7) {
        return 'hex6';
      } else if (this._color.length === 5) {
        return 'hex4';
      } else if (this._color.length === 9) {
        return 'hex8';
      }
    } else if (this._color.substr(0, 4) === 'rgb(') {
      return 'rgb';
    } else if (this._color.substr(0, 5) === 'rgba(') {
      return 'rgba';
    } else if (this._color.substr(0, 4) === 'hsv(') {
      return 'hsv';
    } else if (this._color in Color$1.names) {
      return 'name';
    }
  }
  /**
   * Returns red channel value as integer from 0 to 255.
   */


  get r() {
    return this._channels.r;
  }

  set r(v) {
    this._channels.r = v;
  }
  /**
   * Returns green channel value as integer from 0 to 255.
   */


  get g() {
    return this._channels.g;
  }

  set g(v) {
    this._channels.g = v;
  }
  /**
   * Returns blue channel value as integer from 0 to 255.
   */


  get b() {
    return this._channels.b;
  }

  set b(v) {
    this._channels.b = v;
  }
  /**
   * Returns opacity channel value as integer from 0 to 255.
   */


  get a() {
    return this._channels.a;
  }

  set a(v) {
    this._channels.a = v;
  }
  /**
   * Returns hue channel value as integer from 0 to 360.
   */


  get h() {
    let max = this._max;
    let min = this._min;
    let delta = this._delta;
    let h;
    let {
      r,
      g,
      b
    } = this._channels;
    if (max === min) h = 0;else if (r === max) h = (g - b) / delta;else if (g === max) h = 2 + (b - r) / delta;else if (b === max) h = 4 + (r - g) / delta;
    h = Math.min(h * 60, 360);
    if (h < 0) h += 360;
    h = Math.round(h);
    return h;
  }
  /**
   * Returns saturation channel value as integer from 0 to 100.
   */


  get s() {
    let s;
    let max = this._max;
    if (max === 0) s = 0;else s = this._delta / max * 1000 / 10;
    s = Math.round(s);
    return s;
  }
  /**
   * Returns 'value' channel of hsv space as integer from 0 to 100.
   */


  get v() {
    return Math.round(this._max / 255 * 1000 / 10);
  }
  /**
   * Returns values of the channels as integers from 0 to 255. Format is { r: r, g: g, b: b, a: a }.
   */


  get channels() {
    return Object.assign({}, this._channels);
  }

} // Big List of Colors
// ------------------
// <http://www.w3.org/TR/css3-color/#svg-color>
//noinspection SpellCheckingInspection

Color$1.names = {
  aliceblue: "f0f8ff",
  antiquewhite: "faebd7",
  aqua: "0ff",
  aquamarine: "7fffd4",
  azure: "f0ffff",
  beige: "f5f5dc",
  bisque: "ffe4c4",
  black: "000",
  blanchedalmond: "ffebcd",
  blue: "00f",
  blueviolet: "8a2be2",
  brown: "a52a2a",
  burlywood: "deb887",
  burntsienna: "ea7e5d",
  cadetblue: "5f9ea0",
  chartreuse: "7fff00",
  chocolate: "d2691e",
  coral: "ff7f50",
  cornflowerblue: "6495ed",
  cornsilk: "fff8dc",
  crimson: "dc143c",
  cyan: "0ff",
  darkblue: "00008b",
  darkcyan: "008b8b",
  darkgoldenrod: "b8860b",
  darkgray: "a9a9a9",
  darkgreen: "006400",
  darkgrey: "a9a9a9",
  darkkhaki: "bdb76b",
  darkmagenta: "8b008b",
  darkolivegreen: "556b2f",
  darkorange: "ff8c00",
  darkorchid: "9932cc",
  darkred: "8b0000",
  darksalmon: "e9967a",
  darkseagreen: "8fbc8f",
  darkslateblue: "483d8b",
  darkslategray: "2f4f4f",
  darkslategrey: "2f4f4f",
  darkturquoise: "00ced1",
  darkviolet: "9400d3",
  deeppink: "ff1493",
  deepskyblue: "00bfff",
  dimgray: "696969",
  dimgrey: "696969",
  dodgerblue: "1e90ff",
  firebrick: "b22222",
  floralwhite: "fffaf0",
  forestgreen: "228b22",
  fuchsia: "f0f",
  gainsboro: "dcdcdc",
  ghostwhite: "f8f8ff",
  gold: "ffd700",
  goldenrod: "daa520",
  gray: "808080",
  green: "008000",
  greenyellow: "adff2f",
  grey: "808080",
  honeydew: "f0fff0",
  hotpink: "ff69b4",
  indianred: "cd5c5c",
  indigo: "4b0082",
  ivory: "fffff0",
  khaki: "f0e68c",
  lavender: "e6e6fa",
  lavenderblush: "fff0f5",
  lawngreen: "7cfc00",
  lemonchiffon: "fffacd",
  lightblue: "add8e6",
  lightcoral: "f08080",
  lightcyan: "e0ffff",
  lightgoldenrodyellow: "fafad2",
  lightgray: "d3d3d3",
  lightgreen: "90ee90",
  lightgrey: "d3d3d3",
  lightpink: "ffb6c1",
  lightsalmon: "ffa07a",
  lightseagreen: "20b2aa",
  lightskyblue: "87cefa",
  lightslategray: "789",
  lightslategrey: "789",
  lightsteelblue: "b0c4de",
  lightyellow: "ffffe0",
  lime: "0f0",
  limegreen: "32cd32",
  linen: "faf0e6",
  magenta: "f0f",
  maroon: "800000",
  mediumaquamarine: "66cdaa",
  mediumblue: "0000cd",
  mediumorchid: "ba55d3",
  mediumpurple: "9370db",
  mediumseagreen: "3cb371",
  mediumslateblue: "7b68ee",
  mediumspringgreen: "00fa9a",
  mediumturquoise: "48d1cc",
  mediumvioletred: "c71585",
  midnightblue: "191970",
  mintcream: "f5fffa",
  mistyrose: "ffe4e1",
  moccasin: "ffe4b5",
  navajowhite: "ffdead",
  navy: "000080",
  oldlace: "fdf5e6",
  olive: "808000",
  olivedrab: "6b8e23",
  orange: "ffa500",
  orangered: "ff4500",
  orchid: "da70d6",
  palegoldenrod: "eee8aa",
  palegreen: "98fb98",
  paleturquoise: "afeeee",
  palevioletred: "db7093",
  papayawhip: "ffefd5",
  peachpuff: "ffdab9",
  peru: "cd853f",
  pink: "ffc0cb",
  plum: "dda0dd",
  powderblue: "b0e0e6",
  purple: "800080",
  rebeccapurple: "663399",
  red: "f00",
  rosybrown: "bc8f8f",
  royalblue: "4169e1",
  saddlebrown: "8b4513",
  salmon: "fa8072",
  sandybrown: "f4a460",
  seagreen: "2e8b57",
  seashell: "fff5ee",
  sienna: "a0522d",
  silver: "c0c0c0",
  skyblue: "87ceeb",
  slateblue: "6a5acd",
  slategray: "708090",
  slategrey: "708090",
  snow: "fffafa",
  springgreen: "00ff7f",
  steelblue: "4682b4",
  tan: "d2b48c",
  teal: "008080",
  thistle: "d8bfd8",
  tomato: "ff6347",
  turquoise: "40e0d0",
  violet: "ee82ee",
  wheat: "f5deb3",
  white: "fff",
  whitesmoke: "f5f5f5",
  yellow: "ff0",
  yellowgreen: "9acd32",
  transparent: '0000'
};

function decToHex(dec) {
  let hex = Math.floor(dec).toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}

let formats = {
  hex3: function (string) {
    return {
      r: parseInt(string.substr(1, 1) + string.substr(1, 1), 16),
      g: parseInt(string.substr(2, 1) + string.substr(2, 1), 16),
      b: parseInt(string.substr(3, 1) + string.substr(3, 1), 16),
      a: 255
    };
  },
  hex6: function (string) {
    return {
      r: parseInt(string.substr(1, 2), 16),
      g: parseInt(string.substr(3, 2), 16),
      b: parseInt(string.substr(5, 2), 16),
      a: 255
    };
  },
  hex4: function (string) {
    return {
      r: parseInt(string.substr(2, 1) + string.substr(2, 1), 16),
      g: parseInt(string.substr(3, 1) + string.substr(3, 1), 16),
      b: parseInt(string.substr(4, 1) + string.substr(4, 1), 16),
      a: parseInt(string.substr(1, 1) + string.substr(1, 1), 16)
    };
  },
  hex8: function (string) {
    return {
      r: parseInt(string.substr(3, 2), 16),
      g: parseInt(string.substr(5, 2), 16),
      b: parseInt(string.substr(7, 2), 16),
      a: parseInt(string.substr(1, 2), 16)
    };
  },
  rgb: function (string) {
    let percents = string.match(/%/g);

    if (!percents || percents.length === 3) {
      var channels = string.substring(string.search(/\(/) + 1, string.length - 1).split(',');

      for (var i = 0; i < 3; i++) {
        if (channels[i]) {
          channels[i] = channels[i].trim();
          var percent = channels[i].match(/[.\d\-]+%/);

          if (percent) {
            var points = channels[i].match(/\./g);
            channels[i] = channels[i].search(/[^\d.\-%]/) === -1 && (!points || points.length < 2) ? parseFloat(percent[0]) : NaN;

            if (channels[i] < 0) {
              channels[i] = 0;
            } else if (channels[i] > 100) {
              channels[i] = 100;
            }

            channels[i] = Math.floor(channels[i] * 255 / 100);
          } else {
            channels[i] = channels[i] && channels[i].match(/[^ \-0-9]/) === null && channels[i].match(/[0-9]+/g).length === 1 ? parseInt(channels[i]) : NaN;

            if (channels[i] < 0) {
              channels[i] = 0;
            } else if (channels[i] > 255) {
              channels[i] = 255;
            }
          }
        }
      }
    } else {
      channels = [];
    }

    return {
      r: channels[0],
      g: channels[1],
      b: channels[2],
      a: 255
    };
  },
  rgba: function (string) {
    let channels = formats.rgb(string);
    channels.a = undefined;
    let match = string.match(/[\-0-9\.]+/g);

    if (match && match[3]) {
      let points = match[3].match(/\./g);

      if (!points || points.length === 1) {
        channels.a = parseFloat(match[3]);

        if (channels.a < 0) {
          channels.a = 0;
        } else if (channels.a > 1) {
          channels.a = 1;
        }

        channels.a = Math.round(channels.a * 255);
      }
    }

    return channels;
  },
  name: function (string) {
    let color = new Color$1('#' + Color$1.names[string]);
    return color.channels;
  }
};

let symbolDescriptions = {};
/**
 * Registers symbol class for serialization. After registration, the symbol can be serialized and deserialized with this
 * serialier.
 * @param constructor - constructor (class) of the symbol.
 * @param name - unique name of the symbol type. It is used to find correct constructor on deserialization.
 * @param properties - list of property names that should be serialized.
 */

const registerSymbol = (constructor, name, properties) => {
  symbolDescriptions[name] = {
    Constructor: constructor,
    properties: properties
  };
};
/**
 * Serializes symbol to a key-value JSON object.
 * @param symbol - symbol to be serialized.
 * @param colorsFormat - color format to be used during serialization. If not set, the value from the symbol property
 *                       will be used without change.
 */

const serialize = (symbol, colorsFormat = null) => {
  let keys = Object.keys(symbolDescriptions);

  for (let i = 0; i < keys.length; i++) {
    let desc = symbolDescriptions[keys[i]];

    if (symbol.constructor === desc.Constructor) {
      let serialized = {
        symbolName: keys[i]
      };
      desc.properties.forEach(prop => {
        let value = symbol[prop];

        if (colorsFormat) {
          let color = new Color$1(value);
          if (color.isValid) value = color.toString(colorsFormat);
        }

        if (value instanceof Object) {
          value = copyObject(value);
        }

        serialized[prop] = value;
      });
      return serialized;
    }
  }

  error(new Error('Unknown type of symbol.'));
};
/**
 * Deserializes symbol.
 * @param desc - serialized symbol as JSON object.
 * @param colorsFormat - format of the color properties to be used after deserialization. If not set, the values will be
 *                       set without change.
 */

const deserialize = (desc, colorsFormat = null) => {
  if (!symbolDescriptions[desc.symbolName]) error(new Error('Unknown type of symbol.'));
  let symbol = new symbolDescriptions[desc.symbolName].Constructor();
  symbolDescriptions[desc.symbolName].properties.forEach(prop => {
    let val = desc[prop];

    if (colorsFormat) {
      let color = new Color$1(val.toString());
      if (color.isValid && color.format === colorsFormat) val = color.toString('rgba');
    }

    if (val instanceof Object) {
      val = copyObject(val);
    }

    symbol[prop] = val;
  });
  return symbol;
};

var symbolSerializer = /*#__PURE__*/Object.freeze({
  __proto__: null,
  registerSymbol: registerSymbol,
  serialize: serialize,
  deserialize: deserialize
});

let id = 0;
/**
 * Cross browser DOM event attachment
 * @param {HTMLElement} element - target element
 * @param {String} type - name of the event
 * @param {Function} handler - event handler
 * @returns {Function} - attached handler
 */

const listenDomEvent = function (element, type, handler) {
  if (type === 'wheel') type = getWheelEventType();
  if (!handler.guid) handler.guid = ++id;

  if (!element.events) {
    element.events = {};

    element.handle = event => {
      return commonHandle.call(element, event);
    };
  }

  if (!element.events[type]) {
    element.events[type] = {};

    if (element.addEventListener) {
      element.addEventListener(type, element.handle, false);
    } else if (element.attachEvent) {
      element.attachEvent("on" + type, element.handle);
    }
  }

  element.events[type][handler.guid] = handler;
  return handler;
};
/**
 * Removes an event handler.
 * @param {HTMLElement} element - target element
 * @param {String} type - event name
 * @param {Function} [handler] - handler to be removed. If not specified all handlers will be removed.
 */

const removeDomEventListener = function (element, type, handler) {
  var handlers = element.events && element.events[type];
  if (!handlers) return;

  if (!handler) {
    Object.keys(handlers).forEach(key => {
      delete handlers[key];
    });
  } else {
    delete handlers[handler.guid];
  }

  if (Object.keys(handlers).length > 0) return;

  if (element.removeEventListener) {
    element.removeEventListener(type, element.handle, false);
  } else if (element.detachEvent) {
    element.detachEvent("on" + type, element.handle);
  }

  delete element.events[type];
  if (Object.keys(element.events).length > 0) return;

  try {
    delete element.handle;
    delete element.events;
  } catch (e) {
    // IE
    element.removeAttribute("handle");
    element.removeAttribute("events");
  }
};

function fixEvent(event) {
  event = event || window.event;

  if (event.isFixed) {
    return event;
  }

  event.isFixed = true;

  event.preventDefault = event.preventDefault || function () {
    this.returnValue = false;
  };

  event.stopPropagation = event.stopPropagation || function () {
    this.cancelBubble = true;
  };

  if (!event.target) {
    event.target = event.srcElement;
  }

  if (!event.currentTarget) {
    event.currentTarget = event.srcElement;
  }

  if (event.relatedTarget === undefined && event.fromElement) {
    event.relatedTarget = event.fromElement === event.target ? event.toElement : event.fromElement;
  }

  if (event.pageX == null && event.clientX != null) {
    var html = document.documentElement,
        body = document.body;
    event.pageX = event.clientX + (html && html.scrollLeft || body && body.scrollLeft || 0) - (html.clientLeft || 0);
    event.pageY = event.clientY + (html && html.scrollTop || body && body.scrollTop || 0) - (html.clientTop || 0);
  }

  if (!event.which && event.button) {
    // noinspection JSBitwiseOperatorUsage
    event.which = event.button & 1 ? 1 : event.button & 2 ? 3 : event.button & 4 ? 2 : 0;
  }

  return event;
}

function commonHandle(event) {
  event = fixEvent(event);
  var handlers = this.events[event.type];
  let keys = Object.keys(handlers);

  for (let i = 0; i < keys.length; i++) {
    let ret = handlers[keys[i]].call(this, event);

    if (ret === false) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }
}

function getWheelEventType() {
  if (document.addEventListener) {
    if ('onwheel' in document) {
      return 'wheel';
    } else if ('onmousewheel' in document) {
      return 'mousewheel';
    } else {
      return 'MozMousePixelScroll';
    }
  }
}
/**
 * Returns cross-browser consistent value of wheel rolling direction (-1 or 1).
 * @param {Event} e
 * @returns {number}
 */


const getWheelDirection = function (e) {
  var wheelData = (e.detail ? e.detail * -1 : e.wheelDelta / 40) || e.deltaY * -1;

  if (wheelData > 0) {
    wheelData = 1;
  } else if (wheelData < 0) {
    wheelData = -1;
  }

  return wheelData;
};
/**
 * Returns offset of a mouse event relative to the target element
 * @param {HTMLElement} target
 * @param {MouseEvent} e
 * @returns {{x: Number, y: Number}}
 */

const getMouseOffset = function (target, e) {
  let docPos = target === document || target === window ? {
    x: 0,
    y: 0
  } : getPosition(target);
  return {
    x: e.pageX - docPos.x,
    y: e.pageY - docPos.y
  };
};
/**
 * Returns offset of an element relative to the viewport
 * @param {HTMLElement} e
 * @returns {{x: Number, y: Number}}
 */

const getPosition = function (e) {
  var clientRect = e.getBoundingClientRect(),
      x = window.pageXOffset !== undefined ? window.pageXOffset : (document.documentElement || document.body.parentNode || document.body).scrollLeft,
      y = window.pageYOffset !== undefined ? window.pageYOffset : (document.documentElement || document.body.parentNode || document.body).scrollTop;
  return {
    x: clientRect.left + x,
    y: clientRect.top + y
  };
};

var eventModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  listenDomEvent: listenDomEvent,
  removeDomEventListener: removeDomEventListener,
  getWheelDirection: getWheelDirection,
  getMouseOffset: getMouseOffset,
  getPosition: getPosition
});

/**
 * Symbol that renders a feature to the screen coordinate system. All symbols take as input a feature, target resolution
 * and target crs, and must return a set of renders (rendered primitives) that then can be used to draw the feature.
 * @alias sGis.Symbol
 */

class Symbol {
  /**
   * This async function will be called every time the feature has to be drawn. It returns an array of renders that will actually be displayed on the map.
   * If the symbol cannot render provided feature, empty array is returned.
   * @param feature - feature to be drawn.
   * @param resolution - resolution of the render.
   * @param crs - target coordinate system of the render.\
   * @param painter - painter of the render.
   */
  renderFunctionAsync(feature, resolution, crs, painter) {
    return __awaiter(this, void 0, void 0, function* () {
      return this.renderFunction(feature, resolution, crs, painter);
    });
  }
  /**
   * Returns a copy of the symbol. Only essential properties are copied.
   */


  clone() {
    let desc = serialize(this);
    return deserialize(desc);
  }

}
class DynamicPointSymbol extends Symbol {
  constructor({
    offset = [0, 0],
    onRender
  } = {}) {
    super();
    this.offset = offset;
    this.onRender = onRender;
  }

  renderFunction(feature, resolution, crs) {
    let dynamicFeature = feature;
    if (dynamicFeature.__dynamicSymbolRender) return [dynamicFeature.__dynamicSymbolRender];

    let node = this._getFeatureNode(feature);

    node.style.position = 'absolute';
    dynamicFeature.__dynamicSymbolRender = new DynamicRender({
      node: node,
      onRender: this.onRender,
      update: (bbox = null, resolution = null) => {
        if (bbox === null) bbox = dynamicFeature.__lastBbox;
        if (resolution === null) resolution = dynamicFeature.__lastResolution;
        if (!bbox || !resolution || !dynamicFeature.crs.canProjectTo(bbox.crs)) return;
        let point = dynamicFeature.projectTo(bbox.crs);
        let dx = Math.round((point.x - bbox.xMin) / resolution + this.offset[0]);
        let dy = Math.round((bbox.yMax - point.y) / resolution + this.offset[1]);
        node.style.left = `${dx.toString()}px`;
        node.style.top = `${dy.toString()}px`;
        dynamicFeature.__lastBbox = bbox;
        dynamicFeature.__lastResolution = resolution;
      },
      redraw: () => {
        this._updateFeatureNode(dynamicFeature);
      }
    });

    this._setEventListeners(dynamicFeature);

    return [dynamicFeature.__dynamicSymbolRender];
  }

  getNode(feature) {
    let [render] = this.renderFunction(feature, 1, null);
    return render.node;
  }

  _setEventListeners(dynamicFeature) {
    if (dynamicFeature.eventFlags === MouseEventFlags.None) return;
    Object.keys(mouseEvents).forEach(eventName => {
      if (dynamicFeature.eventFlags & mouseEvents[eventName].flag) {
        listenDomEvent(dynamicFeature.__dynamicSymbolRender.node, mouseEvents[eventName].type, event => {
          dynamicFeature.fire(mouseEvents[eventName].type, {
            node: dynamicFeature.__dynamicSymbolRender.node,
            browserEvent: event
          });
        });
      }
    });
  }

  _updateFeatureNode(feature) {// do nothing
  }

  renderFunctionAsync(feature, resolution, crs) {
    return __awaiter(this, void 0, void 0, function* () {
      return this.renderFunction(feature, resolution, crs);
    });
  }

}

const BEZIER_ARC_C = 0.5522847498307933984022516322796;
/**
 * Rendered arc (circle) on a map.
 * @alias sGis.render.Arc
 */

class Arc extends VectorRender {
  /**
   * @param center - the center of the arc, in the [x, y] format.
   * @param [options] - key-value options of any Arc parameters
   */
  constructor(center, options = {}) {
    super();
    /** The center of the arc in [x, y] format. */

    this.center = [0, 0];
    /** The radius of the arc. */

    this.radius = 5;
    /** The stroke color of the arc (outline color). The value can be any valid css color string. */

    this.strokeColor = 'black';
    /** The stroke width of the arc. */

    this.strokeWidth = 1;
    /** The fill color of the arc. The value can be any valid css color string. */

    this.fillColor = 'transparent';
    /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */

    this.ignoreEvents = false;
    /** Start angle of the sector. */

    this.startAngle = 0;
    /** End angle of the sector. */

    this.endAngle = 2 * Math.PI;
    /** Shows whether the arc is a sector of a circle rather then simple arc. Set to false if you need to draw a circle, for sector has all its boundaries outlined. */

    this.isSector = false;
    /** Direction of the arc. */

    this.clockwise = true;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /**
     * Property of the Canvas 2D API determines the shape used to draw the end points of lines.
     *
     * - `"butt"`
     *      The ends of lines are squared off at the endpoints.
     * - `"round"`
     *      The ends of lines are rounded.
     * - `"square"`
     *      The ends of lines are squared off by adding a box with an equal width and half the height of the line's thickness.
     */

    this.lineCap = "round";
    Object.assign(this, options);
    this.center = center;
  }

  contains(position) {
    let dx = position[0] - this.center[0];
    let dy = position[1] - this.center[1];
    let distance2 = dx * dx + dy * dy;
    return distance2 < (this.radius + 2) * (this.radius + 2);
  }

  get isVector() {
    return true;
  }

  setupRenderCoordinates(domPainter) {
    if (this.mapCoordinates) {
      const xScale = domPainter.getXScale();
      const yScale = domPainter.getYScale();
      this.renderPosition = [xScale(this.mapCoordinates[0]), domPainter.height - yScale(this.mapCoordinates[1])];
    }
  }

  setupRender(ctx, domPainter) {
    if (domPainter) {
      this.setupRenderCoordinates(domPainter);
    }

    ctx.lineWidth = this.strokeWidth;
    ctx.strokeStyle = this.strokeColor;
    ctx.fillStyle = this.fillColor;
    ctx.setLineDash(this.lineDash || []);
    ctx.lineCap = this.lineCap;
  }

  renderObject(ctx) {
    this.renderAsArc(ctx);
  }

  renderAsArc(ctx) {
    const center = this.renderPosition || this.center;
    const isCanvas = ctx instanceof CanvasRenderingContext2D;

    if (isCanvas) {
      ctx.beginPath();
    }

    if (this.isSector) {
      ctx.moveTo(center[0], center[1]);
    } else if (ctx instanceof Path2D) {
      ctx.moveTo(center[0] + this.radius, center[1]);
    }

    ctx.arc(center[0], center[1], this.radius, this.startAngle, this.clockwise ? this.endAngle : -this.endAngle, !this.clockwise);

    if (this.isSector) {
      ctx.lineTo(center[0], center[1]);
    }

    if (isCanvas) {
      ctx.fill();
      ctx.stroke();
    }
  }

  renderAsBezierCurves(ctx) {
    const center = this.center;
    const r = this.radius;
    ctx.translate(center[0], center[1]);
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.bezierCurveTo(BEZIER_ARC_C * r, -r, r, -BEZIER_ARC_C * r, r, 0);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.bezierCurveTo(r, BEZIER_ARC_C * r, BEZIER_ARC_C * r, r, 0, r);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, r);
    ctx.bezierCurveTo(-BEZIER_ARC_C * r, r, -r, BEZIER_ARC_C * r, -r, 0);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-r, 0);
    ctx.bezierCurveTo(-r, -BEZIER_ARC_C * r, -BEZIER_ARC_C * r, -r, 0, -r);
    ctx.fill();
    ctx.stroke();

    if (this.isSector) {
      ctx.beginPath();
      ctx.lineTo(center[0], center[1]);
      ctx.fill();
      ctx.stroke();
    }
  }

}

/**
 * Symbol of point drawn as circle with outline.
 * @alias sGis.symbol.point.Point
 */

class PointSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Diameter of the circle. */

    this.size = 10;
    this._offset = [0, 0];
    /** Color of the inner part of the circle. Can be any valid css color string. */

    this.fillColor = 'black';
    /** Color of the outline of the circle. Can be any valid css color string. */

    this.strokeColor = 'transparent';
    /** Width of the outline. */

    this.strokeWidth = 1;
    Object.assign(this, options);
  }
  /** Offset of the point from the feature position. If set to [0, 0], center of the circle will be at the position of the feature. */


  get offset() {
    return this._offset;
  }

  set offset(value) {
    // TODO: remove deprecated part after 2018
    let deprecated = value;

    if (deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._offset = [deprecated.x, deprecated.y];
    } else {
      this._offset = value;
    }
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution + (this.offset[0] || 0), -position[1] / resolution + (this.offset[1] || 0)];
    return [new Arc(pxPosition, {
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      radius: this.size / 2,
      mapCoordinates: feature.position
    })];
  }

}
registerSymbol(PointSymbol, 'point.Point', ['size', 'offset', 'fillColor', 'strokeColor', 'strokeWidth']);

/**
 * Simple geographical point.
 * @alias sGis.feature.Point
 * @example symbols/Point_Symbols
 */

class PointFeature extends Feature {
  constructor(position, _a = {}) {
    var {
      symbol = new PointSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(Object.assign({
      symbol
    }, params));
    this._position = position;
  }

  projectTo(crs) {
    let projected = Point$1.prototype.projectTo.call(this, crs);
    return new PointFeature(projected.position, {
      crs: crs,
      symbol: this.symbol
    });
  }
  /**
   * Returns a copy of the feature.
   */


  clone() {
    return this.projectTo(this.crs);
  }

  get bbox() {
    return new Bbox(this._position, this._position, this.crs);
  }
  /**
   * Geographical coordinates of the point, given in the feature crs.
   */


  get position() {
    return [this._position[0], this._position[1]];
  }

  set position(position) {
    this._position = position;
    this.redraw();
  }
  /**
   * Point object corresponding to the feature position. This is the same as position property, but also
   * includes the information about coordinate system. If set, the point will first be projected to the feature
   * crs, and then the projected coordinates will be set to the feature.
   */


  get point() {
    return new Point$1(this.position, this.crs);
  }

  set point(point) {
    this.position = point.projectTo(this.crs).position;
  }

  get x() {
    return this._position[0];
  }

  set x(x) {
    this._position[0] = x;
    this.redraw();
  }

  get y() {
    return this._position[1];
  }

  set y(y) {
    this._position[1] = y;
    this.redraw();
  }
  /**
   * @deprecated
   */


  get coordinates() {
    return [this.position[0], this.position[1]];
  }

  set coordinates(position) {
    this.position = [position[0], position[1]];
  }

  get centroid() {
    return this.position;
  }

}

/**
 * Finds distance between two geographical points. If the coordinate system of the points can be projected to the
 * wgs84 crs, the distance will be calculated on a sphere with radius 6,371,009 meters (mean radius of the Earth).
 * @param {sGis.IPoint} a
 * @param {sGis.IPoint} b
 * @returns {Number}
 */

const distance$1 = function (a, b) {
  let l;

  if (a.crs.canProjectTo(wgs84)) {
    let p1 = a.projectTo(wgs84);
    let p2 = b.projectTo(wgs84);
    let lat1 = degToRad(p1.y);
    let lat2 = degToRad(p2.y);
    let dLat = lat2 - lat1;
    let dLon = degToRad(p2.x - p1.x);
    let d = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(d), Math.sqrt(1 - d));
    let R = 6371009;
    l = R * c;
  } else {
    l = Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
  }

  return l;
};
/**
 * Finds geographical length of the polyline or polygon. If the coordinates cannot be projected to wgs84 crs,
 * simple geometrical length will be returned.
 * @param {Position[][]} rings - the set of coordinates
 * @param {sGis.Crs} crs - coordinate system
 * @param {Boolean} [enclose=false] - if set to true, the geometry is treated as polygon, meaning that the result
 *                                    will also include the distance between first and last point of every contour
 * @returns {Number}
 */

const length = function (rings, crs, enclose = false) {
  let length = 0;
  let ringTemp;

  for (let ring = 0, l = rings.length; ring < l; ring++) {
    ringTemp = [].concat(rings[ring]);
    if (enclose) ringTemp.push(ringTemp[0]);

    for (let i = 0, m = ringTemp.length - 1; i < m; i++) {
      length += distance$1(new Point$1(ringTemp[i], crs), new Point$1(ringTemp[i + 1], crs));
    }
  }

  return length;
};
/**
 * Finds geographical area of the polygon. If the coordinates cannot be projected to wgs84 crs, simple geometrical
 * area will be returned.
 * @param {Position[][]} rings - coordinates of the polygon
 * @param {sGis.Crs} crs - coordinate system
 * @returns {Number}
 */

const area = function (rings, crs) {
  let projected;

  if (crs.canProjectTo(conicEqualArea)) {
    projected = projectRings(rings, crs, conicEqualArea);
  } else {
    projected = rings;
  }

  let area = 0;
  projected.forEach(ring => area += polygonArea(ring));
  return area;
};
/**
 * Projects the set of coordinates from one coordinate system to another.
 * If the coordinates cannot be projected, exception will be thrown.
 * @param {Position[][]} rings - coordinates
 * @param {sGis.Crs} fromCrs - source coordinate system
 * @param {sGis.Crs} toCrs - target coordinate system
 * @returns {Position[][]} - new array with projected coordinates
 */

const projectRings = function (rings, fromCrs, toCrs) {
  let projection = fromCrs.projectionTo(toCrs);
  let result = [];
  rings.forEach(ring => {
    let projectedRing = [];
    ring.forEach(position => {
      projectedRing.push(projection(position));
    });
    result.push(projectedRing);
  });
  return result;
};
const projectPoint = (coordinates, fromCrs, toCrs) => {
  const projection = fromCrs.projectionTo(toCrs);
  return projection(coordinates);
};
const projectPoints = function (ring, fromCrs, toCrs) {
  let projection = fromCrs.projectionTo(toCrs);
  let projectedRing = [];
  ring.forEach(position => {
    projectedRing.push(projection(position));
  });
  return projectedRing;
};

function polygonArea(coord) {
  coord = coord.concat([coord[0]]);
  let area = 0;

  for (let i = 0, l = coord.length - 1; i < l; i++) {
    area += (coord[i][0] + coord[i + 1][0]) * (coord[i][1] - coord[i + 1][1]);
  }

  return Math.abs(area / 2);
}
/**
 * Returns a point on the line, closest to the given point
 * @param {Position} point
 * @param {Position[]} line - line coordinates in the form [[x1, y1], [x2, y2]]
 * @returns {Position}
 */


const pointToLineProjection = function (point, line) {
  if (line[0][0] === line[1][0]) {
    return [line[0][0], point[1]];
  } else if (line[0][1] === line[1][1]) {
    return [point[0], line[0][1]];
  } else {
    let lx = line[1][0] - line[0][0];
    let ly = line[1][1] - line[0][1];
    let dx = line[0][0] - point[0];
    let dy = line[0][1] - point[1];
    let t = -(dx * lx + dy * ly) / (lx * lx + ly * ly);
    let x = line[0][0] + t * lx;
    let y = line[0][1] + t * ly;
    return [x, y];
  }
};
/**
 * Checks if a point is located inside a polygon.
 * @param {Number[]} polygon - coordinates of polygon in format [[[x11, y11], [x12, y12], ...], [x21, y21], [x22, y22], ...], ...]. If there is only one counter outer array can be omitted.
 * @param {number[]} point - coordinates of the point [x, y]
 * @param {Number} [tolerance=0] - the tolerance of check. If the point is out of the polygon, but is closer then tolerance, the returned result will be true.
 * @returns {boolean|Array} - true, if the point is inside of polygon, [ring, index] - index of vertex if the point is closer then 'tolerance' to one of the sides of polygon, false otherwise
 */

const contains = function (polygon, point, tolerance = 0) {
  let intersectionCount = 0;
  let adjusted = Array.isArray(polygon[0][0]) ? polygon : [polygon];

  for (let ring = 0, l = adjusted.length; ring < l; ring++) {
    let points = adjusted[ring].concat([adjusted[ring][0]]);
    let prevD = points[0][0] - point[0];
    let prevH = points[0][1] > point[1];

    for (let i = 1; i < points.length; i++) {
      if (pointToLineDistance(point, [points[i - 1], points[i]]) <= tolerance) {
        return [ring, i - 1];
      }

      let D = points[i][0] - point[0];
      let H = points[i][1] > point[1];

      if (!H !== !prevH //otherwise line does not intersect horizontal line
      && (D > 0 || prevD > 0) //line is to the left from the point, but we look to the right
      ) {
        if (!(point[1] === points[i][1] && point[1] === points[i - 1][1])) {
          //checks if line is horizontal and has same Y with point
          if (intersects([[points[i][0], points[i][1]], [points[i - 1][0], points[i - 1][1]]], [point, [Math.max(points[i][0], points[i - 1][0]), point[1]]])) {
            intersectionCount++;
          }
        }
      }

      prevD = D;
      prevH = H;
    }
  }

  return intersectionCount % 2 === 1;
};
/**
 * Returns the minimum distance between the given point and line.
 * @param point
 * @param line - line coordinates in the form [[x1, y1], [x2, y2]]
 */

const pointToLineDistance = function (point, line) {
  return Math.sqrt(pointToLineDistanceSquare(point, line));
};
/**
 * Returns the minimum square distance between the given point and line.
 * @param point
 * @param line
 */

const pointToLineDistanceSquare = function (point, line) {
  let lx = line[1][0] - line[0][0];
  let ly = line[1][1] - line[0][1];
  let dx = line[0][0] - point[0];
  let dy = line[0][1] - point[1];
  let t = 0 - (dx * lx + dy * ly) / (lx * lx + ly * ly);
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.pow(lx * t + dx, 2) + Math.pow(ly * t + dy, 2);
};
/**
 * Returns true if the given lines have at least one common point.
 * @param {Position[]} line1 - line coordinates in the form [[x1, y1], [x2, y2]]
 * @param {Position[]} line2 - line coordinates in the form [[x1, y1], [x2, y2]]
 * @returns {Boolean}
 */

const intersects = function (line1, line2) {
  if (line1[0][0] === line1[1][0]) {
    return line1[0][0] > line2[0][0];
  } else {
    let k = (line1[0][1] - line1[1][1]) / (line1[0][0] - line1[1][0]);
    let b = line1[0][1] - k * line1[0][0];
    let x = (line2[0][1] - b) / k;
    return x > line2[0][0];
  }
};
/**
 * Returns the angle of line relative to horizon in radians. The value can be from -PI to PI, first point is considered base point for rotation.
 * @param {Position[]} line - line coordinates in the form [[x1, y1], [x2, y2]]
 * @return {Number}
 */

const getLineAngle = function (line) {
  if (line[0][0] === line[1][0] && line[0][1] === line[1][1]) return NaN;
  let x = line[1][0] - line[0][0];
  let y = line[1][1] - line[0][1];
  let cos = x / Math.sqrt(x * x + y * y);
  return y >= 0 ? Math.acos(cos) : -Math.acos(cos);
};
/**
 * Returns a point at the specified distance and angle relative to horizon from origin point
 * @param {Position} point - origin point
 * @param {Number} angle - angle in radians
 * @param {Number} distance - distance
 * @returns {Position}
 */

const getPointFromAngleAndDistance = function (point, angle, distance) {
  return [point[0] + Math.cos(angle) * distance, point[1] + Math.sin(angle) * distance];
};
/**
 * Returns false if polygon has self-intersection, segments of zero length or contours with less then 3 points
 * @param {sGis.feature.Polygon|Position[][]} polygon  - polygon feature or coordinates
 * @param {boolean} tolerateSelfIntersection - skip check for self intersection
 * @returns {Boolean}
 */

const isPolygonValid = function (polygon, tolerateSelfIntersection) {
  let coordinates = polygon.rings ? polygon.rings : polygon;
  if (coordinates.length === 0) return false;

  for (let ring = 0; ring < coordinates.length; ring++) {
    if (coordinates[ring].length <= 2) return false;

    for (let i = 0; i < coordinates[ring].length; i++) {
      let p1 = coordinates[ring][i];
      let p2 = coordinates[ring][i + 1] || coordinates[ring][0];
      if (p1[0] == p2[0] && p1[1] === p2[1]) return false;
      if (!tolerateSelfIntersection && hasIntersection(coordinates, [p1, p2], [ring, i])) return false;
    }
  }

  return true;
};

function hasIntersection(coordinates, line, exc) {
  for (let ring = 0; ring < coordinates.length; ring++) {
    for (let i = 0; i < coordinates[ring].length; i++) {
      if (ring === exc[0] && (Math.abs(i - exc[1]) < 2 || exc[1] === 0 && i === coordinates[ring].length - 1 || i === 0 && exc[1] === coordinates[ring].length - 1)) continue;
      if (lineIntersects([coordinates[ring][i], coordinates[ring][i + 1] || coordinates[ring][0]], line)) return true;
    }
  }

  return false;
}

function lineIntersects(l1, l2) {
  let o1 = orient(l1[0], l1[1], l2[0]);
  let o2 = orient(l1[0], l1[1], l2[1]);
  let o3 = orient(l2[0], l2[1], l1[0]);
  let o4 = orient(l2[0], l2[1], l1[1]);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(l1[0], l2[0], l1[1])) return true;
  if (o2 === 0 && onSegment(l1[0], l2[1], l1[1])) return true;
  if (o3 === 0 && onSegment(l2[1], l1[0], l2[1])) return true;
  if (o4 === 0 && onSegment(l2[1], l1[1], l2[1])) return true;
  return false;
}

function orient(p, q, r) {
  let val = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1]);
  if (Math.abs(val) < 0.000001) return 0;
  return val > 0 ? 1 : 2;
}

function onSegment(p, q, r) {
  return q[0] <= Math.max(p[0], r[0]) && q[0] >= Math.min(p[0], r[0]) && q[1] <= Math.max(p[1], r[1]) && q[1] >= Math.min(p[1], r[1]);
}
/**
 * Applies matrix transformation on each feature in the set
 * @param {sGis.Feature[]} features
 * @param {Number[][]} matrix - transformation matrix
 * @param {IPoint|Position} center - the center of transformation
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */


const transform = function (features, matrix, center, mapCrs = null) {
  if (Array.isArray(features)) {
    features.forEach(feature => transformFeature(feature, matrix, center, mapCrs));
  } else {
    transformFeature(features, matrix, center, mapCrs);
  }
};
/**
 * Rotates the features around given point
 * @param {sGis.Feature[]} features
 * @param {Number} angle - rotation angle in radians. Positive values stand for counterclockwise rotation.
 * @param {IPoint|Position} center - rotation center
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */

const rotate = function (features, angle, center, mapCrs = null) {
  let sin = Math.sin(angle);
  let cos = Math.cos(angle);
  transform(features, [[cos, sin, 0], [-sin, cos, 0], [0, 0, 1]], center, mapCrs);
};
/**
 * Scales the features
 * @param {sGis.Feature[]} features
 * @param {Number} scale - the magnitude of scaling. E.g. value of 2 means that the size of features will be increased 2 times.
 * @param {Position} center - center of scaling
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */

const scale = function (features, scale, center, mapCrs = null) {
  transform(features, [[scale[0], 0, 0], [0, scale[1], 0], [0, 0, 1]], center, mapCrs);
};
/**
 * Moves the features
 * @param {sGis.Feature[]} features
 * @param {Number[]} translate - moving values in form [dx, dy]
 * @param {sGis.Crs} [mapCrs] - coordinate system of transformation. If not specified, transformation will be preformed
 * on the coordinates as is. This will result in errors with any non-cartesian coordinates (like lat/lon).
 */

const move = function (features, translate, mapCrs = null) {
  transform(features, [[1, 0, 0], [0, 1, 1], [translate[0], translate[1], 1]], [0, 0], mapCrs);
};

function transformFeature(feature, matrix, center, mapCrs = null) {
  let targetCrs = mapCrs || feature.crs;
  let base = center.crs ? center.projectTo(targetCrs).position : center;

  if (feature.rings) {
    let rings = feature.rings;

    if (targetCrs !== feature.crs) {
      rings = projectRings(rings, feature.crs, targetCrs);
    }

    transformRings(rings, matrix, base);

    if (targetCrs !== feature.crs) {
      rings = projectRings(rings, targetCrs, feature.crs);
    }

    feature.rings = rings;
  } else if (feature.points) {
    let points = feature.points;

    if (targetCrs !== feature.crs) {
      points = projectRings([points], feature.crs, targetCrs)[0];
    }

    points = transformRing(points, matrix, base);

    if (targetCrs !== feature.crs) {
      points = projectRings([points], targetCrs, feature.crs)[0];
    }

    feature.points = points;
  } else if (feature.position) {
    let points = [feature.position];

    if (targetCrs !== feature.crs) {
      points = projectRings([points], feature.crs, targetCrs)[0];
    }

    points = transformRing(points, matrix, base);

    if (targetCrs !== feature.crs) {
      points = projectRings([points], targetCrs, feature.crs)[0];
    }

    feature.position = points[0];
  }
}

function transformRings(rings, matrix, base) {
  rings.forEach((ring, index) => {
    rings[index] = transformRing(ring, matrix, base);
  });
}

function transformRing(ring, matrix, base) {
  extendCoordinates(ring, base);
  let transformed = multiplyMatrix(ring, matrix);
  collapseCoordinates(transformed, base);
  return transformed;
}

var geotoolsModule = /*#__PURE__*/Object.freeze({
  __proto__: null,
  distance: distance$1,
  length: length,
  area: area,
  projectRings: projectRings,
  projectPoint: projectPoint,
  projectPoints: projectPoints,
  pointToLineProjection: pointToLineProjection,
  contains: contains,
  pointToLineDistance: pointToLineDistance,
  pointToLineDistanceSquare: pointToLineDistanceSquare,
  intersects: intersects,
  getLineAngle: getLineAngle,
  getPointFromAngleAndDistance: getPointFromAngleAndDistance,
  isPolygonValid: isPolygonValid,
  transform: transform,
  rotate: rotate,
  scale: scale,
  move: move
});

var FillStyle;

(function (FillStyle) {
  FillStyle[FillStyle["None"] = 0] = "None";
  FillStyle[FillStyle["Color"] = 1] = "Color";
  FillStyle[FillStyle["Image"] = 2] = "Image";
})(FillStyle || (FillStyle = {}));
/**
 * Rendered polygon
 * @alias sGis.render.Poly
 */


class PolyRender extends VectorRender {
  /**
   * @param coordinates - the coordinates of the polygon.
   * @param options - properties to be assigned to the instance
   */
  constructor(coordinates, options = {}) {
    super();
    /** Whether the first and the last points should be connected. */

    this.enclosed = false;
    /** Fill style of the polygon. */

    this.fillStyle = FillStyle.Color;
    /** Stroke color of the polygon. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Fill color of the polygon. Can be any valid css color string. */

    this.fillColor = 'transparent';
    /** Stroke width of the polyline. */

    this.strokeWidth = 1;
    /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */

    this.ignoreEvents = false;
    /** The distance (px) from the drawn line inside which the event is still considered to be inside the line. */

    this.lineContainsTolerance = 4;
    /** Dash pattern for the line as specified in HTML CanvasRenderingContext2D.setLineDash() specification. */

    this.lineDash = [];
    /** Fill image of the polygon */

    this.fillImage = null;
    /** Drop shadow of the polygon {offsetX, offsetY, blur, color}. */

    this.shadow = null;
    /**
     * Property of the Canvas 2D API determines the shape used to draw the end points of lines.
     *
     * - `"butt"`
     *      The ends of lines are squared off at the endpoints.
     * - `"round"`
     *      The ends of lines are rounded.
     * - `"square"`
     *      The ends of lines are squared off by adding a box with an equal width and half the height of the line's thickness.
     */

    this.lineCap = "round";
    /**
     * Property of the Canvas 2D API determines the shape used to join two line segments where they meet.
     *
     * - `"bevel"`
     *      Fills an additional triangular area between the common endpoint of connected segments, and the separate outside rectangular corners of each segment.
     * - `"round"`
     *      Rounds off the corners of a shape by filling an additional sector of disc centered at the common endpoint of connected segments. The radius for these rounded corners is equal to the line width.
     * - `"miter"`
     *      Connected segments are joined by extending their outside edges to connect at a single point, with the effect of filling an additional lozenge-shaped area. This setting is affected by the miterLimit property
     */

    this.lineJoin = "round";
    /**
     * Property of the Canvas 2D API sets the miter limit ratio.
     * @docs https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/miterLimit
     */

    this.miterLimit = 10;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    Object.assign(this, options);
    this.coordinates = coordinates;
  }

  get isVector() {
    return true;
  }

  contains(position) {
    let polygonContains = contains(this.coordinates, position, this.strokeWidth / 2 + this.lineContainsTolerance);
    if (this.enclosed) return !!polygonContains;
    return Array.isArray(polygonContains) && polygonContains[1] !== this.coordinates[polygonContains[0]].length - 1;
  }

  setupRender(ctx, domPainter) {
    if (this.mapCoordinates && domPainter) {
      const xScale = domPainter.getXScale();
      const yScale = domPainter.getYScale();
      this.renderPosition = simplifyCoordinates(this.mapCoordinates.map(ring => {
        return ring.map(point => {
          return [xScale(point[0]), domPainter.height - yScale(point[1])];
        });
      }), this.strokeWidth);
    }

    ctx.lineCap = this.lineCap;
    ctx.lineJoin = this.lineJoin;
    ctx.miterLimit = this.miterLimit;
    ctx.lineWidth = this.strokeWidth;
    ctx.strokeStyle = this.strokeWidth <= 0 ? "transparent" : this.strokeColor;

    if (this.strokeWidth <= 0) {
      ctx.strokeStyle = "transparent";
    } else if (typeof this.strokeColor === "object") {
      const {
        values
      } = this.strokeColor;
      const gradient = ctx.createLinearGradient(0, 0, ctx.canvas.width, 0);
      values.forEach((item, index) => {
        gradient.addColorStop(index / (values.length - 1), item.value);
      });
      ctx.strokeStyle = gradient;
    } else {
      ctx.strokeStyle = this.strokeColor;
    }

    ctx.setLineDash(this.lineDash || []);
  }

  _drawLines(ctx) {
    const coordinates = this.renderPosition || this.coordinates;

    for (let i = 0, ringsCount = coordinates.length; i < ringsCount; i++) {
      const ring = coordinates[i];
      ctx.moveTo(ring[0][0], ring[0][1]);

      for (let j = 1, len = ring.length; j < len; j++) {
        ctx.lineTo(ring[j][0], ring[j][1]);
      }

      if (this.enclosed) {
        ctx.closePath();
      }
    }
  }

  _resetShadow(ctx) {
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowBlur = 0;
    ctx.shadowColor = 'rgba(0, 0, 0, 0)';
  }

  _setShadow(ctx, reset) {
    if (reset || this.shadow === null) {
      this._resetShadow(ctx);
    } else {
      const {
        offsetX,
        offsetY,
        blur,
        color,
        isOuter
      } = this.shadow;
      ctx.shadowOffsetX = offsetX;
      ctx.shadowOffsetY = offsetY;
      ctx.shadowBlur = blur;
      ctx.shadowColor = color;

      if (isOuter) {
        ctx.fillStyle = color;
        ctx.fill();

        this._resetShadow(ctx);

        ctx.fillStyle = 'rgba(0, 0, 0, 1)';
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fill();
        ctx.fillStyle = this.fillColor;
        ctx.globalCompositeOperation = 'source-over';
      }
    }
  }

  renderObject(ctx, domPainter) {
    const coordinates = this.renderPosition || this.coordinates;
    ctx.beginPath();

    this._drawLines(ctx);

    this._setShadow(ctx);

    if (this.isOutsideStroke) {
      ctx.lineWidth = this.strokeWidth * 2;
      ctx.stroke();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "#000";
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }

    if (this.fillStyle === FillStyle.Color) {
      ctx.fillStyle = this.fillColor;
      ctx.fill();
    } else if (this.fillStyle === FillStyle.Image) {
      ctx.fillStyle = ctx.createPattern(this.fillImage, 'repeat');
      const patternOffsetX = coordinates[0][0][0] % this.fillImage.width,
            patternOffsetY = coordinates[0][0][1] % this.fillImage.height;
      ctx.translate(patternOffsetX, patternOffsetY);
      ctx.fill();
      ctx.translate(-patternOffsetX, -patternOffsetY);
    }

    if (this.enclosed) {
      this._setShadow(ctx, true);
    }

    if (this.isInsideStroke) {
      ctx.lineWidth = this.strokeWidth * 2;
      ctx.clip();
      ctx.stroke();
    }

    if (!this.isOutsideStroke && !this.isInsideStroke) {
      ctx.stroke();
    }
  }

}

class CrossPointSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Size of the square. */

    this.size = 11;
    /** Offset of the point from the feature position in {x: dx, y: dy} format. If set to {x:0, y:0}, center of the square will be at the position of the feature. */

    this.offset = [0, 0];
    /** Color of the outline of the square. Can be any valid css color string. */

    this.strokeColor = '#444';
    /** Width of the outline. */

    this.strokeWidth = 1;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution, -position[1] / resolution];
    let halfSize = this.size / 2;
    let offset = this.offset;
    let coordinates = [[[pxPosition[0], pxPosition[1] - halfSize + offset[1]], [pxPosition[0], pxPosition[1] + halfSize + offset[1]]], [[pxPosition[0] - halfSize + offset[0], pxPosition[1]], [pxPosition[0] + halfSize + offset[0], pxPosition[1]]]];
    return [new PolyRender(coordinates, {
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      enclosed: false,
      fillStyle: FillStyle.None
    })];
  }

}

/**
 * Drawing of a new feature is started. When this event is fired, the control will have a new feature as its .activeFeature property.
 * @event DrawingBeginEvent
 */

class DrawingBeginEvent extends sGisEvent {
  constructor() {
    super(DrawingBeginEvent.type);
  }

}
DrawingBeginEvent.type = 'drawingBegin';
/**
 * Drawing of the current feature is finished.
 * @event DrawingFinishEvent
 */

class DrawingFinishEvent extends sGisEvent {
  constructor(feature, browserEvent) {
    super(DrawingFinishEvent.type);
    this.feature = feature;
    this.browserEvent = browserEvent;
  }

}
DrawingFinishEvent.type = 'drawingFinish';
/**
 * Control's active feature has been changed. This event is fired after change process is complete, e.g. drag is over or
 * double click is processed.
 * @event EditEvent
 */

class EditEvent extends sGisEvent {
  constructor() {
    super(EditEvent.type);
  }

}
EditEvent.type = 'edit';
/**
 * Control's active feature is being changed. This event is fired during the change, e.g. on every drag or click event.
 * @event ChangeEvent
 */

class ChangeEvent extends sGisEvent {
  constructor(ringIndex = null, pointIndex = null) {
    super(ChangeEvent.type);
    this.ringIndex = ringIndex;
    this.pointIndex = pointIndex;
  }

}
ChangeEvent.type = 'change';
/**
 * A new point was added to the control's active feature.
 * @event PointAddEvent
 */

class PointAddEvent extends sGisEvent {
  constructor() {
    super(PointAddEvent.type);
  }

}
PointAddEvent.type = 'pointAdd';
/**
 * Base class of all controls. Controls are objects that provide methods for setting interactions between user and map.
 * @alias sGis.Control
 */

class Control extends EventHandler {
  /**
   * @param map - map the control will work with.
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    useTempLayer = false,
    snappingProvider,
    activeLayer,
    isActive = false,
    snappingSymbol = new CrossPointSymbol()
  } = {}) {
    super();
    this._isActive = false;
    /**
     * Temporary feature layer that is added to the map when the control is activated. It is used to show snapping point
     * and can be used as a temporary storage for features the control works with.
     */

    this._tempLayer = null;
    this._map = map;
    this.useTempLayer = useTempLayer;
    this.snappingProvider = snappingProvider;
    this.activeLayer = activeLayer;
    this._snappingSymbol = snappingSymbol;
    if (isActive) this.isActive = isActive;
  }
  /**
   * Makes the control active.
   */


  activate() {
    this.isActive = true;
  }
  /**
   * Makes the control inactive.
   */


  deactivate() {
    this.isActive = false;
  }
  /**
   * Active status of the control.
   */


  get isActive() {
    return this._isActive;
  }

  set isActive(bool) {
    if (!this._map || this._isActive === bool) return;
    this._isActive = bool;

    if (bool) {
      if (this.useTempLayer) {
        this._tempLayer = new FeatureLayer();

        this._map.addLayer(this._tempLayer);
      }

      this._activate();
    } else {
      this._deactivate();

      if (this._tempLayer && this._map.contains(this._tempLayer)) {
        this._map.removeLayer(this._tempLayer);
      }

      this._tempLayer = null;
    }
  }
  /**
   * Using the provider set in .snappingProvider property, this method searches for snapping point based on given
   * parameters. If such a point is found, it adds the snapping feature to the tempLayer (if temp layer is used).
   * Then it returns the position of the snapping point as a return value.
   * If no snapping point was found, it clears the snapping feature and removes the original coordinates.
   * @param point - base point that should be snapped.
   * @param isAltPressed - whether alt key is pressed (to cancel snapping).
   * @param activeContour - active contour of the feature. This is used for axis and orthogonal snapping methods.
   * @param activeIndex - index of the active point in the contour. This is used for axis and orthogonal snapping methods.
   * @param isPolygon - specifies whether the contour should be treated as enclosed (meaning, that last and first
   *                    points should be connected.
   * @param snappingProviderOverride - if specified, this provider will be used instead of the default one.
   */


  _snap(point, isAltPressed, activeContour, activeIndex, isPolygon, snappingProviderOverride) {
    let snappingPoint = null;
    const snappingProvider = snappingProviderOverride || this.snappingProvider;

    if (!isAltPressed && snappingProvider) {
      snappingPoint = snappingProvider.getSnappingPoint(point, activeContour, activeIndex, isPolygon);
    }

    Promise.resolve(snappingPoint).then(snappingPointResult => {
      this._snappingPointRender(point, snappingPointResult);
    });
    return snappingPoint || point;
  }

  _snappingPointRender(currentPosition, snappingPointResult) {
    if (this._tempLayer) {
      const snappingFeature = this._getSnappingFeature(snappingPointResult || currentPosition);

      if (snappingPointResult && !this._tempLayer.has(snappingFeature)) {
        this._tempLayer.add(snappingFeature);
      } else if (!snappingPointResult && this._tempLayer.has(snappingFeature)) {
        this._tempLayer.remove(snappingFeature);
      }

      this._tempLayer.redraw();
    }
  }
  /**
   * Removes snapping point from the temp layer.
   */


  _unsnap() {
    if (this._tempLayer && this._snappingFeature && this._tempLayer.has(this._snappingFeature)) {
      this._tempLayer.remove(this._snappingFeature);
    }
  }

  _getSnappingFeature(point) {
    if (!this._snappingFeature) {
      this._snappingFeature = new PointFeature([0, 0], {
        crs: this._map.crs,
        symbol: this._snappingSymbol
      });
    }

    if (point) {
      this._snappingFeature.position = point;
    }

    return this._snappingFeature;
  }
  /**
   * Map the control works with.
   */


  get map() {
    return this._map;
  }

}

class sGisMouseEvent extends sGisEvent {
  constructor({
    type,
    flag
  }, {
    point,
    browserEvent
  }) {
    super(type);
    this.point = point;
    this.browserEvent = browserEvent;
    this.eventFlag = flag;
  }

}
class sGisClickEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.click, params);
  }

}
sGisClickEvent.type = mouseEvents.click.type;
sGisClickEvent.eventFlag = mouseEvents.click.flag;
class sGisDoubleClickEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.doubleClick, params);
  }

}
sGisDoubleClickEvent.type = mouseEvents.doubleClick.type;
sGisDoubleClickEvent.eventFlag = mouseEvents.doubleClick.flag;
class sGisMouseMoveEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.mouseMove, params);
  }

}
sGisMouseMoveEvent.type = mouseEvents.mouseMove.type;
sGisMouseMoveEvent.eventFlag = mouseEvents.mouseMove.flag;
class sGisMouseOutEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.mouseOut, params);
  }

}
sGisMouseOutEvent.type = mouseEvents.mouseOut.type;
sGisMouseOutEvent.eventFlag = mouseEvents.mouseOut.flag;
class sGisMouseOverEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.mouseOver, params);
  }

}
sGisMouseOverEvent.type = mouseEvents.mouseOver.type;
sGisMouseOverEvent.eventFlag = mouseEvents.mouseOver.flag;
class DragStartEvent extends sGisMouseEvent {
  constructor(draggingObject, params) {
    super(mouseEvents.dragStart, params);
    this.draggingObject = draggingObject;
  }

}
DragStartEvent.type = mouseEvents.dragStart.type;
DragStartEvent.eventFlag = mouseEvents.dragStart.flag;
class DragEvent extends sGisMouseEvent {
  constructor({
    point,
    browserEvent,
    offset,
    pxOffset
  }) {
    super(mouseEvents.drag, {
      point,
      browserEvent
    });
    this.offset = offset;
    this.pxOffset = pxOffset;
  }

}
DragEvent.type = mouseEvents.drag.type;
DragEvent.eventFlag = mouseEvents.drag.flag;
class DragEndEvent extends sGisMouseEvent {
  constructor(params) {
    super(mouseEvents.dragEnd, params);
  }

}
DragEndEvent.type = mouseEvents.dragEnd.type;
DragEndEvent.eventFlag = mouseEvents.dragEnd.flag;

/**
 * Base class for polyline and polygon controls. When active, click on the map will start a new feature, then
 * every next click adds a new point to the feature. If ctrl is held during click, new point is added and then new
 * ring drawing starts. Feature is completed by double click.<br><br>
 *
 * When control is activated, a temporary feature layer is created and added to the map. Feature is drawn on that temp
 * layer. After drawing is finished, if .activeLayer is set, the created feature is removed from the temp layer and
 * added to the active layer.
 *
 * @alias sGis.controls.Poly
 */

class PolyControl extends Control {
  /**
   * @param map - map the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    snappingProvider,
    snappingSymbol,
    activeLayer,
    isActive = false,
    symbol,
    dblClickMinTime = 30
  } = {}) {
    super(map, {
      snappingProvider,
      snappingSymbol,
      activeLayer,
      useTempLayer: true
    });
    this._dblClickTime = 0;
    this._activeFeature = null;
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this._handleClick = this._handleClick.bind(this);
    this._handleMousemove = this._handleMousemove.bind(this);
    this._handleDblclick = this._handleDblclick.bind(this);
    this.symbol = symbol;
    this.isActive = isActive;
    this.dblClickMinTime = dblClickMinTime;
  }

  _activate() {
    this.map.on(sGisClickEvent.type, this._handleClick);
    this.map.on(sGisMouseMoveEvent.type, this._handleMousemove);
    this.map.on(sGisDoubleClickEvent.type, this._handleDblclick);
  }

  _deactivate() {
    this.cancelDrawing();
    this.map.off(sGisClickEvent.type, this._handleClick);
    this.map.off(sGisMouseMoveEvent.type, this._handleMousemove);
    this.map.off(sGisDoubleClickEvent.type, this._handleDblclick);
  }

  _handleClick(event) {
    let clickEvent = event;
    setTimeout(() => {
      if (Date.now() - this._dblClickTime < this.dblClickMinTime) return;

      if (this._activeFeature) {
        if (clickEvent.browserEvent.ctrlKey) {
          this.startNewRing();
        } else {
          this._activeFeature.addPoint(clickEvent.point.position, this._activeFeature.rings.length - 1);

          return;
        }
      } else {
        this.startNewFeature(clickEvent.point);
        this.fire(new DrawingBeginEvent());
      }

      this.fire(new PointAddEvent());
      if (this._tempLayer) this._tempLayer.redraw();
    }, 10);
    event.stopPropagation();
  }
  /**
   * Starts a new feature with the first point at given position. If the control was not active, this method will set it active.
   * @param point
   */


  startNewFeature(point) {
    this.activate();
    this.cancelDrawing();
    this._activeFeature = this._getNewFeature(point.position);
    if (this._tempLayer) this._tempLayer.add(this._activeFeature);
  }

  _handleMousemove(event) {
    let mousemoveEvent = event;

    this._unsnap();

    this._debouncedSnappingHandle(mousemoveEvent);

    if (!this._activeFeature) return;
    let ringIndex = this._activeFeature.rings.length - 1;
    let pointIndex = this._activeFeature.rings[ringIndex].length - 1;
    this._activeFeature.rings[ringIndex][pointIndex] = mousemoveEvent.point.position;

    this._activeFeature.redraw();

    if (this._tempLayer) this._tempLayer.redraw();
    this.fire(new ChangeEvent(ringIndex, pointIndex));
  }

  _snappingHandle(event) {
    if (this._activeFeature) {
      let ringIndex = this._activeFeature.rings.length - 1;
      let pointIndex = this._activeFeature.rings[ringIndex].length - 1;

      const snappingResult = this._snap(event.point.position, event.browserEvent.altKey, this._activeFeature.rings[ringIndex], pointIndex, this._activeFeature.isEnclosed);

      Promise.resolve(snappingResult).then(point => {
        if (!this._activeFeature) {
          return;
        }

        this._activeFeature.rings[ringIndex][pointIndex] = point || event.point.position;

        this._activeFeature.redraw();

        if (this._tempLayer) this._tempLayer.redraw();
        this.fire(new ChangeEvent(ringIndex, pointIndex));
      });
    } else {
      this._snap(event.point.position, event.browserEvent.altKey);
    }
  }

  _handleDblclick(event) {
    let feature = this._activeFeature;
    if (!feature) return;
    let dblclickEvent = event;
    this.finishDrawing();
    event.stopPropagation();
    this._dblClickTime = Date.now();
    this.fire(new DrawingFinishEvent(feature, dblclickEvent.browserEvent));
  }
  /**
   * Cancels drawing of the current feature, removes the feature and the temp layer. No events are fired.
   */


  cancelDrawing() {
    if (!this._activeFeature || !this._tempLayer) return;
    if (this._tempLayer.has(this._activeFeature)) this._tempLayer.remove(this._activeFeature);
    this._activeFeature = null;

    this._unsnap();
  }
  /**
   * Finishes drawing of the current feature and moves it to the active layer (if set). If the current ring has less
   * then two points, the ring is removed. If the feature has no rings, the feature is not added to the active layer.
   */


  finishDrawing() {
    let feature = this._activeFeature;
    if (!feature) return;
    let ringIndex = feature.rings.length - 1;
    this.cancelDrawing();
    if (ringIndex === 0 && feature.rings[ringIndex].length < 3) return;
    feature.removePoint(ringIndex, feature.rings[ringIndex].length - 1);
    if (this.activeLayer) this.activeLayer.add(feature);
  }
  /**
   * Start drawing of a new ring of the feature.
   */


  startNewRing() {
    if (!this._activeFeature) return;
    let rings = this._activeFeature.rings;
    let ringIndex = rings.length;
    let point = rings[ringIndex - 1][rings[ringIndex - 1].length - 1];

    this._activeFeature.setRing(ringIndex, [point]);
  }
  /**
   * The active drawing feature.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    if (!this._isActive) return;
    this.cancelDrawing();
    this._activeFeature = feature;
  }

}

const MAX_RESOLUTION = 22;
const TOLERANCE = 0.001;
const defaultLevels = [{
  // https://www.maptiler.com/google-maps-coordinates-tile-bounds-projection/#22/48.10/-22.32
  resolution: 156543.03392800014,
  indexCount: 1,
  zIndex: 0
}];

for (let i = 1; i < MAX_RESOLUTION; i++) {
  defaultLevels[i] = {
    resolution: defaultLevels[i - 1].resolution / 2,
    indexCount: defaultLevels[i - 1].indexCount * 2,
    zIndex: i
  };
}
/**
 * Tile scheme used by tile layers to calculate indexes and coordinates of the tiles.
 * @alias sGis.TileScheme
 */


class TileScheme {
  constructor({
    origin,
    levels,
    limits = [-Infinity, -Infinity, Infinity, Infinity],
    reversedY = false,
    tileWidth = 256,
    tileHeight = 256
  }) {
    this._levels = levels.sort((a, b) => a.resolution - b.resolution);
    this._origin = origin;
    this.reversedY = reversedY;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.limits = limits;
  }
  /** @see [[TileSchemeConstructorParams.levels]] */


  get levels() {
    return this._levels;
  }
  /**
   * Returns resolution of the closest level in the tile scheme in the given direction. If no such level is found, returns smallest or largest possible resolution.
   * @param resolution - resolution that will be used as a base for search
   * @param direction - if false, will return resolution smaller then given, if true, will return resolution larger then given
   */


  getAdjustedResolution(resolution, direction = false) {
    return this.levels[this.getLevel(resolution, direction)].resolution;
  }
  /**
   * Returns closest level index in the tile scheme that has resolution in the given direction. If no such level is found, returns the last level index.
   * @param resolution - resolution that will be used as a base for search
   * @param direction - if false, resolution level with smaller resolution will be returned. If true, resolution level with larger resolution will be returned.
   */


  getLevel(resolution, direction = false) {
    if (!this.levels || this.levels.length === 0) error('Tile scheme levels are not set');
    let i;

    for (i = 0; i < this.levels.length; i++) {
      if (resolution <= this.levels[i].resolution + TOLERANCE) {
        if (direction) {
          return i === 0 ? i : i - 1;
        }

        return i;
      }
    }

    return i - 1;
  }
  /**
   * Maximum resolution in the scheme
   */


  get maxResolution() {
    return this.levels[this.levels.length - 1].resolution;
  }
  /**
   * Minimum resolution in the scheme
   */


  get minResolution() {
    return this.levels[0].resolution;
  }
  /** @see [[TileSchemeConstructorParams.origin]] */


  get origin() {
    return this._origin;
  }

}
/** Default tile scheme used for Mercator projections. */

TileScheme.default = /*#__PURE__*/new TileScheme({
  origin: [-20037508.342787, 20037508.342787],
  levels: defaultLevels,
  tileWidth: 256,
  tileHeight: 256,
  reversedY: false,
  limits: [-Infinity, -20037508.342787, Infinity, 20037508.342787]
});

var ContentsChangeType;

(function (ContentsChangeType) {
  ContentsChangeType[ContentsChangeType["Add"] = 0] = "Add";
  ContentsChangeType[ContentsChangeType["Remove"] = 1] = "Remove";
  ContentsChangeType[ContentsChangeType["Reorder"] = 2] = "Reorder";
  ContentsChangeType[ContentsChangeType["VisibilityChange"] = 3] = "VisibilityChange";
})(ContentsChangeType || (ContentsChangeType = {}));
/**
 * The contents, their order or visibility of the group is changed.
 * @event ContentsChangeEvent
 */


class ContentsChangeEvent extends sGisEvent {
  constructor(changeType, affectedLayers) {
    super(ContentsChangeEvent.type);
    this.changeType = changeType;
    this.affectedLayers = affectedLayers;
  }

}
ContentsChangeEvent.type = 'contentsChange';
/**
 * Ordered nested list of layers.
 * @alias sGis.LayerGroup
 */

class LayerGroup extends EventHandler {
  /**
   * @param layers - initial list of layers in the group
   */
  constructor(layers = []) {
    super();

    this._fireContentChange = (changeType, layers) => this.fire(new ContentsChangeEvent(changeType, layers));

    this._forwardEvent = ev => this.fire(ev);

    this._isDisplayed = true;
    this._layers = layers;
  }
  /**
   * Adds a layer to the end of the list.
   * @param layer - layer to add.
   * @fires [[ContentsChangeEvent]]
   * @throws If the layer is already in the group, or in any of the child groups.
   */


  addLayer(layer) {
    if (layer === this) error(new Error('Cannot add self to the group'));

    if (this.contains(layer)) {
      error(new Error('Cannot add layer to the group: the layer is already in the group'));
    }

    this._layers.push(layer);

    this._setChildListeners(layer);

    if (layer instanceof LayerGroup) {
      this._setForwardListeners(layer);
    }

    this.fire(new ContentsChangeEvent(ContentsChangeType.Add, [layer]));
  }
  /**
   * Removes the layer from the group.
   * @param layer - layer to remove.
   * @param recurse - if set to true, the layer will be removed from all child groups containing this layer.
   * @fires [[ContentsChangeEvent]]
   * @throws If the layer not in the group.
   */


  removeLayer(layer, recurse = false) {
    let index = this._layers.indexOf(layer);

    let removed = false;

    if (index !== -1) {
      this._layers.splice(index, 1);

      this._removeChildListeners(layer);

      if (layer instanceof LayerGroup) {
        this._removeForwardListeners(layer);
      }

      this.fire(new ContentsChangeEvent(ContentsChangeType.Remove, [layer]));
      removed = true;
    } else if (recurse) {
      for (let i = 0; i < this._layers.length; i++) {
        if (!(this._layers[i] instanceof LayerGroup)) continue;
        let group = this._layers[i];

        if (group.contains(layer)) {
          group.removeLayer(layer, true);
          removed = true;
        }
      }
    }

    if (!removed) error(new Error('The layer is not in the group'));
  }

  _setChildListeners(layer) {
    layer.on(VisibilityChangeEvent.type, () => this._fireContentChange(ContentsChangeType.VisibilityChange, [layer]));
  }

  _removeChildListeners(layer) {
    layer.off(VisibilityChangeEvent.type, () => this._fireContentChange(ContentsChangeType.VisibilityChange, [layer]));
  }

  _setForwardListeners(layerGroup) {
    layerGroup.on(ContentsChangeEvent.type, this._forwardEvent);
  }

  _removeForwardListeners(layerGroup) {
    layerGroup.off(ContentsChangeEvent.type, this._forwardEvent);
  }
  /**
   * Returns true if the group or any of the child groups (recursively) contains the given layer.
   * @param layer - layer to check
   */


  contains(layer) {
    for (let i = 0; i < this._layers.length; i++) {
      if (layer === this._layers[i]) return true;
      if (!(this._layers[i] instanceof LayerGroup)) continue;
      let group = this._layers[i];

      if (group.contains(layer)) {
        return true;
      }
    }

    return false;
  }
  /**
   * Returns index of a layer in the group. If not in the group, returns -1.
   * @param layer
   */


  indexOf(layer) {
    return this._layers.indexOf(layer);
  }
  /**
   * Inserts the layer to the given position. If the layer is already in the group, moves the layer so that new index of the layer equals the specified index.
   * If the index is negative, layer is added to the n-th position from the end of the list.
   * If the index is larger than number of the layers in the group, layer will be added to the end of the list.
   * @param layer - layer to insert.
   * @param index - integer position of the layer after insertion.
   * @fires [[ContentsChangeEvent]]
   * @throws If the given layer cannot be added to the group (e.g. if the result of reordering creates recursive nesting).
   */


  insertLayer(layer, index) {
    let currIndex = this._layers.indexOf(layer);

    let added = false;

    if (currIndex === -1) {
      this.prohibitEvent('layerAdd');
      this.addLayer(layer);
      this.allowEvent('layerAdd');
      currIndex = this._layers.length - 1;
      added = true;
    }

    let length = this._layers.length;
    index = index > length ? length : index < 0 && index < -length ? -length : index;
    if (index < 0) index = length + index;

    this._layers.splice(currIndex, 1);

    this._layers.splice(index, 0, layer);

    let changeType = added ? ContentsChangeType.Add : ContentsChangeType.Reorder;
    this.fire(new ContentsChangeEvent(changeType, [layer]));
  }
  /**
   * Moves the layer to the end of the list. If the layer is not in the group, the effect of this method is same as .add(layer).
   * @param layer - layer to add.
   */


  moveLayerToTop(layer) {
    this.insertLayer(layer, -1);
  }
  /**
   * Returns the flat list of layers in the group. Child LayerGroups are not included.
   * @param recurse - if set to true, the contents of child LayerGroups will be added to the list in the corresponding order.
   *                  E.g. if the group contents are layer1, group1, layer2, then the resulting list will be the result of concatenation of
   *                  [layer1], group1.getLayers(true), [layer2].
   * @param excludeInactive - if set to true, layers with isDisplayed=false and all their children will not be included.
   */


  getLayers(recurse = false, excludeInactive = false) {
    let layers = [];

    this._layers.forEach(layer => {
      if (excludeInactive && !layer.isDisplayed) return;

      if (recurse && layer instanceof LayerGroup) {
        layers = layers.concat(layer.getLayers(recurse, excludeInactive));
      } else {
        layers.push(layer);
      }
    });

    return layers;
  }
  /**
   * The list of the layers and child groups in the group. If assigned, two change event will be fired: one for layers
   * removal and one for layers adding.
   * @fires [[ContentsChangeEvent]]
   */


  get layers() {
    return this._layers;
  }

  set layers(layers) {
    let currLayers = this._layers;

    if (currLayers.length > 0) {
      this._layers.length = 0;
      this.fire(new ContentsChangeEvent(ContentsChangeType.Remove, currLayers));
    }

    if (layers.length > 0) {
      this.prohibitEvent(ContentsChangeEvent.type);

      for (let i = 0; i < layers.length; i++) {
        this.addLayer(layers[i]);
      }

      this.allowEvent(ContentsChangeEvent.type);
      this.fire(new ContentsChangeEvent(ContentsChangeType.Add, layers));
    }
  }
  /**
   * Whether the group is active and should be displayed on the map.
   * @fires [[VisibilityChangeEvent]]
   */


  get isDisplayed() {
    return this._isDisplayed;
  }

  set isDisplayed(bool) {
    if (this._isDisplayed === bool) return;
    this._isDisplayed = bool;
    this.fire(new VisibilityChangeEvent());
  }
  /**
   * Sets .isDisplayed property to true
   */


  show() {
    this.isDisplayed = true;
  }
  /**
   * Sets .isDisplayed property to false
   */


  hide() {
    this.isDisplayed = false;
  }

}

class BboxChangeEvent extends sGisEvent {
  constructor() {
    super(BboxChangeEvent.type);
  }

}
BboxChangeEvent.type = 'bboxChange';
class BboxChangeEndEvent extends sGisEvent {
  constructor() {
    super(BboxChangeEndEvent.type);
  }

}
BboxChangeEndEvent.type = 'bboxChangeEnd';
class AnimationStartEvent extends sGisEvent {
  constructor() {
    super(AnimationStartEvent.type);
  }

}
AnimationStartEvent.type = 'animationStart';
class AnimationEndEvent extends sGisEvent {
  constructor() {
    super(AnimationEndEvent.type);
  }

}
AnimationEndEvent.type = 'animationEnd';
/**
 * Map object with set of layers, specified position, resolution, coordinate system.
 * @alias sGis.Map
 */

class Map$1 extends LayerGroup {
  constructor({
    position = new Point$1([55.755831, 37.617673]).projectTo(webMercator).position,
    resolution = 611.4962262812505 / 2,
    crs = webMercator,
    centerPoint,
    tileScheme = TileScheme.default,
    animationTime = 300,
    changeEndDelay = 300,
    minResolution = -1,
    maxResolution = -1,
    layers = []
  } = {}) {
    super(layers);
    this._animationStopped = true;
    this._animationTarget = null;
    this._crs = crs;
    this._position = centerPoint ? centerPoint.projectTo(crs).position : position;
    this._resolution = resolution;
    this.tileScheme = tileScheme;
    this.animationTime = animationTime;
    this.changeEndDelay = changeEndDelay;
    this._minResolution = minResolution;
    this._maxResolution = maxResolution;

    this._listenForBboxChange();
  }

  _listenForBboxChange() {
    this.on(BboxChangeEvent.type, debounce(this._fireBboxChangeEnd.bind(this), this.changeEndDelay));
  }

  _fireBboxChangeEnd() {
    this.fire(new BboxChangeEndEvent());
  }
  /**
   * Moves the map position by the specified offset
   * @param dx - Offset along X axis in map coordinates, positive direction is right
   * @param dy - Offset along Y axis in map coordinates, positive direction is down
   */


  move(dx, dy) {
    this._position[0] += dx;
    this._position[1] += dy;
    this.fire(new BboxChangeEvent());
  }
  /**
   * Changes the scale of map by scalingK
   * @param scalingK - Coefficient of scaling (Ex. 5 -> 5 times zoom in)
   * @param basePoint - Base point of zooming
   * @param doNotAdjust - do not adjust resolution to the round ones
   */


  changeScale(scalingK, basePoint, doNotAdjust) {
    this.setResolution(this.resolution * scalingK, basePoint, doNotAdjust);
  }
  /**
   * Changes the scale of map by scalingK with animation
   * @param scalingK - Coefficient of scaling (Ex. 5 -> 5 times zoom in)
   * @param basePoint - Base point of zooming
   */


  animateChangeScale(scalingK, basePoint) {
    this.animateSetResolution(this.resolution * scalingK, basePoint);
  }
  /**
   * Changes resolution of the map by k zoom levels. Positive values represent zoom in.
   * @param k - number of levels to zoom
   * @param basePoint - zooming base point
   */


  zoom(k, basePoint) {
    let tileScheme = this.tileScheme;
    let currResolution = this._animationTarget ? this._animationTarget[1] : this.resolution;
    let resolution;

    if (tileScheme) {
      let level = tileScheme.getLevel(currResolution) + (k > 0 ? -1 : 1);
      resolution = tileScheme.levels[level] ? tileScheme.levels[level].resolution : currResolution;
    } else {
      resolution = currResolution * Math.pow(2, -k);
    }

    resolution = Math.min(Math.max(resolution, this.minResolution || 0), this.maxResolution || Number.MAX_VALUE);
    this.animateSetResolution(resolution, basePoint);
  }
  /**
   * Changes resolution of the map so that the new resolution corresponds to an even tile scheme level. Resolution is changed with animation.
   * @param direction - if false will adjust to smaller resolution, if true - to larger
   */


  adjustResolution(direction = false) {
    let resolution = this.resolution;
    let newResolution = this.getAdjustedResolution(resolution, direction);
    let ratio = newResolution / resolution;

    if (ratio > 1.1 || ratio < 0.9) {
      this.animateSetResolution(newResolution);
    } else if (ratio > 1.0001 || ratio < 0.9999) {
      this.setResolution(newResolution);
    }
  }
  /**
   * Returns closest resolution to the given one that corresponds to an even tile scheme level.
   * @param resolution - target resolution
   * @param direction - if false will adjust to smaller resolution, if true - to larger
   */


  getAdjustedResolution(resolution, direction = false) {
    if (!this.tileScheme) return resolution;
    resolution = Math.min(Math.max(resolution, this.minResolution || 0), this.maxResolution || Number.MAX_VALUE);
    return this.tileScheme.getAdjustedResolution(resolution, direction);
  }
  /**
   * Sets new resolution to the map with animation
   * @param resolution
   * @param basePoint - Base point of zooming
   */


  animateSetResolution(resolution, basePoint) {
    let adjustedResolution = this.getAdjustedResolution(resolution);

    let newPosition = this._getScaledPosition(adjustedResolution, basePoint && this._getBase(basePoint));

    this.animateTo(newPosition, adjustedResolution);
  }
  /**
   * Changes position and resolution of the map with animation
   * @param point - target center point of the map
   * @param resolution - target resolution;
   */


  animateTo(point, resolution) {
    if (!Array.isArray(point)) point = point.projectTo(this.crs).position;
    this.stopAnimation();
    this.fire(new AnimationStartEvent());
    let originalPosition = this.centerPoint;
    let originalResolution = this.resolution;
    let dx = point[0] - originalPosition.x;
    let dy = point[1] - originalPosition.y;
    let dr = resolution - originalResolution;
    let startTime = Date.now();
    this._animationStopped = false;
    this._animationTarget = [point, resolution];
    let self = this;
    this._animationTimer = setInterval(function () {
      let time = Date.now() - startTime;

      if (time >= self.animationTime || self._animationStopped) {
        self.setPosition(point, resolution);
        self.stopAnimation();
        self.fire(new AnimationEndEvent());
      } else {
        let x = self._easeFunction(time, originalPosition.x, dx, self.animationTime);

        let y = self._easeFunction(time, originalPosition.y, dy, self.animationTime);

        let r = self._easeFunction(time, originalResolution, dr, self.animationTime);

        self.setPosition(new Point$1([x, y], self.crs), r);
      }
    }, 1000 / 60);
  }

  _getBase(basePoint) {
    return basePoint.projectTo ? basePoint.projectTo(this.crs).position : basePoint;
  }

  _getScaledPosition(newResolution, basePoint = null) {
    let position = this.position;
    let base = basePoint ? basePoint : position;
    let resolution = this.resolution;
    let scalingK = newResolution / resolution;
    return [(position[0] - base[0]) * scalingK + base[0], (position[1] - base[1]) * scalingK + base[1]];
  }
  /**
   * Stops all animations of the map
   */


  stopAnimation() {
    this._animationStopped = true;
    this._animationTarget = null;
    clearInterval(this._animationTimer);
  }

  easing(t) {
    return t < 0.5 ? 2 * t * t : (4 - 2 * t) * t - 1;
  }

  _easeFunction(t, b, c, d) {
    return b + c * this.easing(t / d);
  }
  /**
   * Sets new position and resolution to the map
   * @param point - new center point of the map
   * @param resolution - new resolution of the map
   */


  setPosition(point, resolution) {
    if (!Array.isArray(point)) point = point.projectTo(this.crs).position;
    this.prohibitEvent(BboxChangeEvent.type);
    this.position = point;
    if (resolution) this.resolution = resolution;
    this.allowEvent(BboxChangeEvent.type);
    this.fire(new BboxChangeEvent());
  }
  /**
   * Sets new resolution to the map
   * @param resolution
   * @param basePoint - Base point of zooming
   * @param doNotAdjust - do not adjust resolution to the round ones
   */


  setResolution(resolution, basePoint = null, doNotAdjust = false) {
    this.setPosition(this._getScaledPosition(resolution, basePoint && this._getBase(basePoint)), doNotAdjust ? resolution : this.getAdjustedResolution(resolution));
  }
  /**
   * Return current scale level of the map
   */


  getScaleLevel() {
    return this.tileScheme.levels.length - 1 - this.tileScheme.getLevel(this.resolution);
  }
  /**
   * Geographical position of the center of the map given in map coordinate system
   */


  get position() {
    return this._position;
  }

  set position(position) {
    this._position = position;
    this.fire(new BboxChangeEvent());
  }
  /**
   * Center point of the map
   */


  get centerPoint() {
    return new Point$1(this.position, this.crs);
  }

  set centerPoint(point) {
    this.position = point.projectTo(this.crs).position;
  }
  /**
   * Coordinate system of the map. If the value is set and old crs cannot be projected to the new one, position of the map is set to [0, 0].
   * Otherwise position is projected to the new crs.
   */


  get crs() {
    return this._crs;
  }

  set crs(crs) {
    this._crs = crs;
  }
  /**
   * Resolution of the map. Can be any positive number.
   */


  get resolution() {
    return this._resolution;
  }

  set resolution(resolution) {
    this._resolution = resolution;
    this.fire(new BboxChangeEvent());
  }
  /**
   * Minimum allowed resolution of the map. If not set, the minimum value from the map tile scheme will be used. Must be smaller then max resolution.
   * If current resolution is smaller that the newly assigned minResolution, the current resolution will be adjusted accordingly.
   */


  get minResolution() {
    return this._minResolution > 0 ? this._minResolution : this.tileScheme && this.tileScheme.minResolution;
  }

  set minResolution(resolution) {
    if (resolution > 0) {
      let maxResolution = this.maxResolution;
      if (resolution > maxResolution) error('maxResolution cannot be less then minResolution');
    }

    this._minResolution = resolution;
    if (this.resolution < this.minResolution) this.resolution = resolution;
  }
  /**
   * Maximum allowed resolution of the map. If not set, the maximum value from the map tile scheme will be used. Must be larger then min resolution.
   * If current resolution is larger that the newly assigned maxResolution, the current resolution will be adjusted accordingly.
   */


  get maxResolution() {
    return this._maxResolution > 0 ? this._maxResolution : this.tileScheme && this.tileScheme.maxResolution;
  }

  set maxResolution(resolution) {
    if (resolution > 0) {
      let minResolution = this.minResolution;
      if (resolution < minResolution) error('maxResolution cannot be less then minResolution');
    }

    this._maxResolution = resolution;
    if (this.resolution > this.maxResolution) this.resolution = resolution;
  }

}

var Action;

(function (Action) {
  Action[Action["addPoint"] = 0] = "addPoint";
  Action[Action["movePoint"] = 1] = "movePoint";
  Action[Action["removePoint"] = 2] = "removePoint";
})(Action || (Action = {}));
/**
 * Base class for polylines and polygons.
 * @alias sGis.feature.Poly
 */


class Poly extends Feature {
  constructor(rings, properties) {
    super(properties);

    if (rings && rings.length > 0) {
      if (!Array.isArray(rings[0][0])) rings = [rings];
      this.rings = copyArray(rings);
    } else {
      this._rings = [[]];
    }
  }

  get lastAction() {
    return this._lastAction;
  }

  set lastAction(action) {
    this._lastAction = action;
  }
  /**
   * Array of contours of coordinates. The contours must be not-enclosed for both polylines and polygons (first and last points of a contour must not be same)
   */


  get rings() {
    return this._rings;
  }

  set rings(rings) {
    this._rings = rings;

    this._update();
  }
  /**
   * Adds a point to the end of the specified contour (ring).
   * @param point - point to be added. If sGis.Point is given, the point will be automatically projected to the feature coordinate system.
   * @param [ringN] - number of the ring the point will be added to. If not specified, the point will be added to the last ring.
   */


  addPoint(point, ringN) {
    if (!ringN) ringN = this._rings.length - 1;
    this._lastAction = {
      type: Action.addPoint,
      ringIndex: ringN,
      coordinates: point.position && point.projectTo ? point.projectTo(this.crs).position : point
    };
    this.setPoint(ringN, this._rings[ringN].length, point, true);
  }
  /**
   * Removes a point from the feature.
   * @param ringN - index of the ring (contour) the point will be removed from.
   * @param index - index of the point in the ring.
   */


  removePoint(ringN, index) {
    this._lastAction = {
      type: Action.removePoint,
      ringIndex: ringN,
      pointIndex: index,
      previousCoordinates: this._rings[ringN][index]
    };

    this._rings[ringN].splice(index, 1);

    if (this._rings[ringN].length === 0) {
      this.removeRing(ringN);
    }

    this._update();
  }
  /**
   * Removes a ring (contour) from the feature.
   * @param ringN - index of the ring to be removed.
   */


  removeRing(ringN) {
    this._rings.splice(ringN, 1);

    this._update();
  }

  _update() {
    this._bbox = null;
    this.redraw();
  }
  /**
   * Sets new coordinates for a contour.
   * @param ringN - index of the contour to be set. If the index is larger then the number of rings of the feature, new ring will be appended to the ring list.
   * @param ring - coordinate set of the contour.
   */


  setRing(ringN, ring) {
    ringN = Math.min(ringN, this._rings.length);
    this._rings[ringN] = ring;

    this._update();
  }
  /**
   * Sets a new value for a point in the feature.
   * @param ringN - index of the contour of the point.
   * @param pointN - index of the point in the contour.
   * @param point - new coordinates
   * @param dontRecord - dont record edit action to feature
   */


  setPoint(ringN, pointN, point, dontRecord) {
    pointN = Math.min(pointN, this._rings[ringN].length);

    if (!dontRecord) {
      this._lastAction = {
        type: Action.movePoint,
        ringIndex: ringN,
        pointIndex: pointN,
        previousCoordinates: this._rings[ringN][pointN],
        coordinates: point.position && point.projectTo ? point.projectTo(this.crs).position : point
      };
    }

    this._rings[ringN][pointN] = point.position && point.projectTo ? point.projectTo(this.crs).position : point;

    this._update();
  }
  /**
   * Inserts a new point to the given position.
   * @param ringN - index of the contour the point will be inserted into.
   * @param pointN - index of the point to insert to.
   * @param point - point to be inserted
   */


  insertPoint(ringN, pointN, point) {
    this._lastAction = {
      type: Action.addPoint,
      ringIndex: ringN,
      pointIndex: pointN,
      coordinates: point.position && point.projectTo ? point.projectTo(this.crs).position : point
    };
    pointN = Math.min(pointN, this._rings[ringN].length);

    this._rings[ringN].splice(pointN, 0, [0, 0]);

    this.setPoint(ringN, pointN, point, true);
  }
  /**
   * Bounding box of the feature
   */


  get bbox() {
    if (this._bbox) return this._bbox;
    let xMin = Number.MAX_VALUE;
    let yMin = Number.MAX_VALUE;
    let xMax = -Number.MAX_VALUE;
    let yMax = -Number.MAX_VALUE;

    this._rings.forEach(ring => {
      ring.forEach(point => {
        xMin = Math.min(xMin, point[0]);
        yMin = Math.min(yMin, point[1]);
        xMax = Math.max(xMax, point[0]);
        yMax = Math.max(yMax, point[1]);
      });
    });

    this._bbox = new Bbox([xMin, yMin], [xMax, yMax], this.crs);
    return this._bbox;
  }

  get centroid() {
    let bbox = this.bbox;
    let x = (bbox.xMin + bbox.xMax) / 2;
    let y = (bbox.yMin + bbox.yMax) / 2;
    return [x, y];
  }
  /**
   * @deprecated
   */


  get coordinates() {
    return copyArray(this._rings);
  }

  set coordinates(rings) {
    this.rings = copyArray(rings);
  }

}

/**
 * Symbol of polygon with one color filling.
 * @alias sGis.symbol.polygon.Simple
 */

class PolygonSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Fill color of the polygon. Can be any valid css color string. */

    this.fillColor = 'transparent';
    /** Stroke color of the outline. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Stroke width of the outline. */

    this.strokeWidth = 1;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** Emulation CanvasRenderingContext2D.filter shadow. */

    this.shadow = null;
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];
    let coordinates = getRenderedPolygonCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      enclosed: true,
      fillStyle: FillStyle.Color,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      fillColor: this.fillColor,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit,
      isOutsideStroke: this.isOutsideStroke,
      isInsideStroke: this.isInsideStroke,
      mapCoordinates: feature.rings
    })];
  }

}
registerSymbol(PolygonSymbol, 'polygon.Simple', ['fillColor', 'strokeColor', 'strokeWidth', 'lineDash', 'shadow', 'lineCap', 'lineJoin', 'miterLimit', 'isOutsideStroke', 'isInsideStroke']);

/**
 * Polygon with one or more contours (rings). Coordinates in the contours must not be enclosed (first and last points must not be same).
 * @alias sGis.feature.Polygon
 */

class Polygon extends Poly {
  constructor(rings, _a = {}) {
    var {
      symbol = new PolygonSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(rings, Object.assign({
      symbol
    }, params));
    this.isEnclosed = true;
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return new Polygon(this.rings, {
      crs: this.crs,
      symbol: this.originalSymbol
    });
  }

  projectTo(crs) {
    let projected = projectRings(this.rings, this.crs, crs);
    return new Polygon(projected, {
      crs: this.crs,
      symbol: this.symbol,
      persistOnMap: this.persistOnMap
    });
  }

}

/**
 * Base class for controls that create polygon feature. When the control is activated,
 * a new temporary layer is created and added to the map. The feature is drawn on that temp layer. After drawing is
 * finished, if the .activeLayer property is set, the feature is moved to the active layer.
 * @alias sGis.controls.PolyDrag
 * @fires [[DrawingBeginEvent]]
 * @fires [[DrawingFinishEvent]]
 */

class PolyDrag extends Control {
  /**
   * @param map - map the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    symbol = new PolygonSymbol(),
    activeLayer = null,
    isActive = false,
    createByClick = false
  } = {}) {
    super(map, {
      activeLayer,
      useTempLayer: true
    });
    this.createByClick = false;
    this.symbol = symbol;
    this.createByClick = createByClick;
    this._handleDragStart = this._handleDragStart.bind(this);
    this._handleDrag = this._handleDrag.bind(this);
    this._handleDragEnd = this._handleDragEnd.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleMousemove = this._handleMousemove.bind(this);
    this.isActive = isActive;
  }

  _activate() {
    if (this.createByClick) {
      this.map.on(sGisClickEvent.type, this._handleClick);
      this.map.on(sGisMouseMoveEvent.type, this._handleMousemove);
    } else {
      this.map.on(DragStartEvent.type, this._handleDragStart);
    }
  }

  _deactivate() {
    this._activeFeature = null;

    if (this.createByClick) {
      this.map.off(sGisClickEvent.type, this._handleClick);
      this.map.off(sGisMouseMoveEvent.type, this._handleMousemove);
    } else {
      this._removeDragListeners();

      this.map.off(DragStartEvent.type, this._handleDragStart);
    }
  }

  _handleDragStart(event) {
    this.startDrawing(event);
  }

  _handleDrag(event) {
    this.updateDrawing(event);
  }

  _handleDragEnd(event) {
    this.finishDrawing(event);
  }

  _handleClick(event) {
    if (!this._activeFeature) {
      this.startDrawing(event);
    } else {
      this.finishDrawing(event);
    }
  }

  _handleMousemove(event) {
    if (this._activeFeature) {
      this.updateDrawing(event);
    }
  }

  startDrawing(event) {
    this._activeFeature = new Polygon(this._getNewCoordinates(event.point), {
      crs: event.point.crs,
      symbol: this.symbol
    });

    this._tempLayer.add(this._activeFeature);

    if (!this.createByClick) {
      this.map.on(DragEvent.type, this._handleDrag);
      this.map.on(DragEndEvent.type, this._handleDragEnd);
    }

    this.fire(new DrawingBeginEvent());
  }

  updateDrawing(event) {
    this._activeFeature.rings = this._getUpdatedCoordinates(event.point);

    this._tempLayer.redraw();

    event.stopPropagation();
  }

  finishDrawing(event) {
    let feature = this._activeFeature;
    this._activeFeature = null;

    if (this._tempLayer && this._tempLayer.has(feature)) {
      this._tempLayer.remove(feature);
    }

    if (!this.createByClick) {
      this._removeDragListeners();
    }

    if (this.activeLayer) this.activeLayer.add(feature);
    this.fire(new DrawingFinishEvent(feature, event.browserEvent));
  }

  _removeDragListeners() {
    this.map.off(DragEvent.type, this._handleDrag);
    this.map.off(DragEndEvent.type, this._handleDragEnd);
  }
  /**
   * The feature being drawn.
   */


  get activeFeature() {
    return this._activeFeature;
  }

}

/**
 * Control for drawing circles by dragging from center to the radius.
 * @alias sGis.controls.Circle
 */

class Circle extends PolyDrag {
  constructor() {
    super(...arguments);
    /** The number of segments of the circle. The higher this number is the smoother the circle will be. */

    this.segmentNo = 36;
  }

  _getNewCoordinates(point) {
    this._centerPoint = point.position;
    return [[]];
  }

  _getUpdatedCoordinates(point) {
    if (!this._centerPoint) return [];
    let radius = Math.sqrt(Math.pow(this._centerPoint[0] - point.position[0], 2) + Math.pow(this._centerPoint[1] - point.position[1], 2));
    let angleStep = 2 * Math.PI / this.segmentNo;
    let coordinates = [];

    for (let i = 0; i < this.segmentNo; i++) {
      coordinates.push([this._centerPoint[0] + radius * Math.sin(angleStep * i), this._centerPoint[1] + radius * Math.cos(angleStep * i)]);
    }

    return [coordinates];
  }

}

/**
 * Utility class to save and restore sets of states. Used for undo/redo functions.
 */
class StateManager {
  /**
   * @param maxStates - max number of stored states
   */
  constructor(maxStates = 256) {
    this._maxStates = maxStates;
    this.clear();
  }
  /**
   * Clears all stored states.
   */


  clear() {
    this._states = [];
    this._activeState = -1;
  }
  /**
   * Saves the given state and makes it active.
   * @param state
   */


  setState(state) {
    let index = this._activeState + 1;
    this._states[index] = state;

    this._states.splice(index + 1, this._states.length);

    this._trimStates();

    this._activeState = this._states.length - 1;
  }
  /**
   * Returns current state.
   */


  getCurrentState() {
    return this._states[this._activeState] === undefined ? null : this._states[this._activeState];
  }
  /**
   * Returns previous state and makes it active. If there is no previous state, returns null.
   */


  undo() {
    if (this._activeState <= 0) return null;
    return this._states[--this._activeState];
  }
  /**
   * Returns next state and makes it active. If there is no next state, returns null.
   */


  redo() {
    if (this._activeState === this._states.length - 1) return null;
    return this._states[++this._activeState];
  }

  _trimStates() {
    while (this._states.length > this._maxStates) {
      this._states.shift();
    }
  }

}

/**
 * Control for editing point features. When activeFeature is set, the feature is becoming draggable.
 * @alias sGis.controls.PointEditor
 * @fires [[EditEvent]]
 */

class PointEditor extends Control {
  /**
   * @param map - map object the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    snappingProvider,
    snappingSymbol,
    isActive = false,
    activeLayer
  } = {}) {
    super(map, {
      useTempLayer: true,
      snappingProvider,
      snappingSymbol,
      activeLayer
    });
    this._activeFeature = null;
    this.ignoreEvents = false;
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this._handleDragStart = this._handleDragStart.bind(this);
    this._handleDrag = this._handleDrag.bind(this);
    this._handleDragEnd = this._handleDragEnd.bind(this);
    this.isActive = isActive;
  }

  _activate() {
    if (!this._activeFeature) return;

    this._activeFeature.on(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.on(DragEvent.type, this._handleDrag);

    this._activeFeature.on(DragEndEvent.type, this._handleDragEnd);
  }

  _deactivate() {
    if (!this._activeFeature) return;

    this._activeFeature.off(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.off(DragEvent.type, this._handleDrag);

    this._activeFeature.off(DragEndEvent.type, this._handleDragEnd);
  }
  /**
   * Point to drag. If set to null, the control is deactivated.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    this.deactivate();
    this._activeFeature = feature;
    if (feature) this.activate();
  }

  _handleDragStart(event) {
    if (this.ignoreEvents || !this._activeFeature) return;
    let dragStartEvent = event;
    dragStartEvent.draggingObject = this._activeFeature;
    event.stopPropagation();
  }

  _handleDrag(event) {
    if (!this._activeFeature) return;
    let dragEvent = event;
    this._activeFeature.position = dragEvent.point.position;
    if (this.activeLayer) this.activeLayer.redraw();

    this._unsnap();

    this._debouncedSnappingHandle(dragEvent);
  }

  _snappingHandle(event) {
    if (!this._activeFeature) return;

    const snappingResult = this._snap(event.point.position, event.browserEvent.altKey);

    Promise.resolve(snappingResult).then(point => {
      if (!this._activeFeature) return;
      this._activeFeature.position = point || event.point.position;
      if (this.activeLayer) this.activeLayer.redraw();
    });
  }

  _handleDragEnd() {
    this.fire(new EditEvent());
  }

}

/**
 * Snaps to the given base point.
 */

const emptySnapping = ({
  position
}) => {
  return position;
};
/**
 * Snaps to the closest point among given relevant points and contour vertexes.
 */

const vertexSnapping = ({
  position,
  data,
  snappingDistance
}) => {
  let minSqDist = snappingDistance * snappingDistance;
  let snappingPoint = null;
  data.points.forEach(point => {
    const currSqDist = squareDistance(position, point);

    if (currSqDist < minSqDist) {
      snappingPoint = point;
      minSqDist = currSqDist;
    }
  });
  return snappingPoint;
};
/**
 * Snaps to the closest point on any edge given in data.lines parameter.
 */

const lineSnapping = ({
  position,
  data,
  snappingDistance
}) => {
  let snappingPoint = null;
  let currDistanceSq = snappingDistance * snappingDistance;
  data.lines.forEach(contour => {
    for (let i = 1; i < contour.length; i++) {
      const projectedPoint = pointToLineProjection(position, [contour[i - 1], contour[i]]);
      let minX = Math.min(contour[i - 1][0], contour[i][0]);
      let maxX = Math.max(contour[i - 1][0], contour[i][0]);
      let minY = Math.min(contour[i - 1][1], contour[i][1]);
      let maxY = Math.max(contour[i - 1][1], contour[i][1]);
      if (projectedPoint[0] < minX || projectedPoint[0] > maxX || projectedPoint[1] < minY || projectedPoint[1] > maxY) continue;
      const distanceSq = squareDistance(projectedPoint, position);

      if (distanceSq < currDistanceSq) {
        currDistanceSq = distanceSq;
        snappingPoint = projectedPoint;
      }
    }
  });
  return snappingPoint;
};
/**
 * Snaps to a closest middle point on the lines given in data.lines parameter.
 */

const midPointSnapping = ({
  position,
  data,
  snappingDistance
}) => {
  let snappingPoint = null;
  let currDistanceSq = snappingDistance * snappingDistance;
  data.lines.forEach(contour => {
    for (let i = 1; i < contour.length; i++) {
      const midX = (contour[i - 1][0] + contour[i][0]) / 2;
      const midY = (contour[i - 1][1] + contour[i][1]) / 2;
      const distanceSq = squareDistance([midX, midY], position);

      if (distanceSq < currDistanceSq) {
        currDistanceSq = distanceSq;
        snappingPoint = [midX, midY];
      }
    }
  });
  return snappingPoint;
};
/**
 * In case a contour is being edited, this method takes two edges in the active contour adjusted to the active point
 * (that is point being edited), and then tries to find such a snapping point so that one of the edges (or both of them)
 * are either vertical or horizontal.
 */

const axisSnapping = ({
  position,
  data,
  snappingDistance,
  activeContour,
  activeIndex = -1,
  isEnclosed = false
}) => {
  if (!activeContour || activeIndex < 0 || activeContour.length < 2) return null;
  const lines = [];
  const lastIndex = activeContour.length - 1;

  if (activeIndex === 0 && isEnclosed) {
    lines.push([activeContour[0], activeContour[lastIndex]]);
  }

  if (activeIndex !== 0) {
    lines.push([activeContour[activeIndex], activeContour[activeIndex - 1]]);
  }

  if (activeIndex !== lastIndex) {
    lines.push([activeContour[activeIndex], activeContour[activeIndex + 1]]);
  }

  if (activeIndex === lastIndex && isEnclosed) {
    lines.push([activeContour[lastIndex], activeContour[0]]);
  }

  const basePoints = [];

  for (let i = 0; i < lines.length; i++) {
    for (let axis = 0; axis < 2; axis++) {
      let projection = [lines[i][axis][0], lines[i][(axis + 1) % 2][1]];

      if (Math.abs(projection[0] - position[0]) < snappingDistance && Math.abs(projection[1] - position[1]) < snappingDistance) {
        basePoints[(axis + 1) % 2] = lines[i][1][(axis + 1) % 2];
        break;
      }
    }
  }

  if (basePoints.length > 0) {
    return [basePoints[0] === undefined ? position[0] : basePoints[0], basePoints[1] === undefined ? position[1] : basePoints[1]];
  }

  return null;
};
/**
 * In case a contour is being edited, this method takes two edges in the active contour adjusted to the active point
 * (that is point being edited), and then tries to find such a snapping point so that one of the edges (or both of them)
 * is orthogonal to its neighbours or each other.
 */

const orthogonalSnapping = ({
  position,
  data,
  snappingDistance,
  activeContour,
  activeIndex = -1,
  isEnclosed = false
}) => {
  if (!activeContour || activeIndex < 0 || activeContour.length < 3) return null;
  const lines = [];
  const contourLength = activeContour.length;

  if (isEnclosed) {
    lines.push([activeContour[(activeIndex + 1) % contourLength], activeContour[(activeIndex + 2) % contourLength]]);
    lines.push([activeContour[(contourLength + activeIndex - 1) % contourLength], activeContour[(contourLength + activeIndex - 2) % contourLength]]);
  } else {
    if (activeIndex >= 2) lines.push([activeContour[activeIndex - 1], activeContour[activeIndex - 2]]);
    if (activeIndex <= contourLength - 3) lines.push([activeContour[activeIndex + 1], activeContour[activeIndex + 2]]);
  }

  let basePoint = position;

  for (let i = 0; i < lines.length; i++) {
    let projection = pointToLineProjection(basePoint, lines[i]);
    let dx = projection[0] - lines[i][0][0];
    let dy = projection[1] - lines[i][0][1];

    if (Math.abs(dx) < snappingDistance && Math.abs(dy) < snappingDistance) {
      basePoint = [basePoint[0] - dx, basePoint[1] - dy];
      let direction = i === 0 ? 1 : -1;
      let nextPoint = isEnclosed ? activeContour[(contourLength + activeIndex + direction) % contourLength] : activeContour[activeIndex + direction];
      let prevPoint = isEnclosed ? activeContour[(contourLength + activeIndex - direction) % contourLength] : activeContour[activeIndex - direction];

      if (nextPoint && prevPoint) {
        projection = pointToLineProjection(prevPoint, [activeContour[activeIndex], nextPoint]);

        if (Math.abs(projection[0] - basePoint[0]) < snappingDistance && Math.abs(projection[1] - basePoint[1]) < snappingDistance) {
          basePoint = projection;
        }
      }
    }
  }

  if (basePoint[0] === position[0] && basePoint[1] === position[1]) return null;
  return basePoint;
};

/**
 * Base functionality for snapping based on selected [[SnappingMethod]]s and snapping distance.
 */

class SnappingProviderBase {
  /**
   * @param map - working map of the control that uses snapping.
   * @param __namedParams - snapping parameters.
   */
  constructor(map, {
    snappingDistance = 7,
    snappingMethods = [vertexSnapping, midPointSnapping, lineSnapping, orthogonalSnapping, axisSnapping]
  } = {}) {
    this._map = map;
    this.snappingDistance = snappingDistance;
    this.snappingMethods = snappingMethods;
  }

  getSnappingPoint(position, activeContour, activeIndex, isEnclosed) {
    const data = this._getSnappingData(position);

    const snappingDistance = this.snappingDistance * this._map.resolution;

    for (let i = 0; i < this.snappingMethods.length; i++) {
      const snappingPoint = this.snappingMethods[i]({
        position,
        data,
        snappingDistance,
        activeContour,
        activeIndex,
        isEnclosed
      });
      if (snappingPoint) return snappingPoint;
    }

    return null;
  }

}

/**
 * Provides snapping to points, lines and polygons in a [[FeatureLayer]].
 */

class FeatureLayerSnappingProvider extends SnappingProviderBase {
  /**
   * @param map - working map of the control that uses snapping.
   * @param layer - the layer that contains the features to be snapped to.
   * @param params - snapping parameters.
   */
  constructor(map, layer, params = {}) {
    super(map, params);
    this._layer = layer;
  }

  _getSnappingData(point) {
    const currentPoint = new Point$1(point, this._map.crs);
    let bbox = Bbox.fromPoint(currentPoint, this.snappingDistance * this._map.resolution);

    let features = this._layer.getFeatures(bbox, this._map.resolution);

    let snappingData = {
      points: [],
      lines: []
    };
    features.forEach(feature => {
      if (feature instanceof PointFeature) {
        snappingData.points.push(feature.position);
      } else if (feature instanceof Poly) {
        FeatureLayerSnappingProvider.setPolyData(feature, snappingData);
      }
    });
    snappingData.points = snappingData.points.filter(dataPoint => !currentPoint.equals(new Point$1(dataPoint, this._map.crs)));
    snappingData.lines = snappingData.lines.filter(line => {
      const lineStart = new Point$1(line[0], this._map.crs);
      const lineEnd = new Point$1(line[1], this._map.crs);
      return !(currentPoint.equals(lineStart) || currentPoint.equals(lineEnd));
    });
    return snappingData;
  }
  /**
   * For the given snapping data object, it adds the snapping lines
   * @param poly - line or polygon to be snapped to
   * @param data - snapping data to modify
   */


  static setPolyData(poly, data) {
    const isPolygon = poly instanceof Polygon;
    poly.rings.forEach(ring => {
      ring.forEach(point => {
        data.points.push(point);
      });

      for (let i = 1; i < ring.length; i++) {
        data.lines.push([ring[i - 1], ring[i]]);
      }

      if (isPolygon) data.lines.push([ring[ring.length - 1], ring[0]]);
    });
  }

  clone() {
    return new FeatureLayerSnappingProvider(this._map, this._layer, {
      snappingDistance: this.snappingDistance,
      snappingMethods: this.snappingMethods
    });
  }

}

/**
 * This provider allows to find snapping points on a single polygon or polyline.
 */

class PolySnappingProvider extends SnappingProviderBase {
  /**
   * @param map - working map of the control that uses snapping.
   * @param options - snapping parameters.
   */
  constructor(map, _a = {}) {
    var {
      feature = null
    } = _a,
        params = __rest(_a, ["feature"]);

    super(map, params);
    this.feature = feature;
  }

  _getSnappingData(point) {
    let snappingData = {
      points: [],
      lines: []
    };
    if (this.feature) FeatureLayerSnappingProvider.setPolyData(this.feature, snappingData);
    return snappingData;
  }

  clone() {
    const result = new PolySnappingProvider(this._map, {
      snappingDistance: this.snappingDistance,
      snappingMethods: this.snappingMethods
    });
    result.feature = this.feature;
    return result;
  }

}

/**
 * Control for editing polyline and polygon features. When activeFeature is set, the feature becomes draggable.
 * If a vertex is dragged, the vertex position is changed. If a side is dragged, a new point is added to the side and
 * then being dragged. If inside area of the polygon is dragged, the whole polygon will change position.
 * @alias sGis.controls.PolyEditor
 * @fires [[EditEvent]]
 * @fires [[ChangeEvent]]
 */

class PolyEditor extends Control {
  /**
   * @param map - map object the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, _a = {}) {
    var {
      isActive = false,
      onFeatureRemove = null,
      hoverSnappingProvider
    } = _a,
        controlParams = __rest(_a, ["isActive", "onFeatureRemove", "hoverSnappingProvider"]);

    super(map, Object.assign({
      useTempLayer: true
    }, controlParams));
    /** Distance from a vertex in pixels that will be considered as inside of the vertex. If the cursor is in this range from */

    this.vertexSize = 7;
    /** If user tries to remove the last point of the feature, the control will not remove it but will call this callback */

    this.onFeatureRemove = null;
    /** If set to false it will be not possible to change the shape of the feature. */

    this.vertexChangeAllowed = true;
    /** If set to false it will be not possible to move the feature as whole. */

    this.featureDragAllowed = true;
    this.ignoreEvents = false;
    this._activeRing = null;
    this._activeIndex = null;
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this.onFeatureRemove = onFeatureRemove;
    this._handleMousemove = this._handleMousemove.bind(this);
    this._handleDragStart = this._handleDragStart.bind(this);
    this._handleDrag = this._handleDrag.bind(this);
    this._handleDragEnd = this._handleDragEnd.bind(this);
    this._handleDblClick = this._handleDblClick.bind(this);

    if (hoverSnappingProvider === undefined) {
      this.hoverSnappingProvider = new PolySnappingProvider(map, {
        snappingMethods: [vertexSnapping, lineSnapping]
      });
    }

    this.isActive = isActive;
  }

  _activate() {
    if (!this._activeFeature) return;
    this.map.on(sGisMouseMoveEvent.type, this._handleMousemove);

    this._activeFeature.on(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.on(DragEvent.type, this._handleDrag);

    this._activeFeature.on(DragEndEvent.type, this._handleDragEnd);

    this._activeFeature.on(sGisDoubleClickEvent.type, this._handleDblClick);

    this.hoverSnappingProvider.feature = this._activeFeature;
  }

  _deactivate() {
    if (!this._activeFeature) return;
    this.map.off(sGisMouseMoveEvent.type, this._handleMousemove);

    this._activeFeature.off(DragStartEvent.type, this._handleDragStart);

    this._activeFeature.off(DragEvent.type, this._handleDrag);

    this._activeFeature.off(DragEndEvent.type, this._handleDragEnd);

    this._activeFeature.off(sGisDoubleClickEvent.type, this._handleDblClick);
  }
  /**
   * Feature to edit. If set to null, the control is deactivated.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    this.deactivate();
    this._activeFeature = feature;
    if (feature) this.activate();
  }

  _handleMousemove(event) {
    if (this.ignoreEvents) {
      this._unsnap();

      return;
    }

    this._snap(event.point.position, event.browserEvent.altKey, undefined, undefined, undefined, this.hoverSnappingProvider);
  }

  _getProjectedPoint(position, fromCrs) {
    return new Point$1(position, fromCrs).projectTo(this.map.crs).position;
  }

  _handleDragStart(event) {
    if (this.ignoreEvents || !this.vertexChangeAllowed && !this.featureDragAllowed) return;

    let [ringIndex, pointIndex] = this._getTargetPoint(event.point);

    if (ringIndex === null) {
      [ringIndex, pointIndex] = this._getTargetEdge(event.point);

      if (ringIndex !== null) {
        pointIndex++;

        this._activeFeature.insertPoint(ringIndex, pointIndex, event.point.position);
      }
    }

    this._activeRing = ringIndex;
    this._activeIndex = pointIndex;

    if (this._activeRing !== null || this.featureDragAllowed) {
      event.draggingObject = this._activeFeature;
      event.stopPropagation();
    }
  }

  _getTargetPoint(point) {
    if (!this.activeFeature) return [null, null];
    let closestDistanceSq = this.vertexSize * this.vertexSize * this.map.resolution * this.map.resolution;
    let closestRingIndex = null;
    let closestPointIndex = null;
    this.activeFeature.rings.forEach((ring, ringIndex) => {
      ring.forEach((coord, pointIndex) => {
        let projected = this._getProjectedPoint(coord, this.map.crs);

        let distanceSq = squareDistance(projected, point.position);

        if (distanceSq < closestDistanceSq) {
          closestDistanceSq = distanceSq;
          closestRingIndex = ringIndex;
          closestPointIndex = pointIndex;
        }
      });
    });
    return [closestRingIndex, closestPointIndex];
  }

  _getTargetEdge(point) {
    if (!this.activeFeature) return [null, null];
    let closestDistanceSq = this.vertexSize * this.vertexSize * this.map.resolution * this.map.resolution;
    let closestRingIndex = null;
    let closestEdgeIndex = null;
    this.activeFeature.rings.forEach((ring, ringIndex) => {
      ring.forEach((coord, pointIndex) => {
        let projectedA = this._getProjectedPoint(coord, this.map.crs);

        let projectedB = this._getProjectedPoint(pointIndex === ring.length - 1 ? ring[0] : ring[pointIndex + 1], this.map.crs);

        let distanceSq = pointToLineDistanceSquare(point.position, [projectedA, projectedB]);

        if (distanceSq < closestDistanceSq) {
          closestDistanceSq = distanceSq;
          closestRingIndex = ringIndex;
          closestEdgeIndex = pointIndex;
        }
      });
    });
    return [closestRingIndex, closestEdgeIndex];
  }

  _handleDrag(event) {
    if (this._activeRing === null) return this._handleFeatureDrag(event);

    this._activeFeature.setPoint(this._activeRing, this._activeIndex, event.point.position);

    this._activeFeature.redraw();

    if (this.activeLayer) this.activeLayer.redraw();
    this.fire(new ChangeEvent(this._activeRing, this._activeIndex));

    this._unsnap();

    this._debouncedSnappingHandle(event, this._activeRing, this._activeIndex);
  }

  _snappingHandle(event, activeRing, activeIndex) {
    if (!this._activeFeature) return;

    const snappingResult = this._snap(event.point.position, event.browserEvent.altKey, this._activeFeature.rings[activeRing], activeIndex, this._activeFeature instanceof Polygon);

    Promise.resolve(snappingResult).then(point => {
      if (!this._activeFeature) {
        return;
      }

      this._activeFeature.setPoint(activeRing, activeIndex, point || event.point.position);

      this._activeFeature.redraw();

      if (this.activeLayer) this.activeLayer.redraw();
      this.fire(new ChangeEvent(activeRing, activeIndex));
    });
  }

  get isDraggingVertex() {
    return this._activeRing !== null;
  }

  _handleDragEnd() {
    this._activeRing = null;
    this._activeIndex = null;
    this.fire(new EditEvent());
  }

  _handleFeatureDrag(event) {
    move([this._activeFeature], [-event.offset[0], -event.offset[1]], this.map.crs);

    this._activeFeature.redraw();

    if (this.activeLayer) this.activeLayer.redraw();
    this.fire(new ChangeEvent());
  }

  _handleDblClick(event) {
    if (this.ignoreEvents) return;

    let [ringIndex, pointIndex] = this._getTargetPoint(event.point);

    if (ringIndex === null) return;
    let ring = this._activeFeature.rings[ringIndex];
    let evPoint = event.point.projectTo(this._activeFeature.crs).position;
    let d1 = distance(evPoint, ring[pointIndex]);
    let nextIndex = (pointIndex + 1) % ring.length;
    let d2 = distance(evPoint, ring[nextIndex]);
    if (d2 < d1) pointIndex = nextIndex;

    if (ring.length > 2) {
      this._activeFeature.removePoint(ringIndex, pointIndex);

      this.fire('edit', {
        ringIndex: ringIndex,
        pointIndex: pointIndex
      });
    } else if (this._activeFeature.rings.length > 1) {
      this._activeFeature.removeRing(ringIndex);

      this.fire('edit', {
        ringIndex: ringIndex,
        pointIndex: pointIndex
      });
    } else if (this.onFeatureRemove) {
      this.onFeatureRemove();
    }

    this._unsnap();

    if (this.activeLayer) this.activeLayer.redraw();
    event.stopPropagation();
  }

}

function distance(p1, p2) {
  return Math.sqrt((p1[0] - p2[0]) * (p1[0] - p2[0]) + (p1[1] - p2[1]) * (p1[1] - p2[1]));
}

/**
 * Symbol of point drawn as a square.
 * @alias sGis.symbol.point.Square
 */

class SquareSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Size of the square. */

    this.size = 10;
    /**
     * Clockwise rotation of the square in radians.
     */

    this.angle = 0;
    this._offset = [0, 0];
    /** Color of the inner part of the square. Can be any valid css color string. */

    this.fillColor = 'black';
    /** Color of the outline of the square. Can be any valid css color string. */

    this.strokeColor = 'transparent';
    /** Width of the outline. */

    this.strokeWidth = 1;
    Object.assign(this, options);
  }
  /** Offset of the point from the feature position. If set to [0, 0], center of the circle will be at the position of the feature. */


  get offset() {
    return this._offset;
  }

  set offset(value) {
    // TODO: remove deprecated part after 2018
    let deprecated = value;

    if (deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._offset = [deprecated.x, deprecated.y];
    } else {
      this._offset = value;
    }
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    const position = feature.projectTo(crs).position;
    const pxPosition = [position[0] / resolution, -position[1] / resolution];
    const halfSize = this.size / 2;
    const offset = this.offset;
    const angle = -this.angle || 0;
    const [x, y] = [halfSize + offset[0], halfSize + offset[1]];
    const [cx, cy] = pxPosition;
    const coordinates = [[rotate$1(cx, cy, cx - x, cy - y, angle), rotate$1(cx, cy, cx - x, cy + y, angle), rotate$1(cx, cy, cx + x, cy + y, angle), rotate$1(cx, cy, cx + x, cy - y, angle)]];
    return [new PolyRender(coordinates, {
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      enclosed: true,
      fillStyle: FillStyle.Color
    })];
  }

}
registerSymbol(SquareSymbol, 'point.Square', ['size', 'offset', 'fillColor', 'strokeColor', 'strokeWidth', 'angle']);

/**
 * @event RotationStartEvent
 */

class RotationStartEvent extends sGisEvent {
  constructor() {
    super(RotationStartEvent.type);
  }

}
RotationStartEvent.type = 'rotationStart';
/**
 * @event RotationEndEvent
 */

class RotationEndEvent extends sGisEvent {
  constructor() {
    super(RotationEndEvent.type);
  }

}
RotationEndEvent.type = 'rotationEnd';
/**
 * @event ScalingStartEvent
 */

class ScalingStartEvent extends sGisEvent {
  constructor() {
    super(ScalingStartEvent.type);
  }

}
ScalingStartEvent.type = 'scalingStart';
/**
 * @event ScalingEndEvent
 */

class ScalingEndEvent extends sGisEvent {
  constructor() {
    super(ScalingEndEvent.type);
  }

}
ScalingEndEvent.type = 'scalingEnd';
/**
 * Control for modifying polylines or polygons as whole. When activeFeature is set, it shows points around the feature
 * dragging which one can scale or rotate the feature.
 * @alias sGis.controls.PolyTransform
 * @fires [[RotationStartEvent]]
 * @fires [[RotationEndEvent]]
 * @fires [[ScalingStartEvent]]
 * @fires [[ScalingEndEvent]]
 */

class PolyTransform extends Control {
  /**
   * @param map - map object the control will work with
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    activeLayer = null,
    isActive = false
  } = {}) {
    super(map, {
      activeLayer,
      useTempLayer: true
    });
    /** Symbol of the rotation handle. */

    this.rotationHandleSymbol = new PointSymbol({
      offset: [0, -30]
    });
    /** Symbol of the scaling handles. */

    this.scaleHandleSymbol = new SquareSymbol({
      fillColor: 'transparent',
      strokeColor: 'black',
      strokeWidth: 2,
      size: 7
    });
    /** Distance in pixels between scaling handles and feature bbox. */

    this.scaleHandleOffset = 12;
    /** If set to false the rotation handle will not be displayed. */

    this.enableRotation = true;
    /** If set to false the scaling handle will not be displayed. */

    this.enableScaling = true;
    this.ignoreEvents = false;
    this._handleRotationStart = this._handleRotationStart.bind(this);
    this._handleRotation = this._handleRotation.bind(this);
    this._handleRotationEnd = this._handleRotationEnd.bind(this);
    this._handleScalingEnd = this._handleScalingEnd.bind(this);
    this.isActive = isActive;
  }
  /**
   * Feature to edit. If set to null, the control is deactivated.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    this.deactivate();
    this._activeFeature = feature;
    if (feature) this.activate();
  }
  /**
   * Updates position of the editor handles.
   */


  update() {
    if (this._activeFeature) this._updateHandles();
  }

  _activate() {
    if (!this._activeFeature) return;

    this._setHandles();
  }

  _deactivate() {
    return;
  }

  _setHandles() {
    if (this.enableRotation) this._setRotationHandle();
    if (this.enableScaling) this._setScaleHandles();
  }

  _setRotationHandle() {
    this._rotationHandle = new PointFeature([0, 0], {
      crs: this.map.crs,
      symbol: this.rotationHandleSymbol
    });

    this._updateRotationHandle();

    this._rotationHandle.on(DragStartEvent.type, this._handleRotationStart);

    this._rotationHandle.on(DragEvent.type, this._handleRotation);

    this._rotationHandle.on(DragEndEvent.type, this._handleRotationEnd);

    this._tempLayer.add(this._rotationHandle);
  }

  getScaleHandleSymbol(index) {
    return this.scaleHandleSymbol.clone();
  }

  _setScaleHandles() {
    this._scaleHandles = [];

    for (let i = 0; i < 9; i++) {
      if (i === 4) continue;
      let symbol = this.getScaleHandleSymbol(i);
      let xk = i % 3 - 1;
      let yk = 1 - Math.floor(i / 3);
      symbol.offset = [this.scaleHandleOffset * xk, this.scaleHandleOffset * yk];
      this._scaleHandles[i] = new PointFeature([0, 0], {
        symbol: symbol,
        crs: this.map.crs
      });

      this._scaleHandles[i].on(DragStartEvent.type, this._handleScalingStart.bind(this, i));

      this._scaleHandles[i].on(DragEvent.type, this._handleScaling.bind(this, i));

      this._scaleHandles[i].on(DragEndEvent.type, this._handleScalingEnd);
    }

    this._tempLayer.add(this._scaleHandles);

    this._updateScaleHandles();
  }

  _handleRotationStart(event) {
    if (this.ignoreEvents) return;
    this._rotationBase = this._activeFeature.bbox.projectTo(this.map.crs).center;
    event.draggingObject = this._rotationHandle;
    event.stopPropagation();
    this.fire(new RotationStartEvent());
  }

  _handleRotation(event) {
    let xPrev = event.point.x + event.offset[0];
    let yPrev = event.point.y + event.offset[1];
    let alpha1 = xPrev === this._rotationBase[0] ? Math.PI / 2 : Math.atan2(yPrev - this._rotationBase[1], xPrev - this._rotationBase[0]);
    let alpha2 = event.point.x === this._rotationBase[0] ? Math.PI / 2 : Math.atan2(event.point.y - this._rotationBase[1], event.point.x - this._rotationBase[0]);
    let angle = alpha2 - alpha1;
    rotate([this._activeFeature], angle, this._rotationBase, this.map.crs);
    if (this.activeLayer) this.activeLayer.redraw();

    this._updateHandles();
  }

  _handleRotationEnd() {
    this.fire(new RotationEndEvent());
  }

  _updateHandles() {
    if (this.enableRotation) this._updateRotationHandle();
    if (this.enableScaling) this._updateScaleHandles();

    this._tempLayer.redraw();
  }

  _updateRotationHandle() {
    let bbox = this._activeFeature.bbox.projectTo(this.map.crs);

    this._rotationHandle.position = [(bbox.xMin + bbox.xMax) / 2, bbox.yMax];
  }

  _updateScaleHandles() {
    let bbox = this._activeFeature.bbox.projectTo(this.map.crs);

    let xs = [bbox.xMin, (bbox.xMin + bbox.xMax) / 2, bbox.xMax];
    let ys = [bbox.yMin, (bbox.yMin + bbox.yMax) / 2, bbox.yMax];

    for (let i = 0; i < 9; i++) {
      if (i === 4) continue;
      this._scaleHandles[i].position = [xs[i % 3], ys[Math.floor(i / 3)]];
    }
  }

  _handleScalingStart(index, event) {
    if (this.ignoreEvents) return;
    event.draggingObject = this._scaleHandles[index];
    event.stopPropagation();
    this.fire(new ScalingStartEvent());
  }

  _handleScaling(index, event) {
    const MIN_SIZE = 10;
    let xIndex = index % 3;
    let yIndex = Math.floor(index / 3);
    let baseX = xIndex === 0 ? 2 : xIndex === 2 ? 0 : 1;
    let baseY = yIndex === 0 ? 2 : yIndex === 2 ? 0 : 1;
    let basePoint = this._scaleHandles[baseX + 3 * baseY].position;

    let bbox = this._activeFeature.bbox.projectTo(this.map.crs);

    let resolution = this.map.resolution;
    let tolerance = MIN_SIZE * resolution;
    let width = bbox.width;
    let xScale = baseX === 1 ? 1 : (width + (baseX - 1) * event.offset[0]) / width;
    if (width < tolerance && xScale < 1) xScale = 1;
    let height = bbox.height;
    let yScale = baseY === 1 ? 1 : (height + (baseY - 1) * event.offset[1]) / height;
    if (height < tolerance && yScale < 1) yScale = 1;
    scale([this._activeFeature], [xScale, yScale], basePoint, this.map.crs);
    if (this.activeLayer) this.activeLayer.redraw();

    this._updateHandles();
  }

  _handleScalingEnd() {
    this.fire(new ScalingEndEvent());
  }

}

const PIN_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEQAAABECAYAAAA4E5OyAAAOVUlEQVR4Xu1cZ3RU1Rb+9kwyLWEm' + 'IYkhoUgQIgFUXKEEgbwgRUBAeIBSFAuIir0BPkQfCIrYQUFdKAj6RBBBH0VANFI1NEGaCASlhEDapGeSmfPWvpmUSbtn7gz4frjXyo/k7rPPPt89' + 'Z9/dTgh/kwcCdKXwEEKYAHQF0ANAOwCxAFoCCAJgdutRBKAAwGkAxwEcAbANQAoRFV8JXS8rIEIIC4AhAMYA6AfAqHFRJQA2AvgcwDdEVKhRjuqw' + 'ywKIECISwGMAJgEIYS0EXMgtPYHMkl+Q7TiMgrIzKCg7izJXAZyC1wvoyYgAXRCCApohOKAFQgztEGa8EdbAa0DQVSwmB8ACAPOIKF11hV4y+BUQ' + 'IQRv/akAJgPgI4IcxxGcKVyPs4WbUeLM9FK9cnajPgzNLP3Q3DJAAclNfIRe5R8i4qPmF/IbIEKIvgAWAWjBml0o2obf8z5RdoQ/KczYEW0a3Y0m' + '5p4VYv8EMIGINvtjHp8BEUIEApgN4BkAlFt6Egez5yKjZK8/9KtXRrgxHteHTlaOk3IigdcBTCOiUl8m9gkQIYQNwJcA+rCN+D13CY7a31fsxZUg' + 'titxtgfRxnpPhY35DsAIIrJrnV8zIG7DyZb/BocrBymZU5FRvEerHj6NCzd1QpewOTDoFPt9gL9oRHRRi1BNgLh3xnYAHQrLzmPnpUeQX8ZH+a8j' + '/irdFPEuLAHRrMQh9ne07BSvAXE7WN8D6FZQdg7bL96PIqeml+F39M6l5iIpejHat+7OsncB6EVE5d90SdICyPsAHihxZmHrxfsUX+L/gVJPn0Z2' + 'VhaK7GZM6vUzYpp3YLU+IKIHvdHPK0CEEOxxfibgxLaLE5FVwsfVd9KT4rLAKbz3zoUQOHXqFOz2KjtamBGOmWNOwWIOZrFjiIg9XCmSBkQIEQ7g' + 'GICwQzlv40Tep1IT1GQK0FkQZU7CVaYEhATGKV6pjvjLDbhEqbLjckqP4mLxT0grSkaZq34vvS4wKubTZXTHv8dzGAT2Bq8lIimv0BtA2Okan+X4' + 'FVvT73N/+uUxYW8z1novWgYNRcWOUBvNO+Z0wRocz11cy8t1uVxITU312BnV5QkBdI9YhP7dWVd8REQT1Obj51KACCHYXz4k4KTk9Ltgd3AgKk8x' + 'wSPQ3vaoEqdoIY53DtvnIzWfXR6AwTh58iTy8vIaFFeUZcOssedhNJjZcetARBw9N0iygCwFcNeZgnXYm/WimszK53wU4hvPQFMLB7q+07nCTdid' + 'MR2/nzimCkbFbM3LHsb9w+fzr0uJ6G41LVQBEUJEATgj4NJ/f+EO5JWmqslUnusoAN3C5yHC1KVe/vSMP3E2ay8KSi4oPEHGJmjWOB6R4Uo4VCel' + 'HP0C647fCTbsMlSUE4yXx2YgMMDAA5oTUVpD42QAeRbA3PTindh1iSN6OYoPm4nmloG1mB2OEny0agqOZi2FLToHVBnVl7MKF2A/H4K4xuMwfvir' + 'MBhqp1C+3j4bezOnyynCrnTQHAzvwwE4JhPRa74CwuHqDXsyp+FsIXvq6nR10BDc2PiFWox7f/0ey7aPhDU6W10IgNzzobirx0rEX3dzLf5Xl/dE' + 'gXmHlBznpTjMnnCYeQ8QUUfNgAghmgBIcwkH1p3rVZnIaUhgoM6KvlGrYdBx3FdFW3Yux8bT42AKKpNaRAVTcX4AbolZit43jfIYl5OXhte+bQW9' + 'Qd0RdZYSnhuQieAgJdaJIqLyM1oHNXhkhBCjAfyHQ/ntFx+QWkhb20S0tU704D1+aj8W7UiAyaotMi/ODcSE7j8httWNHnLfWzMK6bRCSq+EsAUY' + '2ENxWkcT0XKtgLzDqcBjuR/imP1D1Yk5HO8fvUHJcFWnZxdeA3O0nDGub5Ki8zF47aGTHo8vZqVi/tY2IFJPNzTKH4xnx37N4zn1+LhWQL4FcEtK' + '5mScL+R4rmGKMHVG94iFHkzf//Q5ktPHqg2Vep4U+RluTuBNW0XTl8WArH+ojndmxWL2vexoYyMR9dcKyCkAMfy55UyYGrWzTUKsVfEMK+nFRV0h' + 'InarDZV6Tpc6Y8aEnz145385CpcC1Y+Nw27DnHGKMU8lolZaAeEMt40NaqmrYa+QJ+ga/gaizP/wmGvKUiuMtnypBasxFduDMXdcrgfb6h/mYn8u' + '57UbJqcjELNHKgbYTkSKda2L1Iwqu7z4+kwXqbRgUuSnCDG0rTaPwPTV+lq+hpry9T1nH+WlYexfVam965fV2PDHcHWRgjBzaLkzR0T1rlsKkDVn' + 'OqlPCKB3kxVoFFi1Gx3OXMxaW+/LkJJZk+n5QTkw6K2Vf045uAFrU2+VkjXztnLje8UA6RX5GWyGayuV42Tzi2sCAVI2mu+kvGX2Y6re47a9q7D5' + '7EhV2UQ6zBhS7gP5AohiQ9aeS2wwL1GhTUL4m2hiTvRQ7vnPw6CzyHmmaqtyFYZg1ugsD7bP18/A0dIZakPhKgvErOG+25ATAK7ZlDYEnExWo3a2' + 'h5WcR3V6Y2Uf2A3qn2w12fzc5rgZT4/kSkMVvbJ4MIoar1Md7ixqhNmjlKzaSSJqXd8ANRvCK+m1/eJEZJTsU52UI9vuEVx2raKjf2zB8gP9wNkt' + 'n4gIo2/YhLire3uIeWphJIKjL6mLzmuBmXdyUwF+IKLawZFbghogHwG4b3/WTPxR8I3qpER69I/+FkZdqAfv3FVdkB/gW80muKwTJg9P8ZB76LcU' + 'LD+SAF2NiLkuRS2FN2HqaK6c4GMiGq91hzwM4N1T+SuU8qQM1XVs7AXn8Pbm9nCSpw8hI4959MKKJ/oehi2oqceQ6Qt6g5r+ICWmjelR3HULRyJ4' + 'hIje0wpIAtc3sh2H8GP6PVITc5TbN2oNAnWNPPhT01OwdFc/r0FhMMZ124SYSM9E05Hf9mHJni4wBavHMazIyOvW47pWisfejYh+0gpIAGetBVzW' + '9ed6S3mrPFHL4GHoGDqt1pyZ+SexOHkocp2c2lSzKQSrvh3uTVqDsGCloF1JTqcTj78Vi8ZtJANGlxkzhuVx/Ze3aBgR1ZuDkMmYfQVg2O7M53Cu' + 'UL7joHPYK2hq4Q4JT2LfZM+Jpdh2/E0U6U6gpMSzFmM0mmB2tUbP2KfQqfW46o0ylYJmzLsDzqtXSu1YZrKUdMLU2xX7s5qI/tnQQBlAODG7hMFg' + 'UGSJu4ESIt5GhLFzvUNKXNnILj6GwtLyRiBLYCRCTW1rGeXqAl7/8DFkhb6LAIOsJkBis3fQJ/5RHnA3EXHCvF6SAYTLX+ku4bBsON8PpS75QI1B' + '6RT2cq2AT34pVZwOhwMvvjUG1Oorr8DQCTNeuC0bOjJwxSuSiBpcgCogrJIQQilD8JeGvzje0jWNxoBTA7IFqpryU/bswAfrxqJpR+87DKL0g/HQ' + 'ICUxtIyIxqnpLgsIR3e7ucy4OW2YhEGsPa1ZH4lrrePRImgQvy01vRRHbsfOrfhiyzTomu2CLUzNCNcWqdPpMSnxEK6yKfFVZyJSdYakAHHvkmQA' + '//DWuNZUk5PQTS19cJWpq1LbNQc0UQzn7t27kW3PRuqZX3DiwjakFexEVFw2yuvV2qgx9cATQ7by4GQi6iUjxRtAktjt5caYLWkjpPIjMgpwQUtP' + 'FiQnJ8NoBnR6mVHqPHpdAB5MPIBIWxwzJxHRj+qjJGu7FYKEEOsBDDiQ/QpS81fJyJfm2bdPPVaSFsatkOahmNCPPQZsIKLaFbN6hEnvEPex4S6U' + '/Q6XPWDLhREocfonrGfZ/gQkkGx4duApmAJC2QG7kYi4xUqKvALEDcocAFO4GXdvZu3qnNSsdTD5CxDODvaLfQ/d2yo1GG7qVU+4VtNHCyDcv86t' + 'Q61/znhGaWrxB/kLEG7Geaifkn/hXM713nY5ew2Ie5fwrYbtfHR+uDDaL013/gDEpIvA0wN/g1EfwkeFuxA9axYSb04TIG5Q/sUdzJw42nlpElzC' + 'u5ptTd18BSQwwIjRndajdaTydeWO5pcl1l+LxRdAOC3DWaNbubPnQDabFu3kCyA6nQ43t34NiXFPsgJrAdxGMvXNOtTVDIh7l3A9gMPIaxmQipYn' + 'LbBoBoQIHSLvxu1dP+ZpfwPQhUhjJkq2x6yhBQoh2gDYKeAKT8mYgrQiuQyWv45MTOgA3JP4X/Z2M9zJHzammsmnHVIxqxCCY/xkpyix7Mp4XFPP' + 'u5Yd0iykByYkflcRybI36nMR2S+AuI8P5+e+cYriwF0ZT3gNireANAvpifGJGzmC5qaTwUQk196ksnf8BogbFL5f96UWULwBpAYYfB1EvSQgeYj8' + 'Ckg1UFY6RbEhJWMyuFlPhmQAYS+0VVhf3HnTGt4ZDs4d+xMM1tPvgLhBYWdgjUuUWvdnv4QzBRwTNkxqgPCntX3UKAyPX8yt4Jws5k+rf9zkaqpd' + 'FkDcoHBD2EZARByxL1DasxuihgAxGAxIaPk0+sTN4nfIZbpbiGi/Gshanl82QNygxADgwmscB4NcAazPo60PELMpGLde9z6uj+aLGMrF5kFEJFl/' + '8B6SywqIGxTuz+Suv/5ZjoNgX6XYWbsWWxcgocEtMCZhFSKD4lkU97uN0nJLyhtYLjsgblA4D8Z3bJ/mi0e7+X5ejeJ5dUDYXsSEJ2FUlxUw6huz' + 'iDc45UBEcv3c3iBQg/eKAFLNgePepyUCzuCj9oU4nvtJZcK6AhCTyYJuMU8iKXYGe59cMriHiPybnmsAsCsKiHu3cAqcaxnXXypOwd6sF1DszFAy' + 'ZmHWqzEifhmaWvn/JeAggNuJiOOTK0ZXHBA3KHynjNsJHuXqHRvbzMxM9G0zr+Kq6Tz3EfH+zpmP0P0lgFQ7QtwttwQAX19jYmt7LxGptwT5uPD6' + 'hv+lgLh3C/8nCW7M4UrUeK0XkP2Fz18OiL8W4i85fwNSA8n/AV6gUZDNezugAAAAAElFTkSuQmCC';
const PIN_BACKGROUND = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEQAAABECAYAAAA4E5OyAAAACXBIWXMAABYlAAAW' + 'JQFJUiTwAAAKT2lDQ1BQaG90b3Nob3AgSUNDIHByb2ZpbGUAAHjanVNnVFPpFj333vRCS4iAlEtvUhUIIFJCi4AUkSYqIQkQSoghodkVUcERRUUE' + 'G8igiAOOjoCMFVEsDIoK2AfkIaKOg6OIisr74Xuja9a89+bN/rXXPues852zzwfACAyWSDNRNYAMqUIeEeCDx8TG4eQuQIEKJHAAEAizZCFz/SMB' + 'APh+PDwrIsAHvgABeNMLCADATZvAMByH/w/qQplcAYCEAcB0kThLCIAUAEB6jkKmAEBGAYCdmCZTAKAEAGDLY2LjAFAtAGAnf+bTAICd+Jl7AQBb' + 'lCEVAaCRACATZYhEAGg7AKzPVopFAFgwABRmS8Q5ANgtADBJV2ZIALC3AMDOEAuyAAgMADBRiIUpAAR7AGDIIyN4AISZABRG8lc88SuuEOcqAAB4' + 'mbI8uSQ5RYFbCC1xB1dXLh4ozkkXKxQ2YQJhmkAuwnmZGTKBNA/g88wAAKCRFRHgg/P9eM4Ors7ONo62Dl8t6r8G/yJiYuP+5c+rcEAAAOF0ftH+' + 'LC+zGoA7BoBt/qIl7gRoXgugdfeLZrIPQLUAoOnaV/Nw+H48PEWhkLnZ2eXk5NhKxEJbYcpXff5nwl/AV/1s+X48/Pf14L7iJIEyXYFHBPjgwsz0' + 'TKUcz5IJhGLc5o9H/LcL//wd0yLESWK5WCoU41EScY5EmozzMqUiiUKSKcUl0v9k4t8s+wM+3zUAsGo+AXuRLahdYwP2SycQWHTA4vcAAPK7b8HU' + 'KAgDgGiD4c93/+8//UegJQCAZkmScQAAXkQkLlTKsz/HCAAARKCBKrBBG/TBGCzABhzBBdzBC/xgNoRCJMTCQhBCCmSAHHJgKayCQiiGzbAdKmAv' + '1EAdNMBRaIaTcA4uwlW4Dj1wD/phCJ7BKLyBCQRByAgTYSHaiAFiilgjjggXmYX4IcFIBBKLJCDJiBRRIkuRNUgxUopUIFVIHfI9cgI5h1xGupE7' + 'yAAygvyGvEcxlIGyUT3UDLVDuag3GoRGogvQZHQxmo8WoJvQcrQaPYw2oefQq2gP2o8+Q8cwwOgYBzPEbDAuxsNCsTgsCZNjy7EirAyrxhqwVqwD' + 'u4n1Y8+xdwQSgUXACTYEd0IgYR5BSFhMWE7YSKggHCQ0EdoJNwkDhFHCJyKTqEu0JroR+cQYYjIxh1hILCPWEo8TLxB7iEPENyQSiUMyJ7mQAkmx' + 'pFTSEtJG0m5SI+ksqZs0SBojk8naZGuyBzmULCAryIXkneTD5DPkG+Qh8lsKnWJAcaT4U+IoUspqShnlEOU05QZlmDJBVaOaUt2ooVQRNY9aQq2h' + 'tlKvUYeoEzR1mjnNgxZJS6WtopXTGmgXaPdpr+h0uhHdlR5Ol9BX0svpR+iX6AP0dwwNhhWDx4hnKBmbGAcYZxl3GK+YTKYZ04sZx1QwNzHrmOeZ' + 'D5lvVVgqtip8FZHKCpVKlSaVGyovVKmqpqreqgtV81XLVI+pXlN9rkZVM1PjqQnUlqtVqp1Q61MbU2epO6iHqmeob1Q/pH5Z/YkGWcNMw09DpFGg' + 'sV/jvMYgC2MZs3gsIWsNq4Z1gTXEJrHN2Xx2KruY/R27iz2qqaE5QzNKM1ezUvOUZj8H45hx+Jx0TgnnKKeX836K3hTvKeIpG6Y0TLkxZVxrqpaX' + 'llirSKtRq0frvTau7aedpr1Fu1n7gQ5Bx0onXCdHZ4/OBZ3nU9lT3acKpxZNPTr1ri6qa6UbobtEd79up+6Ynr5egJ5Mb6feeb3n+hx9L/1U/W36' + 'p/VHDFgGswwkBtsMzhg8xTVxbzwdL8fb8VFDXcNAQ6VhlWGX4YSRudE8o9VGjUYPjGnGXOMk423GbcajJgYmISZLTepN7ppSTbmmKaY7TDtMx83M' + 'zaLN1pk1mz0x1zLnm+eb15vft2BaeFostqi2uGVJsuRaplnutrxuhVo5WaVYVVpds0atna0l1rutu6cRp7lOk06rntZnw7Dxtsm2qbcZsOXYBtuu' + 'tm22fWFnYhdnt8Wuw+6TvZN9un2N/T0HDYfZDqsdWh1+c7RyFDpWOt6azpzuP33F9JbpL2dYzxDP2DPjthPLKcRpnVOb00dnF2e5c4PziIuJS4LL' + 'Lpc+Lpsbxt3IveRKdPVxXeF60vWdm7Obwu2o26/uNu5p7ofcn8w0nymeWTNz0MPIQ+BR5dE/C5+VMGvfrH5PQ0+BZ7XnIy9jL5FXrdewt6V3qvdh' + '7xc+9j5yn+M+4zw33jLeWV/MN8C3yLfLT8Nvnl+F30N/I/9k/3r/0QCngCUBZwOJgUGBWwL7+Hp8Ib+OPzrbZfay2e1BjKC5QRVBj4KtguXBrSFo' + 'yOyQrSH355jOkc5pDoVQfujW0Adh5mGLw34MJ4WHhVeGP45wiFga0TGXNXfR3ENz30T6RJZE3ptnMU85ry1KNSo+qi5qPNo3ujS6P8YuZlnM1Vid' + 'WElsSxw5LiquNm5svt/87fOH4p3iC+N7F5gvyF1weaHOwvSFpxapLhIsOpZATIhOOJTwQRAqqBaMJfITdyWOCnnCHcJnIi/RNtGI2ENcKh5O8kgq' + 'TXqS7JG8NXkkxTOlLOW5hCepkLxMDUzdmzqeFpp2IG0yPTq9MYOSkZBxQqohTZO2Z+pn5mZ2y6xlhbL+xW6Lty8elQfJa7OQrAVZLQq2QqboVFoo' + '1yoHsmdlV2a/zYnKOZarnivN7cyzytuQN5zvn//tEsIS4ZK2pYZLVy0dWOa9rGo5sjxxedsK4xUFK4ZWBqw8uIq2Km3VT6vtV5eufr0mek1rgV7B' + 'yoLBtQFr6wtVCuWFfevc1+1dT1gvWd+1YfqGnRs+FYmKrhTbF5cVf9go3HjlG4dvyr+Z3JS0qavEuWTPZtJm6ebeLZ5bDpaql+aXDm4N2dq0Dd9W' + 'tO319kXbL5fNKNu7g7ZDuaO/PLi8ZafJzs07P1SkVPRU+lQ27tLdtWHX+G7R7ht7vPY07NXbW7z3/T7JvttVAVVN1WbVZftJ+7P3P66Jqun4lvtt' + 'Xa1ObXHtxwPSA/0HIw6217nU1R3SPVRSj9Yr60cOxx++/p3vdy0NNg1VjZzG4iNwRHnk6fcJ3/ceDTradox7rOEH0x92HWcdL2pCmvKaRptTmvtb' + 'Ylu6T8w+0dbq3nr8R9sfD5w0PFl5SvNUyWna6YLTk2fyz4ydlZ19fi753GDborZ752PO32oPb++6EHTh0kX/i+c7vDvOXPK4dPKy2+UTV7hXmq86' + 'X23qdOo8/pPTT8e7nLuarrlca7nuer21e2b36RueN87d9L158Rb/1tWeOT3dvfN6b/fF9/XfFt1+cif9zsu72Xcn7q28T7xf9EDtQdlD3YfVP1v+' + '3Njv3H9qwHeg89HcR/cGhYPP/pH1jw9DBY+Zj8uGDYbrnjg+OTniP3L96fynQ89kzyaeF/6i/suuFxYvfvjV69fO0ZjRoZfyl5O/bXyl/erA6xmv' + '28bCxh6+yXgzMV70VvvtwXfcdx3vo98PT+R8IH8o/2j5sfVT0Kf7kxmTk/8EA5jz/GMzLdsAAAAgY0hSTQAAeiUAAICDAAD5/wAAgOkAAHUwAADq' + 'YAAAOpgAABdvkl/FRgAAB7lJREFUeNrsnH9QVNcVx793Xdhll44GUbEzKNoWzQI6/go4ExHTGujYRg1MYtOpzsQ/mkAaK4nB/6haZ5pO80eTOGxm' + 'MmOaTuM/TabSzDBaBIV1tJUxbEQKnSWY8kcMYQwICyzD8u0fvkeXLOze3XcfvExyZt7M7tt7zj3nw333x7nvIkjiW/m/2GP9KIRQVhFJJ4BCAI8C' + '8ADIBZADwA0gTSs2BiAI4A6A/wDoBNAK4F9CiHGFvsT+ca5LQcUukgdI1pMcZ/IyTvK8ZsulAsicMZsBhOQKkqdJfkn18qVme4XlgZBMI3mC5BjN' + 'lzGSvyGZZkkgJHeT/JTzL5+S3G0ZICRTSP6e5BQXTqY0H1KMAhGxAo83ypBcDOCvAH5kkVGzEUCFEGIo2VEmaSBap3YBwEaLTSX8AB4XQvTPGxCt' + 'ZfgA5Ft0ftUB4NG5WkqsmG1JTrAarAYjHA7jxImT6OnpgeZbA0mH2jF59vJeWkwmJyf5i4OHmJLq4OqcNezr69N/8po6ypB8xmowQqEQ9z9ZzpRU' + 'x/T1SGERx8amp0I/MwUIyUySA1aDsW//kzNg6FdlVZVebIDkUjOAvG0lGMFgkHv37psVhn41NTfrxd9WCoSkZ4EnXlEwdu9+PCaMlFQH8ws2MBQK' + '6RM3jwwQ2VHmOABhhdFkdHQU+/btx+UrV+KW7e7uxjvv/Ama7zUy9uPOQ0iuBNAHYJHq4O7du4eBgQGMjo4CAFwuFzIzM5GRkTGnTmdnJ4p3luD+' + '/ftSdaxduxYdtz6G3W4PA8gWQnxmKB9C8pjqjvDNM2foycufs6l78vL55pkzenOPkgsXLzLV4Yz7yOhXfX29rnrMcB9Csl0VDL/fz4ING6UDKdiw' + 'kX6/f1Zbr7xSI23nqacP6GrthoCQzFIFo6WllRlLM6WD0K+HMpaypaU1yt7IyAhXrc6RsuFO/w6DwaCummWkU92loq/o7e1FeUUFhoeHE9YdGRlB' + 'eUUFent7Z9x3u92orj4qZWNiYgLXrl/Xv5bEKhsPSJEKIJWVVRgcHExaf3BwEJWVVVH3Dx08CIdDbrnia/XpH7cbAbLOKAyf7youNTUZhnqpqQk+' + '39UZ95YsWYIfPvaY3PL39m2pmOIByTUayLt/flfZMD2brR3FO6R0A4GAVEzxgGQYDaKlpVUZkNlsrV+/Xkr37t3PpGKKB2Sxig5Vlcxm67srV0p2' + 'zkGpmGwwUVRteMWyZ7PJhRAKhaTKmQpECCE9CsiIw+GISmuGQhNSuk6nUwmQIaNB5OXlKQPi8Xii7t25I/dIulwuqZjiARkwGkRZaakyID8uK4tO' + 'sfs/ltLNysqSiikekP8aDeLw4WeRkpJiGIbdbsfhw89G3f9HY6OU/upVq6RiigfE8BCRnZ2NyuefNwykqrIS2dnZUfmO9vZ2Kf3c3FypmOIBuami' + 'qZ86dRJbtmxJWn/z5s04depk1P3X33gjARub5GKKs9otUrXa7e//goVF2xNe7RYWbWd//xdR9rq7u+lyp0vb6enp0VWLjCz/7SSHlL2/MDbG6uqX' + '6ExzxQ3AmeZidfVLkdsJM/ZhSnbtkoaxbv3DuuqQFpOhBNEHqpPEgUCANTXHmZdfEOV8Xn4Ba2qOMxAIzKmfSHIoJdXBo0erddUPVGTMDpmZQQ+H' + 'wwyFQgyFQgyHw3HL/+7VVxN+7K5du66rH1QBJJ1k0AqbUi++eCRhGA978qZ3L0imG96GEEKMAHh/Ibce2trasKN4J+q83oR1n3vul/rH97VYjG92' + 'k9w6768ETU3R57vKp54+kFCGPfJatnwFh4eHdZNbZWK2Sy7S2kheAbDTrFZw48YNDA4N4ZOeT3Dzo5tobLyEvr4+QzZ/9cILSE9PB4DLQog2JRtV' + 'EVRLADSbBSTV4VRqb/myZejq+rcOpEQIcSWyhRhe/gshLuPBizJfC6mtrdVhNETCUNZCNLL5AD4C5B61hWohRUWFuNzcDJvNNglgkxCi46v9ppIE' + 'kWb4NSu3jNTUVHjr6vRM2mtfhWFGxuwkgIBVgZw+/Vs9kRQAcCLhLF+SbyEW4sFbiHYrPTJlpaU4f/5vEEJM4sFbiP+ca6qhNKeqVVRrpZaRk5OD' + 's2fP6n/E2rlgmNJCNMo2APUA9ix0C3G73fC1tuj52w8B7BVCTMWajCrPumsVPgOgeyFbht1ux3vv/UWH0Q3g57FgmLoNIYS4D+CnKpLRycpbXq+e' + 'fB4A8BPNp+RFxfEQktuMroiTWavU1XkjV7LblMSs8LxMGcmJ+QLi9b6lq06QLFXWCBSfqHoiWSgGYDyh9KlQfeZOgxIyA4jLnc5z585N54ySgTHv' + 'QLQKdyWanJZ51+zCxYuRyeISU/pNs46pktxEsl8FkO99/we8devW9I4GyU2mDSQmn9tdQ7LTCJAdxcW8+/nnerHbJNco8GthgGiVLybZkAyQI0d+' + 'HfnyboN2kgtfayCaA4tI/kEWyOIlD0V2ntR0Fyn0Z2GBRDhSTnI4FpCt2x5hV1eXfnuYZLkJflgDiObMOpL+2YC8/PIxjo9P/4sAP8l1JvlgHSCa' + 'Q06Sr0cC6ejoiPz6R+2wI74RQCIc20Mycmu/n+SeeajXmkA051aQ/JDk30kun6c6kzvq/k0U27cIZsr/BgDbzNoD8uJVDwAAAABJRU5ErkJggg==';
const PIN_FOREGROUND = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEQAAABECAYAAAA4E5OyAAAACXBIWXMAABYlAAAW' + 'JQFJUiTwAAAKT2lDQ1BQaG90b3Nob3AgSUNDIHByb2ZpbGUAAHjanVNnVFPpFj333vRCS4iAlEtvUhUIIFJCi4AUkSYqIQkQSoghodkVUcERRUUE' + 'G8igiAOOjoCMFVEsDIoK2AfkIaKOg6OIisr74Xuja9a89+bN/rXXPues852zzwfACAyWSDNRNYAMqUIeEeCDx8TG4eQuQIEKJHAAEAizZCFz/SMB' + 'APh+PDwrIsAHvgABeNMLCADATZvAMByH/w/qQplcAYCEAcB0kThLCIAUAEB6jkKmAEBGAYCdmCZTAKAEAGDLY2LjAFAtAGAnf+bTAICd+Jl7AQBb' + 'lCEVAaCRACATZYhEAGg7AKzPVopFAFgwABRmS8Q5ANgtADBJV2ZIALC3AMDOEAuyAAgMADBRiIUpAAR7AGDIIyN4AISZABRG8lc88SuuEOcqAAB4' + 'mbI8uSQ5RYFbCC1xB1dXLh4ozkkXKxQ2YQJhmkAuwnmZGTKBNA/g88wAAKCRFRHgg/P9eM4Ors7ONo62Dl8t6r8G/yJiYuP+5c+rcEAAAOF0ftH+' + 'LC+zGoA7BoBt/qIl7gRoXgugdfeLZrIPQLUAoOnaV/Nw+H48PEWhkLnZ2eXk5NhKxEJbYcpXff5nwl/AV/1s+X48/Pf14L7iJIEyXYFHBPjgwsz0' + 'TKUcz5IJhGLc5o9H/LcL//wd0yLESWK5WCoU41EScY5EmozzMqUiiUKSKcUl0v9k4t8s+wM+3zUAsGo+AXuRLahdYwP2SycQWHTA4vcAAPK7b8HU' + 'KAgDgGiD4c93/+8//UegJQCAZkmScQAAXkQkLlTKsz/HCAAARKCBKrBBG/TBGCzABhzBBdzBC/xgNoRCJMTCQhBCCmSAHHJgKayCQiiGzbAdKmAv' + '1EAdNMBRaIaTcA4uwlW4Dj1wD/phCJ7BKLyBCQRByAgTYSHaiAFiilgjjggXmYX4IcFIBBKLJCDJiBRRIkuRNUgxUopUIFVIHfI9cgI5h1xGupE7' + 'yAAygvyGvEcxlIGyUT3UDLVDuag3GoRGogvQZHQxmo8WoJvQcrQaPYw2oefQq2gP2o8+Q8cwwOgYBzPEbDAuxsNCsTgsCZNjy7EirAyrxhqwVqwD' + 'u4n1Y8+xdwQSgUXACTYEd0IgYR5BSFhMWE7YSKggHCQ0EdoJNwkDhFHCJyKTqEu0JroR+cQYYjIxh1hILCPWEo8TLxB7iEPENyQSiUMyJ7mQAkmx' + 'pFTSEtJG0m5SI+ksqZs0SBojk8naZGuyBzmULCAryIXkneTD5DPkG+Qh8lsKnWJAcaT4U+IoUspqShnlEOU05QZlmDJBVaOaUt2ooVQRNY9aQq2h' + 'tlKvUYeoEzR1mjnNgxZJS6WtopXTGmgXaPdpr+h0uhHdlR5Ol9BX0svpR+iX6AP0dwwNhhWDx4hnKBmbGAcYZxl3GK+YTKYZ04sZx1QwNzHrmOeZ' + 'D5lvVVgqtip8FZHKCpVKlSaVGyovVKmqpqreqgtV81XLVI+pXlN9rkZVM1PjqQnUlqtVqp1Q61MbU2epO6iHqmeob1Q/pH5Z/YkGWcNMw09DpFGg' + 'sV/jvMYgC2MZs3gsIWsNq4Z1gTXEJrHN2Xx2KruY/R27iz2qqaE5QzNKM1ezUvOUZj8H45hx+Jx0TgnnKKeX836K3hTvKeIpG6Y0TLkxZVxrqpaX' + 'llirSKtRq0frvTau7aedpr1Fu1n7gQ5Bx0onXCdHZ4/OBZ3nU9lT3acKpxZNPTr1ri6qa6UbobtEd79up+6Ynr5egJ5Mb6feeb3n+hx9L/1U/W36' + 'p/VHDFgGswwkBtsMzhg8xTVxbzwdL8fb8VFDXcNAQ6VhlWGX4YSRudE8o9VGjUYPjGnGXOMk423GbcajJgYmISZLTepN7ppSTbmmKaY7TDtMx83M' + 'zaLN1pk1mz0x1zLnm+eb15vft2BaeFostqi2uGVJsuRaplnutrxuhVo5WaVYVVpds0atna0l1rutu6cRp7lOk06rntZnw7Dxtsm2qbcZsOXYBtuu' + 'tm22fWFnYhdnt8Wuw+6TvZN9un2N/T0HDYfZDqsdWh1+c7RyFDpWOt6azpzuP33F9JbpL2dYzxDP2DPjthPLKcRpnVOb00dnF2e5c4PziIuJS4LL' + 'Lpc+Lpsbxt3IveRKdPVxXeF60vWdm7Obwu2o26/uNu5p7ofcn8w0nymeWTNz0MPIQ+BR5dE/C5+VMGvfrH5PQ0+BZ7XnIy9jL5FXrdewt6V3qvdh' + '7xc+9j5yn+M+4zw33jLeWV/MN8C3yLfLT8Nvnl+F30N/I/9k/3r/0QCngCUBZwOJgUGBWwL7+Hp8Ib+OPzrbZfay2e1BjKC5QRVBj4KtguXBrSFo' + 'yOyQrSH355jOkc5pDoVQfujW0Adh5mGLw34MJ4WHhVeGP45wiFga0TGXNXfR3ENz30T6RJZE3ptnMU85ry1KNSo+qi5qPNo3ujS6P8YuZlnM1Vid' + 'WElsSxw5LiquNm5svt/87fOH4p3iC+N7F5gvyF1weaHOwvSFpxapLhIsOpZATIhOOJTwQRAqqBaMJfITdyWOCnnCHcJnIi/RNtGI2ENcKh5O8kgq' + 'TXqS7JG8NXkkxTOlLOW5hCepkLxMDUzdmzqeFpp2IG0yPTq9MYOSkZBxQqohTZO2Z+pn5mZ2y6xlhbL+xW6Lty8elQfJa7OQrAVZLQq2QqboVFoo' + '1yoHsmdlV2a/zYnKOZarnivN7cyzytuQN5zvn//tEsIS4ZK2pYZLVy0dWOa9rGo5sjxxedsK4xUFK4ZWBqw8uIq2Km3VT6vtV5eufr0mek1rgV7B' + 'yoLBtQFr6wtVCuWFfevc1+1dT1gvWd+1YfqGnRs+FYmKrhTbF5cVf9go3HjlG4dvyr+Z3JS0qavEuWTPZtJm6ebeLZ5bDpaql+aXDm4N2dq0Dd9W' + 'tO319kXbL5fNKNu7g7ZDuaO/PLi8ZafJzs07P1SkVPRU+lQ27tLdtWHX+G7R7ht7vPY07NXbW7z3/T7JvttVAVVN1WbVZftJ+7P3P66Jqun4lvtt' + 'Xa1ObXHtxwPSA/0HIw6217nU1R3SPVRSj9Yr60cOxx++/p3vdy0NNg1VjZzG4iNwRHnk6fcJ3/ceDTradox7rOEH0x92HWcdL2pCmvKaRptTmvtb' + 'Ylu6T8w+0dbq3nr8R9sfD5w0PFl5SvNUyWna6YLTk2fyz4ydlZ19fi753GDborZ752PO32oPb++6EHTh0kX/i+c7vDvOXPK4dPKy2+UTV7hXmq86' + 'X23qdOo8/pPTT8e7nLuarrlca7nuer21e2b36RueN87d9L158Rb/1tWeOT3dvfN6b/fF9/XfFt1+cif9zsu72Xcn7q28T7xf9EDtQdlD3YfVP1v+' + '3Njv3H9qwHeg89HcR/cGhYPP/pH1jw9DBY+Zj8uGDYbrnjg+OTniP3L96fynQ89kzyaeF/6i/suuFxYvfvjV69fO0ZjRoZfyl5O/bXyl/erA6xmv' + '28bCxh6+yXgzMV70VvvtwXfcdx3vo98PT+R8IH8o/2j5sfVT0Kf7kxmTk/8EA5jz/GMzLdsAAAAgY0hSTQAAeiUAAICDAAD5/wAAgOkAAHUwAADq' + 'YAAAOpgAABdvkl/FRgAABjRJREFUeNrsnH1oV1UYxz+b8wVfVlnZQ6kh2qBCrLbSf8ICjah/VIRKDSukUlNEQchETSHFFwxDSRA1gjJQfPljgr04' + 'EcR3Y8EKUwQxeNKcpi42p1t/3Gf063I3z7m/c3/7WfvC/tjduec853Ofc85znnPvSlpbW+nSPyrtQtAFpEOVuRRS1bwbEpFSYBjwFPAkMAgYCPQB' + 'elqxJqABuACcB+qAU8BZVW0JYMMdy5S4zCH5ABGRJ4BXgLHA/SmruQzsA/aqat1dCUREngemmkeE1I/AF6p68K4AIiJDgflAZcbD/QSwUlXPFiUQ' + 'myPeAt4v4GTdAnwObHWZY1yABDFcRO4FNgAzCrxylVqbG8yGzl92ReRhYDNQ1YmrZRWw2WzpPCAi8giwCRhcBCHEYGCT2VR4ICLSH1gPDCiiuGoA' + 'sN5sKxwQEekGrLLAKoQa7SeEBgKrRKQszc1lKRudBYxIee9fQA1wGPgZuKCqzQa6u3XocWAU8ALQO0UbI4APgE99b/RedkVkuE2iJSmizS3ALlVt' + 'dPTEXsA44O0UUW4r8I6q/pRZHGJD5UugwtO47cBnqtqQcoj2Ma+c6HnraeBNVb2dVRzysieMZmCBqq5IC8MeSIOqrgAWWJ2uqjCbww8Zi0S/AYY4' + '1n0LmK2qRzt48n3N6Afs0h/AaVW90cE9zwHrPOa/c8Brqtri4iE+k+ooDxgAS5NgiEgJMBp4A3g6wUtbROQU8DVwQFVbYw/nqIh8DCxztGOI2X4o' + '9LL7qkfZPapanQBDgI3Aatv8lbZjU6WV2SgJj1VV9wJ7srC9xDGn2gPYn5PI6UjXgPGq+mcMxjDb7/gGTfXADFU9E6uvHNgFlDvU0QS8CNwM5SHD' + 'HWEAbEuA0d/GfZoIsj+wLh59quo1YJtjHT2tD8GGjGtuowXYkXB9fp4h/gCrI2k5v+1YxzMhgQxzTdqo6uWYd1QAYwKE5GOsrlwvqQdOOt7/WEgg' + 'jzqWO5ZwbVzAzds4xzZT96HUw2VddLad5TqURiZcOxOyD65A+jiW+z0h5hgYEMggq7PdNvPtgysQ13LxZa0vYVOKpVZnfEkN1ofQ+c/usd8bbOUJ' + 'pRbgRkKMFJS4aw7DRQ/FVoEWolO4ULoQD+UBCdkHVyBXHcsNzWMVSLuKDQ3ZB1cgridVzyZc2x0QSFJdVSH74ArE1e0rReS+2LCpI0oZ5qua+Lmu' + 'tVUVsg+uQH5xLNetneBpuW3S0qre6kgK1LqF7IMrEJ8T9yki0i/mJZeBmR5zUXzsz0zYEvQDpnjUUxcayHXHsvcAsxNyGL8SvQ1Q69GJWmCq3RvX' + 'LGvLRdeDArHl87BHR8aLyNiEen4DphHlRmvvAGIBMM3uiSeaxgITPOw54vrCjU8K8Qeil15ctURErqrqsQS4+4B9NilWAA/any8R5VSvtFepiFQC' + 'SzyH3feuBX2SzD2sI309DGkiyrofCLHmisho4BPck1VYZPuSqt4MegyhqjeBas8+9ATWiMhcO3RKC6KXiMwF1njCAKg224OG7m36iuhEzFeTgB0i' + 'MsE8zRVEDxGZQJQZm5Si3VazmeBDJsfI5Z5zSVzXgO+AI0Rnu9o24dnZjxCd7Y4kyrSV59HWt6r6YY7tmQAZbE8s1E75Vs7GqzfpD+CTdsYTVfW8' + 'DxDvTlkDOwPuT8rMC8oDwgDYmQsjqzmkTRuAKxSvrpiNFASInbusLWIga+NnQ1l7CHZUWVOEMGqSjlEzB2JaBlwsIhgXcT8EDw/E3HKhrRSdrVvA' + 'wrRDJZSHoKoniU7qO1urzRY6FYhB2W6xSWdpu9lAUQAxrSR6ZaLQ2m9tU1RALPxeCBwvIIzjwEchPi7KwkNQ1SZgToGgHAfm+OxkCw7EoDQWAEob' + 'jMbQFWfyKUcOlEMZVH8oKxiZAcmBMg//pFJHqgbmZQUjUyAGpRlYTPRKd77aAixuey8+K5WRsexwer2InAMWpWizGViWz/6kaDwkYTP4LlFm3VWX' + 'gPcKBaOgQAxKLTAZtxflTgCT7R7+k0AMSj0wHdhKcsK61f423coWVJl/2d2R7EX+pfz75f9FHX0wkGd7xechMdBHiT4COGg/r2cFI6iH/J/U9e8y' + 'uoB0AfHS3wMAkOtpr8ibyvkAAAAASUVORK5CYII=';

class StaticVectorImageRender extends StaticImageRender {
  constructor({
    src,
    position,
    mapPosition,
    width,
    height,
    onLoad,
    offset,
    angle = 0
  }) {
    super({
      src,
      width,
      height,
      onLoad,
      offset
    });
    this._cachedSin = null;
    this._cachedCos = null;
    this.position = position;
    this.mapPosition = mapPosition;
    this.angle = angle;
  }

  contains(position) {
    if (this.angle === 0) return this._boxContains(position[0], position[1]);
    let sin = this._sin;
    let cos = this._cos;
    let dx = position[0] - this.position[0];
    let dy = position[1] - this.position[1];
    let rotatedX = this.position[0] + dx * cos + dy * sin;
    let rotatedY = this.position[1] + dy * cos - dx * sin;
    return this._boxContains(rotatedX, rotatedY);
  }

  _boxContains(x, y) {
    let minX = this.position[0] + this.offset[0];
    let minY = this.position[1] + this.offset[1];
    return x > minX && x < minX + this.width && y > minY && y < minY + this.height;
  }

  get angle() {
    return this._angle;
  }

  set angle(value) {
    this._angle = value;
    this._cachedCos = null;
    this._cachedSin = null;
  }

  get _sin() {
    if (this._cachedSin === null) this._cachedSin = Math.sin(this.angle);
    return this._cachedSin;
  }

  get _cos() {
    if (this._cachedCos === null) this._cachedCos = Math.cos(this.angle);
    return this._cachedCos;
  }

}

/**
 * Symbol of point drawn as circle with outline.
 * @alias sGis.symbol.point.Image
 */

class StaticImageSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Width of the image. If not set, image will be automatically resized according to height. If both width and height are not set, original image size will be used. */

    this.width = 32;
    /** Height of the image. If not set, image will be automatically resized according to width. If both width and height are not set, original image size will be used. */

    this.height = 32;
    this._anchorPoint = [16, 32];
    /** Source of the image. Can be url or data:url string. */

    this.source = PIN_IMAGE;
    /**
     * Clockwise rotation of the image in radians.
     */

    this.angle = 0;
    Object.assign(this, options);
  }
  /**
   * Anchor point of the image. If set to [0, 0], image's left top corner will be at the feature position.<br>
   *     Anchor point does not scale with width and height parameters.
   */


  get anchorPoint() {
    return this._anchorPoint;
  }

  set anchorPoint(anchorPoint) {
    // TODO: remove deprecated part after 2018
    let deprecated = anchorPoint;

    if (deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._anchorPoint = [deprecated.x, deprecated.y];
    } else {
      this._anchorPoint = anchorPoint;
    }
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution, -position[1] / resolution];
    return [new StaticVectorImageRender({
      src: this.source,
      position: pxPosition,
      mapPosition: position,
      angle: this.angle,
      width: this.width,
      height: this.height,
      offset: [-this.anchorPoint[0], -this.anchorPoint[1]]
    })];
  }

  renderFunctionAsync(feature, resolution, crs) {
    return __awaiter(this, void 0, void 0, function* () {
      const renders = this.renderFunction(feature, resolution, crs);
      return Promise.all(renders.map(render => render.readyPromise)).then(() => renders);
    });
  }

}
registerSymbol(StaticImageSymbol, 'point.Image', ['width', 'height', 'anchorPoint', 'source', 'angle']);

const MAX_TRIES = 3;
/**
 * Symbol of point drawn as masked image.
 * @alias sGis.symbol.point.MaskedImage
 */

class MaskedImage extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor({
    width,
    height,
    angle,
    anchorPoint,
    imageSource,
    maskSource,
    maskColor,
    onUpdate
  } = {}) {
    super();
    /** Width of the image. If not set, image will be automatically resized according to height. If both width and height are not set, original image size will be used. */

    this.width = 32;
    /** Height of the image. If not set, image will be automatically resized according to width. If both width and height are not set, original image size will be used. */

    this.height = 32;
    this._anchorPoint = [16, 32];
    /**
     * Clockwise rotation of the image in radians.
     */

    this.angle = 0;
    this._imageSource = PIN_BACKGROUND;
    this._maskSource = PIN_FOREGROUND;
    this._maskColor = '#9bdb00';
    this.width = width;
    this.height = height;
    this.angle = angle;
    this.anchorPoint = anchorPoint;
    this._maskColor = maskColor;
    this.onUpdate = onUpdate;
    this.setImageSource(imageSource);
    this.setMaskSource(maskSource);

    this._updateMasked();
  }
  /**
   * Anchor point of the image. If set to [0, 0], image's left top corner will be at the feature position.<br>
   *     Anchor point does not scale with width and height parameters.
   */


  get anchorPoint() {
    return this._anchorPoint;
  }

  set anchorPoint(anchorPoint) {
    // TODO: remove deprecated part after 2018
    let deprecated = anchorPoint;

    if (deprecated && deprecated.x !== undefined && deprecated.y !== undefined) {
      warn('Using anchorPoint in {x, y} format is deprecated. Use [x, y] format instead.');
      this._anchorPoint = [deprecated.x, deprecated.y];
    } else {
      this._anchorPoint = anchorPoint;
    }
  }

  prepareSymbol() {
    return Promise.all([this.imageReadyPromise, this.maskReadyPromise]).then(() => {
      this._updateMasked();
    });
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof PointFeature) || !this._maskedSrc) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution, -position[1] / resolution];
    return [new StaticVectorImageRender({
      src: this._maskedSrc,
      position: pxPosition,
      mapPosition: position,
      angle: this.angle,
      width: this.width,
      height: this.height,
      offset: [-this.anchorPoint[0], -this.anchorPoint[1]]
    })];
  }

  renderFunctionAsync(feature, resolution, crs) {
    return this.prepareSymbol().then(() => this.renderFunction(feature, resolution, crs));
  }
  /**
   * Source of the base image. Can be url or data:url string.
   */


  get imageSource() {
    return this._imageSource;
  }

  set imageSource(source) {
    this._imageSource = source;
    this._image = new Image();
    this._image.src = source;

    if (this._image.complete) {
      this._updateMasked();
    } else {
      this._image.onload = this._updateMasked.bind(this);
    }
  }
  /**
   * Source of the mask image. Can be url or data:url string.
   */


  get maskSource() {
    return this._maskSource;
  }

  set maskSource(source) {
    this._maskSource = source;
    this._mask = new Image();
    this._mask.src = source;

    if (this._mask.complete) {
      this._updateMasked();
    } else {
      this._mask.onload = this._updateMasked.bind(this);
    }
  }
  /**
   * Color of the mask. Can be any valid css color string.
   */


  get maskColor() {
    return this._maskColor;
  }

  set maskColor(color) {
    this._maskColor = color;

    this._updateMasked();
  }

  setImageSource(src) {
    this._imageSource = src;
    this._image = new Image();
    this.imageReadyPromise = loadImage(this._image, src, null);
    return this.imageReadyPromise;
  }

  setMaskSource(src) {
    this._maskSource = src;
    this._mask = new Image();
    this.maskReadyPromise = loadImage(this._mask, src, null);
    return this.maskReadyPromise;
  }

  _isLoaded() {
    return this._image.complete && this._mask.complete;
  }

  _updateMasked(triesNo = 0) {
    if (!this._mask || !this._image || !this._isLoaded()) return;
    let canvas = document.createElement('canvas');
    canvas.width = this._mask.width;
    canvas.height = this._mask.height;
    let ctx = canvas.getContext('2d');

    if (canvas.width === 0 || canvas.height === 0) {
      // IE sometimes forgets to change the size of canvas and fails to draw the image. Need to check for that.
      if (triesNo > MAX_TRIES) return;
      setTimeout(() => this._updateMasked(triesNo + 1), 0);
      return;
    }

    ctx.drawImage(this._mask, 0, 0);
    let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    this._recolorMask(imageData);

    ctx.putImageData(imageData, 0, 0);
    let resultCanvas = document.createElement('canvas');
    resultCanvas.width = this._image.width;
    resultCanvas.height = this._image.height;
    let resultCtx = resultCanvas.getContext('2d');

    if (resultCanvas.width === 0 || resultCanvas.height === 0) {
      // IE sometimes forgets to change the size of canvas and fails to draw the image. Need to check for that.
      if (triesNo > MAX_TRIES) return;
      setTimeout(() => this._updateMasked(triesNo + 1), 0);
      return;
    }

    resultCtx.drawImage(this._image, 0, 0);
    resultCtx.drawImage(canvas, 0, 0);
    this._maskedSrc = resultCanvas.toDataURL();
    if (this.onUpdate) this.onUpdate();
  }

  _recolorMask(imageData) {
    let maskColor = new Color$1(this.maskColor);
    let alphaNormalizer = 65025;
    let d = imageData.data;

    for (let i = 0; i < d.length; i += 4) {
      let r = d[i];
      let a = d[i + 3];
      let srcA = a * maskColor.a / alphaNormalizer;
      d[i + 3] = +Math.round(Math.min(1, srcA) * 255);
      d[i] = maskColor.r * r / 255;
      d[i + 1] = maskColor.g * r / 255;
      d[i + 2] = maskColor.b * r / 255;
    }
  }

}
registerSymbol(MaskedImage, 'point.MaskedImage', ['width', 'height', 'anchorPoint', 'imageSource', 'maskSource', 'maskColor', 'angle']);

/**
 * Symbol of a highlighted feature for editor.
 * @alias sGis.symbol.Editor
 */

class EditorSymbol extends Symbol {
  /**
   * @param {Object} [options] - key-value list of properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Base symbol of the feature. Used to render original feature with the highlight. */

    this.baseSymbol = new PointSymbol();
    /** Color of the halo (highlight). Can be any valid css color string. */

    this.color = 'rgba(97,239,255,0.5)';
    /** Size of the halo around the feature. */

    this.haloSize = 5;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    let baseRender = this.baseSymbol.renderFunction(feature, resolution, crs);
    let halo;

    for (let i = 0; i < baseRender.length; i++) {
      if (baseRender[i] instanceof Arc) {
        halo = new Arc(baseRender[i].center, {
          fillColor: this.color,
          radius: parseFloat(baseRender[i].radius) + this.haloSize,
          strokeColor: 'transparent'
        });
        break;
      } else if (baseRender[i] instanceof PolyRender) {
        halo = new PolyRender(baseRender[i].coordinates, {
          enclosed: baseRender[i].enclosed,
          fillStyle: baseRender[i].fillStyle === FillStyle.None ? FillStyle.None : FillStyle.Color,
          strokeColor: this.color,
          fillColor: this.color,
          strokeWidth: parseFloat(baseRender[i].strokeWidth) + 2 * this.haloSize
        });
        break;
      } else if (this.baseSymbol instanceof StaticImageSymbol || this.baseSymbol instanceof MaskedImage) {
        halo = new Arc([baseRender[i].position[0] - +this.baseSymbol.anchorPoint[0] + this.baseSymbol.width / 2, baseRender[i].position[1] - +this.baseSymbol.anchorPoint[1] + this.baseSymbol.height / 2], {
          fillColor: this.color,
          radius: this.baseSymbol.width / 2 + this.haloSize,
          strokeColor: 'transparent'
        });
        break;
      }
    }

    if (halo) baseRender.unshift(halo);
    return baseRender;
  }

}

class CombinedSnappingProvider {
  constructor(providers) {
    this.providers = providers;
  }

  getSnappingPoint(point, activeContour, activeIndex, isPolygon) {
    for (let i = 0; i < this.providers.length; i++) {
      const snappingPoint = this.providers[i].getSnappingPoint(point, activeContour, activeIndex, isPolygon);
      if (snappingPoint !== null) return snappingPoint;
    }
  }

  clone() {
    let children = this.providers.map(x => x.clone());
    return new CombinedSnappingProvider(children);
  }

}

class FeatureSelectEvent extends sGisEvent {
  constructor(feature) {
    super(FeatureSelectEvent.type);
    this.feature = feature;
  }

}
FeatureSelectEvent.type = 'featureSelect';
class FeatureDeselectEvent extends sGisEvent {
  constructor(feature) {
    super(FeatureDeselectEvent.type);
    this.feature = feature;
  }

}
FeatureDeselectEvent.type = 'featureDeselect';
class FeatureRemoveEvent extends sGisEvent {
  constructor(feature) {
    super(FeatureRemoveEvent.type);
    this.feature = feature;
  }

}
FeatureRemoveEvent.type = 'featureRemove';
const modes = ['vertex', 'rotate', 'scale', 'drag'];
/**
 * Control for editing points, polylines and polygons. It uses PointEditor, PolyEditor, PolyTransform and Snapping classes for editing corresponding features.
 * @alias sGis.controls.Editor
 */

class Editor extends Control {
  /**
   * @param map - map object the control will work with
   * @param options - key-value set of properties to be set to the instance
   */
  constructor(map, {
    snappingProvider,
    activeLayer,
    isActive = false
  } = {}) {
    super(map, {
      snappingProvider,
      activeLayer
    });
    this._ignoreEvents = false;
    this._scaling = true;
    this._rotation = true;
    this._dragging = true;
    this._vertexEditing = true;
    this._activeFeature = null;
    this._deselectAllowed = true;
    /**
     * If set to true the feature will be deleted in one of two cases:<br>
     *     1) User removes last point of polyline or polygon.
     *     2) User presses "del" button
     */

    this.allowDeletion = true;
    this._ns = '.' + getGuid();
    this._setListener = this._setListener.bind(this);
    this._removeListener = this._removeListener.bind(this);
    this._onEdit = this._onEdit.bind(this);

    this._setEditors();

    this._states = new StateManager();
    this._deselect = this._deselect.bind(this);
    this.setMode(modes);
    this._handleFeatureAdd = this._handleFeatureAdd.bind(this);
    this._handleFeatureRemove = this._handleFeatureRemove.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this.isActive = isActive;
  }

  _setEditors() {
    this._pointEditor = new PointEditor(this.map, {
      snappingProvider: this.snappingProvider,
      activeLayer: this.activeLayer
    });

    this._pointEditor.on(EditEvent.type, this._onEdit);

    this._polyEditor = new PolyEditor(this.map, {
      snappingProvider: this._getPolyEditorSnappingProvider(),
      onFeatureRemove: this._delete.bind(this),
      activeLayer: this.activeLayer
    });

    this._polyEditor.on(EditEvent.type, this._onEdit);

    this._polyEditor.on(ChangeEvent.type, this._updateTransformControl.bind(this));

    this._polyTransform = new PolyTransform(this.map);

    this._polyTransform.on('rotationEnd scalingEnd', this._onEdit);
  }

  setSnappingProvider(provider) {
    this.snappingProvider = provider;
    if (this._pointEditor) this._pointEditor.snappingProvider = provider;
    if (this._polyEditor) this._polyEditor.snappingProvider = this._getPolyEditorSnappingProvider();
  }

  _getPolyEditorSnappingProvider() {
    if (!this.snappingProvider) return undefined;
    const result = this.snappingProvider.clone();

    if (result instanceof SnappingProviderBase) {
      result.snappingMethods = result.snappingMethods.concat([emptySnapping]);
    } else if (result instanceof CombinedSnappingProvider) {
      result.providers.forEach(child => {
        if (child instanceof SnappingProviderBase) {
          child.snappingMethods = child.snappingMethods.concat([emptySnapping]);
        }
      });
    }

    return result;
  }

  _onEdit() {
    this.fire('edit');

    this._saveState();
  }

  _activate() {
    if (!this.activeLayer) return;
    this.activeLayer.features.forEach(this._setListener, this);
    this.activeLayer.on(FeaturesAddEvent.type, this._handleFeatureAdd);
    this.activeLayer.on(FeaturesRemoveEvent.type, this._handleFeatureRemove);
    this.activeLayer.redraw();
    this.map.on(sGisClickEvent.type, this._onMapClick.bind(this));
    listenDomEvent(document, 'keydown', this._handleKeyDown);
  }

  _handleFeatureAdd(event) {
    let featuresAddEvent = event;
    featuresAddEvent.features.forEach(f => this._setListener(f));
  }

  _handleFeatureRemove(event) {
    let featuresRemoveEvent = event;
    featuresRemoveEvent.features.forEach(f => this._removeListener(f));
  }

  _setListener(feature) {
    feature.on(sGisClickEvent.type + this._ns, this._handleFeatureClick.bind(this, feature));
  }

  _removeListener(feature) {
    feature.off(sGisClickEvent.type + this._ns);
  }

  _onMapClick() {
    if (!this.ignoreEvents) this._deselect();
  }

  _deactivate() {
    this._deselect();

    if (!this.activeLayer) return;
    this.activeLayer.features.forEach(this._removeListener, this);
    this.activeLayer.off('featureAdd', this._handleFeatureAdd);
    this.activeLayer.off('featureRemove', this._handleFeatureRemove);
    this.map.off('click', this._deselect);
    removeDomEventListener(document, 'keydown', this._handleKeyDown);
  }
  /**
   * Selects a given feature if it is in the active layer.
   * @param feature
   */


  select(feature) {
    this.activeFeature = feature;
  }
  /**
   * Clears selection if any.
   */


  deselect() {
    this.activeFeature = null;
  }
  /**
   * Currently selected for editing feature.
   */


  get activeFeature() {
    return this._activeFeature;
  }

  set activeFeature(feature) {
    if (feature) this.activate();

    this._select(feature);
  }

  _handleFeatureClick(feature, event) {
    if (!this.isActive) {
      this._removeListener(feature);

      return;
    }

    if (this.ignoreEvents) return;
    event.stopPropagation();

    this._select(feature);
  }

  _select(feature) {
    if (this._activeFeature === feature) return;

    this._deselect();

    this._activeFeature = feature;
    if (!feature) return;
    feature.setTempSymbol(new EditorSymbol({
      baseSymbol: feature.symbol
    }));

    if (feature instanceof PointFeature) {
      this._pointEditor.activeLayer = this.activeLayer;
      this._pointEditor.activeFeature = feature;
    } else if (feature instanceof Poly) {
      this._activatePolyControls(feature);
    }

    if (this.activeLayer) this.activeLayer.redraw();

    this._saveState();

    this.fire(new FeatureSelectEvent(feature));
  }

  _activatePolyControls(feature) {
    this._polyEditor.featureDragAllowed = this._dragging;
    this._polyEditor.vertexChangeAllowed = this._vertexEditing;
    this._polyEditor.activeLayer = this.activeLayer;
    this._polyEditor.activeFeature = feature;
    this._polyTransform.enableRotation = this._rotation;
    this._polyTransform.enableScaling = this._scaling;
    this._polyTransform.activeLayer = this.activeLayer;
    this._polyTransform.activeFeature = feature;
  }

  _deselect() {
    if (!this._activeFeature || !this._deselectAllowed) return;

    this._pointEditor.deactivate();

    this._polyEditor.deactivate();

    this._polyTransform.deactivate();

    let feature = this._activeFeature;

    this._activeFeature.clearTempSymbol();

    this._activeFeature = null;
    if (this.activeLayer) this.activeLayer.redraw();
    this.fire(new FeatureDeselectEvent(feature));
  }

  _updateTransformControl() {
    if (this._polyTransform.isActive) this._polyTransform.update();
  }
  /**
   * Sets the editing mode. Available modes are:<br>
   *     * vertex - editing vertexes of polygons and polylines.
   *     * rotate - rotation of polygons and polylines
   *     * drag - dragging of whole features
   *     * scale - scaling of polygons and polylines
   *     * all - all modes are active
   * @param mode - can be coma separated list or array of mode names
   */


  setMode(mode) {
    if (mode === 'all') mode = modes;
    if (!Array.isArray(mode)) mode = mode.split(',').map(x => x.trim());
    this._vertexEditing = mode.indexOf('vertex') >= 0;
    this._rotation = mode.indexOf('rotate') >= 0;
    this._dragging = mode.indexOf('drag') >= 0;
    this._scaling = mode.indexOf('scale') >= 0;

    if (this._activeFeature instanceof Poly) {
      this._polyEditor.deactivate();

      this._polyTransform.deactivate();

      this._activatePolyControls(this._activeFeature);
    }
  }
  /**
   * If deselecting was prohibited, this methods turns it on again.
   */


  allowDeselect() {
    this._deselectAllowed = true;
  }
  /**
   * Prevents feature to be deselected by any user or code interaction. It will not have effect if the control is deactivated though.
   */


  prohibitDeselect() {
    this._deselectAllowed = false;
  }

  _delete() {
    if (this._deselectAllowed && this.allowDeletion && this._activeFeature) {
      let feature = this._activeFeature;
      this.prohibitEvent(FeatureDeselectEvent.type);

      this._deselect();

      this.allowEvent(FeatureDeselectEvent.type);
      if (this.activeLayer) this.activeLayer.remove(feature);

      this._saveState();

      this.fire(new FeatureRemoveEvent(feature));
    }
  }

  _handleKeyDown(event) {
    switch (event.which) {
      case 27:
        this._deselect();

        return false;
      // esc

      case 46:
        this._delete();

        return false;
      // del

      case 90:
        if (event.ctrlKey) {
          this.undo();
          return false;
        }

        break;
      // ctrl+z

      case 89:
        if (event.ctrlKey) {
          this.redo();
          return false;
        }

        break;
      // ctrl+y
    }
  }

  _saveState() {
    let coordinates = this.activeFeature === null ? null : this.activeFeature instanceof Poly ? this.activeFeature.rings : this.activeFeature.position;

    this._states.setState({
      feature: this._activeFeature,
      coordinates: coordinates
    });
  }
  /**
   * Undo last change.
   */


  undo() {
    this._setState(this._states.undo());
  }
  /**
   * Redo a change that was undone.
   */


  redo() {
    this._setState(this._states.redo());
  }

  _setState(state) {
    if (!state || !this.activeLayer) return this._deselect();

    if (!state.coordinates && state.feature && this.activeLayer.features.indexOf(state.feature) >= 0) {
      this.activeFeature = null;
      this.activeLayer.remove(state.feature);
    } else if (state.coordinates && state.feature && this.activeLayer.features.indexOf(state.feature) < 0) {
      this._setCoordinates(state);

      this.activeLayer.add(state.feature);
      this.activeFeature = state.feature;
    } else if (state.coordinates) {
      this._setCoordinates(state);

      this.activeFeature = state.feature;
    }

    this._updateTransformControl();

    this.activeLayer.redraw();
  }

  _setCoordinates(state) {
    if (state.feature instanceof PointFeature) {
      state.feature.position = state.coordinates;
    } else if (state.feature instanceof Poly) {
      state.feature.coordinates = state.coordinates;
    }
  }

  get ignoreEvents() {
    return this._ignoreEvents;
  }

  set ignoreEvents(bool) {
    this._ignoreEvents = bool;
    this._pointEditor.ignoreEvents = bool;
    this._polyEditor.ignoreEvents = bool;
    this._polyTransform.ignoreEvents = bool;
  }

  get pointEditor() {
    return this._pointEditor;
  }

  get polyEditor() {
    return this._polyEditor;
  }

  get polyTransform() {
    return this._polyTransform;
  }

}

class MultiPointSymbol extends Symbol {
  constructor(baseSymbol) {
    super();
    this.baseSymbol = baseSymbol;
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof MultiPoint)) return [];
    let renders = [];
    feature.points.forEach(point => {
      let f = new PointFeature(point, {
        crs: feature.crs,
        symbol: this.baseSymbol
      });
      renders = renders.concat(f.render(resolution, crs));
    });
    return renders;
  }

}

/**
 * Represents a set of points on a map that behave as one feature: have same symbol, can be added, transformed or removed as one.
 * @alias sGis.feature.MultiPoint
 */

class MultiPoint extends Feature {
  constructor(points = [], _a = {}) {
    var {
      symbol = new MultiPointSymbol(new PointSymbol())
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(Object.assign({
      symbol
    }, params));
    this._points = points;
  }
  /**
   * Set of points' coordinates.
   */


  get points() {
    return this._points;
  }

  set points(points) {
    this._points = points.slice();

    this._update();
  }

  projectTo(crs) {
    let projected = projectPoints(this.points, this.crs, crs);
    return new MultiPoint(projected, {
      symbol: this.symbol,
      crs: crs,
      persistOnMap: this.persistOnMap
    });
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return this.projectTo(this.crs);
  }
  /**
   * Adds a point to the end of the coordinates' list.
   * @param point - if sGis.IPoint instance is given, it will be automatically projected to the multipoint coordinate system.
   */


  addPoint(point) {
    if (point.position && point.crs) {
      this._points.push(point.projectTo(this.crs).position);
    } else {
      this._points.push([point[0], point[1]]);
    }

    this._update();
  }

  _update() {
    this._bbox = null;
    this.redraw();
  }

  get bbox() {
    if (this._bbox) return this._bbox;
    let xMin = Number.MAX_VALUE;
    let yMin = Number.MAX_VALUE;
    let xMax = Number.MIN_VALUE;
    let yMax = Number.MIN_VALUE;

    this._points.forEach(point => {
      xMin = Math.min(xMin, point[0]);
      yMin = Math.min(yMin, point[1]);
      xMax = Math.max(xMax, point[0]);
      yMax = Math.max(yMax, point[1]);
    });

    this._bbox = new Bbox([xMin, yMin], [xMax, yMax], this.crs);
    return this._bbox;
  }
  /**
   * @deprecated
   */


  get coordinates() {
    return this._points.slice();
  }

  set coordinates(points) {
    this.points = points;
  }

  get centroid() {
    return this.bbox.center;
  }

}

/**
 * Control for creating point features. When active, any click on the map will create a new point feature and add it
 * to the active layer. If active layer is not set, the point feature will be given through 'drawingFinish' event.
 * @alias sGis.controls.Point
 * @fires [[DrawingFinishEvent]]
 */

class PointControl extends Control {
  /**
   * @param map
   * @param __namedParameters - key-value set of properties to be set to the instance
   */
  constructor(map, {
    activeLayer,
    snappingProvider,
    snappingSymbol,
    isActive = false,
    symbol = new PointSymbol()
  } = {}) {
    super(map, {
      activeLayer,
      snappingProvider,
      snappingSymbol,
      useTempLayer: true
    });
    this._debouncedSnappingHandle = debounce(this._snappingHandle.bind(this), 50);
    this._handleClick = this._handleClick.bind(this);
    this._handleMouseMove = this._handleMouseMove.bind(this);
    this.symbol = symbol;
    this.isActive = isActive;
  }

  _activate() {
    this.map.on(sGisClickEvent.type, this._handleClick);
    this.map.on(sGisMouseMoveEvent.type, this._handleMouseMove);
  }

  _deactivate() {
    this.map.off(sGisClickEvent.type, this._handleClick);
    this.map.off(sGisMouseMoveEvent.type, this._handleMouseMove);
  }

  _handleClick(event) {
    event.stopPropagation();
    let clickEvent = event;
    let feature = new PointFeature(clickEvent.point.position, {
      crs: this.map.crs,
      symbol: this.symbol
    });
    if (this.activeLayer) this.activeLayer.add(feature);
    this.fire(new DrawingFinishEvent(feature, clickEvent.browserEvent));
  }

  _handleMouseMove(event) {
    let mouseMoveEvent = event;

    this._unsnap();

    this._debouncedSnappingHandle(mouseMoveEvent);
  }

  _snappingHandle(event) {
    this._snap(event.point.position, event.browserEvent.altKey);
  }

}

/**
 * Symbol of polyline drawn as simple line.
 * @alias sGis.symbol.polyline.Simple
 */

class PolylineSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Stroke color of the outline. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Stroke width of the outline. */

    this.strokeWidth = 1;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** Emulation CanvasRenderingContext2D.filter drop-shadow. */

    this.shadow = null;
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];
    let coordinates = this.getRenderedCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      fillStyle: FillStyle.None,
      enclosed: false,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit,
      mapCoordinates: feature.rings
    })];
  }
  /**
   * Projects coordinates of a poly feature to the requested crs and resolution.
   * @param feature
   * @param resolution
   * @param crs
   * @param tolerance
   */


  getRenderedCoordinates(feature, resolution, crs, tolerance) {
    let result;

    if (this.previousPoly === feature && this.previousCrs.equals(crs) && this.previousResolution === resolution && feature.lastAction && feature.lastAction.type !== Action.removePoint) {
      const lastAction = feature.lastAction;
      let newResult = this.previousSimplified;

      if (lastAction.type === Action.movePoint) {
        const newSimplifiedRing = simplifyCoordinates([feature.rings[lastAction.ringIndex]].map(ring => {
          return ring.map(point => {
            return [Math.round(point[0] / resolution), Math.round(point[1] / -resolution)];
          });
        }), tolerance);
        newResult[lastAction.ringIndex] = newSimplifiedRing[0];
      }

      if (lastAction.type === Action.addPoint) {
        const renderCoordinates = [Math.round(lastAction.coordinates[0] / resolution), Math.round(lastAction.coordinates[1] / -resolution)];
        newResult[lastAction.ringIndex].push(renderCoordinates);
      }

      result = newResult;
    } else {
      result = getRenderedPolygonCoordinates(feature, resolution, crs, tolerance);
    }

    this.previousSimplified = result;
    this.previousPoly = feature;
    this.previousCrs = crs;
    this.previousResolution = resolution;
    feature.lastAction = null;
    return result;
  }

}
registerSymbol(PolylineSymbol, 'polyline.Simple', ['strokeColor', 'strokeWidth', 'shadow', 'lineCap', 'lineDash', 'lineJoin', 'miterLimit']);

/**
 * A line or a set of geographical lines.
 * @alias sGis.feature.Polyline
 */

class Polyline extends Poly {
  constructor(rings, _a = {}) {
    var {
      symbol = new PolylineSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(rings, Object.assign({
      symbol
    }, params));
    this.isEnclosed = false;
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return new Polyline(this.rings, {
      crs: this.crs,
      symbol: this.originalSymbol
    });
  }

  projectTo(crs) {
    let projected = projectRings(this.rings, this.crs, crs);
    return new Polyline(projected, {
      crs: this.crs,
      symbol: this.symbol,
      persistOnMap: this.persistOnMap
    });
  }

}

/**
 * Control for drawing polyline features.
 * @alias sGis.controls.Polyline
 */

class PolylineControl extends PolyControl {
  /**
   * @param map - map the control will work with
   * @param properties - key-value set of properties to be set to the instance
   */
  constructor(map, _a = {}) {
    var {
      symbol = new PolylineSymbol()
    } = _a,
        controlOptions = __rest(_a, ["symbol"]);

    super(map, Object.assign({
      symbol
    }, controlOptions));
  }

  _getNewFeature(position) {
    return new Polyline([[position, position]], {
      crs: this.map.crs,
      symbol: this.symbol
    });
  }

}

/**
 * Control for drawing polygon features.
 * @alias sGis.control.Polygon
 */

class PolygonControl extends PolyControl {
  /**
   * @param map - map the control will work with
   * @param properties - key-value set of properties to be set to the instance
   */
  constructor(map, _a = {}) {
    var {
      symbol = new PolygonSymbol()
    } = _a,
        controlOptions = __rest(_a, ["symbol"]);

    super(map, Object.assign({
      symbol
    }, controlOptions));
  }

  _getNewFeature(position) {
    return new Polygon([[position, position]], {
      crs: this.map.crs,
      symbol: this.symbol
    });
  }

}

/**
 * Control for drawing rectangles by dragging from corner to corner.
 * @alias sGis.controls.Rectangle
 */

class Rectangle extends PolyDrag {
  _getNewCoordinates(point) {
    const position = point.position;
    return [[position, position, position, position]];
  }

  _getUpdatedCoordinates(point) {
    const baseCoord = this._activeFeature.rings[0][0];
    const pointCoord = point.position;
    return [[baseCoord, [baseCoord[0], pointCoord[1]], pointCoord, [pointCoord[0], baseCoord[1]]]];
  }

}

class StaticHtmlImageRender extends StaticImageRender {
  constructor({
    src,
    bbox,
    width,
    height,
    opacity,
    onLoad,
    onLoadStart,
    onLoadEnd,
    onDisplayed = undefined,
    onRemoved = undefined
  }) {
    super({
      src,
      width,
      height,
      opacity,
      onLoad,
      onLoadStart,
      onLoadEnd
    });
    this.bbox = bbox;
    this.onDisplayed = onDisplayed;
    this.onRemoved = onRemoved;
  }

  contains(position) {
    let resolution = this.bbox.width / this.width;
    return this.bbox.contains(new Point$1([position[0] * resolution, -position[1] * resolution], this.bbox.crs));
  }

}

/**
 * Represents a layer that is fully drawn by server and is displayed as an image overlay.
 * @alias sGis.DynamicLayer
 */

class DynamicLayer extends Layer {
  /**
   * @param properties - properties to be set to the corresponding fields
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance
   */
  constructor(properties = {}, extensions) {
    super(properties, extensions);
    this._forceUpdate = false;
    this.delayedUpdate = true;
    const {
      onLoad,
      onLoadStart,
      onLoadEnd,
      onError
    } = properties;
    this.onLoad = onLoad;
    this.onLoadStart = onLoadStart;
    this.onLoadEnd = onLoadEnd;
    this.onError = onError;
  }

  getRenders(bbox, resolution, painter) {
    if (!this.checkVisibility(resolution)) return [];

    if (this.crs) {
      if (bbox.crs.canProjectTo(this.crs)) {
        bbox = bbox.projectTo(this.crs);
      } else {
        return [];
      }
    }

    let needRedraw = this._forceUpdate || !this._currentRender || !bbox.equals(this._currentRender.bbox);

    if (needRedraw) {
      this._loadNextRender(bbox, resolution, painter);
    }

    if (this._nextRender !== this._currentRender && this._nextRender && this._nextRender.isImageLoaded) {
      this._currentRender = this._nextRender;
    }

    this._forceUpdate = false;
    return this._currentRender && !this._currentRender.error ? [this._currentRender] : [];
  }

  _loadNextRender(bbox, resolution, painter) {
    if (this._currentRender === this._nextRender) {
      let height = painter.height;
      let width = painter.width;
      let src = this.getUrl(bbox, painter); // we only load images for changed bbox, so it's safe to disable cache here

      src += `&ts=${Date.now()}`;
      this._nextRender = new StaticHtmlImageRender({
        src,
        bbox,
        height,
        width,
        opacity: this.opacity,
        onLoadStart: this.onLoadStart,
        onLoadEnd: this.onLoadEnd,
        onLoad: () => {
          this._startNextLoad();

          this.redraw();
        },
        onError: err => {
          this.fire(new LayerErrorEvent(err));
        }
      });
      this._toLoad = null;
    } else {
      if (this._nextRender && !this._nextRender.isImageLoaded) {
        this._nextRender.deleteNode(); // create new new StaticHtmlImageRender


        this._nextRender = this._currentRender;

        this._loadNextRender(bbox, resolution, painter);
      }

      this._toLoad = {
        bbox,
        resolution,
        painter
      };
    }
  }

  _startNextLoad() {
    if (this._toLoad) {
      this._loadNextRender(this._toLoad.bbox, this._toLoad.resolution, this._toLoad.painter);

      this._toLoad = null;
    }
  }
  /**
   * Ensures update of the layer image
   */


  forceUpdate() {
    this._forceUpdate = true;
    this.fire(new PropertyChangeEvent('source'));
  }

  get opacity() {
    return this.getOpacity();
  }

  set opacity(opacity) {
    this.setOpacity(opacity);
  }

  setOpacity(value) {
    if (this._currentRender) this._currentRender.opacity = value;
    if (this._nextRender) this._nextRender.opacity = value;
    super.setOpacity(value);
  }
  /**
   * Coordinate system of the layer
   */


  get crs() {
    return this._crs;
  }

  set crs(crs) {
    this._crs = crs;
  }

}

function ascending(a, b) {
  return a == null || b == null ? NaN : a < b ? -1 : a > b ? 1 : a >= b ? 0 : NaN;
}

function descending(a, b) {
  return a == null || b == null ? NaN
    : b < a ? -1
    : b > a ? 1
    : b >= a ? 0
    : NaN;
}

function bisector(f) {
  let compare1, compare2, delta;

  // If an accessor is specified, promote it to a comparator. In this case we
  // can test whether the search value is (self-) comparable. We can’t do this
  // for a comparator (except for specific, known comparators) because we can’t
  // tell if the comparator is symmetric, and an asymmetric comparator can’t be
  // used to test whether a single value is comparable.
  if (f.length !== 2) {
    compare1 = ascending;
    compare2 = (d, x) => ascending(f(d), x);
    delta = (d, x) => f(d) - x;
  } else {
    compare1 = f === ascending || f === descending ? f : zero$1;
    compare2 = f;
    delta = f;
  }

  function left(a, x, lo = 0, hi = a.length) {
    if (lo < hi) {
      if (compare1(x, x) !== 0) return hi;
      do {
        const mid = (lo + hi) >>> 1;
        if (compare2(a[mid], x) < 0) lo = mid + 1;
        else hi = mid;
      } while (lo < hi);
    }
    return lo;
  }

  function right(a, x, lo = 0, hi = a.length) {
    if (lo < hi) {
      if (compare1(x, x) !== 0) return hi;
      do {
        const mid = (lo + hi) >>> 1;
        if (compare2(a[mid], x) <= 0) lo = mid + 1;
        else hi = mid;
      } while (lo < hi);
    }
    return lo;
  }

  function center(a, x, lo = 0, hi = a.length) {
    const i = left(a, x, lo, hi - 1);
    return i > lo && delta(a[i - 1], x) > -delta(a[i], x) ? i - 1 : i;
  }

  return {left, center, right};
}

function zero$1() {
  return 0;
}

function number$1(x) {
  return x === null ? NaN : +x;
}

const ascendingBisect = bisector(ascending);
const bisectRight = ascendingBisect.right;
bisector(number$1).center;
var bisect = bisectRight;

const e10 = Math.sqrt(50),
    e5 = Math.sqrt(10),
    e2 = Math.sqrt(2);

function tickSpec(start, stop, count) {
  const step = (stop - start) / Math.max(0, count),
      power = Math.floor(Math.log10(step)),
      error = step / Math.pow(10, power),
      factor = error >= e10 ? 10 : error >= e5 ? 5 : error >= e2 ? 2 : 1;
  let i1, i2, inc;
  if (power < 0) {
    inc = Math.pow(10, -power) / factor;
    i1 = Math.round(start * inc);
    i2 = Math.round(stop * inc);
    if (i1 / inc < start) ++i1;
    if (i2 / inc > stop) --i2;
    inc = -inc;
  } else {
    inc = Math.pow(10, power) * factor;
    i1 = Math.round(start / inc);
    i2 = Math.round(stop / inc);
    if (i1 * inc < start) ++i1;
    if (i2 * inc > stop) --i2;
  }
  if (i2 < i1 && 0.5 <= count && count < 2) return tickSpec(start, stop, count * 2);
  return [i1, i2, inc];
}

function ticks(start, stop, count) {
  stop = +stop, start = +start, count = +count;
  if (!(count > 0)) return [];
  if (start === stop) return [start];
  const reverse = stop < start, [i1, i2, inc] = reverse ? tickSpec(stop, start, count) : tickSpec(start, stop, count);
  if (!(i2 >= i1)) return [];
  const n = i2 - i1 + 1, ticks = new Array(n);
  if (reverse) {
    if (inc < 0) for (let i = 0; i < n; ++i) ticks[i] = (i2 - i) / -inc;
    else for (let i = 0; i < n; ++i) ticks[i] = (i2 - i) * inc;
  } else {
    if (inc < 0) for (let i = 0; i < n; ++i) ticks[i] = (i1 + i) / -inc;
    else for (let i = 0; i < n; ++i) ticks[i] = (i1 + i) * inc;
  }
  return ticks;
}

function tickIncrement(start, stop, count) {
  stop = +stop, start = +start, count = +count;
  return tickSpec(start, stop, count)[2];
}

function tickStep(start, stop, count) {
  stop = +stop, start = +start, count = +count;
  const reverse = stop < start, inc = reverse ? tickIncrement(stop, start, count) : tickIncrement(start, stop, count);
  return (reverse ? -1 : 1) * (inc < 0 ? 1 / -inc : inc);
}

function initRange(domain, range) {
  switch (arguments.length) {
    case 0: break;
    case 1: this.range(domain); break;
    default: this.range(range).domain(domain); break;
  }
  return this;
}

function define(constructor, factory, prototype) {
  constructor.prototype = factory.prototype = prototype;
  prototype.constructor = constructor;
}

function extend(parent, definition) {
  var prototype = Object.create(parent.prototype);
  for (var key in definition) prototype[key] = definition[key];
  return prototype;
}

function Color() {}

var darker = 0.7;
var brighter = 1 / darker;

var reI = "\\s*([+-]?\\d+)\\s*",
    reN = "\\s*([+-]?(?:\\d*\\.)?\\d+(?:[eE][+-]?\\d+)?)\\s*",
    reP = "\\s*([+-]?(?:\\d*\\.)?\\d+(?:[eE][+-]?\\d+)?)%\\s*",
    reHex = /^#([0-9a-f]{3,8})$/,
    reRgbInteger = new RegExp(`^rgb\\(${reI},${reI},${reI}\\)$`),
    reRgbPercent = new RegExp(`^rgb\\(${reP},${reP},${reP}\\)$`),
    reRgbaInteger = new RegExp(`^rgba\\(${reI},${reI},${reI},${reN}\\)$`),
    reRgbaPercent = new RegExp(`^rgba\\(${reP},${reP},${reP},${reN}\\)$`),
    reHslPercent = new RegExp(`^hsl\\(${reN},${reP},${reP}\\)$`),
    reHslaPercent = new RegExp(`^hsla\\(${reN},${reP},${reP},${reN}\\)$`);

var named = {
  aliceblue: 0xf0f8ff,
  antiquewhite: 0xfaebd7,
  aqua: 0x00ffff,
  aquamarine: 0x7fffd4,
  azure: 0xf0ffff,
  beige: 0xf5f5dc,
  bisque: 0xffe4c4,
  black: 0x000000,
  blanchedalmond: 0xffebcd,
  blue: 0x0000ff,
  blueviolet: 0x8a2be2,
  brown: 0xa52a2a,
  burlywood: 0xdeb887,
  cadetblue: 0x5f9ea0,
  chartreuse: 0x7fff00,
  chocolate: 0xd2691e,
  coral: 0xff7f50,
  cornflowerblue: 0x6495ed,
  cornsilk: 0xfff8dc,
  crimson: 0xdc143c,
  cyan: 0x00ffff,
  darkblue: 0x00008b,
  darkcyan: 0x008b8b,
  darkgoldenrod: 0xb8860b,
  darkgray: 0xa9a9a9,
  darkgreen: 0x006400,
  darkgrey: 0xa9a9a9,
  darkkhaki: 0xbdb76b,
  darkmagenta: 0x8b008b,
  darkolivegreen: 0x556b2f,
  darkorange: 0xff8c00,
  darkorchid: 0x9932cc,
  darkred: 0x8b0000,
  darksalmon: 0xe9967a,
  darkseagreen: 0x8fbc8f,
  darkslateblue: 0x483d8b,
  darkslategray: 0x2f4f4f,
  darkslategrey: 0x2f4f4f,
  darkturquoise: 0x00ced1,
  darkviolet: 0x9400d3,
  deeppink: 0xff1493,
  deepskyblue: 0x00bfff,
  dimgray: 0x696969,
  dimgrey: 0x696969,
  dodgerblue: 0x1e90ff,
  firebrick: 0xb22222,
  floralwhite: 0xfffaf0,
  forestgreen: 0x228b22,
  fuchsia: 0xff00ff,
  gainsboro: 0xdcdcdc,
  ghostwhite: 0xf8f8ff,
  gold: 0xffd700,
  goldenrod: 0xdaa520,
  gray: 0x808080,
  green: 0x008000,
  greenyellow: 0xadff2f,
  grey: 0x808080,
  honeydew: 0xf0fff0,
  hotpink: 0xff69b4,
  indianred: 0xcd5c5c,
  indigo: 0x4b0082,
  ivory: 0xfffff0,
  khaki: 0xf0e68c,
  lavender: 0xe6e6fa,
  lavenderblush: 0xfff0f5,
  lawngreen: 0x7cfc00,
  lemonchiffon: 0xfffacd,
  lightblue: 0xadd8e6,
  lightcoral: 0xf08080,
  lightcyan: 0xe0ffff,
  lightgoldenrodyellow: 0xfafad2,
  lightgray: 0xd3d3d3,
  lightgreen: 0x90ee90,
  lightgrey: 0xd3d3d3,
  lightpink: 0xffb6c1,
  lightsalmon: 0xffa07a,
  lightseagreen: 0x20b2aa,
  lightskyblue: 0x87cefa,
  lightslategray: 0x778899,
  lightslategrey: 0x778899,
  lightsteelblue: 0xb0c4de,
  lightyellow: 0xffffe0,
  lime: 0x00ff00,
  limegreen: 0x32cd32,
  linen: 0xfaf0e6,
  magenta: 0xff00ff,
  maroon: 0x800000,
  mediumaquamarine: 0x66cdaa,
  mediumblue: 0x0000cd,
  mediumorchid: 0xba55d3,
  mediumpurple: 0x9370db,
  mediumseagreen: 0x3cb371,
  mediumslateblue: 0x7b68ee,
  mediumspringgreen: 0x00fa9a,
  mediumturquoise: 0x48d1cc,
  mediumvioletred: 0xc71585,
  midnightblue: 0x191970,
  mintcream: 0xf5fffa,
  mistyrose: 0xffe4e1,
  moccasin: 0xffe4b5,
  navajowhite: 0xffdead,
  navy: 0x000080,
  oldlace: 0xfdf5e6,
  olive: 0x808000,
  olivedrab: 0x6b8e23,
  orange: 0xffa500,
  orangered: 0xff4500,
  orchid: 0xda70d6,
  palegoldenrod: 0xeee8aa,
  palegreen: 0x98fb98,
  paleturquoise: 0xafeeee,
  palevioletred: 0xdb7093,
  papayawhip: 0xffefd5,
  peachpuff: 0xffdab9,
  peru: 0xcd853f,
  pink: 0xffc0cb,
  plum: 0xdda0dd,
  powderblue: 0xb0e0e6,
  purple: 0x800080,
  rebeccapurple: 0x663399,
  red: 0xff0000,
  rosybrown: 0xbc8f8f,
  royalblue: 0x4169e1,
  saddlebrown: 0x8b4513,
  salmon: 0xfa8072,
  sandybrown: 0xf4a460,
  seagreen: 0x2e8b57,
  seashell: 0xfff5ee,
  sienna: 0xa0522d,
  silver: 0xc0c0c0,
  skyblue: 0x87ceeb,
  slateblue: 0x6a5acd,
  slategray: 0x708090,
  slategrey: 0x708090,
  snow: 0xfffafa,
  springgreen: 0x00ff7f,
  steelblue: 0x4682b4,
  tan: 0xd2b48c,
  teal: 0x008080,
  thistle: 0xd8bfd8,
  tomato: 0xff6347,
  turquoise: 0x40e0d0,
  violet: 0xee82ee,
  wheat: 0xf5deb3,
  white: 0xffffff,
  whitesmoke: 0xf5f5f5,
  yellow: 0xffff00,
  yellowgreen: 0x9acd32
};

define(Color, color, {
  copy(channels) {
    return Object.assign(new this.constructor, this, channels);
  },
  displayable() {
    return this.rgb().displayable();
  },
  hex: color_formatHex, // Deprecated! Use color.formatHex.
  formatHex: color_formatHex,
  formatHex8: color_formatHex8,
  formatHsl: color_formatHsl,
  formatRgb: color_formatRgb,
  toString: color_formatRgb
});

function color_formatHex() {
  return this.rgb().formatHex();
}

function color_formatHex8() {
  return this.rgb().formatHex8();
}

function color_formatHsl() {
  return hslConvert(this).formatHsl();
}

function color_formatRgb() {
  return this.rgb().formatRgb();
}

function color(format) {
  var m, l;
  format = (format + "").trim().toLowerCase();
  return (m = reHex.exec(format)) ? (l = m[1].length, m = parseInt(m[1], 16), l === 6 ? rgbn(m) // #ff0000
      : l === 3 ? new Rgb((m >> 8 & 0xf) | (m >> 4 & 0xf0), (m >> 4 & 0xf) | (m & 0xf0), ((m & 0xf) << 4) | (m & 0xf), 1) // #f00
      : l === 8 ? rgba(m >> 24 & 0xff, m >> 16 & 0xff, m >> 8 & 0xff, (m & 0xff) / 0xff) // #ff000000
      : l === 4 ? rgba((m >> 12 & 0xf) | (m >> 8 & 0xf0), (m >> 8 & 0xf) | (m >> 4 & 0xf0), (m >> 4 & 0xf) | (m & 0xf0), (((m & 0xf) << 4) | (m & 0xf)) / 0xff) // #f000
      : null) // invalid hex
      : (m = reRgbInteger.exec(format)) ? new Rgb(m[1], m[2], m[3], 1) // rgb(255, 0, 0)
      : (m = reRgbPercent.exec(format)) ? new Rgb(m[1] * 255 / 100, m[2] * 255 / 100, m[3] * 255 / 100, 1) // rgb(100%, 0%, 0%)
      : (m = reRgbaInteger.exec(format)) ? rgba(m[1], m[2], m[3], m[4]) // rgba(255, 0, 0, 1)
      : (m = reRgbaPercent.exec(format)) ? rgba(m[1] * 255 / 100, m[2] * 255 / 100, m[3] * 255 / 100, m[4]) // rgb(100%, 0%, 0%, 1)
      : (m = reHslPercent.exec(format)) ? hsla(m[1], m[2] / 100, m[3] / 100, 1) // hsl(120, 50%, 50%)
      : (m = reHslaPercent.exec(format)) ? hsla(m[1], m[2] / 100, m[3] / 100, m[4]) // hsla(120, 50%, 50%, 1)
      : named.hasOwnProperty(format) ? rgbn(named[format]) // eslint-disable-line no-prototype-builtins
      : format === "transparent" ? new Rgb(NaN, NaN, NaN, 0)
      : null;
}

function rgbn(n) {
  return new Rgb(n >> 16 & 0xff, n >> 8 & 0xff, n & 0xff, 1);
}

function rgba(r, g, b, a) {
  if (a <= 0) r = g = b = NaN;
  return new Rgb(r, g, b, a);
}

function rgbConvert(o) {
  if (!(o instanceof Color)) o = color(o);
  if (!o) return new Rgb;
  o = o.rgb();
  return new Rgb(o.r, o.g, o.b, o.opacity);
}

function rgb$1(r, g, b, opacity) {
  return arguments.length === 1 ? rgbConvert(r) : new Rgb(r, g, b, opacity == null ? 1 : opacity);
}

function Rgb(r, g, b, opacity) {
  this.r = +r;
  this.g = +g;
  this.b = +b;
  this.opacity = +opacity;
}

define(Rgb, rgb$1, extend(Color, {
  brighter(k) {
    k = k == null ? brighter : Math.pow(brighter, k);
    return new Rgb(this.r * k, this.g * k, this.b * k, this.opacity);
  },
  darker(k) {
    k = k == null ? darker : Math.pow(darker, k);
    return new Rgb(this.r * k, this.g * k, this.b * k, this.opacity);
  },
  rgb() {
    return this;
  },
  clamp() {
    return new Rgb(clampi(this.r), clampi(this.g), clampi(this.b), clampa(this.opacity));
  },
  displayable() {
    return (-0.5 <= this.r && this.r < 255.5)
        && (-0.5 <= this.g && this.g < 255.5)
        && (-0.5 <= this.b && this.b < 255.5)
        && (0 <= this.opacity && this.opacity <= 1);
  },
  hex: rgb_formatHex, // Deprecated! Use color.formatHex.
  formatHex: rgb_formatHex,
  formatHex8: rgb_formatHex8,
  formatRgb: rgb_formatRgb,
  toString: rgb_formatRgb
}));

function rgb_formatHex() {
  return `#${hex(this.r)}${hex(this.g)}${hex(this.b)}`;
}

function rgb_formatHex8() {
  return `#${hex(this.r)}${hex(this.g)}${hex(this.b)}${hex((isNaN(this.opacity) ? 1 : this.opacity) * 255)}`;
}

function rgb_formatRgb() {
  const a = clampa(this.opacity);
  return `${a === 1 ? "rgb(" : "rgba("}${clampi(this.r)}, ${clampi(this.g)}, ${clampi(this.b)}${a === 1 ? ")" : `, ${a})`}`;
}

function clampa(opacity) {
  return isNaN(opacity) ? 1 : Math.max(0, Math.min(1, opacity));
}

function clampi(value) {
  return Math.max(0, Math.min(255, Math.round(value) || 0));
}

function hex(value) {
  value = clampi(value);
  return (value < 16 ? "0" : "") + value.toString(16);
}

function hsla(h, s, l, a) {
  if (a <= 0) h = s = l = NaN;
  else if (l <= 0 || l >= 1) h = s = NaN;
  else if (s <= 0) h = NaN;
  return new Hsl(h, s, l, a);
}

function hslConvert(o) {
  if (o instanceof Hsl) return new Hsl(o.h, o.s, o.l, o.opacity);
  if (!(o instanceof Color)) o = color(o);
  if (!o) return new Hsl;
  if (o instanceof Hsl) return o;
  o = o.rgb();
  var r = o.r / 255,
      g = o.g / 255,
      b = o.b / 255,
      min = Math.min(r, g, b),
      max = Math.max(r, g, b),
      h = NaN,
      s = max - min,
      l = (max + min) / 2;
  if (s) {
    if (r === max) h = (g - b) / s + (g < b) * 6;
    else if (g === max) h = (b - r) / s + 2;
    else h = (r - g) / s + 4;
    s /= l < 0.5 ? max + min : 2 - max - min;
    h *= 60;
  } else {
    s = l > 0 && l < 1 ? 0 : h;
  }
  return new Hsl(h, s, l, o.opacity);
}

function hsl(h, s, l, opacity) {
  return arguments.length === 1 ? hslConvert(h) : new Hsl(h, s, l, opacity == null ? 1 : opacity);
}

function Hsl(h, s, l, opacity) {
  this.h = +h;
  this.s = +s;
  this.l = +l;
  this.opacity = +opacity;
}

define(Hsl, hsl, extend(Color, {
  brighter(k) {
    k = k == null ? brighter : Math.pow(brighter, k);
    return new Hsl(this.h, this.s, this.l * k, this.opacity);
  },
  darker(k) {
    k = k == null ? darker : Math.pow(darker, k);
    return new Hsl(this.h, this.s, this.l * k, this.opacity);
  },
  rgb() {
    var h = this.h % 360 + (this.h < 0) * 360,
        s = isNaN(h) || isNaN(this.s) ? 0 : this.s,
        l = this.l,
        m2 = l + (l < 0.5 ? l : 1 - l) * s,
        m1 = 2 * l - m2;
    return new Rgb(
      hsl2rgb(h >= 240 ? h - 240 : h + 120, m1, m2),
      hsl2rgb(h, m1, m2),
      hsl2rgb(h < 120 ? h + 240 : h - 120, m1, m2),
      this.opacity
    );
  },
  clamp() {
    return new Hsl(clamph(this.h), clampt(this.s), clampt(this.l), clampa(this.opacity));
  },
  displayable() {
    return (0 <= this.s && this.s <= 1 || isNaN(this.s))
        && (0 <= this.l && this.l <= 1)
        && (0 <= this.opacity && this.opacity <= 1);
  },
  formatHsl() {
    const a = clampa(this.opacity);
    return `${a === 1 ? "hsl(" : "hsla("}${clamph(this.h)}, ${clampt(this.s) * 100}%, ${clampt(this.l) * 100}%${a === 1 ? ")" : `, ${a})`}`;
  }
}));

function clamph(value) {
  value = (value || 0) % 360;
  return value < 0 ? value + 360 : value;
}

function clampt(value) {
  return Math.max(0, Math.min(1, value || 0));
}

/* From FvD 13.37, CSS Color Module Level 3 */
function hsl2rgb(h, m1, m2) {
  return (h < 60 ? m1 + (m2 - m1) * h / 60
      : h < 180 ? m2
      : h < 240 ? m1 + (m2 - m1) * (240 - h) / 60
      : m1) * 255;
}

var constant = x => () => x;

function linear$1(a, d) {
  return function(t) {
    return a + t * d;
  };
}

function exponential(a, b, y) {
  return a = Math.pow(a, y), b = Math.pow(b, y) - a, y = 1 / y, function(t) {
    return Math.pow(a + t * b, y);
  };
}

function gamma(y) {
  return (y = +y) === 1 ? nogamma : function(a, b) {
    return b - a ? exponential(a, b, y) : constant(isNaN(a) ? b : a);
  };
}

function nogamma(a, b) {
  var d = b - a;
  return d ? linear$1(a, d) : constant(isNaN(a) ? b : a);
}

var rgb = (function rgbGamma(y) {
  var color = gamma(y);

  function rgb(start, end) {
    var r = color((start = rgb$1(start)).r, (end = rgb$1(end)).r),
        g = color(start.g, end.g),
        b = color(start.b, end.b),
        opacity = nogamma(start.opacity, end.opacity);
    return function(t) {
      start.r = r(t);
      start.g = g(t);
      start.b = b(t);
      start.opacity = opacity(t);
      return start + "";
    };
  }

  rgb.gamma = rgbGamma;

  return rgb;
})(1);

function numberArray(a, b) {
  if (!b) b = [];
  var n = a ? Math.min(b.length, a.length) : 0,
      c = b.slice(),
      i;
  return function(t) {
    for (i = 0; i < n; ++i) c[i] = a[i] * (1 - t) + b[i] * t;
    return c;
  };
}

function isNumberArray(x) {
  return ArrayBuffer.isView(x) && !(x instanceof DataView);
}

function genericArray(a, b) {
  var nb = b ? b.length : 0,
      na = a ? Math.min(nb, a.length) : 0,
      x = new Array(na),
      c = new Array(nb),
      i;

  for (i = 0; i < na; ++i) x[i] = interpolate(a[i], b[i]);
  for (; i < nb; ++i) c[i] = b[i];

  return function(t) {
    for (i = 0; i < na; ++i) c[i] = x[i](t);
    return c;
  };
}

function date(a, b) {
  var d = new Date;
  return a = +a, b = +b, function(t) {
    return d.setTime(a * (1 - t) + b * t), d;
  };
}

function interpolateNumber(a, b) {
  return a = +a, b = +b, function(t) {
    return a * (1 - t) + b * t;
  };
}

function object(a, b) {
  var i = {},
      c = {},
      k;

  if (a === null || typeof a !== "object") a = {};
  if (b === null || typeof b !== "object") b = {};

  for (k in b) {
    if (k in a) {
      i[k] = interpolate(a[k], b[k]);
    } else {
      c[k] = b[k];
    }
  }

  return function(t) {
    for (k in i) c[k] = i[k](t);
    return c;
  };
}

var reA = /[-+]?(?:\d+\.?\d*|\.?\d+)(?:[eE][-+]?\d+)?/g,
    reB = new RegExp(reA.source, "g");

function zero(b) {
  return function() {
    return b;
  };
}

function one(b) {
  return function(t) {
    return b(t) + "";
  };
}

function string(a, b) {
  var bi = reA.lastIndex = reB.lastIndex = 0, // scan index for next number in b
      am, // current match in a
      bm, // current match in b
      bs, // string preceding current number in b, if any
      i = -1, // index in s
      s = [], // string constants and placeholders
      q = []; // number interpolators

  // Coerce inputs to strings.
  a = a + "", b = b + "";

  // Interpolate pairs of numbers in a & b.
  while ((am = reA.exec(a))
      && (bm = reB.exec(b))) {
    if ((bs = bm.index) > bi) { // a string precedes the next number in b
      bs = b.slice(bi, bs);
      if (s[i]) s[i] += bs; // coalesce with previous string
      else s[++i] = bs;
    }
    if ((am = am[0]) === (bm = bm[0])) { // numbers in a & b match
      if (s[i]) s[i] += bm; // coalesce with previous string
      else s[++i] = bm;
    } else { // interpolate non-matching numbers
      s[++i] = null;
      q.push({i: i, x: interpolateNumber(am, bm)});
    }
    bi = reB.lastIndex;
  }

  // Add remains of b.
  if (bi < b.length) {
    bs = b.slice(bi);
    if (s[i]) s[i] += bs; // coalesce with previous string
    else s[++i] = bs;
  }

  // Special optimization for only a single match.
  // Otherwise, interpolate each of the numbers and rejoin the string.
  return s.length < 2 ? (q[0]
      ? one(q[0].x)
      : zero(b))
      : (b = q.length, function(t) {
          for (var i = 0, o; i < b; ++i) s[(o = q[i]).i] = o.x(t);
          return s.join("");
        });
}

function interpolate(a, b) {
  var t = typeof b, c;
  return b == null || t === "boolean" ? constant(b)
      : (t === "number" ? interpolateNumber
      : t === "string" ? ((c = color(b)) ? (b = c, rgb) : string)
      : b instanceof color ? rgb
      : b instanceof Date ? date
      : isNumberArray(b) ? numberArray
      : Array.isArray(b) ? genericArray
      : typeof b.valueOf !== "function" && typeof b.toString !== "function" || isNaN(b) ? object
      : interpolateNumber)(a, b);
}

function interpolateRound(a, b) {
  return a = +a, b = +b, function(t) {
    return Math.round(a * (1 - t) + b * t);
  };
}

function constants(x) {
  return function() {
    return x;
  };
}

function number(x) {
  return +x;
}

var unit = [0, 1];

function identity$1(x) {
  return x;
}

function normalize$1(a, b) {
  return (b -= (a = +a))
      ? function(x) { return (x - a) / b; }
      : constants(isNaN(b) ? NaN : 0.5);
}

function clamper(a, b) {
  var t;
  if (a > b) t = a, a = b, b = t;
  return function(x) { return Math.max(a, Math.min(b, x)); };
}

// normalize(a, b)(x) takes a domain value x in [a,b] and returns the corresponding parameter t in [0,1].
// interpolate(a, b)(t) takes a parameter t in [0,1] and returns the corresponding range value x in [a,b].
function bimap(domain, range, interpolate) {
  var d0 = domain[0], d1 = domain[1], r0 = range[0], r1 = range[1];
  if (d1 < d0) d0 = normalize$1(d1, d0), r0 = interpolate(r1, r0);
  else d0 = normalize$1(d0, d1), r0 = interpolate(r0, r1);
  return function(x) { return r0(d0(x)); };
}

function polymap(domain, range, interpolate) {
  var j = Math.min(domain.length, range.length) - 1,
      d = new Array(j),
      r = new Array(j),
      i = -1;

  // Reverse descending domains.
  if (domain[j] < domain[0]) {
    domain = domain.slice().reverse();
    range = range.slice().reverse();
  }

  while (++i < j) {
    d[i] = normalize$1(domain[i], domain[i + 1]);
    r[i] = interpolate(range[i], range[i + 1]);
  }

  return function(x) {
    var i = bisect(domain, x, 1, j) - 1;
    return r[i](d[i](x));
  };
}

function copy(source, target) {
  return target
      .domain(source.domain())
      .range(source.range())
      .interpolate(source.interpolate())
      .clamp(source.clamp())
      .unknown(source.unknown());
}

function transformer() {
  var domain = unit,
      range = unit,
      interpolate$1 = interpolate,
      transform,
      untransform,
      unknown,
      clamp = identity$1,
      piecewise,
      output,
      input;

  function rescale() {
    var n = Math.min(domain.length, range.length);
    if (clamp !== identity$1) clamp = clamper(domain[0], domain[n - 1]);
    piecewise = n > 2 ? polymap : bimap;
    output = input = null;
    return scale;
  }

  function scale(x) {
    return x == null || isNaN(x = +x) ? unknown : (output || (output = piecewise(domain.map(transform), range, interpolate$1)))(transform(clamp(x)));
  }

  scale.invert = function(y) {
    return clamp(untransform((input || (input = piecewise(range, domain.map(transform), interpolateNumber)))(y)));
  };

  scale.domain = function(_) {
    return arguments.length ? (domain = Array.from(_, number), rescale()) : domain.slice();
  };

  scale.range = function(_) {
    return arguments.length ? (range = Array.from(_), rescale()) : range.slice();
  };

  scale.rangeRound = function(_) {
    return range = Array.from(_), interpolate$1 = interpolateRound, rescale();
  };

  scale.clamp = function(_) {
    return arguments.length ? (clamp = _ ? true : identity$1, rescale()) : clamp !== identity$1;
  };

  scale.interpolate = function(_) {
    return arguments.length ? (interpolate$1 = _, rescale()) : interpolate$1;
  };

  scale.unknown = function(_) {
    return arguments.length ? (unknown = _, scale) : unknown;
  };

  return function(t, u) {
    transform = t, untransform = u;
    return rescale();
  };
}

function continuous() {
  return transformer()(identity$1, identity$1);
}

function formatDecimal(x) {
  return Math.abs(x = Math.round(x)) >= 1e21
      ? x.toLocaleString("en").replace(/,/g, "")
      : x.toString(10);
}

// Computes the decimal coefficient and exponent of the specified number x with
// significant digits p, where x is positive and p is in [1, 21] or undefined.
// For example, formatDecimalParts(1.23) returns ["123", 0].
function formatDecimalParts(x, p) {
  if ((i = (x = p ? x.toExponential(p - 1) : x.toExponential()).indexOf("e")) < 0) return null; // NaN, ±Infinity
  var i, coefficient = x.slice(0, i);

  // The string returned by toExponential either has the form \d\.\d+e[-+]\d+
  // (e.g., 1.2e+3) or the form \de[-+]\d+ (e.g., 1e+3).
  return [
    coefficient.length > 1 ? coefficient[0] + coefficient.slice(2) : coefficient,
    +x.slice(i + 1)
  ];
}

function exponent(x) {
  return x = formatDecimalParts(Math.abs(x)), x ? x[1] : NaN;
}

function formatGroup(grouping, thousands) {
  return function(value, width) {
    var i = value.length,
        t = [],
        j = 0,
        g = grouping[0],
        length = 0;

    while (i > 0 && g > 0) {
      if (length + g + 1 > width) g = Math.max(1, width - length);
      t.push(value.substring(i -= g, i + g));
      if ((length += g + 1) > width) break;
      g = grouping[j = (j + 1) % grouping.length];
    }

    return t.reverse().join(thousands);
  };
}

function formatNumerals(numerals) {
  return function(value) {
    return value.replace(/[0-9]/g, function(i) {
      return numerals[+i];
    });
  };
}

// [[fill]align][sign][symbol][0][width][,][.precision][~][type]
var re = /^(?:(.)?([<>=^]))?([+\-( ])?([$#])?(0)?(\d+)?(,)?(\.\d+)?(~)?([a-z%])?$/i;

function formatSpecifier(specifier) {
  if (!(match = re.exec(specifier))) throw new Error("invalid format: " + specifier);
  var match;
  return new FormatSpecifier({
    fill: match[1],
    align: match[2],
    sign: match[3],
    symbol: match[4],
    zero: match[5],
    width: match[6],
    comma: match[7],
    precision: match[8] && match[8].slice(1),
    trim: match[9],
    type: match[10]
  });
}

formatSpecifier.prototype = FormatSpecifier.prototype; // instanceof

function FormatSpecifier(specifier) {
  this.fill = specifier.fill === undefined ? " " : specifier.fill + "";
  this.align = specifier.align === undefined ? ">" : specifier.align + "";
  this.sign = specifier.sign === undefined ? "-" : specifier.sign + "";
  this.symbol = specifier.symbol === undefined ? "" : specifier.symbol + "";
  this.zero = !!specifier.zero;
  this.width = specifier.width === undefined ? undefined : +specifier.width;
  this.comma = !!specifier.comma;
  this.precision = specifier.precision === undefined ? undefined : +specifier.precision;
  this.trim = !!specifier.trim;
  this.type = specifier.type === undefined ? "" : specifier.type + "";
}

FormatSpecifier.prototype.toString = function() {
  return this.fill
      + this.align
      + this.sign
      + this.symbol
      + (this.zero ? "0" : "")
      + (this.width === undefined ? "" : Math.max(1, this.width | 0))
      + (this.comma ? "," : "")
      + (this.precision === undefined ? "" : "." + Math.max(0, this.precision | 0))
      + (this.trim ? "~" : "")
      + this.type;
};

// Trims insignificant zeros, e.g., replaces 1.2000k with 1.2k.
function formatTrim(s) {
  out: for (var n = s.length, i = 1, i0 = -1, i1; i < n; ++i) {
    switch (s[i]) {
      case ".": i0 = i1 = i; break;
      case "0": if (i0 === 0) i0 = i; i1 = i; break;
      default: if (!+s[i]) break out; if (i0 > 0) i0 = 0; break;
    }
  }
  return i0 > 0 ? s.slice(0, i0) + s.slice(i1 + 1) : s;
}

var prefixExponent;

function formatPrefixAuto(x, p) {
  var d = formatDecimalParts(x, p);
  if (!d) return x + "";
  var coefficient = d[0],
      exponent = d[1],
      i = exponent - (prefixExponent = Math.max(-8, Math.min(8, Math.floor(exponent / 3))) * 3) + 1,
      n = coefficient.length;
  return i === n ? coefficient
      : i > n ? coefficient + new Array(i - n + 1).join("0")
      : i > 0 ? coefficient.slice(0, i) + "." + coefficient.slice(i)
      : "0." + new Array(1 - i).join("0") + formatDecimalParts(x, Math.max(0, p + i - 1))[0]; // less than 1y!
}

function formatRounded(x, p) {
  var d = formatDecimalParts(x, p);
  if (!d) return x + "";
  var coefficient = d[0],
      exponent = d[1];
  return exponent < 0 ? "0." + new Array(-exponent).join("0") + coefficient
      : coefficient.length > exponent + 1 ? coefficient.slice(0, exponent + 1) + "." + coefficient.slice(exponent + 1)
      : coefficient + new Array(exponent - coefficient.length + 2).join("0");
}

var formatTypes = {
  "%": (x, p) => (x * 100).toFixed(p),
  "b": (x) => Math.round(x).toString(2),
  "c": (x) => x + "",
  "d": formatDecimal,
  "e": (x, p) => x.toExponential(p),
  "f": (x, p) => x.toFixed(p),
  "g": (x, p) => x.toPrecision(p),
  "o": (x) => Math.round(x).toString(8),
  "p": (x, p) => formatRounded(x * 100, p),
  "r": formatRounded,
  "s": formatPrefixAuto,
  "X": (x) => Math.round(x).toString(16).toUpperCase(),
  "x": (x) => Math.round(x).toString(16)
};

function identity(x) {
  return x;
}

var map = Array.prototype.map,
    prefixes = ["y","z","a","f","p","n","µ","m","","k","M","G","T","P","E","Z","Y"];

function formatLocale(locale) {
  var group = locale.grouping === undefined || locale.thousands === undefined ? identity : formatGroup(map.call(locale.grouping, Number), locale.thousands + ""),
      currencyPrefix = locale.currency === undefined ? "" : locale.currency[0] + "",
      currencySuffix = locale.currency === undefined ? "" : locale.currency[1] + "",
      decimal = locale.decimal === undefined ? "." : locale.decimal + "",
      numerals = locale.numerals === undefined ? identity : formatNumerals(map.call(locale.numerals, String)),
      percent = locale.percent === undefined ? "%" : locale.percent + "",
      minus = locale.minus === undefined ? "−" : locale.minus + "",
      nan = locale.nan === undefined ? "NaN" : locale.nan + "";

  function newFormat(specifier) {
    specifier = formatSpecifier(specifier);

    var fill = specifier.fill,
        align = specifier.align,
        sign = specifier.sign,
        symbol = specifier.symbol,
        zero = specifier.zero,
        width = specifier.width,
        comma = specifier.comma,
        precision = specifier.precision,
        trim = specifier.trim,
        type = specifier.type;

    // The "n" type is an alias for ",g".
    if (type === "n") comma = true, type = "g";

    // The "" type, and any invalid type, is an alias for ".12~g".
    else if (!formatTypes[type]) precision === undefined && (precision = 12), trim = true, type = "g";

    // If zero fill is specified, padding goes after sign and before digits.
    if (zero || (fill === "0" && align === "=")) zero = true, fill = "0", align = "=";

    // Compute the prefix and suffix.
    // For SI-prefix, the suffix is lazily computed.
    var prefix = symbol === "$" ? currencyPrefix : symbol === "#" && /[boxX]/.test(type) ? "0" + type.toLowerCase() : "",
        suffix = symbol === "$" ? currencySuffix : /[%p]/.test(type) ? percent : "";

    // What format function should we use?
    // Is this an integer type?
    // Can this type generate exponential notation?
    var formatType = formatTypes[type],
        maybeSuffix = /[defgprs%]/.test(type);

    // Set the default precision if not specified,
    // or clamp the specified precision to the supported range.
    // For significant precision, it must be in [1, 21].
    // For fixed precision, it must be in [0, 20].
    precision = precision === undefined ? 6
        : /[gprs]/.test(type) ? Math.max(1, Math.min(21, precision))
        : Math.max(0, Math.min(20, precision));

    function format(value) {
      var valuePrefix = prefix,
          valueSuffix = suffix,
          i, n, c;

      if (type === "c") {
        valueSuffix = formatType(value) + valueSuffix;
        value = "";
      } else {
        value = +value;

        // Determine the sign. -0 is not less than 0, but 1 / -0 is!
        var valueNegative = value < 0 || 1 / value < 0;

        // Perform the initial formatting.
        value = isNaN(value) ? nan : formatType(Math.abs(value), precision);

        // Trim insignificant zeros.
        if (trim) value = formatTrim(value);

        // If a negative value rounds to zero after formatting, and no explicit positive sign is requested, hide the sign.
        if (valueNegative && +value === 0 && sign !== "+") valueNegative = false;

        // Compute the prefix and suffix.
        valuePrefix = (valueNegative ? (sign === "(" ? sign : minus) : sign === "-" || sign === "(" ? "" : sign) + valuePrefix;
        valueSuffix = (type === "s" ? prefixes[8 + prefixExponent / 3] : "") + valueSuffix + (valueNegative && sign === "(" ? ")" : "");

        // Break the formatted value into the integer “value” part that can be
        // grouped, and fractional or exponential “suffix” part that is not.
        if (maybeSuffix) {
          i = -1, n = value.length;
          while (++i < n) {
            if (c = value.charCodeAt(i), 48 > c || c > 57) {
              valueSuffix = (c === 46 ? decimal + value.slice(i + 1) : value.slice(i)) + valueSuffix;
              value = value.slice(0, i);
              break;
            }
          }
        }
      }

      // If the fill character is not "0", grouping is applied before padding.
      if (comma && !zero) value = group(value, Infinity);

      // Compute the padding.
      var length = valuePrefix.length + value.length + valueSuffix.length,
          padding = length < width ? new Array(width - length + 1).join(fill) : "";

      // If the fill character is "0", grouping is applied after padding.
      if (comma && zero) value = group(padding + value, padding.length ? width - valueSuffix.length : Infinity), padding = "";

      // Reconstruct the final output based on the desired alignment.
      switch (align) {
        case "<": value = valuePrefix + value + valueSuffix + padding; break;
        case "=": value = valuePrefix + padding + value + valueSuffix; break;
        case "^": value = padding.slice(0, length = padding.length >> 1) + valuePrefix + value + valueSuffix + padding.slice(length); break;
        default: value = padding + valuePrefix + value + valueSuffix; break;
      }

      return numerals(value);
    }

    format.toString = function() {
      return specifier + "";
    };

    return format;
  }

  function formatPrefix(specifier, value) {
    var f = newFormat((specifier = formatSpecifier(specifier), specifier.type = "f", specifier)),
        e = Math.max(-8, Math.min(8, Math.floor(exponent(value) / 3))) * 3,
        k = Math.pow(10, -e),
        prefix = prefixes[8 + e / 3];
    return function(value) {
      return f(k * value) + prefix;
    };
  }

  return {
    format: newFormat,
    formatPrefix: formatPrefix
  };
}

var locale;
var format;
var formatPrefix;

defaultLocale({
  thousands: ",",
  grouping: [3],
  currency: ["$", ""]
});

function defaultLocale(definition) {
  locale = formatLocale(definition);
  format = locale.format;
  formatPrefix = locale.formatPrefix;
  return locale;
}

function precisionFixed(step) {
  return Math.max(0, -exponent(Math.abs(step)));
}

function precisionPrefix(step, value) {
  return Math.max(0, Math.max(-8, Math.min(8, Math.floor(exponent(value) / 3))) * 3 - exponent(Math.abs(step)));
}

function precisionRound(step, max) {
  step = Math.abs(step), max = Math.abs(max) - step;
  return Math.max(0, exponent(max) - exponent(step)) + 1;
}

function tickFormat(start, stop, count, specifier) {
  var step = tickStep(start, stop, count),
      precision;
  specifier = formatSpecifier(specifier == null ? ",f" : specifier);
  switch (specifier.type) {
    case "s": {
      var value = Math.max(Math.abs(start), Math.abs(stop));
      if (specifier.precision == null && !isNaN(precision = precisionPrefix(step, value))) specifier.precision = precision;
      return formatPrefix(specifier, value);
    }
    case "":
    case "e":
    case "g":
    case "p":
    case "r": {
      if (specifier.precision == null && !isNaN(precision = precisionRound(step, Math.max(Math.abs(start), Math.abs(stop))))) specifier.precision = precision - (specifier.type === "e");
      break;
    }
    case "f":
    case "%": {
      if (specifier.precision == null && !isNaN(precision = precisionFixed(step))) specifier.precision = precision - (specifier.type === "%") * 2;
      break;
    }
  }
  return format(specifier);
}

function linearish(scale) {
  var domain = scale.domain;

  scale.ticks = function(count) {
    var d = domain();
    return ticks(d[0], d[d.length - 1], count == null ? 10 : count);
  };

  scale.tickFormat = function(count, specifier) {
    var d = domain();
    return tickFormat(d[0], d[d.length - 1], count == null ? 10 : count, specifier);
  };

  scale.nice = function(count) {
    if (count == null) count = 10;

    var d = domain();
    var i0 = 0;
    var i1 = d.length - 1;
    var start = d[i0];
    var stop = d[i1];
    var prestep;
    var step;
    var maxIter = 10;

    if (stop < start) {
      step = start, start = stop, stop = step;
      step = i0, i0 = i1, i1 = step;
    }
    
    while (maxIter-- > 0) {
      step = tickIncrement(start, stop, count);
      if (step === prestep) {
        d[i0] = start;
        d[i1] = stop;
        return domain(d);
      } else if (step > 0) {
        start = Math.floor(start / step) * step;
        stop = Math.ceil(stop / step) * step;
      } else if (step < 0) {
        start = Math.ceil(start * step) / step;
        stop = Math.floor(stop * step) / step;
      } else {
        break;
      }
      prestep = step;
    }

    return scale;
  };

  return scale;
}

function linear() {
  var scale = continuous();

  scale.copy = function() {
    return copy(scale, linear());
  };

  initRange.apply(scale, arguments);

  return linearish(scale);
}

const MIN_WHEEL_DELAY = 300;
/**
 * @alias sGis.painter.domPainter.EventDispatcher
 */

class EventDispatcher {
  constructor(baseNode, master) {
    this._hoverRender = null;
    this._master = master;

    this._setListeners(baseNode);

    this._baseNode = baseNode;
    this._onDocumentMousemove = this._onDocumentMousemove.bind(this);
    this._onDocumentMouseup = this._onDocumentMouseup.bind(this);
    this._wheelTimer = 0;
    this._touchHandler = {
      dragPrevPosition: []
    };
  }

  _dispatchEvent(event) {
    let map = this._master.map;
    let layerRenderers = this._master.layerRenderers;
    let position = [event.point.x / map.resolution, -event.point.y / map.resolution];

    if (event instanceof sGisMouseOutEvent) {
      if (this._hoverRender) this._hoverRender.triggerEvent(event);
      this._hoverRender = null;
      if (!event.isCanceled) map.fire(event);
      return;
    }

    let mouseOutEvent, mouseOverEvent;
    let targetRender = null;

    for (let i = layerRenderers.length - 1; i >= 0; i--) {
      targetRender = layerRenderers[i].getEventCatcher(event.eventFlag, position);
      if (targetRender) break;
    }

    if (!mouseOutEvent && event instanceof sGisMouseMoveEvent && this._hoverRender !== targetRender) {
      if (this._hoverRender) {
        mouseOutEvent = new sGisMouseOutEvent(event);

        this._hoverRender.triggerEvent(mouseOutEvent);
      }

      if (targetRender) {
        mouseOverEvent = new sGisMouseOverEvent(event);
        targetRender.triggerEvent(mouseOverEvent);
      }

      this._hoverRender = targetRender;
    }

    if (targetRender) targetRender.triggerEvent(event);
    if (!event.isCanceled) map.fire(event);
  }

  _listenFor(node, eventType, handler) {
    listenDomEvent(node, eventType, event => {
      let target = event && event.target;
      let cancelEvent = event.defaultPrevented;

      while (target && target !== node) {
        if (target.doNotBubbleToMap) {
          cancelEvent = true;
          break;
        }

        target = target.parentNode;
      }

      if (!cancelEvent) handler(event);
    });
  }

  _setListeners(baseNode) {
    this._listenFor(baseNode, 'mousedown', this._onmousedown.bind(this));

    this._listenFor(baseNode, 'mousedown', this._onmousedown.bind(this));

    this._listenFor(baseNode, 'wheel', this._onwheel.bind(this));

    this._listenFor(baseNode, 'click', this._onclick.bind(this));

    this._listenFor(baseNode, 'dblclick', this._ondblclick.bind(this));

    this._listenFor(baseNode, 'mousemove', this._onmousemove.bind(this));

    this._listenFor(baseNode, 'mouseleave', this._onmouseleave.bind(this));

    this._listenFor(baseNode, 'touchstart', this._ontouchstart.bind(this));

    this._listenFor(baseNode, 'touchmove', this._ontouchmove.bind(this));

    this._listenFor(baseNode, 'touchend', this._ontouchend.bind(this));
  }

  _onmousedown(event) {
    this._clickCatcher = true;

    if (event.which === 1) {
      this._dragPosition = getMouseOffset(event.currentTarget, event);
      listenDomEvent(document, 'mousemove', this._onDocumentMousemove);
      listenDomEvent(document, 'mouseup', this._onDocumentMouseup);

      document.ondragstart = function () {
        return false;
      };

      document.body.onselectstart = function () {
        return false;
      };
    }

    event.preventDefault();
    event.stopPropagation();
  }

  _onDocumentMousemove(event) {
    let map = this._master.map;
    let mousePosition = getMouseOffset(this._master.wrapper, event);
    let dxPx = this._dragPosition.x - mousePosition.x;
    let dyPx = this._dragPosition.y - mousePosition.y;
    let resolution = map.resolution;

    let point = this._master.getPointFromPxPosition(mousePosition.x, mousePosition.y);

    if (Math.abs(dxPx) > 2 || Math.abs(dyPx) > 2 || !this._clickCatcher) {
      this._lastDrag = {
        x: dxPx * resolution,
        y: -dyPx * resolution
      };

      if (this._clickCatcher) {
        this._clickCatcher = null;

        let originalPoint = this._master.getPointFromPxPosition(this._dragPosition.x, this._dragPosition.y);

        let dragStartEvent = new DragStartEvent(map, {
          point: originalPoint,
          browserEvent: event
        });

        this._dispatchEvent(dragStartEvent);

        this._draggingObject = dragStartEvent.draggingObject;
      }

      this._dragPosition = mousePosition;
      let dragEvent = new DragEvent({
        point,
        browserEvent: event,
        offset: [this._lastDrag.x, this._lastDrag.y],
        pxOffset: [dxPx, dyPx]
      });

      this._draggingObject.fire(dragEvent);
    }
  }

  _onDocumentMouseup(event) {
    this._clearDocumentListeners();

    if (this._draggingObject) {
      this._draggingObject.fire(new DragEndEvent(this._getMouseEventDescription(event)));
    }

    this._draggingObject = null;
    this._lastDrag = null;
  }

  remove() {
    this._clearDocumentListeners();
  }

  _clearDocumentListeners() {
    removeDomEventListener(document, 'mousemove', this._onDocumentMousemove);
    removeDomEventListener(document, 'mouseup', this._onDocumentMouseup);
    document.ondragstart = null;
    document.body.onselectstart = null;
  }

  _onwheel(event) {
    let time = Date.now();

    if (time - this._wheelTimer > MIN_WHEEL_DELAY) {
      this._wheelTimer = time;
      let map = this._master.map;
      let wheelDirection = getWheelDirection(event);
      let mouseOffset = getMouseOffset(event.currentTarget, event);
      map.zoom(wheelDirection, this._master.getPointFromPxPosition(mouseOffset.x, mouseOffset.y));
    }

    event.preventDefault();
  }

  _getMouseEventDescription(event) {
    let mouseOffset = getMouseOffset(event.currentTarget, event);

    let point = this._master.getPointFromPxPosition(mouseOffset.x, mouseOffset.y);

    return {
      point: point,
      browserEvent: event
    };
  }

  _onclick(event) {
    if (this._clickCatcher) {
      this._dispatchEvent(new sGisClickEvent(this._getMouseEventDescription(event)));
    }
  }

  _ondblclick(event) {
    this._clickCatcher = null;

    this._dispatchEvent(new sGisDoubleClickEvent(this._getMouseEventDescription(event)));
  }

  _onmousemove(event) {
    this._dispatchEvent(new sGisMouseMoveEvent(this._getMouseEventDescription(event)));
  }

  _onmouseleave(event) {
    if (event.target === this._baseNode) this._dispatchEvent(new sGisMouseOutEvent(this._getMouseEventDescription(event)));
  }

  _ontouchstart(event) {
    if (!this._touches) this._touches = [];

    for (let i = 0; i < event.changedTouches.length; i++) {
      let touch = event.changedTouches[i];
      if (!this._touches.some(t => t.id === touch.identifier)) this._touches.push({
        id: touch.identifier,
        position: [touch.pageX, touch.pageY]
      });
    }

    this._touchHandler.lastDrag = {
      x: 0,
      y: 0
    };
    if (event.touches.length > 1) event.preventDefault();
  }

  _ontouchmove(event) {
    this._clearTouches(event);

    let touches = Array.prototype.slice.apply(event.touches);
    let map = this._master.map;

    if (touches.length === 1 && this._touchHandler.lastDrag) {
      let touch = event.targetTouches[0];
      let [prevX, prevY] = this._touches[0].position;
      let dxPx = prevX - touch.pageX;
      let dyPx = prevY - touch.pageY;
      let resolution = map.resolution;
      let touchOffset = getMouseOffset(event.currentTarget, touch);

      let point = this._master.getPointFromPxPosition(touchOffset.x, touchOffset.y);

      let fakeMouseEvent = new MouseEvent('mousemove', event);

      if (this._touchHandler.lastDrag.x === 0 && this._touchHandler.lastDrag.y === 0) {
        let dragStartEvent = new DragStartEvent(map, {
          point,
          browserEvent: fakeMouseEvent
        });

        this._dispatchEvent(dragStartEvent);

        this._draggingObject = dragStartEvent.draggingObject;
      }

      this._touchHandler.lastDrag = {
        x: dxPx * resolution,
        y: 0 - dyPx * resolution
      };
      let dragEvent = new DragEvent({
        point,
        browserEvent: fakeMouseEvent,
        offset: [this._touchHandler.lastDrag.x, this._touchHandler.lastDrag.y],
        pxOffset: [dxPx, dyPx]
      });

      this._draggingObject.fire(dragEvent);

      this._touches[0].position = [touch.pageX, touch.pageY];
    } else if (touches.length > 1) {
      this._master.forbidUpdate();

      this._touchHandler.lastDrag = null;
      this._touchHandler.scaleChanged = true;
      let t1 = touches.find(t => t.identifier === this._touches[0].id);
      let t2 = touches.find(t => t.identifier === this._touches[1].id);
      let [x11, y11] = this._touches[0].position;
      let [x12, y12] = [t1.pageX, t1.pageY];
      let [x21, y21] = this._touches[1].position;
      let [x22, y22] = [t2.pageX, t2.pageY];
      let c1 = [(x11 + x21) / 2, (y11 + y21) / 2];
      let c2 = [(x12 + x22) / 2, (y12 + y22) / 2];
      let base = [(c1[0] + c2[0]) / 2, (c1[1] + c2[1]) / 2];
      let len1 = Math.sqrt(Math.pow(x11 - x21, 2) + Math.pow(y11 - y21, 2));
      let len2 = Math.sqrt(Math.pow(x12 - x22, 2) + Math.pow(y12 - y22, 2));
      let baseOffset = getMouseOffset(event.target, {
        pageX: base[0],
        pageY: base[1]
      });

      let basePoint = this._master.getPointFromPxPosition(baseOffset.x, baseOffset.y);

      let dc = [c1[0] - c2[0], c2[1] - c1[1]];
      let zoomK = len1 / len2;
      if (len1 !== len2 && len2 !== 0) map.changeScale(zoomK, basePoint, true);
      map.move(dc[0] * map.resolution, dc[1] * map.resolution);
      this._touches[0].position = [x12, y12];
      this._touches[1].position = [x22, y22];
      this._touchHandler.lastZoomDirection = zoomK < 1;
    }

    event.preventDefault();
  }

  _ontouchend(event) {
    this._clearTouches(event);

    for (let i = 0; i < event.changedTouches.length; i++) {
      let index = this._touches.findIndex(touch => touch.id === event.changedTouches[i].identifier);

      if (index >= 0) this._touches.splice(index, 1);
    }

    this._touchHandler.lastDrag = null;

    if (this._touchHandler.scaleChanged) {
      this._master.allowUpdate();

      this._master.map.adjustResolution(this._touchHandler.lastZoomDirection);

      this._touchHandler.scaleChanged = false;
    } else {
      if (this._draggingObject) {
        this._draggingObject.fire(new DragEndEvent({
          point: null,
          browserEvent: new MouseEvent('mouseup', event)
        }));

        this._draggingObject = null;
      }
    }
  }

  _clearTouches(event) {
    let touches = Array.prototype.slice.apply(event.touches);

    for (let i = this._touches.length - 1; i >= 0; i--) {
      if (!touches.some(touch => touch.identifier === this._touches[i].id)) this._touches.splice(i, 1);
    }
  }

}

var HorizontalAlignment;

(function (HorizontalAlignment) {
  HorizontalAlignment[HorizontalAlignment["Left"] = 0] = "Left";
  HorizontalAlignment[HorizontalAlignment["Center"] = 1] = "Center";
  HorizontalAlignment[HorizontalAlignment["Right"] = 2] = "Right";
})(HorizontalAlignment || (HorizontalAlignment = {}));

var VerticalAlignment;

(function (VerticalAlignment) {
  VerticalAlignment[VerticalAlignment["Top"] = 0] = "Top";
  VerticalAlignment[VerticalAlignment["Middle"] = 1] = "Middle";
  VerticalAlignment[VerticalAlignment["Bottom"] = 2] = "Bottom";
})(VerticalAlignment || (VerticalAlignment = {}));
/**
 * Text label that is drawn as a vector element. This render prerenders itself to the offscreen canvas and then allows
 * layer renderer to draw it as a simple image.
 */


class VectorLabel extends StaticRender {
  constructor({
    position,
    mapPosition,
    text,
    horizontalAlignment = HorizontalAlignment.Right,
    verticalAlignment = VerticalAlignment.Middle,
    fontSize = 14,
    fontFamily = 'arial',
    fontStyle = null,
    fontWeight,
    strokeColor = 'white',
    strokeWidth = 2,
    fillColor = 'black'
  }) {
    super();
    this.offset = [0, 0];
    this._text = text;
    this.horizontalAlignment = horizontalAlignment;
    this.verticalAlignment = verticalAlignment;
    this.fontSize = fontSize;
    this.fontFamily = fontFamily;
    this.fontStyle = fontStyle;
    this.fontWeight = fontWeight;
    this.strokeColor = strokeColor;
    this.strokeWidth = strokeWidth;
    this.fillColor = fillColor;
    this.position = position;
    this.mapPosition = mapPosition;
  }

  contains(position) {
    let dx = position[0] - this.position[0];
    let dy = position[1] - this.position[1];
    return this._boxContains(dx, dy);
  }

  _boxContains(x, y) {
    let minX = this.position[0] + this.offset[0];
    let minY = this.position[1] + this.offset[1];
    return x > minX && x < minX + this.width && y > minY && y < minY + this.height;
  }

  get node() {
    if (this._canvas) return this._canvas;

    this._render();

    return this._canvas;
  }

  get width() {
    return this.node.width;
  }

  get height() {
    return this.node.height;
  }

  get font() {
    let font = '';
    if (this.fontStyle) font += this.fontStyle + ' ';
    if (this.fontWeight) font += this.fontWeight + ' ';
    font += `${this.fontSize}px ${this.fontFamily}`;
    return font;
  }

  _render() {
    this._canvas = document.createElement('canvas');
    this._canvas.height = this._canvas.width = 0;

    let ctx = this._canvas.getContext('2d');

    ctx.font = this.font;
    let measure = ctx.measureText(this._text);
    this._canvas.width = Math.ceil(measure.width);
    this._canvas.height = Math.ceil(this.fontSize);
    let dy = 0;

    if (this.verticalAlignment === VerticalAlignment.Bottom) {
      ctx.textBaseline = 'bottom';
      dy = this._canvas.height;
    } else if (this.verticalAlignment === VerticalAlignment.Middle) {
      ctx.textBaseline = 'middle';
      dy = this._canvas.height / 2;
    } else {
      ctx.textBaseline = 'top';
    }

    ctx.font = this.font;

    if (this.strokeWidth > 0) {
      ctx.strokeStyle = this.strokeColor;
      ctx.lineWidth = this.strokeWidth;
      ctx.strokeText(this._text, 0, dy);
    }

    ctx.fillStyle = this.fillColor;
    ctx.fillText(this._text, 0, dy);

    this._setOffset();
  }

  _setOffset() {
    let dx = 0;
    let dy = 0;

    if (this.horizontalAlignment === HorizontalAlignment.Left) {
      dx = -this._canvas.width;
    } else if (this.horizontalAlignment === HorizontalAlignment.Center) {
      dx = -this._canvas.width / 2;
    }

    if (this.verticalAlignment === VerticalAlignment.Top) {
      dy = -this._canvas.height;
    } else if (this.verticalAlignment === VerticalAlignment.Middle) {
      dy = -this._canvas.height / 2;
    }

    this.offset = [Math.round(dx), Math.round(dy)];
  }

}

/**
 * @alias sGis.painter.domPainter.Canvas
 * @ignore
 */

class Canvas {
  constructor(canvasNode) {
    if (canvasNode) {
      this._canvasNode = canvasNode;
      this._ctx = this._canvasNode.getContext('2d');
    } else {
      this._createNode();
    }
  }

  _createNode() {
    this._canvasNode = document.createElement('canvas');
    this._canvasNode.style.pointerEvents = 'none';
    this._ctx = this._canvasNode.getContext('2d');
  }

  reset(bbox, resolution, width, height) {
    this._ctx.clearRect(0, 0, this._canvasNode.width, this._canvasNode.height);

    this._canvasNode.width = width;
    this._canvasNode.height = height;
    this._isEmpty = true;
    this.bbox = bbox;
  }

  get width() {
    return this._canvasNode.width;
  }

  get height() {
    return this._canvasNode.height;
  }

  draw(render, domPainter) {
    if (render instanceof VectorRender) {
      render.drawOnCanvas(this._ctx, domPainter);
    } else if (render instanceof StaticVectorImageRender || render instanceof VectorLabel) {
      this._drawImage(render, domPainter);
    } else {
      error('Unknown vector geometry type.');
    }

    this._isEmpty = false;
  }

  setIndex(index) {
    if (index !== this.zIndex) {
      this._canvasNode.style.zIndex = index;
      this.zIndex = index;
    }
  }

  _drawImage(render, domPainter) {
    let x, y;

    if (render.mapPosition && domPainter) {
      [x, y] = render.mapPosition;
      const xScale = domPainter.getXScale();
      const yScale = domPainter.getYScale();
      x = Math.round(xScale(x));
      y = domPainter.height - Math.round(yScale(y));
    } else {
      [x, y] = render.position;
    }

    x = Math.round(x);
    y = Math.round(y);

    this._ctx.translate(x, y);

    if (!(render instanceof VectorLabel)) {
      this._ctx.rotate(render.angle);
    }

    let opacity = !(render instanceof VectorLabel) ? render.opacity : 1;
    if (opacity !== 1) this._ctx.globalAlpha = opacity;

    this._ctx.drawImage(render.node, render.offset[0], render.offset[1], render.width, render.height);

    if (opacity !== 1) this._ctx.globalAlpha = 1;

    if (!(render instanceof VectorLabel)) {
      this._ctx.rotate(-render.angle);
    }

    this._ctx.translate(-x, -y);
  }

  get isEmpty() {
    return this._isEmpty;
  }

  get node() {
    return this._canvasNode;
  }

}

const DEFAULT_CHUNK_SIZE = 50;
class PathRender extends VectorRender {
  constructor({
    path,
    renders,
    chunkSize = DEFAULT_CHUNK_SIZE
  }) {
    super();
    this.isComplex = true;
    this.cachedPaths = {};
    this.renders = renders;
    this.chunkSize = chunkSize;

    if (path) {
      this.path = path;
    } else {
      this.initializePath();
    }
  }

  initializePath() {
    this.path = new Path2D();
    this.addRendersToPath();
  }

  addRendersToPath(domPainter) {
    this.validateCache();

    for (let i = 0; i <= Math.round(this.renders.length / this.chunkSize); i++) {
      if (this.cachedPaths[i] && !this.cachedPaths[i].updateNeeded) {
        this.path.addPath(this.cachedPaths[i].path);
        this.cachedPaths[i] = Object.assign(Object.assign({}, this.cachedPaths[i]), {
          hadUpdate: false
        });
      } else {
        let path = new Path2D();

        for (let j = 0; j < this.chunkSize; j++) {
          const render = this.renders[i * this.chunkSize + j];

          if (!render) {
            break;
          }

          this.addObject(path, render, domPainter);
        }

        this.cachedPaths[i] = {
          path,
          updateNeeded: false,
          hadUpdate: true
        };
        this.path.addPath(path);
      }
    }

    this.previousRenders = this.renders;
  }

  validateCache() {
    if (!this.previousRenders) {
      return;
    }

    const isAdd = this.previousRenders.length < this.renders.length;
    const isDelete = this.previousRenders.length > this.renders.length;

    if (isDelete) {
      this.cachedPaths = {};
      return;
    }

    for (let i = 0; i <= Math.round(this.renders.length / this.chunkSize); i++) {
      for (let j = 0; j < this.chunkSize; j++) {
        const render = this.renders[i * this.chunkSize + j];

        if (!render) {
          break;
        }

        if (render && render.updateNeeded) {
          this.updateNeeded = true;
          this.cachedPaths[i] = Object.assign(Object.assign({}, this.cachedPaths[i]), {
            updateNeeded: true
          });

          if (isAdd && this.cachedPaths[i + 1]) {
            for (let j = i + 1; j <= Math.round(this.renders.length / this.chunkSize); j++) {
              if (this.cachedPaths[j]) {
                this.cachedPaths[j] = Object.assign(Object.assign({}, this.cachedPaths[j]), {
                  updateNeeded: true
                });
              }
            }

            return;
          }

          break;
        }
      }
    }
  }

  updateRenders(renders, domPainter) {
    this.path = new Path2D();
    this.renders = renders;
    this.addRendersToPath(domPainter);
  }

  contains(position) {
    return this.renders.findIndex(render => render.contains(position)) !== -1;
  }

}

/**
 * @alias sGis.painter.domPainter.CanvasGroupRenderer
 * @ignore
 */

class CanvasGroupRenderer {
  constructor(canvasContainer, domPainter) {
    this.canvasGroups = [];
    this.canvasContainer = canvasContainer;
    this.domPainter = domPainter;
  }

  setIndex(index) {
    if (this.zIndex !== index) {
      for (let canvasGroup of this.canvasGroups) {
        if (canvasGroup.canvas) {
          canvasGroup.canvas.setIndex(index + 1);
        }
      }

      this.zIndex = index + 1;
    }
  }

  removeCanvas(canvas) {
    if (this.canvasContainer) this.canvasContainer.removeNode(canvas.node);
  }

  resetCanvas(canvas, bbox) {
    this.removeCanvas(canvas);
    canvas.reset(bbox, this.domPainter.map.resolution, this.domPainter.width, this.domPainter.height);
  }

  drawRender(canvas, render) {
    canvas.draw(render, this.domPainter);
  }

  addCanvasToDom(canvas, bbox) {
    this.canvasContainer.addNode(canvas.node, canvas.width, canvas.height, bbox);
  }

  clear() {
    for (let i = 0; i < this.canvasGroups.length; i++) {
      const {
        canvas
      } = this.canvasGroups[i];

      if (canvas && canvas.node.parentNode) {
        this.canvasContainer.removeNode(canvas.node);
      }

      this.canvasGroups[i] = {
        canvas: null,
        render: null,
        updateNeeded: false
      };
    }
  }

  moveToContainer(canvasContainer) {
    for (let canvasGroup of this.canvasGroups) {
      const {
        canvas
      } = canvasGroup;

      if (canvas && canvas.node.parentNode) {
        this.canvasContainer.removeNode(canvas.node);
        canvasContainer.addNode(canvas.node, canvas.width, canvas.height, canvas.bbox);
      }
    }

    this.canvasContainer = canvasContainer;
  }

  render(renders) {
    var _a;

    if (this.canvasGroups.length === 0) {
      this.canvasGroups = new Array(renders.length);
    }

    const canvasGroupsToRender = [];

    for (let i = 0; i < renders.length; i++) {
      const canvas = ((_a = this.canvasGroups[i]) === null || _a === void 0 ? void 0 : _a.canvas) || new Canvas();
      canvas.setIndex(renders[i] instanceof PathRender ? this.zIndex + 1 : this.zIndex);
      this.canvasGroups[i] = {
        canvas: canvas,
        render: renders[i],
        updateNeeded: renders[i].updateNeeded
      };
      canvasGroupsToRender.push({
        canvas: canvas,
        render: renders[i],
        updateNeeded: renders[i].updateNeeded
      });
    }

    const bbox = this.domPainter.bbox;
    const activeCanvasGroups = this.canvasGroups.filter(({
      canvas
    }) => !!canvas);

    for (let i = 0; i < activeCanvasGroups.length - renders.length; i++) {
      const canvas = activeCanvasGroups[activeCanvasGroups.length - 1 - i].canvas;
      this.resetCanvas(canvas, bbox);
    }

    const canvasGroupsToUpdate = canvasGroupsToRender.filter(({
      updateNeeded
    }) => updateNeeded);

    for (let canvasGroup of canvasGroupsToUpdate) {
      this.resetCanvas(canvasGroup.canvas, bbox);
      this.drawRender(canvasGroup.canvas, canvasGroup.render);

      if (!canvasGroup.canvas.isEmpty) {
        this.addCanvasToDom(canvasGroup.canvas, bbox);
      }
    }
  }

}

/**
 * @alias sGis.painter.domPainter.LayerRenderer
 * @ignore
 */

class LayerRenderer {
  /**
   * @constructor
   * @param master
   * @param layer
   * @param index
   * @param useCanvas
   */
  constructor(master, layer, index, useCanvas = true) {
    this.delayedUpdateTime = 500;
    this.listensFor = [MouseEventFlags.MouseClick, MouseEventFlags.DoubleClick, MouseEventFlags.MouseDown, MouseEventFlags.MouseMove, MouseEventFlags.MouseOut, MouseEventFlags.MouseOver, MouseEventFlags.MouseUp, MouseEventFlags.DragStart];
    this._renders = [];
    this._master = master;
    this._layer = layer;

    if (layer instanceof FeatureLayer) {
      this._useCanvasGroupRenderer = true;
      this._canvasGroupRenderer = new CanvasGroupRenderer(this._currentContainer, master);
    } else {
      this._useCanvas = useCanvas;
    }

    this._canvas = new Canvas();

    this._resetEventCatcherMaps();

    this._setListeners();

    this.setIndex(index);

    this._forceUpdate();
  }

  get layer() {
    return this._layer;
  }

  _setListeners() {
    this._layer.on('propertyChange', () => {
      this._forceUpdate();
    });
  }

  _resetEventCatcherMaps() {
    this._eventCatchers = {};
    this.listensFor.forEach(eventName => {
      this._eventCatchers[eventName] = new Map();
    });
  }

  _forceUpdate() {
    this.updateNeeded = true;
  }

  setIndex(index) {
    if (index === this._index) return;
    let zIndex = index * 2 + 1;

    for (let render of this._renders) {
      if (render instanceof StaticImageRender || render instanceof DynamicRender) {
        render.node.style.zIndex = zIndex.toString();
      }
    }

    this._canvas.setIndex(index * 2);

    if (this._useCanvasGroupRenderer) {
      this._canvasGroupRenderer.setIndex(index * 2);
    }

    this._index = index;
    this._zIndex = zIndex;
  }

  clear() {
    for (let render of this._renders) {
      this._removeRender(render);
    }

    this._renders = [];

    this._resetEventCatcherMaps();

    if (this._canvasContainer) this._canvasContainer.removeNode(this._canvas.node);
    if (this._updateTimer) clearTimeout(this._updateTimer);
    if (this._canvasGroupRenderer) this._canvasGroupRenderer.clear();
  }

  update() {
    if (this._layer.delayedUpdate) {
      if (this._updateTimer) clearTimeout(this._updateTimer);

      if (this.updateNeeded) {
        this._rerender();
      } else {
        this._updateTimer = setTimeout(() => {
          this._rerender();
        }, this.delayedUpdateTime);
      }
    } else {
      this._rerender();
    }

    this.updateNeeded = false;
  }

  updateDynamic() {
    this._renders.forEach(render => {
      if (render instanceof DynamicRender) render.update(this._master.bbox, this._master.map.resolution);
    });
  }

  _removeOutdatedRenders(newRenders) {
    for (let i = this._renders.length - 1; i >= 0; i--) {
      if (newRenders.indexOf(this._renders[i]) < 0) {
        this._removeRender(this._renders[i]);
      }
    }
  }

  _removeCanvas() {
    if (this._canvasContainer) this._canvasContainer.removeNode(this._canvas.node);
  }

  _addCanvasToDom(bbox) {
    this._canvasContainer = this.currentContainer;

    this._canvasContainer.addNode(this._canvas.node, this._canvas.width, this._canvas.height, bbox);
  }

  _resetCanvas(bbox) {
    this._removeCanvas();

    this._canvas.reset(bbox, this._master.map.resolution, this._master.width, this._master.height);
  }

  _renderWithCanvasGroup(renders, bbox) {
    let complexRenders = [];

    for (let render of renders) {
      if (render.isComplex) {
        complexRenders.push(render);

        this._setRenderListeners(render);
      } else if (render instanceof StaticVectorImageRender || render instanceof VectorRender || this._renders.indexOf(render) < 0) {
        this._drawRender(render);
      }
    }

    this._canvasGroupRenderer.render(complexRenders);

    if (!this._canvas.isEmpty) {
      this._addCanvasToDom(bbox);
    }
  }

  _renderWithCanvas(renders, bbox) {
    this._draw(renders);

    if (!this._canvas.isEmpty) {
      this._addCanvasToDom(bbox);
    }
  }

  _rerender() {
    let bbox = this._master.bbox;

    let renders = this._layer.getRenders(bbox, this._master.map.resolution, this._master);

    if (this._layer.updateProhibited) return;
    if (this.currentContainer !== this._master.currContainer) this.moveToLastContainer();

    this._resetCanvas(bbox);

    this._removeOutdatedRenders(renders);

    if (this._useCanvasGroupRenderer) {
      this._renderWithCanvasGroup(renders, bbox);
    } else {
      const vectorImages = renders.filter(r => r instanceof StaticVectorImageRender);

      if (vectorImages.length > 0 && renders.length !== vectorImages.length) {
        this._drawVectorSync(renders, vectorImages, bbox);
      } else {
        this._renderWithCanvas(renders, bbox);
      }
    }

    this._renders = renders;
  }

  _drawVectorSync(renders, images, bbox) {
    Promise.all(images.map(({
      readyPromise
    }) => readyPromise)).then(() => {
      this._renderWithCanvas(renders, bbox);

      this._renders = renders;
    });
  }

  _draw(renders) {
    for (let render of renders) {
      if (render instanceof StaticVectorImageRender || render instanceof VectorRender || this._renders.indexOf(render) < 0) {
        this._drawRender(render);
      }
    }
  }

  _drawRender(render) {
    if (render instanceof StaticRender) {
      this._drawStaticRender(render);
    } else if (render instanceof DynamicRender) {
      this._drawDynamicRender(render);
    }

    this._setRenderListeners(render);
  }

  _setRenderListeners(render) {
    this.listensFor.forEach(eventFlag => {
      if (render.listensFor & eventFlag) this._eventCatchers[eventFlag].set(render, render);
    });
  }

  _removeRenderListeners(render) {
    this.listensFor.forEach(eventFlag => {
      if (render.listensFor & eventFlag) this._eventCatchers[eventFlag].delete(render);
    });
  }

  _drawStaticRender(render) {
    if (render instanceof StaticHtmlImageRender) {
      this._drawImageRender(render);
    } else if (render instanceof VectorRender) {
      this._drawVectorRender(render);
    } else if (render instanceof StaticVectorImageRender) {
      this._drawAfterLoad(render);
    }
  }

  _drawImageRender(render) {
    render.node.style.zIndex = this._zIndex.toString();

    this._currentContainer.addNode(render.node, render.width, render.height, render.bbox);

    if (render.onDisplayed) render.onDisplayed();
  }

  _drawAfterLoad(render) {
    let image = render.node;

    if (image instanceof HTMLImageElement) {
      if (image.complete) return this._drawVectorRender(render);
      listenDomEvent(image, 'load', () => {
        if (this._renders.indexOf(render) >= 0) {
          this._drawVectorRender(render);

          if (!this._canvas.node.parentNode) this._addCanvasToDom(this._master.bbox);
        }
      });
    } else {
      this._drawVectorRender(render);
    }
  }

  _drawVectorRender(render) {
    this._canvas.draw(render, this._master);
  }

  _drawDynamicRender(render) {
    render.update(this._master.bbox, this._master.map.resolution);

    this._master.dynamicContainer.appendChild(render.node);

    this._renders.push(render);

    if (render.onRender) render.onRender();
  }

  get currentContainer() {
    return this._currentContainer;
  }

  set currentContainer(container) {
    if (this._currentContainer !== container) {
      this._currentContainer = container;

      this._master.resolveLayerOverlay();

      if (this._useCanvasGroupRenderer) {
        this._canvasGroupRenderer.canvasContainer = container;
      }
    }
  }

  _removeRender(render) {
    if ((render instanceof StaticImageRender || render instanceof DynamicRender) && render.node.parentNode) {
      render.node.parentNode.removeChild(render.node);
      if (render instanceof StaticHtmlImageRender && render.onRemoved) render.onRemoved();
    }

    this._removeRenderListeners(render);
  }

  moveToLastContainer() {
    this._moveRendersToLastContainer();

    if (this._useCanvasGroupRenderer) {
      this._canvasGroupRenderer.moveToContainer(this._master.currContainer);
    } else if (this._canvas.node.parentNode) {
      this._canvasContainer.removeNode(this._canvas.node);

      this._master.currContainer.addNode(this._canvas.node, this._canvas.width, this._canvas.height, this._canvas.bbox);

      this._canvasContainer = this._master.currContainer;
    }

    this.currentContainer = this._master.currContainer;
  }

  _moveRendersToLastContainer() {
    let container = this._master.currContainer;

    for (let render of this._renders) {
      if (render instanceof StaticHtmlImageRender) {
        container.addNode(render.node, render.width, render.height, render.bbox);
      }
    }
  }

  getEventCatcher(eventFlag, position) {
    if (!this._eventCatchers[eventFlag]) return null;
    let keys = Array.from(this._eventCatchers[eventFlag].keys());

    for (let i = keys.length - 1; i >= 0; i--) {
      let render = keys[i];

      if (render.contains(position)) {
        return render;
      }
    }

    return null;
  }

}

let containerStyle = 'width: 100%; height: 100%; transform-origin: left top; position: absolute;';
/**
 * @alias sGis.painter.domPainter.Container
 */

class Container {
  constructor(wrapper, bbox, resolution, onEmpty) {
    this._onEmpty = onEmpty;
    this._bbox = bbox;
    this._resolution = resolution;

    this._setContainer(wrapper);
  }

  _setContainer(wrapper) {
    this._container = document.createElement('div');
    this._container.style.cssText = containerStyle;
    wrapper.appendChild(this._container);
  }

  remove() {
    this._container.innerHTML = '';
    if (this._container.parentNode) this._container.parentNode.removeChild(this._container);
  }

  get isEmpty() {
    return this._container.childElementCount === 0;
  }

  get scale() {
    return this._scale;
  }

  get crs() {
    return this._bbox.crs;
  }

  updateTransform(parentBbox, parentResolution) {
    if (parentBbox.crs !== this._bbox.crs) parentBbox = parentBbox.projectTo(this._bbox.crs);
    this._scale = this._resolution / parentResolution;
    setNodeTransform(this._container, this._scale, (this._bbox.xMin - parentBbox.xMin) / parentResolution, (parentBbox.yMax - this._bbox.yMax) / parentResolution);
  }

  addNode(node, width, height, bbox) {
    if (bbox.crs !== this._bbox.crs) {
      if (!bbox.crs.canProjectTo(this._bbox.crs)) return;
      bbox = bbox.projectTo(this._bbox.crs);
    }

    Container._setNodeStyle(node);

    setNodeTransform(node, bbox.width / width / this._resolution, (bbox.xMin - this._bbox.xMin) / this._resolution, (this._bbox.yMax - bbox.yMax) / this._resolution);

    this._container.appendChild(node);
  }

  addFixedSizeNode(node, position, offset = [0, 0]) {
    Container._setNodeStyle(node);

    setNodeTransform(node, 1, position[0] + offset[0] - this._bbox.xMin / this._resolution, position[1] + offset[1] + this._bbox.yMax / this._resolution);

    this._container.appendChild(node);
  }

  get resolution() {
    return this._resolution;
  }

  static _setNodeStyle(node) {
    node.style.position = 'absolute';
    node.style.transformOrigin = 'left top';
  }

  removeNode(node) {
    if (node.parentNode === this._container) {
      this._container.removeChild(node);

      if (this._container.childElementCount === 0) {
        this._onEmpty();
      }
    }
  }

}

function setNodeTransform(node, scale, tx, ty) {
  tx = browserAdj(normalize(tx));
  ty = browserAdj(normalize(ty));
  scale = normalize(scale);
  node.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0px) scale(' + scale.toPrecision(6) + ',' + scale.toPrecision(6) + ')';
}

function normalize(n) {
  return Math.abs(n - Math.round(n)) < 0.001 ? Math.round(n) : n;
}

function browserAdj(n) {
  if (!isIE) {
    return Math.round(n);
  }

  return n;
}

let innerWrapperStyle = 'position: relative; overflow: hidden; width: 100%; height: 100%;';
let layerWrapperStyle = 'position: absolute; width: 100%; height: 100%; z-index: 0;';
class MapResize extends sGisEvent {
  constructor(width, height) {
    super(MapResize.type);
    this.width = width;
    this.height = height;
  }

}
MapResize.type = 'mapResize';
class DomPainter extends EventHandler {
  /**
   * @param map - the map to be drawn.
   * @param options - key-value list of properties to be assigned to the instance.
   */
  constructor(map, {
    wrapper = null
  } = {}) {
    super();
    this._fireMapResize = debounce((width, height) => this.fire(new MapResize(width, height)), 300);
    this._map = map;
    this.wrapper = wrapper;
    this._layerRenderers = new Map();
    this._containers = [];
    this._position = [Infinity, Infinity];
    this._resolution = Infinity;
    this._needUpdate = true;
    this._updateAllowed = true;

    this._updateLayerList();

    this._setEventListeners();

    this._repaintBound = this._repaint.bind(this);

    this._repaint();
  }
  /**
   * DOM element, inside of which the map will be drawn. If null is given, the map will not be drawn. If string is given, an element with given id will be searched.
   */


  get wrapper() {
    return this._wrapper;
  }

  set wrapper(wrapper) {
    if (this._wrapper) this._clearDOM();

    if (wrapper) {
      this._initDOM(wrapper);

      this._eventDispatcher = new EventDispatcher(this._innerWrapper, this);
      this._needUpdate = true;
      this._redrawNeeded = true;
    }

    this.fire('wrapperChange');
  }

  get layerRenderers() {
    return Array.from(this._layerRenderers.values());
  }
  /**
   * Sets position and resolution of the map to show the full bounding box in the center of the map
   * @param bbox
   * @param animate - if set to true, the position will be changed gradually with animation.
   * @returns the actual bbox of the map after the change.
   */


  show(bbox, animate = true) {
    let projected = bbox.projectTo(this.map.crs);
    let xResolution = projected.width / this.width;
    let yResolution = projected.height / this.height;
    let method = animate ? 'animateTo' : 'setPosition';
    let center = projected.center;
    this.map[method](center, this.map.getAdjustedResolution(Math.max(xResolution, yResolution)));
    return new Bbox([center[0] - this.width * xResolution, center[1] - this.height * yResolution], [center[0] + this.width * xResolution, center[1] + this.height * yResolution], this.map.crs);
  }

  _updateLayerList() {
    let mapLayers = this._map.getLayers(true, true);

    for (let layer of this._layerRenderers.keys()) {
      if (mapLayers.indexOf(layer) < 0) this._removeLayer(layer);
    }

    mapLayers.forEach((layer, index) => {
      let renderer = this._layerRenderers.get(layer);

      if (renderer) {
        renderer.setIndex(index);
      } else {
        this._addLayer(layer, index);
      }
    });
  }

  _addLayer(layer, index) {
    this._layerRenderers.set(layer, new LayerRenderer(this, layer, index));
  }

  _removeLayer(layer) {
    this._layerRenderers.get(layer).clear();

    this._layerRenderers.delete(layer);
  }

  _setEventListeners() {
    this._map.on(ContentsChangeEvent.type, this._updateLayerList.bind(this));

    this._map.on(DragEvent.type, this._onMapDrag.bind(this));

    this._map.on(sGisDoubleClickEvent.type, this._onMapDblClick.bind(this));

    this._map.on(AnimationStartEvent.type, this.forbidUpdate.bind(this));

    this._map.on(AnimationEndEvent.type, this.allowUpdate.bind(this));
  }
  /**
   * Prevents the map to be redrawn.
   */


  forbidUpdate() {
    this._updateAllowed = false;
  }
  /**
   * Allows redrawing of the map again after .forbidUpdate() has been called.
   */


  allowUpdate() {
    this._updateAllowed = true;
  }

  _repaint() {
    this._updateSize();

    if (this.isDisplayed) {
      if (this._needUpdate && this._updateAllowed) {
        this._setNewContainer();

        this._needUpdate = false;
      }

      this._updateBbox();

      let layers = this._map.getLayers(true, true);

      let redrawNeeded = this._redrawNeeded;

      if (this._updateAllowed) {
        for (let i = layers.length - 1; i >= 0; i--) {
          let renderer = this._layerRenderers.get(layers[i]);

          if (renderer && (redrawNeeded || renderer.updateNeeded)) {
            try {
              renderer.update();
            } catch (e) {
              warn(e);
              renderer.updateNeeded = false;
            }
          }
        }

        this._redrawNeeded = false;
      }

      if (redrawNeeded) {
        layers.forEach(layer => {
          let renderer = this._layerRenderers.get(layer);

          renderer && renderer.updateDynamic();
        });
      }
    }

    window.requestAnimationFrame(this._repaintBound);
  }

  _setNewContainer() {
    this._containers.push(new Container(this._staticRendersContainer, this.bbox, this._map.resolution, this._removeEmptyContainers.bind(this)));
  }

  _removeEmptyContainers() {
    // Check all containers except the last one, for we never remove it
    for (let i = this._containers.length - 2; i >= 0; i--) {
      if (this._containers[i].isEmpty) {
        this._removeContainer(i);
      }
    }
  }

  _removeContainer(i) {
    this._containers[i].remove();

    this._containers.splice(i, 1);
  }

  _updateSize() {
    const newWidth = this._wrapper ? this._wrapper.clientWidth || this._wrapper.offsetWidth : 0;
    const newHeight = this._wrapper ? this._wrapper.clientHeight || this._wrapper.offsetHeight : 0;

    if (this._width !== newWidth || this._height !== newHeight) {
      this._fireMapResize(newWidth, newHeight);
    }

    this._width = newWidth;
    this._height = newHeight;
  }
  /**
   * Returns true is the map is currently displayed in the DOM>
   */


  get isDisplayed() {
    return this._width > 0 && this._height > 0;
  }

  _updateBbox() {
    let mapPosition = this._map.position;

    if (this._position[0] !== mapPosition[0] || this._position[1] !== mapPosition[1] || !softEquals(this._map.resolution, this._resolution) || this._bboxWidth !== this._width || this._bboxHeight !== this._height) {
      this._position = [mapPosition[0], mapPosition[1]];
      this._resolution = this._map.resolution;
      let dx = this._width * this._resolution / 2;
      let dy = this._height * this._resolution / 2;
      this._bbox = new Bbox([mapPosition[0] - dx, mapPosition[1] - dy], [mapPosition[0] + dx, mapPosition[1] + dy], this._map.crs);

      this._containers.forEach(container => {
        if (container.crs.canProjectTo(this._map.crs)) {
          container.updateTransform(this._bbox, this._resolution);
        } else {
          this._removeContainer(this._containers.indexOf(container));

          if (this._containers.length === 0) this._setNewContainer();
        }
      });

      if (this._containers.length > 0 && this._containers[this._containers.length - 1].scale !== 1) this._needUpdate = true;
      this._bboxWidth = this._width;
      this._bboxHeight = this._height;
      this._redrawNeeded = true;
    }
  }
  /**
   * Current bbox of the map drawn by this painter.
   */


  get bbox() {
    if (!this._bbox) this._updateBbox();
    return this._bbox;
  }
  /**
   * The map this painter draws.
   */


  get map() {
    return this._map;
  }

  get currContainer() {
    return this._containers[this._containers.length - 1];
  }

  get dynamicContainer() {
    return this._dynamicRendersContainer;
  }
  /**
   * Width of the map on the screen in pixels.
   */


  get width() {
    return this._width;
  }
  /**
   * Height of the map on the screen in pixels.
   */


  get height() {
    return this._height;
  }

  _initDOM(node) {
    let wrapper = node instanceof HTMLElement ? node : document.getElementById(node);
    if (!wrapper) error('The element with ID "' + node + '" is not found.');
    this._innerWrapper = document.createElement('div');
    this._innerWrapper.style.cssText = innerWrapperStyle;
    this._staticRendersContainer = document.createElement('div');
    this._staticRendersContainer.style.cssText = layerWrapperStyle;
    this._dynamicRendersContainer = document.createElement('div');
    this._dynamicRendersContainer.style.cssText = layerWrapperStyle;

    this._innerWrapper.appendChild(this._staticRendersContainer);

    this._innerWrapper.appendChild(this._dynamicRendersContainer);

    wrapper.appendChild(this._innerWrapper);
    this._wrapper = wrapper;
  }

  _clearDOM() {
    if (this._innerWrapper.parentNode) this._innerWrapper.parentNode.removeChild(this._innerWrapper);
    this._innerWrapper = null;
    this._staticRendersContainer = null;
    this._dynamicRendersContainer = null;
    this._wrapper = null;

    this._eventDispatcher.remove();

    this._eventDispatcher = null;

    this._clearContainers();
  }

  _clearContainers() {
    this._containers.forEach((container, i) => {
      this._removeContainer(i);
    });

    this._map.getLayers(true, true).reverse().forEach(layer => {
      let renderer = this._layerRenderers.get(layer);

      renderer.clear();
    });
  }

  get innerWrapper() {
    return this._innerWrapper;
  }

  resolveLayerOverlay() {
    let prevContainerIndex = 0;

    this._map.getLayers(true, true).forEach(layer => {
      let renderer = this._layerRenderers.get(layer);

      if (!renderer) return;

      let containerIndex = this._containers.indexOf(renderer.currentContainer);

      if (containerIndex < prevContainerIndex) {
        renderer.moveToLastContainer();
        prevContainerIndex = this._containers.length - 1;
      } else {
        prevContainerIndex = containerIndex;
      }
    });

    this._removeEmptyContainers();
  }
  /**
   * Returns the point in map coordinates, that is located at the given offset from the left top corner of the map.
   * @param x
   * @param y
   */


  getPointFromPxPosition(x, y) {
    let resolution = this._map.resolution;
    let bbox = this.bbox;
    return new Point$1([bbox.xMin + x * resolution, bbox.yMax - y * resolution], bbox.crs);
  }
  /**
   * For the given point, returns the px offset on the screen from the left top corner of the map.
   * @param position - point in the map coordinates
   */


  getPxPosition(position) {
    return [(position[0] - this.bbox.xMin) / this._map.resolution, (this.bbox.yMax - position[1]) / this._map.resolution];
  }

  _onMapDrag(event) {
    setTimeout(() => {
      if (event.isCanceled) return;

      this._map.move(event.offset[0], event.offset[1]);
    }, 0);
  }

  _onMapDblClick(event) {
    setTimeout(() => {
      if (event.isCanceled) return;

      this._map.animateSetResolution(this._map.resolution / 2, event.point);
    }, 0);
  }

  get staticRendersContainer() {
    return this._staticRendersContainer;
  }

  setStaticBackgroundColor(backgroundColor) {
    if (this._staticRendersContainer && this._staticRendersContainer.style.backgroundColor !== backgroundColor) {
      this._staticRendersContainer.style.backgroundColor = backgroundColor;
    }
  }

  resetStaticBackgroundColor() {
    if (this._staticRendersContainer && this._staticRendersContainer.style.backgroundColor) {
      this._staticRendersContainer.style.backgroundColor = "";
    }
  }

  getXScale() {
    const {
      xMin,
      xMax
    } = this.bbox;
    return linear().domain([xMin, xMax]).range([0, this.width]);
  }

  getYScale() {
    const {
      yMin,
      yMax
    } = this.bbox;
    return linear().domain([yMin, yMax]).range([0, this.height]);
  }

}

var NS = 'http://www.w3.org/2000/svg';
/**
 * @alias sGis.painter.domPainter.SvgRender
 */

class SvgRender {
  constructor(render, adjK = 1) {
    this._baseRender = render;
    this._adjK = adjK;
  }

  getNode(callback) {
    if (!this._node) this._setNode();
    callback(null, this._node);
  }

  get baseRender() {
    return this._baseRender;
  }

  get position() {
    return this._position;
  }

  _setNode() {
    if (this._baseRender instanceof Arc) {
      if (this._baseRender.startAngle == 0 || this._baseRender.endAngle == 2 * Math.PI) {
        this._setArcNode();
      } else {
        this._setSegmentNode();
      }
    } else if (this._baseRender instanceof PolyRender) {
      this._setPolyNode();
    }
  }

  _setPolyNode() {
    var path = this._getSvgPath();

    if (this._baseRender.enclosed) {
      path.d += ' Z';
      path.d = path.d.replace(/ M/g, ' Z M');
    }

    this._node = this._getPathNode({
      stroke: this._baseRender.strokeColor,
      'stroke-dasharray': this._baseRender.lineDash && this._baseRender.lineDash.length > 0 ? this._baseRender.lineDash.join(',') : undefined,
      'stroke-width': this._baseRender.strokeWidth,
      fill: this._baseRender.fillStyle === FillStyle.Color ? this._baseRender.fillColor : 'none',
      fillImage: this._baseRender.fillStyle === FillStyle.Image ? this._baseRender.fillImage : undefined,
      width: path.width,
      height: path.height,
      x: path.x,
      y: path.y,
      viewBox: [path.x, path.y, path.width, path.height].join(' '),
      d: path.d
    });
    this._position = [path.x, path.y];
  }

  _getPathNode(properties) {
    if (properties.fillImage) {
      var defs = document.createElementNS(NS, 'defs');
      var pattern = document.createElementNS(NS, 'pattern');
      var id = getGuid();
      pattern.setAttribute('id', id);
      pattern.setAttribute('patternUnits', 'userSpaceOnUse');
      pattern.setAttribute('x', properties.x);
      pattern.setAttribute('y', properties.y);
      pattern.setAttribute('width', properties.fillImage.width);
      pattern.setAttribute('height', properties.fillImage.height);
      var image = document.createElementNS(NS, 'image');
      image.setAttributeNS("http://www.w3.org/1999/xlink", 'xlink:href', properties.fillImage.src);
      image.setAttribute('width', properties.fillImage.width);
      image.setAttribute('height', properties.fillImage.height);
      pattern.appendChild(image);
      defs.appendChild(pattern);
    }

    var path = document.createElementNS(NS, 'path');
    var svgAttributes = setAttributes(path, properties);

    var svg = this._getSvgBase(svgAttributes);

    if (properties.fillImage) {
      svg.setAttribute('xmlns', NS);
      svg.setAttribute('xmlns:xlink', "http://www.w3.org/1999/xlink");
      path.setAttribute('fill', 'url(#' + id + ')');
      svg.appendChild(defs);
    }

    svg.appendChild(path);
    return svg;
  }

  _setSegmentNode() {
    var path = this._getSegment();

    this._node = this._getPathNode({
      stroke: this._baseRender.strokeColor,
      'stroke-width': this._baseRender.strokeWidth,
      fill: this._baseRender.fillColor,
      width: path.width,
      height: path.height,
      x: path.x,
      y: path.y,
      viewBox: [path.x, path.y, path.width, path.height].join(' '),
      d: path.d
    });
    this._position = [path.x, path.y];
  }

  _getSegment() {
    var r = this._baseRender.radius;
    var r2 = r * 2 + this._baseRender.strokeWidth;
    var x = this._baseRender.center[0];
    var y = this._baseRender.center[1];
    var x1 = x + r * Math.cos(this._baseRender.startAngle);
    var y1 = y + r * Math.sin(this._baseRender.startAngle);
    var x2 = x + r * Math.cos(this._baseRender.endAngle);
    var y2 = y + r * Math.sin(this._baseRender.endAngle);
    var largeFlag = Math.abs(this._baseRender.endAngle - this._baseRender.startAngle) % (Math.PI * 2) > Math.PI ? 1 : 0;
    var path = `M ${x},${y} L ${x1},${y1} A ${r},${r} 0 ${largeFlag} 1 ${x2},${y2}`;
    var x0 = x - r - this._baseRender.strokeWidth / 2;
    var y0 = y - r - this._baseRender.strokeWidth / 2;
    return {
      x: x0,
      y: y0,
      width: r2,
      height: r2,
      d: path
    };
  }

  _setArcNode() {
    var r2 = this._baseRender.radius * 2 + this._baseRender.strokeWidth;
    var x = this._baseRender.center[0] * this._adjK - this._baseRender.radius - this._baseRender.strokeWidth / 2;
    var y = this._baseRender.center[1] * this._adjK - this._baseRender.radius - this._baseRender.strokeWidth / 2;
    this._node = this._getCircle({
      r: this._baseRender.radius,
      cx: this._baseRender.center[0] * this._adjK,
      cy: this._baseRender.center[1] * this._adjK,
      stroke: this._baseRender.strokeColor,
      'stroke-width': this._baseRender.strokeWidth,
      fill: this._baseRender.fillColor,
      width: r2,
      height: r2,
      viewBox: [x, y, r2, r2].join(' ')
    });
    this._position = [x, y];
  }

  _getCircle(properties) {
    var circle = document.createElementNS(NS, 'circle');
    var svgAttributes = setAttributes(circle, properties);

    var svg = this._getSvgBase(svgAttributes);

    svg.appendChild(circle);
    return svg;
  }

  _getSvgBase(properties) {
    var svg = document.createElementNS(NS, 'svg');
    setAttributes(svg, properties);
    svg.setAttribute('style', 'pointerEvents: none;');
    return svg;
  }

  _getSvgPath() {
    var d = '';
    var coordinates = this._baseRender.coordinates;
    var x = coordinates[0][0][0] * this._adjK;
    var y = coordinates[0][0][1] * this._adjK;
    var xMax = x;
    var yMax = y;

    for (var ring = 0; ring < coordinates.length; ring++) {
      d += 'M' + this._adj(coordinates[ring][0]).join(' ') + ' ';

      for (var i = 1; i < coordinates[ring].length; i++) {
        d += 'L' + this._adj(coordinates[ring][i]).join(' ') + ' ';
        x = Math.min(x, coordinates[ring][i][0] * this._adjK);
        y = Math.min(y, coordinates[ring][i][1] * this._adjK);
        xMax = Math.max(xMax, coordinates[ring][i][0] * this._adjK);
        yMax = Math.max(yMax, coordinates[ring][i][1] * this._adjK);
      }
    }

    var width = xMax - x + this._baseRender.strokeWidth;
    var height = yMax - y + this._baseRender.strokeWidth;
    x -= this._baseRender.strokeWidth / 2;
    y -= this._baseRender.strokeWidth / 2;
    d = d.trim();
    return {
      width: width,
      height: height,
      x: x,
      y: y,
      d: d
    };
  }

  _adj(position) {
    return [position[0] * this._adjK, position[1] * this._adjK];
  }

}
var svgAttributes = ['width', 'height', 'viewBox'];

function setAttributes(element, attributes) {
  var isSvg = element instanceof SVGSVGElement;
  var notSet = {};

  for (var i in attributes) {
    if (attributes.hasOwnProperty(i) && i !== 'fillImage' && attributes[i] !== undefined) {
      if (!isSvg && svgAttributes.indexOf(i) !== -1) {
        notSet[i] = attributes[i];
        continue;
      }

      if (i === 'stroke' || i === 'fill') {
        var color = new Color$1(attributes[i]);

        if (color.a < 255 || color.format === 'rgba') {
          element.setAttribute(i, color.toString('rgb'));
          if (color.a < 255) element.setAttribute(i + '-opacity', color.a / 255);
          continue;
        }
      }

      element.setAttribute(i, attributes[i]);
    }
  }

  return notSet;
}

/**
 * Point geometry rendered to the screen coordinates for drawing.
 * @alias sGis.render.Point
 */

class Point extends VectorRender {
  /**
   * @param coordinates - the rendered (px) coordinates of the point in [x, y] format.
   * @param __namedParameters - properties to be set to the corresponding fields.
   */
  constructor(coordinates, {
    color = 'black',
    ignoreEvents = false,
    mapCoordinates
  } = {}) {
    super();
    /** The color of the point. Can be any valid css color string. */

    this.color = 'black';
    /** Specifies whether this render can catch mouse events. If true, this render will be transparent for any pointer events. */

    this.ignoreEvents = false;
    this._coord = coordinates;
    this.color = color;
    this.ignoreEvents = ignoreEvents;
    this.mapCoordinates = mapCoordinates;
  }

  get isVector() {
    return true;
  }

  contains(position, tolerance = 2) {
    let dx = position[0] - this._coord[0];
    let dy = position[1] - this._coord[1];
    let distance2 = dx * dx + dy * dy;
    return distance2 < tolerance * tolerance;
  }
  /**
   *  The rendered (px) coordinates of the point in [x, y] format.
   */


  get coordinates() {
    return this._coord;
  }

  setupRender(ctx, domPainter) {
    if (this.mapCoordinates && domPainter) {
      const xScale = domPainter.getXScale();
      const yScale = domPainter.getYScale();
      this.renderPosition = [xScale(this.mapCoordinates[0]), domPainter.height - yScale(this.mapCoordinates[1])];
    }

    ctx.strokeStyle = this.color;
    ctx.fillStyle = this.color;
  }

  renderObject(ctx, domPainter) {
    const renderCoordinates = this.renderPosition || this.coordinates;
    ctx.fillRect(renderCoordinates[0], renderCoordinates[1], 1, 1);
  }

}

const ALPHA_NORMALIZER = 65025;
/**
 * Symbol of polygon with brush filling.
 * @alias sGis.symbol.polygon.BrushFill
 */

class BrushFill extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    this._fillBackground = 'transparent';
    this._fillForeground = 'black';
    this._fillBrush = [[255, 255, 0, 0, 0, 0, 0, 0, 255, 255], [255, 255, 255, 0, 0, 0, 0, 0, 0, 255], [255, 255, 255, 255, 0, 0, 0, 0, 0, 0], [0, 255, 255, 255, 255, 0, 0, 0, 0, 0], [0, 0, 255, 255, 255, 255, 0, 0, 0, 0], [0, 0, 0, 255, 255, 255, 255, 0, 0, 0], [0, 0, 0, 0, 255, 255, 255, 255, 0, 0], [0, 0, 0, 0, 0, 255, 255, 255, 255, 0], [0, 0, 0, 0, 0, 0, 255, 255, 255, 255], [255, 0, 0, 0, 0, 0, 0, 255, 255, 255]];
    /** Stroke color of the outline. Can be any valid css color string. */

    this.strokeColor = 'black';
    /** Stroke width of the outline. */

    this.strokeWidth = 1;
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** Emulation CanvasRenderingContext2D.filter drop-shadow. */

    this.shadow = null;
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    this._initialized = false;
    if (options) Object.assign(this, options);
    this._initialized = true;

    this._updateBrush();
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];
    let coordinates = getRenderedPolygonCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      enclosed: true,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      fillStyle: FillStyle.Image,
      fillImage: this._brush,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit,
      isOutsideStroke: this.isOutsideStroke,
      isInsideStroke: this.isInsideStroke
    })];
  }
  /**
   * Brush pattern for filling.
   */


  get fillBrush() {
    return this._fillBrush;
  }

  set fillBrush(brush) {
    this._fillBrush = brush;

    this._updateBrush();
  }
  /**
   * Brush background color. Can be any valid css color string.
   */


  get fillBackground() {
    return this._fillBackground;
  }

  set fillBackground(color) {
    this._fillBackground = color;

    this._updateBrush();
  }
  /**
   * Brush foreground color. Can be any valid css color string.
   */


  get fillForeground() {
    return this._fillForeground;
  }

  set fillForeground(color) {
    this._fillForeground = color;

    this._updateBrush();
  }

  _updateBrush() {
    if (!this._initialized) return;
    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');
    let brush = this.fillBrush;
    let foreground = new Color$1(this.fillForeground);
    let background = new Color$1(this.fillBackground);
    canvas.height = brush.length;
    canvas.width = brush[0].length;

    for (let i = 0, l = brush.length; i < l; i++) {
      for (let j = 0, m = brush[i].length; j < m; j++) {
        let srcA = brush[i][j] * foreground.a / ALPHA_NORMALIZER,
            dstA = background.a / 255 * (1 - srcA),
            a = +Math.min(1, srcA + dstA).toFixed(2),
            r = Math.round(Math.min(255, background.r * dstA + foreground.r * srcA)),
            g = Math.round(Math.min(255, background.g * dstA + foreground.g * srcA)),
            b = Math.round(Math.min(255, background.b * dstA + foreground.b * srcA));
        ctx.fillStyle = 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
        ctx.fillRect(j, i, 1, 1);
      }
    }

    this._brush = new Image();
    this._brush.src = canvas.toDataURL();
  }

}
registerSymbol(BrushFill, 'polygon.BrushFill', ['fillBrush', 'fillBackground', 'fillForeground', 'strokeColor', 'strokeWidth', 'lineDash', 'shadow', 'lineCap', 'lineJoin', 'miterLimit', 'isOutsideStroke', 'isInsideStroke']);

/**
 * Symbol of polygon with image filling.
 * @alias sGis.symbol.polygon.ImageFill
 */

class ImageFill extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor({
    src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
    strokeWidth = 1,
    strokeColor = 'block',
    lineDash = [],
    shadow = null
  } = {}) {
    super();
    this._image = new Image();
    /** @see [[PolyRender.lineDash]] */

    this.lineDash = [];
    /** @see [[PolyRender.lineCap]] */

    this.lineCap = "round";
    /** @see [[PolyRender.lineJoin]] */

    this.lineJoin = "round";
    /** @see [[PolyRender.miterLimit]] */

    this.miterLimit = 10;
    /** Emulation CanvasRenderingContext2D.filter drop-shadow. */

    this.shadow = null;
    /** Drawn stroke outside. */

    this.isOutsideStroke = false;
    /** Drawn stroke inside. */

    this.isInsideStroke = false;
    this.strokeWidth = strokeWidth;
    this.strokeColor = strokeColor;
    this.lineDash = lineDash;
    this._src = src;
    this.shadow = shadow;

    this._updateImage();
  }

  renderFunction(feature, resolution, crs) {
    if (!(feature instanceof Poly)) return [];

    if (!this._image.complete) {
      this._image.onload = feature.redraw.bind(feature);
      return [];
    }

    let coordinates = getRenderedPolygonCoordinates(feature, resolution, crs);
    return [new PolyRender(coordinates, {
      enclosed: true,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth,
      fillStyle: FillStyle.Image,
      fillImage: this._image,
      lineDash: this.lineDash,
      shadow: this.shadow,
      lineCap: this.lineCap,
      lineJoin: this.lineJoin,
      miterLimit: this.miterLimit,
      isOutsideStroke: this.isOutsideStroke,
      isInsideStroke: this.isInsideStroke
    })];
  }
  /**
   * Source for the filling image. Can be url or data:url string.
   */


  get src() {
    return this._src;
  }

  set src(src) {
    this._src = src;

    this._updateImage();
  }

  _updateImage() {
    this._image = new Image();
    this._image.src = this._src;
  }

}
registerSymbol(ImageFill, 'polygon.ImageFill', ['src', 'strokeColor', 'strokeWidth', 'lineDash', 'shadow', 'lineCap', 'lineJoin', 'miterLimit', 'isOutsideStroke', 'isInsideStroke']);

class TileRender extends StaticHtmlImageRender {
  constructor() {
    super(...arguments);
    this.isComplete = true;
  }

}
/**
 * A layer that is drawn as a set of tile images received from server. The layer calculates tile indexes (x, y, z)
 * according to the tile scheme and requested resolution, then inserts them into url mask and creates ImageFeatures
 * for each tile. The features are cached by the layer to prevent unnecessary recalculations and garbage collection.
 * @alias sGis.TileLayer
 */


class TileLayer extends Layer {
  /**
   * @param urlMask - url of the source for tiles in format http(s)://...../..{x}..{y}..{z} - where x an y are indexes of tiles for the scale level z
   * @param __namedParameters - properties to be set to the corresponding fields.
   * @param extensions - [JS ONLY]additional properties to be copied to the created instance
   */
  constructor(urlMask, _a = {}, extensions) {
    var {
      tileScheme = TileScheme.default,
      crs = webMercator,
      cycleX = true,
      cycleY = false,
      cacheSize = 256,
      transitionTime = 500
    } = _a,
        LayerParams = __rest(_a, ["tileScheme", "crs", "cycleX", "cycleY", "cacheSize", "transitionTime"]);

    super(LayerParams, extensions);
    this._tileCache = {};
    this._cachedIndexes = [];
    this._previousTiles = [];
    this._urlMask = urlMask;
    this.tileScheme = tileScheme;
    this.crs = crs;
    this.cycleX = cycleX;
    this.cycleY = cycleY;
    this._cacheSize = cacheSize;
    this._transitionTime = transitionTime;
    this._tileFadeIn = this._tileFadeIn.bind(this);
  }
  /**
   * Returns url of a tile.
   * @param xIndex - index of tile along x axis.
   * @param yIndex - index of tile along y axis.
   * @param level - scale level of the tile.
   */


  getTileUrl(xIndex, yIndex, level) {
    if (typeof this._urlMask === "string") {
      return this._urlMask.replace('{x}', xIndex.toString()).replace('{y}', yIndex.toString()).replace('{z}', level.toString());
    } else {
      return this._urlMask(xIndex, yIndex, level);
    }
  }

  getRenders(bbox, resolution) {
    let indexes = this._getTileIndexes(bbox, resolution);

    let renders = [];

    this._cancelUnloadedTile(indexes);

    let isSetComplete = true;

    for (let index of indexes) {
      let tile = this._getRender(index);

      if (tile.isImageLoaded && !tile.error) renders.push(tile);
      if (!tile.error && (!tile.isComplete || !tile.isImageLoaded)) isSetComplete = false;
    }

    if (isSetComplete) {
      this._previousTiles = [];
    } else {
      this._previousTiles.forEach(tile => {
        if (renders.indexOf(tile) < 0) renders.push(tile);
      });
    }

    this._previousTiles = renders;
    return renders;
  }

  _getRender(index) {
    let tileId = TileLayer._getTileId(index.z, index.x, index.y);

    if (this._tileCache[tileId]) return this._tileCache[tileId];
    let adjX = this.cycleX ? this._getAdjustedIndex(index.x, index.level) : index.x;
    let adjY = this.cycleY ? this._getAdjustedIndex(index.y, index.level) : index.y;

    let bbox = this._getTileBbox(index);

    let tile = new TileRender({
      src: this.getTileUrl(adjX, adjY, index.z),
      width: this.tileWidth,
      height: this.tileHeight,
      bbox,
      onLoad: () => this.redraw()
    });

    this._cacheTile(tileId, tile);

    if (this.transitionTime <= 0) return tile;
    tile.node.style.opacity = '0';
    tile.node.style.transition = `opacity ${this.transitionTime / 1000}s`;
    tile.isComplete = false;

    tile.onDisplayed = () => {
      setTimeout(() => {
        tile.node.style.opacity = this.opacity.toString();
        setTimeout(() => {
          tile.isComplete = true;
          this.redraw();
        }, this.transitionTime);
      }, 0);
    };

    tile.onRemoved = () => {
      tile.node.style.opacity = '0';
      tile.isComplete = false;
    };

    return tile;
  }

  _getTileBbox(index) {
    let resolution = this.tileScheme.levels[index.level].resolution;
    let width = this.tileWidth * resolution;
    let x = this.tileScheme.origin[0] + index.x * width;
    let height = this.tileHeight * resolution;
    if (!this.tileScheme.reversedY) height *= -1;
    let yOffset = index.y * height;
    let y = this.tileScheme.origin[1] + yOffset;
    return new Bbox([x, y], [x + width, y + height], this.crs);
  }

  _cacheTile(id, tile) {
    if (this._tileCache[id]) return;
    this._tileCache[id] = tile;

    this._cachedIndexes.push(id);

    if (this._cachedIndexes.length > this._cacheSize) {
      delete this._tileCache[this._cachedIndexes.shift()];
    }
  }

  _cancelUnloadedTile(indexes) {
    Object.keys(this._tileCache).forEach(tileId => {
      const tile = this._tileCache[tileId];
      const indexDoesNotExistInCurrentBBox = !indexes.some(index => TileLayer._getTileId(index.z, index.x, index.y) === tileId);

      if (indexDoesNotExistInCurrentBBox && !tile.isImageLoaded) {
        tile.deleteNode();
        delete this._tileCache[tileId];
      }
    });
  }

  _getTileIndexes(bbox, resolution) {
    if (!this.crs.canProjectTo(bbox.crs)) return [];
    if (!this.checkVisibility(resolution)) return [];
    let level = this.tileScheme.getLevel(resolution);
    if (level < 0) return [];

    let trimmedBbox = this._getTrimmedBbox(bbox); // FIXME Проецирование proj4 ломает зацикливание по x
    // let trimmedBbox = this._getTrimmedBbox(bbox.projectTo(this.crs));


    if (trimmedBbox.width === 0 || trimmedBbox.height === 0) return [];
    let layerResolution = this.tileScheme.levels[level].resolution;
    if (layerResolution * 2 < resolution) return [];
    let xStartIndex = Math.floor((trimmedBbox.xMin - this.tileScheme.origin[0]) / this.tileWidth / layerResolution);
    let xEndIndex = Math.ceil((trimmedBbox.xMax - this.tileScheme.origin[0]) / this.tileWidth / layerResolution);
    let yStartIndex, yEndIndex;

    if (this.tileScheme.reversedY) {
      yStartIndex = Math.floor((trimmedBbox.yMin - this.tileScheme.origin[1]) / this.tileHeight / layerResolution);
      yEndIndex = Math.ceil((trimmedBbox.yMax - this.tileScheme.origin[1]) / this.tileHeight / layerResolution);
    } else {
      yStartIndex = Math.floor((this.tileScheme.origin[1] - trimmedBbox.yMax) / this.tileHeight / layerResolution);
      yEndIndex = Math.ceil((this.tileScheme.origin[1] - trimmedBbox.yMin) / this.tileHeight / layerResolution);
    }

    let indexes = [];

    for (let xIndex = xStartIndex; xIndex < xEndIndex; xIndex++) {
      for (let yIndex = yStartIndex; yIndex < yEndIndex; yIndex++) {
        indexes.push({
          x: xIndex,
          y: yIndex,
          z: this.tileScheme.levels[level].zIndex,
          level: level
        });
      }
    }

    return indexes;
  }

  static _getTileId(level, xIndex, yIndex) {
    return [level, xIndex, yIndex].join(',');
  }

  _getAdjustedIndex(index, level) {
    let desc = this.tileScheme.levels[level];
    if (!desc.indexCount || desc.indexCount <= 0 || index >= 0 && index < desc.indexCount) return index;

    while (index < 0) index += desc.indexCount;

    return index % desc.indexCount;
  }
  /**
   * Width of the tiles in px. Same as tile scheme's tileWidth.
   */


  get tileWidth() {
    return this.tileScheme.tileWidth;
  }
  /**
   * Height of the tiles in px. Same as tile scheme's tileHeight.
   */


  get tileHeight() {
    return this.tileScheme.tileHeight;
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(opacity) {
    this._opacity = opacity;

    for (let tileId of Object.keys(this._tileCache)) {
      if (this._tileCache[tileId].isImageLoaded) this._tileCache[tileId].node.style.opacity = opacity.toString();
    }

    this.redraw();
  }

  clearCache() {
    this._tileCache = {};
    this._cachedIndexes = [];
    this._previousTiles = [];
  }
  /**
   * Opacity transition time in milliseconds with which tiles are added to the map.
   */


  get transitionTime() {
    return this._transitionTime;
  }

  set transitionTime(time) {
    this._transitionTime = time;
  }

  _getTrimmedBbox(bbox) {
    if (!this.tileScheme.limits) return bbox;
    let limits = this.tileScheme.limits;
    let xMin = Math.max(bbox.xMin, limits[0]);
    let yMin = Math.max(bbox.yMin, limits[1]);
    let xMax = Math.min(bbox.xMax, limits[2]);
    let yMax = Math.min(bbox.yMax, limits[3]);
    if (xMax < xMin) xMax = xMin;
    if (yMax < yMin) yMax = yMin;
    return new Bbox([xMin, yMin], [xMax, yMax], bbox.crs);
  }

  _tileFadeIn(image) {
    if (this._transitionTime <= 0) return;
    image.style.transition = 'opacity ' + this.transitionTime / 1000 + 's linear';
    image.style.opacity = this.opacity.toString();
  }

}

/**
 * @example symbols/Label_Symbols
 */

class StaticLabelSymbol extends Symbol {
  constructor({
    fontSize,
    fontFamily,
    fontStyle,
    horizontalAlignment,
    verticalAlignment,
    offset = [5, 0],
    strokeColor,
    strokeWidth,
    fillColor
  } = {}) {
    super();
    this.fontSize = fontSize;
    this.fontFamily = fontFamily;
    this.fontStyle = fontStyle;
    this.horizontalAlignment = horizontalAlignment;
    this.verticalAlignment = verticalAlignment;
    this.offset = offset;
    this.strokeColor = strokeColor;
    this.strokeWidth = strokeWidth;
    this.fillColor = fillColor;
  }

  renderFunction(feature, resolution, crs) {
    if (!feature.crs.canProjectTo(crs)) return [];
    let position = feature.projectTo(crs).position;
    let pxPosition = [position[0] / resolution + (this.offset[0] || 0), -position[1] / resolution + (this.offset[1] || 0)];
    return [new VectorLabel({
      position: pxPosition,
      mapPosition: position,
      text: feature.content,
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      fontStyle: this.fontStyle,
      verticalAlignment: this.verticalAlignment,
      horizontalAlignment: this.horizontalAlignment,
      fillColor: this.fillColor,
      strokeColor: this.strokeColor,
      strokeWidth: this.strokeWidth
    })];
  }

}

const DEFAULT_LABEL_SYMBOL = /*#__PURE__*/new StaticLabelSymbol();
/**
 * Text label on the map.
 * @example symbols/Label_Symbols
 */

class LabelFeature extends PointFeature {
  constructor(position, _a) {
    var {
      content = '',
      symbol = DEFAULT_LABEL_SYMBOL
    } = _a,
        params = __rest(_a, ["content", "symbol"]);

    super(position, Object.assign({
      symbol
    }, params));
    this._content = content;
  }
  /**
   * The text of the label.
   */


  get content() {
    return this._content;
  }

  set content(value) {
    this._content = value;
    this.redraw();
  }

  clone() {
    return new LabelFeature(this.position, {
      crs: this.crs,
      symbol: this.symbol,
      content: this.content,
      persistOnMap: this.persistOnMap
    });
  }

  projectTo(crs) {
    let projected = Point$1.prototype.projectTo.call(this, crs).position;
    return new LabelFeature(projected, {
      crs,
      symbol: this.symbol,
      content: this.content,
      persistOnMap: this.persistOnMap
    });
  }

}

setStyleNode(`
    .sGis-balloon {
        border: 3px solid rgba(0, 0, 0, 0.3);
        border-radius: 7px;
        transform: translate(-50%, -100%);
    }
    
    .sGis-balloon:before {
        content: " ";
        position: absolute;
        background: white;
        border: 3px solid rgba(0, 0, 0, 0.3);
        width: 10px;
        height: 10px;
        margin-left: 0;
        transform: translate(-50%, -50%) rotate(45deg);
        left: 50%;
        top: 100%;
    }
    
    .sGis-balloon > div {
        border-radius: 5px;
        background: white;
        position: relative;
        min-height: 40px;
        min-width: 40px;
        overflow: hidden;
        padding: 5px;
    }
`);
class BalloonSymbol extends DynamicPointSymbol {
  constructor({
    onRender,
    offset = [0, -7]
  } = {}) {
    super({
      offset,
      onRender
    });
  }

  _getFeatureNode(feature) {
    let balloonFeature = feature;
    let node = document.createElement('div');
    node.className = 'sGis-balloon';
    let container = document.createElement('div');
    if (balloonFeature.content) container.appendChild(balloonFeature.content);
    node.appendChild(container);
    let mapNode = node;
    mapNode.doNotBubbleToMap = true;
    return node;
  }

}

/**
 * This feature lets you put a custom html blocks on the map in the form of balloons over the map. The position of the balloon
 * is set as geographic coordinates of a point.
 * @alias sGis.feature.Balloon
 * @example controls/Balloon_Control
 */

class Balloon extends PointFeature {
  constructor(position, _a) {
    var {
      content,
      symbol = new BalloonSymbol(),
      persistOnMap = true
    } = _a,
        params = __rest(_a, ["content", "symbol", "persistOnMap"]);

    super(position, Object.assign({
      symbol,
      persistOnMap
    }, params));

    if (content instanceof HTMLElement) {
      this._content = content;
    } else {
      this._content = this._getNode(content);
    }
  }

  _getNode(htmlString) {
    let div = document.createElement('div');
    div.innerHTML = htmlString;

    if (div.children.length === 1) {
      return div.firstChild;
    } else {
      return div;
    }
  }

  get content() {
    return this._content;
  }

}

const DEFAULT_CLASS_NAME = 'sGis-dynamicLabel';
const DEFAULT_STYLE = `
    transform: translate(0, -50%);
    font: 14px arial;
    color: black;
    text-shadow: -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white; 
`;
setCssClasses({
  [DEFAULT_CLASS_NAME]: DEFAULT_STYLE
});
/**
 * @example symbols/Label_Symbols
 */

class DynamicLabelSymbol extends DynamicPointSymbol {
  constructor({
    cssClassName = DEFAULT_CLASS_NAME,
    offset
  } = {}) {
    super({
      offset
    });
    this.cssClassName = cssClassName;
  }

  _getFeatureNode(feature) {
    let labelFeature = feature;
    let node = document.createElement('span');
    node.innerText = labelFeature.content;
    if (this.cssClassName) node.className = this.cssClassName;
    return node;
  }

  _updateFeatureNode(feature) {
    let node = this.getNode(feature);
    node.innerText = feature.content;
  }

}

const OFFSET = 10;
/**
 * @example controls/Balloon_Control
 */

class BalloonControl extends Control {
  constructor(map, {
    painter
  } = {}) {
    super(map, {
      useTempLayer: true
    });
    this.painter = painter;
    this._ns = '.' + getGuid;
    this._onMapClick = this._onMapClick.bind(this);
    this._symbol = new BalloonSymbol({
      onRender: this._onRender.bind(this)
    });
  }

  attach(feature, html) {
    let balloon = new Balloon([0, 0], {
      crs: feature.crs,
      content: html,
      symbol: this._symbol
    });
    feature.on(sGisClickEvent.type + this._ns, this._showBalloon.bind(this, feature, balloon));
  }

  detach(feature) {
    feature.off(this._ns);
  }

  _showBalloon(feature, balloon, event) {
    event.stopPropagation();

    if (feature instanceof Poly) {
      balloon.position = feature.centroid;
    } else if (feature instanceof PointFeature) {
      balloon.position = feature.position;
    }

    if (this._activeBalloon && this._tempLayer) {
      this._tempLayer.remove(this._activeBalloon);
    }

    if (!this._isActive) this.activate();
    if (this._tempLayer) this._tempLayer.add(balloon);
    this._activeBalloon = balloon;
  }

  _onRender() {
    if (!this.painter || !this._activeBalloon) return;

    let node = this._symbol.getNode(this._activeBalloon);

    if (!node) return;
    let size = node.getBoundingClientRect();

    let position = this._activeBalloon.projectTo(this.map.crs).position;

    let resolution = this.map.resolution;
    let halfWidth = size.width * resolution / 2;
    let height = size.height * resolution;
    let balloonBbox = new Bbox([position[0] - halfWidth, position[1]], [position[0] + halfWidth, position[1] + height], this._activeBalloon.crs).offset([OFFSET * resolution, OFFSET * resolution]);
    let mapBbox = this.painter.bbox;
    let newBbox = mapBbox.clone();

    if (newBbox.yMin > balloonBbox.yMin) {
      newBbox.yMin = balloonBbox.yMin;
      newBbox.yMax = newBbox.yMin + mapBbox.height;
    } else if (newBbox.yMax < balloonBbox.yMax) {
      newBbox.yMax = balloonBbox.yMax;
      newBbox.yMin = newBbox.yMax - mapBbox.height;
    }

    if (newBbox.xMin > balloonBbox.xMin && newBbox.xMax < balloonBbox.xMax) {
      newBbox.xMin = balloonBbox.center[0] - mapBbox.width / 2;
      newBbox.xMax = balloonBbox.center[1] + mapBbox.width / 2;
    } else if (newBbox.xMin > balloonBbox.xMin) {
      newBbox.xMin = balloonBbox.xMin;
      newBbox.xMax = newBbox.xMin + mapBbox.width;
    } else if (newBbox.xMax < balloonBbox.xMax) {
      newBbox.xMax = balloonBbox.xMax;
      newBbox.xMin = newBbox.xMax - mapBbox.width;
    }

    this.painter.show(newBbox);
  }

  _activate() {
    this.map.on('click', this._onMapClick);
  }

  _deactivate() {
    this.map.off('click', this._onMapClick);
    this._activeBalloon = undefined;
  }

  _onMapClick() {
    this.deactivate();
  }

}

/**
 * A line or a set of geographical lines.
 * @alias sGis.feature.CombinedPolyline
 */

class CombinedPolyline extends Polyline {
  constructor(rings, _a = {}) {
    var {
      symbol = new CombinedPolylineSymbol()
    } = _a,
        params = __rest(_a, ["symbol"]);

    super(rings, Object.assign({
      symbol
    }, params));
    this.isEnclosed = false;
    this.values = [];
  }
  /**
   * Returns a copy of the feature. Only generic properties are copied.
   */


  clone() {
    return new CombinedPolyline(this.rings, {
      crs: this.crs,
      symbol: this.originalSymbol
    });
  }

  projectTo(crs) {
    let projected = projectRings(this.rings, this.crs, crs);
    return new CombinedPolyline(projected, {
      crs: this.crs,
      symbol: this.symbol,
      persistOnMap: this.persistOnMap
    });
  }

}

/**
 * Symbol of polyline drawn as simple line.
 * @alias sGis.symbol.polyline.Combined
 */

class CombinedPolylineSymbol extends Symbol {
  /**
   * @param options - key-value list of the properties to be assigned to the instance.
   */
  constructor(options = {}) {
    super();
    /** Values of the symbol. */

    this.values = [];
    Object.assign(this, options);
  }

  renderFunction(feature, resolution, crs) {
    const result = [];
    if (!(feature instanceof CombinedPolyline)) return result;
    const coordinates = this.getRenderedCoordinates(feature, resolution, crs);
    this.values.forEach(polyline => {
      result.push(new PolyRender(coordinates, {
        fillStyle: FillStyle.Color,
        enclosed: false,
        strokeColor: polyline.stroke.color,
        strokeWidth: getParameterValue(polyline.stroke.width),
        lineDash: getParameterValue(polyline.stroke.intervals),
        shadow: null,
        lineCap: polyline.lineCap,
        lineJoin: polyline.lineJoin,
        miterLimit: polyline.miterLimit
      }));
    });
    return result;
  }
  /**
   * Projects coordinates of a poly feature to the requested crs and resolution.
   * @param feature
   * @param resolution
   * @param crs
   * @param tolerance
   */


  getRenderedCoordinates(feature, resolution, crs, tolerance) {
    let result;

    if (this.previousPoly === feature && this.previousCrs.equals(crs) && this.previousResolution === resolution && feature.lastAction && feature.lastAction.type !== Action.removePoint) {
      const lastAction = feature.lastAction;
      let newResult = this.previousSimplified;

      if (lastAction.type === Action.movePoint) {
        const newSimplifiedRing = simplifyCoordinates([feature.rings[lastAction.ringIndex]].map(ring => {
          return ring.map(point => {
            return [Math.round(point[0] / resolution), Math.round(point[1] / -resolution)];
          });
        }), tolerance);
        newResult[lastAction.ringIndex] = newSimplifiedRing[0];
      }

      if (lastAction.type === Action.addPoint) {
        const renderCoordinates = [Math.round(lastAction.coordinates[0] / resolution), Math.round(lastAction.coordinates[1] / -resolution)];
        newResult[lastAction.ringIndex].push(renderCoordinates);
      }

      result = newResult;
    } else {
      result = getRenderedPolygonCoordinates(feature, resolution, crs, tolerance);
    }

    this.previousSimplified = result;
    this.previousPoly = feature;
    this.previousCrs = crs;
    this.previousResolution = resolution;
    feature.lastAction = null;
    return result;
  }

}
registerSymbol(CombinedPolylineSymbol, 'polyline.Combined', ['values']);

/**
 * Convenience method for simple map initialization
 * @param position
 * @param centerPoint
 * @param resolution
 * @param crs
 * @param layers
 * @param wrapper
 * @param pluginDefinitions
 * @example init_Setting_position_and_resolution
 */

const init = function ({
  position = undefined,
  centerPoint = undefined,
  resolution = undefined,
  crs = undefined,
  layers = undefined,
  wrapper = undefined,
  pluginDefinitions = []
}) {
  let map = new Map$1({
    crs,
    position,
    centerPoint,
    resolution,
    layers
  });
  let painter = new DomPainter(map, {
    wrapper
  });
  let p = pluginDefinitions.map(pluginDefinition => {
    let name = pluginDefinition.name;

    if (!plugins[name]) {
      throw new Error(`Plugin ${name} is not available.`);
    }

    return new plugins[name](map, painter, pluginDefinition.properties);
  });
  return {
    map,
    painter,
    p
  };
};
const plugins = {
  registerPlugin(name, Constructor) {
    if (plugins[name]) {
      warn(`Plugin with name ${name} already registered. Skipping.`);
    } else {
      plugins[name] = Constructor;
    }
  }

};

const math = mathModule;
const geotools = geotoolsModule;
const event = eventModule;
const version = "0.5.0-alpha.68";
const releaseDate = "27.04.2024";
let utilsModulesExt = {};
Object.assign(utilsModulesExt, utilsModule, {
  Color: Color$1
});
const controls = {
  Circle: Circle,
  Editor: Editor,
  MultiPoint: MultiPoint,
  Point: PointControl,
  PointEditor: PointEditor,
  Poly: PolyControl,
  PolyDrag: PolyDrag,
  PolyEditor: PolyEditor,
  Polyline: PolylineControl,
  Polygon: PolygonControl,
  PolyTransform: PolyTransform,
  Rectangle: Rectangle,
  snapping: {
    CombinedSnappingProvider: CombinedSnappingProvider
  },
  BalloonControl: BalloonControl
};
const Crs = Crs$1;
const CRS = CrsModule;
const feature = {
  MultiPoint: MultiPoint,
  Point: PointFeature,
  Poly: Poly,
  Polygon: Polygon,
  Polyline: Polyline,
  CombinedPolyline: CombinedPolyline,
  Label: LabelFeature,
  Balloon: Balloon
};
const painter = {
  DomPainter: DomPainter,
  domPainter: {
    Container: Container,
    EventDispatcher: EventDispatcher,
    SvgRender: SvgRender,
    LayerRenderer: LayerRenderer,
    Canvas: Canvas
  }
};
const render = {
  Arc: Arc,
  HtmlElement: HTMLElement,
  Point: Point,
  Polygon: PolyRender
};
const symbol = {
  point: {
    Image: StaticImageSymbol,
    MaskedImage: MaskedImage,
    Point: PointSymbol,
    Square: SquareSymbol
  },
  polygon: {
    BrushFill: BrushFill,
    ImageFill: ImageFill,
    Simple: PolygonSymbol
  },
  polyline: {
    Simple: PolylineSymbol,
    Combined: CombinedPolylineSymbol
  },
  Editor: EditorSymbol,
  label: {
    DynamicLabelSymbol: DynamicLabelSymbol,
    StaticLabelSymbol: StaticLabelSymbol
  },
  Balloon: BalloonSymbol
};
const utils = utilsModulesExt;
const serializer = {
  symbolSerializer: symbolSerializer
};

export { Bbox, CRS, Control, Crs, DynamicLayer, EventHandler, Feature, FeatureLayer, Layer, LayerGroup, Map$1 as Map, Point$1 as Point, Symbol, TileLayer, TileScheme, controls, event, feature, geotools, init, math, painter, releaseDate, render, serializer, symbol, utils, version };
//# sourceMappingURL=sGis.esm.js.map
